/**
 * NewJourneys — the unified Journey designer (route `newjourneys`, rail
 * "New journeys"). The UX is the Gold Master prototype `New Journeys.dc.html`
 * (phases 2–5) shipped as designed: journeys list (stats, search, filters,
 * sort, list/cards, bulk bar), the Pages + Flow designer, the Experience
 * Designer, the template picker and the page-type registry picker. Every style
 * literal below is the prototype's — the operator ruled this surface does NOT
 * use the design system.
 *
 * What is REAL here (2026-08-19 drop):
 *  • List rows are DERIVED from the tenant's own objects — queues (listQueues),
 *    events (useEvents) and the appointment catalogue (getServiceCatalogue).
 *    Opening a row edits that object's journey; the row links back to the
 *    object's own screen.
 *  • Attachments are PICKED from the real libraries: question groups
 *    (useQuestions), surveys (listSurveys), messages (useMessaging), coupons
 *    (useDiscounts), ticket groups (useTicketGroups). Every picker also links
 *    to the canonical editor (plan ruling ④).
 *  • Appointment slots come from the journey availability op
 *    (useJourneyAvailability) — never invented; an empty range says so.
 *  • Edits persist in two places, by ruling: the fields the OWNING object really
 *    has go onto that object (a queue takes name / hero image / services through
 *    `updateQueue`, so a designer rename shows up in the queue console), and
 *    everything with no column anywhere — pages, blocks, flow, attachments,
 *    experiences — goes through `newjourneys-api`, the single seam for the six
 *    ops requested in CD-API-REQUEST-newjourneys-documents-2026-08-19.md. Until
 *    that contract lands the seam is a per-tenant local stub, and the surface
 *    says which half went where (see nj-writeback).
 *
 * Legacy queue / event / appointment editors and the old Journeys rail item are
 * untouched (ruling ⑤).
 */
import { t as i18nT, currentLocale } from "../../lib/i18n";
import { useTenantChrome } from "../../lib/branded-chrome";
import { ResolvesHoldsPicker, holdsResolvesLabel, readResolvesHolds } from "../../lib/holds";
import { AudienceSection, audienceChanged, audienceErrorsFrom, readAudience, type AudienceErrors } from "../../lib/audience";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type CSSProperties, type MouseEvent as ReactMouseEvent } from "react";
import { Screen } from "../../shell/chrome";
import { toast as shellToast } from "../../shell/toast";
import {
  useEditorHistory, EditorHistoryButtons, useCanvasWidth, CanvasColumn, CanvasWidthBar, CanvasEdge,
  SavedBadge, useDragAutoScroll,
} from "../shared/editor-shell";
import { OverlayPortal } from "../../shell/Overlay";
import { useViewport } from "../../shell/useResponsive";
import { ErrorBoundary } from "../../shell/ErrorBoundary";
import { Designer, landingDraftSeed } from "./AllJourneys";
import { projectPublicEventToDesignerRow } from "../../lib/events-public-data";
import { RowActions } from "../../shell/composites";
import { useFolderLibrary, FolderShellFrame, FolderShellBars, FolderShellOverlays } from "../shared/library-shell";
import { journeysSectionTabs } from "../../shell/list-composites";
import { useNav } from "../../shell/router";
import { useTenant, getUser, can } from "../../shell/access";
import { moduleKnownAbsent, useTenantModules } from "../../lib/tenant-modules";
import { useApprovals, APPROVALS_COMING_SOON } from "../approvals/approvals-store";
import { getServiceCatalogue, type ServiceCatalogue } from "../workforce/wf-api";
import { listQueues } from "../queues/queues-api";
import { useEvents } from "../events/useEvents";
import { useQuestions } from "../questions/useQuestions";
import { useMessaging } from "../messaging/useMessaging";
import { useFlows, flowStats } from "../messaging/useFlows";
import { useDiscounts } from "../promos/useDiscounts";
import { useStaff } from "../staff/useStaff";
import { useResources } from "../resources/useResources";
import { useServices, ServicesStore } from "../services/services-data";
import {
  listQueueTypes, listMessageBoards, getQueueBoard, sendQueueMessage, broadcastQueueMessage, mintBoardDisplayUrl, newIdempotencyKey,
  type QueueType, type QueueSummary, type MessageBoard, type QueueEntry, type QueueBoard as QueueBoardState,
} from "../queues/queues-api";
import { Q_DAYS, TIME_OPTS, hmLabel12, qDayLabel, qHoursLabel, zoneLabel } from "../queues/queue-hours";
import { BOARD_BLOCKS, BoardQueueCard, boardColors, boardPublicUrl, boardBlockUid, defaultBoardBlocks, useBoardPager, type BoardBlockRec, type BoardNameFormat } from "../queues/board-blocks";
import { CatalogPicker, resolveSelection, describeSelection, type PickerItem, type PickerGroup, type PickerSel } from "./catalog-picker";
import { useTenantPublicConfig, publicUrlForTitle } from "../../lib/public-url";
import { queueJoinUrl } from "../queues/queue-join-url";
import { copyText } from "../../lib/copy-text";
import { QrCode, downloadQrPng } from "../../shell/QrCode";
import { NumberField } from "../../lib/NumberField";
/* Tickets step on the Tickets v2 module (CC 0195): the old ticket-types / ticket-groups stores are not used here. */
import { useV2Tickets, v2Money } from "./nj-tickets-v2";
import { listSurveys, validateAddress, suggestAddresses, listGuests, listGuestSegments, messageGuest, apiRaw as njApiRaw } from "@v0y4ge/api-client";
import { useLocations } from "../locations/useLocations";
import { studioRequestOptions } from "../../lib/studio-config";
import { tzShortLabel } from "../../lib/datetime";
import { REPEAT_LABELS_ALL } from "../events/event-config-types";
import { useJourneyAvailability } from "./availability-data";
import { listJourneyStore, putJourneyDoc, deleteJourneyDoc, putExperience, deleteExperience, NJ_STUBBED } from "./newjourneys-api";
import { writeBackDoc, WRITES_BACK, OWNER_NOUN } from "./nj-writeback";
import { stampSave, createdCell, modifiedCell, currentActor, type AuditCell } from "./nj-audit";
import { attachJourney, needsAttach, attachBlockers, ATTACH_NOUN, publishJourneyPages, formatFromMethod, completeTicketTypeConfigs, holdEventId, createFailureKind, type AttachResult } from "./nj-attach";
import {
  loadEventDoc, eventReadiness, patchEventBasics, patchEventDetails, patchEventSchedule,
  patchEventTickets, patchEventRegistration, patchEventGuestList, patchEventMessaging, publishEventRecord,
  takeEventRecordOffline, cancelEventRecord, isEventLiveLocked, sameEventFormat, allDayEnd, sendableEnd, EVENT_LIVE_LOCKED_KEYS, liveCapacityAllowed,
  patchQueueConfig, setQueueLifecycle, queueReadiness, saveBoardConfig, deleteBoardConfig, boardReadiness,
  type GuestEntry,
} from "./nj-binding";
import { Input, Toggle, Dropdown, ChoiceTile, DatePicker, TimeZonePicker, Icon, Spinner, Button, StatusPill, MoreMenu, Banner, DevicePreviewToggle } from "@v0y4ge/jrni-ui";
import { LoadingRegion, GhostFormFields, GhostFramedJourney, SkeletonBox } from "../../lib/loading-skeletons";
import { scheduleEndState } from "../events/creation-store";
import { JourneyFlowBuilder } from "./flow/JourneyFlowBuilder";
import { OSRail, OSCanvas, osLabel, osSeedConfig } from "./onestop-canvas";
import { BlockCanvas, BLOCK_ADD_DND, BLOCK_MOVE_DND } from "./block-canvas";
import { OneStopInspector, type OneStopLibs } from "./onestop-inspector";
import { oneStopSkeletonBlocks } from "./onestop-seed";
import { mintBlockId, slugFromTitle } from "./onestop-page-api";
import { withPreviewData } from "./onestop-preview-data";
import { tzContext } from "../../lib/datetime";
import {
  qfVocabFor, qfFlatten, qfMapAll,
  qfMkPage, qfMkComms, qfMkSurvey, qfMkWait, qfMkDecision, qfMkExit,
  type QFFlow, type QFStep,
} from "./flow/jf-model";
import { journeyPages } from "./flow/journey-pages";
import { blockVariant } from "../shared/block-registry";
import {
  ApptSetupPanel, apptReadiness, APPT_SETUP_LABEL, APPT_SETUP_DEFAULTS,
  type ApptView, type ApptLibs,
} from "./appt-setup";
import { njFlowOf } from "./flow/nj-flow-migrate";
import {
  REG, regOf, INV, blocksFor, CDEF, NETS, BLOCK_DEF, BLOCK_CFG, CORE_DEF, CORE_CFG,
  mapContent, findContentBlock, addToCell, blockCells,
  ATTACH_TYPES, LIB_META, TYPE_TINT, TYPE_GLYPH, HUB_VERB, docType, njUid, mkPage, hoistHero,
  docFromQueue, docFromEvent, docFromApptGroup, docFromBoard, templateDoc,
  stepNavFor, COMMIT_LABEL_BY_TYPE,
  type Doc, type NJPage, type Block, type Exp, type CfgRow, type EventLike,
  type QueueConfigLike, type BoardConfigLike,
} from "./nj-model";
/* The journey END PAGE (stage 3, 2026-09-01). The registry and the state ops
   are in confirmation-sections.ts; the rail, the canvas and the inspector scope
   are in confirmation-page.tsx — this screen owns neither, it only supplies the
   sample booking and the patcher. NOT the booking confirmation page, which is a
   different feature living in ConfirmationPages.tsx. */
import { csMigrate, csToggle, csTypeOf, csAt, type CSState } from "./confirmation-sections";
import { CSRail, CSCanvas, CSInspector, type CSBooking } from "./confirmation-page";

import { useT } from "@v0y4ge/i18n-runtime";

/** The Setup tab's registration-window dropdowns stay hidden until the relative window fields exist (CC 0137). */
const REGISTRATION_WINDOW_ENABLED = false;

/* ── Showing stored values ───────────────────────────────────────────
 * Block option values (Left, Primary, Wide 16:9…) are stored in English and
 * compared as-is, so they never change; this is only what each one shows.
 * A value missing here (a page label, a ratio) shows as stored. */
const NJ_CFG_OPTION: Record<string, () => string> = {
  "Left": () => i18nT("journeys.newJourneys.cfgOption.left", "Left"),
  "Center": () => i18nT("journeys.newJourneys.cfgOption.center", "Center"),
  "Right": () => i18nT("journeys.newJourneys.cfgOption.right", "Right"),
  "Small": () => i18nT("journeys.newJourneys.cfgOption.small", "Small"),
  "Body": () => i18nT("journeys.newJourneys.cfgOption.body", "Body"),
  "Large": () => i18nT("journeys.newJourneys.cfgOption.large", "Large"),
  "Neutral": () => i18nT("journeys.newJourneys.cfgOption.neutral", "Neutral"),
  "Info": () => i18nT("journeys.newJourneys.cfgOption.info", "Info"),
  "Warning": () => i18nT("journeys.newJourneys.cfgOption.warning", "Warning"),
  "Success": () => i18nT("journeys.newJourneys.cfgOption.success", "Success"),
  "Primary": () => i18nT("journeys.newJourneys.cfgOption.primary", "Primary"),
  "Secondary": () => i18nT("journeys.newJourneys.cfgOption.secondary", "Secondary"),
  "Text link": () => i18nT("journeys.newJourneys.cfgOption.textLink", "Text link"),
  "Wide 16:9": () => i18nT("journeys.newJourneys.cfgOption.wide", "Wide 16:9"),
  "Square": () => i18nT("journeys.newJourneys.cfgOption.square", "Square"),
  "Tall 4:5": () => i18nT("journeys.newJourneys.cfgOption.tall45", "Tall 4:5"),
  "Banner 3:1": () => i18nT("journeys.newJourneys.cfgOption.banner", "Banner 3:1"),
  "Gradient": () => i18nT("journeys.newJourneys.cfgOption.gradient", "Gradient"),
  "Solid brand": () => i18nT("journeys.newJourneys.cfgOption.solidBrand", "Solid brand"),
  "Photo": () => i18nT("journeys.newJourneys.cfgOption.photo", "Photo"),
  "Compact": () => i18nT("journeys.newJourneys.cfgOption.compact", "Compact"),
  "Standard": () => i18nT("journeys.newJourneys.cfgOption.standard", "Standard"),
  "Tall": () => i18nT("journeys.newJourneys.cfgOption.tall", "Tall"),
  "Grid": () => i18nT("journeys.newJourneys.cfgOption.grid", "Grid"),
  "Carousel": () => i18nT("journeys.newJourneys.cfgOption.carousel", "Carousel"),
  "Building": () => i18nT("journeys.newJourneys.cfgOption.building", "Building"),
  "Campus": () => i18nT("journeys.newJourneys.cfgOption.campus", "Campus"),
  "City": () => i18nT("journeys.newJourneys.cfgOption.city", "City"),
  "Hours / min / sec": () => i18nT("journeys.newJourneys.cfgOption.hoursMinSec", "Hours / min / sec"),
  "Days / hours / min": () => i18nT("journeys.newJourneys.cfgOption.daysHoursMin", "Days / hours / min"),
  "Inline": () => i18nT("journeys.newJourneys.cfgOption.inline", "Inline"),
  "Card": () => i18nT("journeys.newJourneys.cfgOption.card", "Card"),
  "Solid": () => i18nT("journeys.newJourneys.cfgOption.solid", "Solid"),
  "Dashed": () => i18nT("journeys.newJourneys.cfgOption.dashed", "Dashed"),
  "Dotted": () => i18nT("journeys.newJourneys.cfgOption.dotted", "Dotted"),
  "Tight": () => i18nT("journeys.newJourneys.cfgOption.tight", "Tight"),
  "Normal": () => i18nT("journeys.newJourneys.cfgOption.normal", "Normal"),
  "Loose": () => i18nT("journeys.newJourneys.cfgOption.loose", "Loose"),
  "Medium": () => i18nT("journeys.newJourneys.cfgOption.medium", "Medium"),
  "URL": () => i18nT("journeys.newJourneys.cfgOption.url", "URL"),
  "Content page": () => i18nT("journeys.newJourneys.cfgOption.contentPage", "Content page"),
  "Pick a page…": () => i18nT("journeys.newJourneys.cfgOption.pickPage", "Pick a page…"),
};
function njCfgOptionText(value: string): string {
  const text = NJ_CFG_OPTION[value];
  return text ? text() : value;
}
/** attachBlockers() returns these English fragments; this is what each shows. */
function njBlockerText(blocker: string): string {
  return blocker === "a name" ? i18nT("journeys.newJourneys.blocker.name", "a name") // ui-config: compared with attachBlockers' values
    : blocker === "a location" ? i18nT("journeys.newJourneys.blocker.location", "a location") // ui-config: compared with attachBlockers' values
    : blocker;
}
/** "a, b and c" in the viewer's language. */
function njListAnd(items: string[]): string {
  try { return new Intl.ListFormat(currentLocale(), { type: "conjunction" }).format(items); }
  catch { return items.join(", "); }
}
/* ── Prototype style literals ───────────────────────────────────────── */
const FONT = 'var(--jrni-font-family-sans, "DM Sans", "Helvetica Neue", sans-serif)';
const SEC = { fontSize: 10, fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase" as const, color: "#697180", marginBottom: 8 };
const FIELD = { border: "1px solid #D1D4DC", borderRadius: 8, padding: "9px 11px", fontSize: 12, color: "#B5BAC6" };
const INPUT = { width: "100%", boxSizing: "border-box" as const, border: "1px solid #D1D4DC", borderRadius: 6, padding: "8px 10px", fontSize: 12.5, fontFamily: FONT, color: "#333333" };
const NOTE = { border: "1px solid #DCCCFC", background: "var(--jrni-color-primary-4, #F6F2FE)", borderRadius: 8, padding: "8px 10px", fontSize: 10.5, lineHeight: 1.5, color: "var(--jrni-color-primary-text, #5142C7)" };
const TZ_OPTIONS = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Europe/London", "Europe/Paris", "Europe/Zurich", "Asia/Singapore", "Australia/Sydney", "UTC"];
const WARN = { border: "1px solid #F3D9A4", background: "#FFFAED", borderRadius: 8, padding: "9px 11px", fontSize: 11, lineHeight: 1.55, color: "#916D0F" };
const MONO = { fontFamily: "ui-monospace, monospace" };
const glyphBox = (tint: string, color: string, size = 30, fs = 13) => ({ width: size, height: size, borderRadius: size > 30 ? 9 : 8, background: tint, color, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: fs, flex: "none" } as const);
/* The designer header is a COMPACT TOOLBAR, so every control in it sits at the
   design system's dense control height (T.control.sm = 32) — jrni-ui's own
   compact tier, the same one Btn size="sm", Input size="sm" and every select
   trigger use. It was five different heights (28 back, 28 page nav, ~30 the two
   segmented strips, 34 Preview, 40 the Btn-default Undo/Redo), which is what
   made the bar read as misaligned rather than dense. */
const HDR_H = 32;
const PRIMARY = { height: 34, padding: "0 16px", border: "none", borderRadius: 8, background: "var(--jrni-color-primary-text, #5142C7)", color: "#FFFFFF", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" } as const;
const GHOST = { height: 34, padding: "0 14px", border: "1px solid #D1D4DC", borderRadius: 8, background: "#FFFFFF", color: "#333333", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" } as const;

/* One template for the list header AND its rows — they must not drift.
   The name track carries a floor and the widest fr share so it can't be
   squeezed out (its children are nowrap+ellipsis, so a bare `1fr` collapses
   to nothing at narrow widths); the secondary columns are minmax so they give
   their slack back to the name instead of holding fixed width. */
/* Grid: check · Journey · Type · Status · Pages · Created by · Updated · Actions.
   "Created by" (2026-08-27) sits beside Updated so the two halves of the record's
   history read together; both are minmax so they give slack back to the name. */
const LIST_COLS = "34px minmax(190px,2.6fr) minmax(76px,1fr) minmax(68px,0.9fr) 40px minmax(96px,1.1fr) minmax(96px,1fr) 140px";

function ensureFont() {
  if (document.getElementById("nj-dm-sans")) return;
  const l = document.createElement("link");
  l.id = "nj-dm-sans"; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700;9..40,800&display=swap";
  document.head.appendChild(l);
}

const Switch = ({ on, onClick }: { on: boolean; onClick: (e: any) => void }) => (
  <button onClick={onClick} style={{ width: 30, height: 17, borderRadius: 999, border: "none", background: on ? "var(--jrni-color-primary-text, #5142C7)" : "#D1D4DC", position: "relative", cursor: "pointer", flex: "none", padding: 0 }}>
    <span style={{ position: "absolute", top: 2, left: on ? 15 : 2, width: 13, height: 13, borderRadius: 999, background: "#FFFFFF", transition: "left 120ms" }} />
  </button>
);

type Route = "home" | "designer" | "experience";
type LibKind = "questions" | "survey" | "promo" | "message" | "tickets";

/** Measure a box with a CALLBACK ref rather than an effect (2026-09-02).
 *  An effect keyed on "the overlay is open" fires before a React portal's own
 *  container effect has run, so `ref.current` is still null, the observer is
 *  never attached, and the measurement stays at its initial zero for the life of
 *  the overlay — which left the Preview board painting at scale(1) and cropped
 *  on all four sides. A callback ref runs when the node actually arrives. */
function useFitBox() {
  const [fit, setFit] = useState({ w: 0, h: 0 });
  const roRef = useRef<ResizeObserver | null>(null);
  const attach = useCallback((node: HTMLDivElement | null) => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (!node) { setFit({ w: 0, h: 0 }); return; }
    const read = () => { const r = node.getBoundingClientRect(); if (r.width && r.height) setFit({ w: r.width, h: r.height }); };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(node);
    roRef.current = ro;
  }, []);
  return { fit, attach };
}

/** How many columns a board splits its queues across (2026-09-02, operator).
 *  Kept DELIBERATELY few: card height is a function of column width, so more
 *  columns is not a way to fit more queues — narrowing cards to 226px wrapped
 *  their block content and tripled their height. Wide cards stay short, and the
 *  queues that do not fit on one screen cycle instead (see useBoardPager). */
function boardColumnsFor(n: number, landscape: boolean) {
  if (n <= 0) return 1;
  if (landscape) return Math.min(n, 3);
  return n <= 6 ? 1 : 2;
}

/* Event Setup preview (2026-10-05): one customer-page row from the draft record, cached by
   content so the preview mount is handed a new array only when the event actually changed. */
let _njPvKey = "";
let _njPvRows: unknown[] = [];
const _njPvSeed = landingDraftSeed();
/* Preview frames (2026-10-05): native size each device is drawn at, then scaled to the column. */
const NJ_PV_BRAND_DEFAULT = "#5142C7";
const NJ_PV_DIMS = { desktop: { w: 1280, h: 800 }, tablet: { w: 1180, h: 820 }, mobile: { w: 393, h: 852 } } as const;
type NjPvDevice = keyof typeof NJ_PV_DIMS;
const NJ_PV_COL_DEFAULT: Record<NjPvDevice, number> = { desktop: 640, tablet: 600, mobile: 426 };
const njPvRead = (k: string) => { try { return window.localStorage.getItem(k); } catch { return null; } };
const njPvWrite = (k: string, v: string) => { try { window.localStorage.setItem(k, v); } catch { /* storage off */ } };
function njPreviewRows(row: Record<string, unknown>, brand: string): unknown[] {
  const key = JSON.stringify(row) + "|" + brand;
  if (key !== _njPvKey) { _njPvKey = key; _njPvRows = [projectPublicEventToDesignerRow(row as any, brand)]; }
  return _njPvRows;
}

/** One quiet retry when the platform says it is starting up (SERVICE_WARMING), after its
 *  Retry-After (1-5 s). Any other error, or a second warming answer, is thrown as is. */
async function retryWarming<T>(fn: () => Promise<T>): Promise<T> {
  try { return await fn(); }
  catch (e) {
    const err = e as { code?: unknown; retryAfterSeconds?: unknown } | null;
    if (err?.code !== "SERVICE_WARMING") throw e;
    const secs = typeof err.retryAfterSeconds === "number" ? err.retryAfterSeconds : 2;
    await new Promise((r) => setTimeout(r, Math.min(5, Math.max(1, secs)) * 1000));
    return fn();
  }
}

export default function NewJourneys() {
  /* The workspace brand from the Studio Branding record, so the Event Setup preview is drawn the way
     customers see the public page (2026-10-08). Loading or unreadable: the default colour stays. */
  const njChrome = useTenantChrome(true);
  const t = useT();
  /* Hold categories and the SIS-attribute audience come from the student portal (CC, 2026-10-04). On a
     tenant KNOWN not to compose it they are not shown; an unknown module list keeps today's screen. */
  const tenantMods = useTenantModules();
  const portalAbsent = moduleKnownAbsent("portal", tenantMods);
  /* A tenant KNOWN not to compose the queue module is not asked for queues, queue types or boards:
     those routes would be refused. An unknown module list keeps today's reads. */
  const queueAbsent = moduleKnownAbsent("queue", tenantMods);
  /** The owner / attach nouns as ICU selects (the maps in nj-writeback / nj-attach stay English data). */
  const njOwnerNoun = (kind?: string | null) => t("journeys.newJourneys.ownerNoun", "{kind, select, queue {queue} event {event} onestop {One-Stop page} other {object}}", { kind: kind === "queue" || kind === "event" || kind === "onestop" ? kind : "other" });
  const NJ_ATTACH_KIND: Record<string, string> = { Event: "event", Queue: "queue", Board: "board", Appointment: "appointment", "One-Stop": "onestop", Experience: "experience" };
  const njTypeName = (ty: string) => t("journeys.newJourneys.typeName", "{ty, select, Queue {Queue} Event {Event} Appointment {Appointment} Board {Board} Experience {Experience} Mixed {Mixed} Pages {Pages} OneStop {One-Stop} other {{ty}}}", { ty: ty === "One-Stop" ? "OneStop" : ty });
  /** What a list row's status pill shows; the row's `status` stays the English identity the filters read. */
  const njStatusText = (status: string, edited: boolean) => t("journeys.newJourneys.listStatus", "{edited, select, yes {{status, select, Live {Live · edited} Draft {Draft · edited} Cancelled {Canceled · edited} other {{status} · edited}}} other {{status, select, Live {Live} Draft {Draft} Cancelled {Cancelled} other {{status}}}}}", { status, edited: edited ? "yes" : "no" });
  const njAttachNoun = (type?: string | null) => t("journeys.newJourneys.attachNoun", "{kind, select, event {event} queue {queue} board {display board} appointment {line of business} onestop {One-Stop page} experience {experience} other {record}}", { kind: NJ_ATTACH_KIND[type ?? ""] ?? "other" });
  const nav = useNav();
  /* Desktop gets the Events-tab layout for event Setup (2026-10-05): a sectioned left rail. */
  const njDesktop = useViewport() === "desktop";
  /* The preview column needs room for three columns; below 1100px it steps aside (was 1280 —
     a 1230px window lost the preview entirely, 2026-10-05). */
  const [njWide, setNjWide] = useState(() => typeof window === "undefined" || window.innerWidth >= 1100);
  useEffect(() => { const on = () => setNjWide(window.innerWidth >= 1100); window.addEventListener("resize", on); return () => window.removeEventListener("resize", on); }, []);
  /* Preview device + column width (2026-10-05): Desktop / Tablet (landscape) / Mobile, and a
     draggable column edge. Both remembered on this browser. */
  const [pvDevice, setPvDeviceRaw] = useState<NjPvDevice>(() => { const v = njPvRead("nj.preview.device"); return v === "desktop" || v === "tablet" || v === "mobile" ? v : "mobile"; });
  const [pvColW, setPvColW] = useState<number>(() => { const n = Number(njPvRead("nj.preview.colW")); return Number.isFinite(n) && n >= 320 ? n : NJ_PV_COL_DEFAULT.mobile; });
  const setPvDevice = (d: NjPvDevice) => { setPvDeviceRaw(d); njPvWrite("nj.preview.device", d); const w = NJ_PV_COL_DEFAULT[d]; setPvColW(w); njPvWrite("nj.preview.colW", String(w)); };
  const [pvAvail, setPvAvail] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  /* No ResizeObserver (2026-10-05): v479/v480 froze the page with one. The box is measured on
     mount and on window resize only, and a state write happens only on a real (≥1px) change. */
  const pvBoxEl = useRef<HTMLDivElement | null>(null);
  const measurePvBox = useCallback(() => {
    const el = pvBoxEl.current; if (!el) return;
    const w = Math.max(0, Math.floor(el.clientWidth - 32));
    /* Never fall back to width-only: before layout settles, take the height from the window. */
    const h = Math.max(0, Math.floor(el.clientHeight > 0 ? el.clientHeight - 32 : window.innerHeight - el.getBoundingClientRect().top - 32));
    setPvAvail((p) => (Math.abs(p.w - w) < 1 && Math.abs(p.h - h) < 1 ? p : { w, h }));
  }, []);
  const pvBoxRef = useCallback((el: HTMLDivElement | null) => { pvBoxEl.current = el; if (el) { window.requestAnimationFrame(measurePvBox); window.setTimeout(measurePvBox, 150); window.setTimeout(measurePvBox, 600); } }, [measurePvBox]);
  useEffect(() => { window.addEventListener("resize", measurePvBox); return () => window.removeEventListener("resize", measurePvBox); }, [measurePvBox]);
  /* The box gets its height only once the editor has laid out; re-measure a few times after mount
     and after a device / width change (each write bails out unless the size really changed). */
  useEffect(() => { const ids = [0, 120, 400, 1000].map((ms) => window.setTimeout(measurePvBox, ms)); return () => ids.forEach((id) => window.clearTimeout(id)); }, [measurePvBox, pvDevice, pvColW]);
  /* …and after EVERY render of this screen (verifier, 2026-10-05): the box gets its real height when
     the editor finishes loading, which is a render, not a timer. Cheap — it bails unless the size
     changed, and the box's size never depends on the card, so it cannot loop. */
  useEffect(() => { measurePvBox(); });
  const pvColMax = () => Math.max(320, (typeof window === "undefined" ? 1440 : window.innerWidth) - 236 - 440);
  const startPvDrag = (e: ReactMouseEvent) => {
    e.preventDefault();
    const x0 = e.clientX, w0 = pvColW;
    let last = w0;
    const move = (ev: MouseEvent) => { last = Math.round(Math.min(pvColMax(), Math.max(320, w0 + (x0 - ev.clientX)))); setPvColW(last); };
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); document.body.style.cursor = ""; njPvWrite("nj.preview.colW", String(last)); window.requestAnimationFrame(measurePvBox); };
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };
  const tenant = useTenant() as any;
  const slug = tenant?.slug ?? "default";
  useEffect(ensureFont, []);

  /* ── Real objects ───────────────────────────────────────────────── */
  const [queues, setQueues] = useState<any[]>([]);
  const [queuesLoaded, setQueuesLoaded] = useState(false);
  useEffect(() => {
    let live = true;
    if (queueAbsent) { setQueuesLoaded(true); return () => { live = false; }; }
    /* Also gated on queues.read, the capability the route requires; a role without it is treated as "no queues" (CC, 2026-10-05). */
    if (!can("queues.read")) { setQueuesLoaded(true); return () => { live = false; }; }
    listQueues().then((p: any) => { if (live) { setQueues(p?.data ?? []); setQueuesLoaded(true); } }).catch(() => { if (live) setQueuesLoaded(true); });
    return () => { live = false; };
  }, [slug, queueAbsent]);
  const { events, loading: eventsLoading } = useEvents();
  const [cat, setCat] = useState<ServiceCatalogue | null>(null);
  /* services.read gates the catalogue read; without it `cat` stays null, the same as a failed read (CC, 2026-10-05). */
  useEffect(() => { let live = true; if (!can("services.read")) return () => { live = false; }; getServiceCatalogue().then((c) => { if (live) setCat(c); }).catch(() => {}); return () => { live = false; }; }, [slug]);

  const questions = useQuestions();
  const messaging = useMessaging() as any;
  const promos = useDiscounts() as any;
  /* Individual ticket types belong to the deprecated library (CC 0195): none are offered. */
  const allTicketTypes: any[] = [];
  const { members: staffMembers } = useStaff();
  const { resources: resourceRecs } = useResources();
  useServices();
  useTenantPublicConfig();
  const [queueTypes, setQueueTypes] = useState<QueueType[]>([]);
  const [boards, setBoards] = useState<MessageBoard[]>([]);
  const [boardsLoaded, setBoardsLoaded] = useState(false);
  useEffect(() => {
    let live = true;
    if (queueAbsent) { setBoardsLoaded(true); return () => { live = false; }; }
    listQueueTypes().then((r: any) => { if (live) setQueueTypes(r?.types ?? []); }).catch(() => {});
    listMessageBoards().then((r: any) => { if (live) { setBoards(r?.boards ?? r?.data ?? []); setBoardsLoaded(true); } }).catch(() => { if (live) setBoardsLoaded(true); });
    return () => { live = false; };
  }, [slug, queueAbsent]);
  const flows = useFlows();
  const allFlows = flows.all();
  const [surveys, setSurveys] = useState<any[]>([]);
  useEffect(() => {
    let live = true;
    /* `listSurveys` is absent from the stubbed api-client some preview builds
       bundle against — calling an undefined export would throw at mount. */
    if (typeof listSurveys !== "function") return;
    (listSurveys as any)({}).then((r: any) => { if (live) setSurveys(r?.surveys ?? r?.data ?? (Array.isArray(r) ? r : [])); }).catch(() => {});
    return () => { live = false; };
  }, [slug]);

  const qGroups = questions.groups() as any[];
  const messages = (messaging.all?.() ?? []) as any[];
  /* Two different incentives, and the library already knows it: a COUPON is a
     code the student enters, a PROMO applies automatically. They are separate
     tabs in the promos library and separate fields on a service or an event, so
     they are separated here rather than offered as one undifferentiated list. */
  const allDiscounts = (promos.all?.() ?? []) as any[];
  const coupons = allDiscounts.filter((d: any) => d.type !== "promo");
  const autoPromos = allDiscounts.filter((d: any) => d.type === "promo");

  /* ── Documents: derived from real objects, overlaid with saved edits ── */
  const derived = useMemo(() => {
    const out: Record<string, Doc> = {};
    /* `ownerFields` forwards whatever created/modified fields the owning record
       carries, so a derived row nobody has opened in the designer can still say
       who made the queue / event / catalogue entry it came from. No op returns
       them today (CD-API-REQUEST-journey-audit-2026-08-27) — the forward is
       deliberately blind so the column lights up the day they ship, with no
       further UI change; nj-audit ignores anything unparseable. */
    const ownerFields = (o: any) => ({
      createdAt: o?.createdAt ?? o?.created_at, createdBy: o?.createdBy ?? o?.created_by,
      updatedAt: o?.updatedAt ?? o?.updated_at, updatedBy: o?.updatedBy ?? o?.updated_by,
    });
    queues.forEach((q: any) => { const r = docFromQueue({ id: q.id, name: q.name, status: q.status, locationName: q.locationName, services: q.services, joinCode: q.joinCode, avgWaitMins: q.avgWaitMins, waiting: q.waiting, ...ownerFields(q) }); out[r.key] = r.doc; });
    (events ?? []).forEach((e: any) => { const r = docFromEvent({ id: e.id, title: e.title, status: e.status, date: e.date, time: e.time, location: e.location, ticketTypeIds: e.ticketTypeIds, isPaid: e.isPaid, description: e.description, bookingQuestions: e.bookingQuestions, surveys: e.surveys, ...ownerFields(e) }); out[r.key] = r.doc; });
    const bookable = ((cat?.services ?? []) as any[]).filter((s) => s.durationMin);
    const lobs = (cat?.lobs ?? []) as any[];
    if (lobs.length) {
      lobs.forEach((l) => {
        const ids = bookable.filter((s) => s.lob === l.id).map((s) => s.id);
        if (ids.length) { const r = docFromApptGroup({ id: l.id, name: l.name, serviceIds: ids, ...ownerFields(l) }); out[r.key] = r.doc; }
      });
    } else if (bookable.length) {
      const r = docFromApptGroup({ id: "all", name: t("journeys.newJourneys.defaultBookingName", "Booking"), serviceIds: bookable.map((s) => s.id) });
      out[r.key] = r.doc;
    }
    /* Display boards are journeys too (2026-08-24): one page, no customer
     * input, configuration in the boards library. */
    boards.forEach((b) => {
      const r = docFromBoard({
        id: b.id, name: b.name, queueIds: b.queueIds ?? [], theme: b.theme, message: b.message,
        queueNames: (b.queueIds ?? []).map((id) => queues.find((q: any) => q.id === id)?.name).filter(Boolean) as string[],
        /* `updatedAt: b.updatedAt` was here BEFORE the spread and was therefore
           dead — `ownerFields` sets `updatedAt` too and wins (2026-09-08).
           Removed rather than reordered: ownerFields' version is the one that
           has been running, and it also reads the snake_case form. */
        ...ownerFields(b),
      });
      out[r.key] = r.doc;
    });
    return out;
  }, [queues, events, cat, boards]);

  const [overlays, setOverlays] = useState<Record<string, Doc> | null>(null);
  const [exps, setExps] = useState<Record<string, Exp>>({});
  useEffect(() => {
    let live = true;
    /* Clear FIRST, synchronously, so a tenant switch cannot leak. The merge
       below keeps anything written since the read started — which is the fix —
       but "anything in state" must not include the previous tenant's documents,
       and this effect re-runs on `slug`. Replacing used to do this clearing as a
       side effect of throwing everything away. */
    setOverlays(null);
    setExps({});
    /* WATCHDOG (2026-09-17, PP). `overlays === null` is the loading condition,
       so the screen shows "Loading journeys…" for exactly as long as that
       promise is unsettled — and a promise that never settles is not covered by
       either the success path or the `.catch` below. Seen once in this session:
       the Journeys screen hung with no way in, while every endpoint it reads
       answered in under a second when probed by hand, so nothing was actually
       in flight. I could not reproduce it, and an unreproduced hang is exactly
       the case a catch cannot fix.

       So the fallback is time-based rather than outcome-based: after 8s with no
       answer, render from records alone. That is the same degraded state the
       `.catch` produces and it is always better than a spinner — the queues,
       events and catalogue rows do not come from this store, so the author gets
       a working screen with their designer edits missing rather than a screen
       they cannot enter. A late answer still merges in, because the resolve
       path keeps anything already in state. */
    const watchdog = window.setTimeout(() => {
      if (!live) return;
      setOverlays((prev) => {
        if (prev !== null) return prev;
        console.warn("[newjourneys] journey store did not answer within 8s — rendering journeys from records only");
        return {};
      });
    }, 8000);
    listJourneyStore(slug).then((env) => {
      window.clearTimeout(watchdog);
      if (!live) return;
      /* MERGE, do not replace (operator, 2026-09-02: a journey created in the
         designer did not appear in the list).
         This read starts on mount and resolves whenever the store answers. A
         journey created before it lands was written into `overlays` by
         `saveDoc` — and `setOverlays(env.documents)` threw it away, so the new
         row vanished from the list and the designer was editing a document
         nothing would ever show. Autosave was working; this discarded its
         result.
         Anything already in state WINS over the fetched copy: a write that has
         happened is newer than a read that started before it. The fetched set
         still fills in everything this session has not touched. */
      setOverlays((prev) => ({ ...((env.documents ?? {}) as Record<string, Doc>), ...(prev ?? {}) }));
      setExps((prev) => ({ ...((env.experiences ?? {}) as Record<string, Exp>), ...prev }));
    }).catch((storeErr) => {
      window.clearTimeout(watchdog);
      /* The overlay store is unreachable (2026-09-16). `overlays === null` IS
         the loading condition, so an unhandled rejection here left the whole
         Journeys screen on "Loading journeys from your queues, events and
         catalogue…" forever — no error, no list, no designer, no way in. Seen
         against a deployment answering 501 on the overlay store.

         Overlays are exactly that: designer edits layered ON TOP of real
         queues, events and catalogue entries. If the layer cannot be read the
         objects underneath still exist, so the honest fallback is "no overlays"
         and a list derived from the records — not an indefinite spinner.
         Unsaved-elsewhere edits are simply not shown; nothing is overwritten,
         because a write only happens on an actual edit.

         The error is LOGGED, not swallowed (CD, 2026-09-16): a store that is
         down otherwise looks identical to a tenant with no overlays — the same
         class of silence this catch was added to remove. The screen still
         degrades quietly for the AUTHOR, who has nothing to act on, but an
         engineer reading the console can tell the two apart. */
      console.warn("[newjourneys] overlay store unreachable — rendering journeys from records only:", storeErr);
      if (!live) return;
      setOverlays((prev) => prev ?? {});
    });
    return () => { live = false; window.clearTimeout(watchdog); };
  }, [slug]);

  /* An overlay replaces its derived row wholesale — with ONE exception: a
     document stored before author stamps existed (2026-08-27) has no audit
     block, and dropping the derived row's owner-sourced one would lose the only
     history that row can report. A designer block always wins. */
  /* An event opened by link (`journey=event:<id>`) that the events list does not
     carry (another scope, a later page, a failed list read) is read on its own and
     held here, under the list's rows (CC, 2026-10-06). Not an overlay: overlays are
     saved documents, and this one is only the record's derived document. */
  const [linkedDocs, setLinkedDocs] = useState<Record<string, Doc>>({});
  const docs = useMemo(() => {
    const merged: Record<string, Doc> = { ...linkedDocs, ...derived };
    Object.entries(overlays ?? {}).forEach(([k, o]) => {
      merged[k] = o.audit ? o : { ...o, audit: derived[k]?.audit };
    });
    return merged;
  }, [linkedDocs, derived, overlays]);
  const docKeys = Object.keys(docs);

  /* ── UI state ───────────────────────────────────────────────────── */
  /** Drag payload for palette → column drops (stage 4). A custom type rather
   *  than text/plain, so an unrelated text drag can't be read as a block. */
  /* Payload names come from block-canvas now — one definition, so a palette and
     a canvas cannot disagree about what a drag is carrying. */
  /** Drag payload for reordering a block that is ALREADY on the page. A second
   *  type rather than a flag inside the first, because the two drags accept
   *  different drops: a palette drag may land in a column, a move may not
   *  (a column's children are edited in place, and a move across that boundary
   *  would silently change which array owns the block). */

  const [ui, setUi] = useState({
    route: "home" as Route, curDoc: "", curExp: "" as string, fromExp: "" as string,
    view: "pages" as "pages" | "flow", cur: 0, sel: null as string | null, selStep: null as string | null,
    /** Selected SECTION on a confirmation page. Its own slot beside `sel`
     *  because a section is not a block: it has no uid in `content`, and the
     *  two inspectors are different scopes. */
    selSection: null as string | null,
    tplOpen: false, typePicker: null as { at: number | null } | null, addMenu: null as number | null,
    typesPicker: false,
    lib: null as LibKind | null, expPicker: null as null | "anchor" | "attach", expPickerTab: "all",
    simCalled: false, dragPg: null as number | null, dragOver: null as number | null,
    dragStep: null as string | null, dragOverStep: null as string | null,
    listQuery: "", listFilter: "all", listType: "all", listSort: "updated", listView: "list" as "list" | "grid", listSel: [] as string[],
    rowMenu: null as string | null, renameKey: "", renameVal: "",
    menuPos: null as null | { right: number; top: number | null; bottom: number | null },
    setupView: null as null | "basics" | "details" | "schedule" | "tickets" | "guests" | "messaging" | "publish"
      | "qsetup" | "qhours" | "qstaffing" | "qresources" | "qservices" | "boardcfg" | "onestopcfg"
      | "aservices" | "aplaces" | "awindow" | "aperservice" | "adetails" | "amessaging" | "registration",
    boardSel: null as string | null,
    /** How the lobby TV is MOUNTED (2026-09-02, operator). A board is always
     *  full screen, so the only real variable is the screen's orientation:
     *  landscape for a TV on a wall bracket, portrait for one turned on its
     *  side in an entrance. Landscape is the default because it is what almost
     *  every mounted display is. Designer-local — the published board reads the
     *  real screen, so this is a check that the layout holds, not a property of
     *  the board. */
    boardOrientation: "landscape" as "landscape" | "portrait",
    /** The editor pane. Open by default — an author who selected a block wants to
     *  edit it — and minimizable to a tab, because on a narrow window the page
     *  matters more than the form. */
    inspOpen: true,
    /** "‹containerUid›:‹cellIndex›" while a block is dragged over a column. */
    dropCell: null as string | null,
    /** Top-level insertion point while dragging — the index in the page's
     *  content that the dragged block would land at, or null when the pointer
     *  is not over the page body. */
    /** The block being dragged, so it can dim and so a drop onto its own
     *  position is a no-op rather than a pointless history entry. */
    /** Hovered block — drives the remove affordance. Selected OR hovered, so a
     *  block can be removed without selecting it first. */
    /** Sample service previewed in a page's runtime slot, remembered per page
     *  (operator ruling 2026-08-31) — page uid → catalogue service id. */
    sampleByPage: {} as Record<string, string>,
    viewportByPage: {} as Record<string, "desktop" | "mobile">,
    pagesOpen: false,
    /* Preview (2026-08-25) — the journey's pages as a customer sees them, with
       no authoring chrome. Its own device toggle, so entering preview never
       changes the width the author was editing at. */
    previewOpen: false,
    /* Preview-only viewport (2026-09-02, operator). Seeded from the page when
       preview opens, then independent: switching device in preview asks "how
       does this look on a phone", which is a question about the preview, not an
       edit to the page. It is never written back to `page.viewport`, so the
       canvas behind the scrim stays at whatever width the author was working
       at. */
    previewViewport: "desktop" as "desktop" | "mobile",
  });
  const set = (patch: Partial<typeof ui>) => setUi((u) => ({ ...u, ...patch }));
  /* The library modals are transient: leaving the Tickets tab or the designer
   * must not leave one mounted over whatever opens next. */
  useEffect(() => {
    if (ui.typesPicker && (ui.route !== "designer" || ui.setupView !== "tickets")) setUi((u) => ({ ...u, typesPicker: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.route, ui.setupView]);
  /* The editor overlay reserves its width only when the row can afford it.
     Measured with a ResizeObserver rather than a layout effect on every commit:
     a per-commit measure can OSCILLATE here, because the reservation changes the
     canvas pane's padding, which reflows its content, which can add or remove a
     scrollbar, which changes the measured width, which flips the reservation
     back — React error #185, maximum update depth. The observer only fires on a
     real box change, and the 12px tolerance below swallows a scrollbar's worth
     of jitter so the decision cannot chatter across its own threshold. */
  const [rowW, setRowW] = useState(0);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const rowRoRef = useRef<ResizeObserver | null>(null);
  const designerRowRefCb = useCallback((el: HTMLDivElement | null) => {
    if (rowRoRef.current) { rowRoRef.current.disconnect(); rowRoRef.current = null; }
    rowRef.current = el;
    if (!el) return;
    const read = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      /* Tolerance, not equality: without it a 15px scrollbar appearing is a
         width change, and the width change is what flips the reservation. */
      if (w) setRowW((prev) => (Math.abs(prev - w) < 12 ? prev : w));
    };
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(read);
      ro.observe(el);
      rowRoRef.current = ro;
    }
    read();
  }, []);
  useEffect(() => () => { if (rowRoRef.current) rowRoRef.current.disconnect(); }, []);
  const INSP_MAX = 420;
  /** Can the row afford the editor BESIDE the canvas? 420 is the floor — below
   *  it the preview stops being a preview. */
  const reserveInsp = rowW > 0 && rowW - 212 - 40 - INSP_MAX >= 420;
  /** Never wider than what leaves a readable strip of canvas beside it. */
  const inspW = rowW > 0 ? Math.max(260, Math.min(INSP_MAX, rowW - 212 - 300)) : INSP_MAX;
  /* Selecting a block opens the editor if it was minimized (operator,
   *  2026-09-02). Applies to every journey type: the designer's own blocks, a
   *  confirmation page's sections and a One-Stop page's blocks all set the same
   *  two slots.
   *
   *  This reverses an earlier call not to re-open on click, and the distinction
   *  is why. That call was about a stray click on the canvas; CHOOSING a block is
   *  a statement that you want to edit it, and an editor that stays shut then is
   *  a control that ignored you.
   *
   *  Keyed on the selection, NOT on `inspOpen`, which is what keeps the earlier
   *  concern intact: minimizing the panel while a block stays selected does not
   *  re-run this, so the panel stays shut until you select something ELSE. And
   *  DESELECTING never closes it — an author who cleared a selection has not
   *  asked for the panel to go away. */
  useEffect(() => {
    /* Only where the editor sits BESIDE the canvas. Where it overlays, opening
       on selection hides the very thing that was selected — and the canvas
       carries the per-block and per-section move/remove controls, so it would
       hide the controls the selection exists to reach. */
    if (!reserveInsp) return;
    if (ui.sel !== null || ui.selSection !== null) setUi((u) => (u.inspOpen ? u : { ...u, inspOpen: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.sel, ui.selSection, reserveInsp]);

  /* ONE toast for the whole product (2026-09-15, PP). The designer used to draw
     its own dark pill bottom-centre, so "Journey link copied" here looked
     nothing like the same message from the Queues list — two toast systems in one
     app. Every call site keeps its single-string signature; this maps it onto the
     shell toast every other screen uses. Tone comes from the words that already
     mean failure in these strings, and " — " already separates what happened from
     why, which is exactly title/subtitle. */
  /* The workspace's own zone (CC 0235 §2), read once from /v1/me/tenant-time-zone (any
     signed-in member). A new event's schedule defaults to its location's zone, else this,
     and only then the display zone. Null means the workspace has none configured. */
  const [tenantZone, setTenantZone] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    njApiRaw<{ timezone?: string | null; data?: { timezone?: string | null } }>("/v1/me/tenant-time-zone", undefined, "getTenantTimeZone")
      .then((r) => { const z = r?.data?.timezone ?? r?.timezone ?? null; if (alive && z) setTenantZone(z); })
      .catch(() => { /* no workspace zone: the display zone stays the fallback */ });
    return () => { alive = false; };
  }, []);
  /** The last failed create or publish, shown beside the header buttons until the next try (CC 0235).
   *  `action` is what "Try again" re-runs (CC 0250): "create" saves the draft again and never
   *  publishes; "publish" re-runs Publish. `doc` is the document it belongs to, so switching
   *  documents doesn't carry another document's error. */
  const [publishFail, setPublishFail] = useState<{ text: string; retry: boolean; action: "create" | "publish"; doc: string; check?: boolean } | null>(null);
  const showToast = (msg: string) => {
    const text = String(msg ?? "");
    const danger = /couldn't|cannot|can't|blocked|didn't|failed|unchanged by the failure/i.test(text);
    const cut = text.indexOf(" — ");
    const title = cut > 0 ? text.slice(0, cut) : text;
    const subtitle = cut > 0 ? text.slice(cut + 3) : undefined;
    shellToast({ tone: danger ? "danger" : "success", title, ...(subtitle ? { subtitle } : {}) });
  };

  /* ── Persistence (the one seam) ─────────────────────────────────── */
  /* Overlay first (it holds pages, flow and attachments — nothing else has a
     column for those), then push the fields the OWNING object really has onto
     that object: a queue takes name / hero / services through updateQueue, so a
     rename here shows up in the queue console. See nj-writeback for why an event
     and an appointment group can't take a write yet. */
  /* A journey created from a template or Add Event lives in memory until its first
     real change (CC 0122): opening the designer used to PUT an untitled draft before
     any edit, and every abandoned Add Event left one in the list. The first
     saveDoc on the key persists it. */
  const unsavedRef = useRef<Set<string>>(new Set());
  /* Resolves when the server has stored the document (CC 0285): most callers fire and forget, but the
     event create waits on it so the draft's event id is stored before the create is sent. */
  const saveDoc = (key: string, doc: Doc): Promise<void> => {
    unsavedRef.current.delete(key);
    /* Every write to a journey passes through here, so this is the one place the
       author stamps are minted: first save records the creator, every save
       records the last modifier (nj-audit). A derived row's owner-sourced block
       is replaced by a designer block the moment someone saves here — which is
       correct: from then on a person, not the owning record, is the last editor. */
    const stamped: Doc = { ...doc, audit: stampSave(doc.audit) };
    setOverlays((o) => ({ ...(o ?? {}), [key]: stamped }));
    const stored = putJourneyDoc(slug, key, stamped);
    if (!WRITES_BACK(stamped.origin?.kind)) return stored;
    void writeBackDoc(stamped).then((r) => { if (r.wrote.length) showToast(t("journeys.newJourneys.toast.savedOntoObject", "Saved onto the {noun} — {fields} updated on the object itself", { noun: njOwnerNoun(stamped.origin?.kind), fields: r.wrote.join(", ") })); });
    return stored;
  };
  /* Shut the editor when the row stops being able to afford it. Once, on the
     transition, so a deliberate open on a narrow row is not fought by this. */
  const affordedRef = useRef(true);
  useEffect(() => {
    if (rowW === 0) return;
    if (affordedRef.current && !reserveInsp) setUi((u) => (u.inspOpen ? { ...u, inspOpen: false } : u));
    affordedRef.current = reserveInsp;
  }, [reserveInsp, rowW]);
  /* Undo / redo (stage 3) — the shared editor shell's stack, over the DOCUMENT
     only: pages, blocks and flow. Setup writes to the event or queue RECORD
     through its own ops, which this stack cannot reverse, so it is DISABLED
     while a Setup view is open rather than silently reversing a block edit the
     operator made ten minutes ago in a different view (operator ruling). */
  const history = useEditorHistory<Doc>({
    disabled: !!ui.setupView,
    clone: (d) => JSON.parse(JSON.stringify(d)) as Doc,
  });
  const patchDoc = (fn: (d: Doc) => Doc) => {
    const cur = docs[ui.curDoc];
    if (!cur) return;
    history.push(cur);
    saveDoc(ui.curDoc, { ...fn(cur), edited: true });
  };
  /** Restoring is a plain save of the recovered document — it must NOT go
   *  through patchDoc, or undo would push the state it is replacing. */
  const restoreDoc = (next: Doc) => { if (ui.curDoc) saveDoc(ui.curDoc, { ...next, edited: true }); };
  const patchExp = (fn: (e: Exp) => Exp) => {
    const cur = exps[ui.curExp];
    if (!cur) return;
    const next = fn(cur);
    setExps((x) => ({ ...x, [ui.curExp]: next }));
    void putExperience(slug, ui.curExp, next);
  };

  /* ── Attach — mint the record this journey is about ────────────────
     Until this existed, a designer-created journey stayed a `local:` document
     forever: Setup values were held on it and never reached a record, and the
     wizard was the only surface in the app that could bring an event into
     existence (build plan stage 3's disclosed residual). One button per type,
     each calling that type's own create op — see nj-attach. */
  const [attachBusy, setAttachBusy] = useState(false);
  /* Events created in THIS designer session. Only these may write a guest list (CC, 2026-10-06):
     the guest-list route REPLACES the event's stored list and nothing reads it back, so an event
     opened from the server would show an empty list and a save would wipe its guests. */
  const createdEventIds = useRef<Set<string>>(new Set());
  const guestListManagedElsewhere = (id: string | null) => !!id && !createdEventIds.current.has(id);
  /* Live-ops message composer (event Publish view). Declared here rather than
     beside the view it drives: the component early-returns a loading shell
     above, so a hook declared further down runs on some renders and not others
     — React error #310. */
  const [evMsg, setEvMsg] = useState<{ tierId: string | null } | null>(null);
  const [evMsgText, setEvMsgText] = useState("");
  const [evMsgSubject, setEvMsgSubject] = useState("");
  const [evMsgChannel, setEvMsgChannel] = useState<"email" | "sms">("email");
  const [evMsgSending, setEvMsgSending] = useState(false);
  /* Add Event with an unfinished event draft of the author's own (CC 0280): asks "Continue draft" or
     "Start a new event" instead of reopening it unasked. Holds the draft's key while asking. */
  const [resumeAsk, setResumeAsk] = useState<string | null>(null);
  /* Discarding that draft (JAI-2553): "confirm" asks first, "busy" while the delete is out, "failed" keeps the draft and says so. */
  const [resumeDiscard, setResumeDiscard] = useState<"confirm" | "busy" | "failed" | null>(null);
  /* The ticket group picker applies only on Done (CC 0280): a row click marks the choice here.
     Closing the picker any other way drops it. */
  const [libPending, setLibPending] = useState<string | null>(null);
  useEffect(() => { setLibPending(null); }, [ui.lib]);

  const runAttach = async (): Promise<AttachResult | null> => {
    const held = docs[ui.curDoc];
    if (!held || attachBusy) return null;
    setPublishFail(null);
    /* Fold the LIVE held setup into the document before attaching. Setup edits
       reach `doc.setup` through a 600ms debounce (patchRecord), so a create
       pressed straight after typing read a document that was one debounce
       behind — the event was made with only the fields that happened to have
       landed. The component's own state is the truth at this instant. */
    const d: Doc = held.type === "Queue" ? { ...held, qsetup: { ...(held.qsetup ?? {}), ...q0 } }
      : held.type === "Board" ? { ...held, boardCfg: { ...(held.boardCfg ?? {}), ...b0 } }
      /* Made in the zone the author saw (CC 0250): an event whose dates were never edited has no
         timezone of its own yet, and the create would otherwise fall back to the browser's. */
      : { ...held, setup: { ...(held.setup ?? {}), ...r0, timezone: r0.timezone || schedZone } as Doc["setup"] };
    const missing = attachBlockers(d);
    if (missing.length) {
      showToast(t("journeys.newJourneys.toast.addFirst", "Add {items} first — the {noun} can't be created without it", { items: njListAnd(missing.map(njBlockerText)), noun: njAttachNoun(d.type) }));
      return null;
    }
    /* A pending Setup autosave belongs to the pre-attach key: the live Setup was folded into `d` above, and the
       debounce firing after the re-key would write the old local document back (a ghost "Live, not attached").
       It is cancelled here and remembered: if the create fails, `d` is saved to the journey's own key below,
       so a refused create never loses what the author just typed. */
    const pendingSetup = setupSaveRef.current !== null;
    if (setupSaveRef.current) { window.clearTimeout(setupSaveRef.current); setupSaveRef.current = null; flushPendingRef.current = null; }
    /* One event id per draft (CC 0277), kept on the document so it survives a reload: every
       create attempt sends it, so "Try again" after an unclear failure can only finish the
       first attempt. A stored id is always reused; a new one is minted only when the draft has
       none, and the create is not sent until the server has stored it (CC 0285). If that save
       fails, nothing is created and the draft stays on screen. */
    setAttachBusy(true);
    let eventId: string | undefined;
    if (d.type === "Event") {
      try {
        eventId = (await holdEventId(d as Doc & { pendingEventId?: string }, (x) => saveDoc(ui.curDoc, x as Doc))).id;
      } catch (e) {
        setAttachBusy(false);
        const reason = (e instanceof Error ? e.message : String(e)).replace(/[.!?]?$/, ".");
        const text = `Couldn't save the draft before creating the event: ${reason} Nothing was created, and your draft is still here.`;
        setPublishFail({ text, retry: true, action: "create", doc: ui.curDoc });
        showToast(text);
        return null;
      }
    }
    try {
      /* Never retried automatically (CC 0250): the server can answer SERVICE_WARMING after the
         create committed. The kept error's "Try again" is the retry, and it reuses eventId. */
      const r = await attachJourney(d, { ticketTypes: allTicketTypes as any[], eventId });
      /* Re-key the document onto its new owner. Pages, blocks, flow steps and
         attachments are carried across verbatim — the attach adds an origin,
         it does not rebuild the journey. */
      const next: Doc = { ...d, name: r.origin.label || d.name, origin: r.origin, edited: false, updated: t("journeys.newJourneys.updatedJustNow", "Just now") };
      delete (next as Doc & { pendingEventId?: string }).pendingEventId;
      /* The held record moves to the new event's key so Setup keeps reading the
         values that were just used to create it — without this the panel reloads
         from the record and briefly shows blanks. */
      if (r.origin.kind === "event" && r.origin.id) { createdEventIds.current.add(r.origin.id); setRecords((m) => ({ ...m, [r.origin.id!]: { ...r0, id: r.origin.id! } })); }
      const oldKey = ui.curDoc;
      setOverlays((o) => { const x = { ...(o ?? {}) }; delete x[oldKey]; x[r.key] = next; return x; });
      void putJourneyDoc(slug, r.key, next);
      // A never-persisted draft has nothing on the server to delete (CC 0122).
      if (!unsavedRef.current.has(oldKey)) void deleteJourneyDoc(slug, oldKey);
      unsavedRef.current.delete(oldKey);
      set({ curDoc: r.key });
      showToast(r.note);
      return { ...r, doc: next };
    } catch (e) {
      if (pendingSetup) { saveDoc(ui.curDoc, eventId ? { ...d, pendingEventId: eventId } as Doc : d); markSaved(); }
      /* Shown next to the header buttons as well as in a toast, so a failed create can't go unnoticed (CC 0235).
         "Nothing was changed" only when the server refused outright (CC 0277/0278); otherwise the create may
         have committed, and "Try again" is safe because it reuses this draft's event id. */
      const kind = createFailureKind(e);
      const noun = njAttachNoun(d.type);
      const reason = (e instanceof Error ? e.message : String(e)).replace(/[.!?]?$/, ".");
      const tail = kind === "refused" ? "Nothing was changed."
        : kind === "not-confirmed" ? `We couldn't confirm the ${noun}.`
        : `We couldn't confirm whether the ${noun} was created.`;
      setPublishFail({ text: `Couldn't create the ${noun}: ${reason} ${tail}`, retry: kind !== "not-confirmed", check: d.type === "Event" && kind !== "refused", action: "create", doc: ui.curDoc });
      /* The toast says the same as the kept error (CC 0285): "unchanged" only when the server refused. */
      showToast(`Couldn't create the ${noun}: ${reason} ${tail}`);
      return null;
    } finally { setAttachBusy(false); }
  };

  const D: Doc | null = docs[ui.curDoc] ?? null;
  const curIdx = D ? Math.min(ui.cur, D.pages.length - 1) : 0;
  const p: NJPage | null = D ? D.pages[curIdx]! : null;
  /** Per-page memory (stage 4) — mobile until the author opts a page into
   *  desktop width, so nothing already open changes width unasked.
   *
   *  A Display Board is the exception and is not a preference: it is a lobby TV,
   *  so it is always full width. Without this it would open at the 440px phone
   *  width with no toggle on screen to escape it. */
  /** The page's canvas width, remembered ON THE DOCUMENT (operator, 2026-09-02)
   *  so an author returns to the page the way they left it. It rides the same
   *  autosave as everything else on the page — `patchPage` → `patchDoc` →
   *  `saveDoc` — rather than a second persistence path of its own.
   *
   *  A Display Board ignores it: that canvas is a lobby TV, always full width,
   *  and with no toggle on screen there would be no way out of a stored
   *  `mobile`. */
  const curViewport: "desktop" | "mobile" =
    /* Inlined rather than reading `isBoardJourney`, which is declared 200 lines
       further down — a `const` read before its declaration is a TDZ crash, not a
       lint warning. */
    (D?.origin?.kind === "board" || D?.type === "Board") ? "desktop"
      : (p?.viewport ?? "mobile");
  const setViewport = (v: "desktop" | "mobile") => patchPage({ viewport: v });
  /* Canvas width from the shared editor shell (stage 3). The Desktop / Mobile
     toggle STAYS — it is what people know, and it keeps its per-page memory —
     and now seeds the width the handles then drag freely from. Never persisted:
     the drag is a check that the layout holds, not a property of the page. */
  /* Canvas width. Two states only, both named for what an author is checking
     rather than for a number: Mobile is 440px — the iPhone 16 Pro Max's logical
     width, the widest phone worth designing against — and Desktop is however wide
     the designer's own window is, because "does this hold on a big screen" is a
     question about THEIR screen, not about 720.

     The width pills and the drag handles are gone (operator, 2026-09-02): an
     arbitrary pixel width is a number nobody was checking against anything, and
     two controls for one decision made the toggle look like a preset rather than
     the answer. `useCanvasWidth` re-seeds whenever `initial` changes, so the
     RESTORED 2026-09-02 (operator): the width presets and the drag handles are
     back. Removing them left Desktop/Mobile as the only control, and Desktop
     means "as wide as this pane" — so there was no way to check a layout at any
     width in between, and no way to sweep through the breakpoints and watch the
     page reflow. The toggle still seeds the width and keeps its per-page memory;
     the presets jump to a named width, and the two bars either side of the
     canvas drag it freely. The drag is never persisted — it is a check that the
     layout holds, not a property of the page. */
  const pageWidth = useCanvasWidth({ initial: curViewport === "desktop" ? 4000 : 440, min: 320, max: 4000 });
  /* The width a block should LAY OUT against (2026-09-02, operator). Responsive
     blocks read this rather than the canvas width directly, because the preview
     overlay renders through the same block renderer: while it is open the phone
     preview must stack its columns even though the canvas behind it is wide. */
  const renderWidth = ui.previewOpen ? (ui.previewViewport === "desktop" ? 1280 : 440) : pageWidth.measured;
  /* Named for what each one IS, so the row reads as widths rather than as
     devices competing with the Desktop/Mobile toggle beside it. */
  const WIDTH_PRESETS = [
    { w: 440, label: t('journeys.newJourneys.label', "Phone") },
    { w: 768, label: t('journeys.newJourneys.label2', "Tablet") },
    { w: 1180, label: t('journeys.newJourneys.label3', "Laptop") },
    { w: 4000, label: t('journeys.newJourneys.label4', "Full") },
  ];

  /* ── Stage 2/3 — the live event record + readiness (Setup views read this) ── */
  const [records, setRecords] = useState<Record<string, EventLike>>({});
  /** The record as the server last answered it, per event id: what a held edit is compared
   *  against, so re-choosing a value the event already has is not sent as a change. */
  const eBase = useRef<Record<string, EventLike>>({});
  /** The event whose record read failed, and a counter Retry bumps to read it again. */
  const [eventReadFailed, setEventReadFailed] = useState<string | null>(null);
  const [eventReadTry, setEventReadTry] = useState(0);
  const eventOriginId = D?.origin?.kind === "event" ? D.origin.id : null;
  useEffect(() => {
    if (!eventOriginId || records[eventOriginId]) return;
    let live = true;
    setEventReadFailed(null);
    loadEventDoc(eventOriginId).then((res) => {
      if (!live) return;
      if (res) { eBase.current[eventOriginId] = res.record; setRecords((r) => ({ ...r, [eventOriginId]: res.record })); }
      else setEventReadFailed(eventOriginId);
    }).catch(() => { if (live) setEventReadFailed(eventOriginId); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventOriginId, eventReadTry]);
  /** Record state key: the owning event when there is one, otherwise the
   *  journey's own key — so a template-created journey's Setup fields are
   *  editable BEFORE it has an event to write to. */
  const recKey = eventOriginId ?? ui.curDoc;
  const rec: EventLike | null = (recKey ? records[recKey] ?? null : null)
    ?? (!eventOriginId && D?.setup ? (D.setup as EventLike) : null);
  /** Fallback-including record, used everywhere (canvas preview AND Setup
   *  views) — declared here, early, so both can reference it safely. */
  /* An existing event's status is the RECORD's, never a fallback or the journey document's
     ("Draft" there is only the list's label): unknown ("") until the record answers (CC, 2026-10-06). */
  const r0: EventLike = rec ?? ({ id: eventOriginId ?? "", title: D?.name ?? "", status: eventOriginId ? "" : "draft" } as EventLike);
  /* The record's status as the events worker derives it: draft, scheduled (published, not
     started), live (published, under way), completed, cancelled, archived. PUBLISHED is every
     state an attendee can see; LIVE is the one in which the worker refuses changes to what
     people registered against (403 EVENT_LIVE_LOCKED). */
  const evStatus = String(r0.status ?? "").toLowerCase();
  const isLive = !!eventOriginId && evStatus === "live";
  /** An ended event (the worker's "completed") is locked the same way as a live one, with its own reason. */
  const isEnded = !!eventOriginId && evStatus === "completed";
  const isLocked = isLive || isEnded;
  const isPublishedEvent = !!eventOriginId && (evStatus === "live" || evStatus === "scheduled" || evStatus === "completed" || evStatus === "published");
  /** The header's Save / Publish wait for an existing event's record, so a published event is
   *  never offered "Publish" or "Save draft" on the strength of a placeholder status. */
  const eventStatusKnown = !eventOriginId || !!records[eventOriginId];
  /* An existing event's Setup waits for its record: the ghost while the read runs, the reason and Retry when it
     fails. Never the empty placeholder form, which reads as data and which a save would write back. */
  const eventLoadingPane = eventOriginId && !eventStatusKnown ? (
    eventReadFailed === eventOriginId ? (
      <div data-testid="nj-event-read-failed">
        <Banner tone="danger" title={t("journeys.newJourneys.eventReadFailed", "This event couldn't be loaded. Nothing has been changed.")}
          action={<Button size="sm" variant="secondary" testid="nj-event-retry" onClick={() => setEventReadTry((n) => n + 1)}>{t("common.retry", "Retry")}</Button>} />
      </div>
    ) : (
      <LoadingRegion label={t("journeys.newJourneys.eventLoading", "Loading the event…")} testId="nj-event-loading">
        <GhostFormFields count={5} />
      </LoadingRegion>
    )
  ) : null;
  /* ── Tickets v2 (CC 0195) ──────────────────────────────────────────────────
     The library is listTicketGroupsV2; an existing event's tiers are its own
     ticketing (getEventTicketing). `ticketGroups` keeps the shape the step and the
     tickets page already render ({ id, name, description, tiers }). */
  const v2SelGroupId: string | null = ((r0.ticketGroupIds ?? []) as string[])[0] ?? ((D?.attach as any)?.ticketGroupId ?? null);
  const v2 = useV2Tickets(eventOriginId, v2SelGroupId);
  /* Save draft (CC, 2026-10-07): the ticket group chosen before the first save is applied to the
     event that save creates, as a pick on an existing event is. Only Publish used to apply it, so a
     reopened draft read "No ticket group yet" and could not be published. Submitting a new event for
     approval creates it the same way. A refused apply keeps the event and the choice (Publish applies
     it again) and says why. */
  const attachWithGroup = async (): Promise<AttachResult | null> => {
    const groupId = v2SelGroupId;
    const r = await runAttach();
    const evId = r && r.origin.kind === "event" ? r.origin.id : null;
    if (evId && groupId) {
      try { await v2.apply(evId, groupId); }
      catch (e) {
        showToast(i18nT("journeys.newJourneys.toast.ticketGroupApplyFailed", "Couldn't apply the ticket group: {msg}", { msg: e instanceof Error ? e.message : String(e) }));
      }
    }
    return r;
  };
  const saveDraft = async (): Promise<void> => { await attachWithGroup(); };
  const v2EventTiers = v2.ticketing && v2.ticketing.tiers.length ? v2.ticketing.tiers : null;
  const ticketGroups = (v2.groups ?? []).filter((g) => g.latestVersion).map((g) => ({
    ...g, tiers: (v2.ticketing?.ticketGroupId === g.id && v2EventTiers) ? v2EventTiers : (v2.tiersByGroup[g.id] ?? []),
  }));
  const tgMoney = (major: unknown) => v2Money(Number(major) || 0, v2.ticketing?.currency || "USD");
  /* Events → Tickets, on this event's Event tickets view when the event exists. */
  const goEventTickets = () => nav.go("tickets", eventOriginId ? { tab: "events", eventId: eventOriginId } : { tab: "events" });
  /** Readiness reads r0, NOT the nullable rec: on an empty record every check
   *  must come back UNMET. Deriving from `rec` made this `[]`, which made
   *  doneForStep() vacuously true — green dots and an enabled Publish on a
   *  journey with nothing filled in. */
  const readiness = eventReadiness(r0);
  /** Every required field on a Setup step, met. `step` is the Setup tab key —
     the readiness rows used to name the deleted wizard's routes ("create.dates"),
     which meant this screen carried a translation table for a router that no
     longer exists. Extracted 2026-09-02; the rows now speak this screen's own
     vocabulary. */
  const doneForStep = (step: string) => !readiness.some((c) => c.step === step && !c.done);
  const setupDone = {
    basics: doneForStep("basics"), details: doneForStep("details"),
    schedule: doneForStep("schedule"), tickets: doneForStep("tickets"),
  };
  /** Front-loading (decision 3): a new event's Pages group starts collapsed
   *  behind a hint until Basics is done — guidance, never a lock. Chips in the
   *  page preview are the substitute signal, routing to the field that fills them. */
  const isEventJourney = D?.origin?.kind === "event" || D?.type === "Event";
  const pagesCollapsed = !!isEventJourney && !ui.pagesOpen && !setupDone.basics;
  const missingChips = (isEventJourney
    ? [
        !r0.title ? { label: t('journeys.newJourneys.label5', "Event title"), to: "basics" } : null,
        !r0.start ? { label: t('journeys.newJourneys.label6', "Date and venue"), to: "schedule" } : null,
      ].filter(Boolean)
    : []) as { label: string; to: "basics" | "schedule" }[];
  const patchRecord = (fn: (e: EventLike) => EventLike) => {
    if (!recKey) return;
    const base = records[recKey] ?? (!eventOriginId && D?.setup ? (D.setup as EventLike) : null)
      ?? ({ id: eventOriginId ?? "", title: D?.name ?? "", status: eventOriginId ? "" : "draft" } as EventLike);
    const next = fn(base);
    setRecords((r) => ({ ...r, [recKey]: next }));
    /* No owning event yet — keep the values in the journey document so they
     * survive reload. Debounced: patchDoc → putJourneyDoc persists the WHOLE
     * document, so writing per keystroke would be one save per character. */
    if (!eventOriginId) {
      markSaving();
      if (setupSaveRef.current) window.clearTimeout(setupSaveRef.current);
      const run = () => {
        /* Same as the queue's: an event named only in Setup showed as
           "Untitled event journey" in the list. */
        patchDoc((d) => {
          const typed = String((next as { title?: unknown }).title ?? "").trim();
          const own = (d.name ?? "").trim();
          const rename = typed && (!own || /^untitled/i.test(own));
          return { ...d, setup: next, ...(rename ? { name: typed } : {}) };
        });
        markSaved();
        setupSaveRef.current = null;
        flushPendingRef.current = null;
      };
      flushPendingRef.current = () => { if (setupSaveRef.current) { window.clearTimeout(setupSaveRef.current); run(); } };
      setupSaveRef.current = window.setTimeout(run, 600);
    }
  };

  /* ── Event Setup: held edits, one Save (2026-09-17, PP) ──────────────
     An event's Setup fields used to write on every blur — forty-two separate
     `patchEventX(...)` calls across seven sections — so an author editing an
     event was committing a field at a time with no way to see, hold or review
     what had changed. The queue designer answered the same question in
     2026-09-16 with an explicit Save, and an event now works the same way:
     edits land in `records[recKey]` (already local state, so every field keeps
     rendering what was typed) and the WRITE is held here until Save.

     Held per SECTION, not per field, because the seven `patchEventX` writers
     are the event contract's own shape — one PATCH per touched section on
     Save, not forty-two. A section nobody touched is never written.

     Publish is untouched and still immediate: Save commits the record, Publish
     decides whether the world can see it — the same split as the queue. */
  const EVENT_PATCHERS: Record<string, (id: string, patch: any) => Promise<any>> = {
    basics: patchEventBasics, details: patchEventDetails, schedule: patchEventSchedule,
    tickets: patchEventTickets, registration: patchEventRegistration,
    guestList: patchEventGuestList, messaging: patchEventMessaging,
  };
  const ePending = useRef<Record<string, any>>({});
  const [eDirty, setEDirty] = useState(false);
  /** The sections the last Save could not write, each with the server's error code. */
  const [eSaveErrors, setESaveErrors] = useState<{ section: string; code: string; message: string }[]>([]);
  /* JAI-2020: 422 AUDIENCE_INVALID from the last event / queue save, per condition. */
  const [eAudienceErrors, setEAudienceErrors] = useState<AudienceErrors | null>(null);
  const [qAudienceErrors, setQAudienceErrors] = useState<AudienceErrors | null>(null);
  /** Hold one section's patch until Save. Merges, so two edits to the same
   *  section are one write with both fields. */
  const holdEvent = (section: keyof typeof EVENT_PATCHERS | string, patch: any) => {
    if (!eventOriginId) return;
    if (section === "guestList" && guestListManagedElsewhere(eventOriginId)) return;
    /* A live event's registered-against fields are read-only (EVENT_LIVE_LOCKED); a change to
       one that still reaches here (a drop onto the card, say) is not held, so Save never sends
       what the worker would refuse. */
    let held = patch;
    /* Save never changes the event's status (CC, 2026-10-06): publishing, unpublishing and
       cancelling are their own confirmed actions, each through its own route. */
    if (held && typeof held === "object" && !Array.isArray(held) && "status" in held) {
      held = Object.fromEntries(Object.entries(held as Record<string, unknown>).filter(([k]) => k !== "status"));
      if (!Object.keys(held).length) return;
    }
    if (isLive && held && typeof held === "object" && !Array.isArray(held)) {
      held = Object.fromEntries(Object.entries(held).filter(([k]) => !EVENT_LIVE_LOCKED_KEYS.has(k)));
      /* Capacity may only grow while live (or be lifted to unlimited): a decrease is refused here
         with the same reason the server gives, before anything is held. */
      if ("capacity" in held && !liveCapacityAllowed(eBase.current[eventOriginId]?.capacity, (held as { capacity?: number }).capacity)) {
        const { capacity: _drop, ...rest } = held as Record<string, unknown>;
        void _drop;
        held = rest;
        showToast(t("journeys.newJourneys.toast.liveCapacityDecrease", "Locked while the event is live; unpublish to change. Capacity can only go up while the event runs."));
      }
      if (!Object.keys(held).length) return;
    }
    ePending.current[section] = { ...(ePending.current[section] ?? {}), ...held };
    setEDirty(true);
  };
  /** Commit every held section. Failure keeps the edits AND the dirty flag, so
   *  Save stays live and nothing is lost — the rule the queue's failed-save fix
   *  established (CD, 2026-09-16). */
  const saveEventNow = async (): Promise<boolean> => {
    if (!eventOriginId) return true;
    const id = eventOriginId;
    // An existing event's guest list is never sent: the route replaces it (see createdEventIds).
    if (guestListManagedElsewhere(id)) delete ePending.current.guestList;
    const pending = { ...ePending.current };
    const sections = Object.keys(pending);
    if (!sections.length) { setEDirty(false); return true; }
    markSaving();
    /* Contract shape at the one dispatch point (2026-09-30): the update input has
       `format` (string), not Setup's `method` object, and every ticketTypeConfigs
       entry needs qty + kind + price. Same conversions the create path uses. */
    /* Only what changed against the LOADED record is sent (CC, 2026-10-06): an unchanged value
       re-sent onto a live event is refused (403 EVENT_LIVE_LOCKED) and a no-op on any other.
       Held keys are Setup's names; the record keeps a few under its own. */
    const baseKey: Record<string, string> = { locationAddress: "address", locationName: "venueName" };
    const same = (a: unknown, b: unknown) => {
      const n = (v: unknown) => (v == null || v === "" ? null : typeof v === "object" ? JSON.stringify(v) : String(v));
      return n(a) === n(b);
    };
    const toContract = (section: string, p: Record<string, any>) => {
      const o = { ...p };
      delete o.status; // a Save is never a status write (M, CC 2026-10-06)
      const base = eBase.current[id] as unknown as Record<string, unknown> | undefined;
      if (base) for (const k of Object.keys(o)) {
        if (k === "method" || k === "entries") continue; // the format compares below; a guest list is never on the record
        if (same(o[k], base[baseKey[k] ?? k])) delete o[k];
      }
      if ("method" in o) {
        const f = formatFromMethod(o.method); delete o.method;
        /* Sent only when the format really changed: re-choosing the event's own format is
           not a change, whichever spelling the record stores (CC, 2026-10-06). */
        const before = eBase.current[id]?.method;
        if (f && !(before && sameEventFormat(f, formatFromMethod(before)))) o.format = f;
      }
      if ("ticketTypeConfigs" in o) o.ticketTypeConfigs = completeTicketTypeConfigs(o.ticketTypeIds ?? r0.ticketTypeIds, o.ticketTypeConfigs, allTicketTypes as any[]) ?? {};
      /* An all-day event ends at 23:59:59 on its last day in its zone; a single day sent
         start == end at midnight, which the worker refuses (CC, 2026-10-06). */
      const allDay = "allDay" in o ? !!o.allDay : !!r0.allDay;
      if (section === "schedule" && allDay && ("allDay" in o || "start" in o || "end" in o)) {
        const end = allDayEnd((o.end ?? r0.end) || (o.start ?? r0.start), String(o.timezone ?? r0.timezone ?? tzContext().zone));
        if (end) o.end = end;
      }
      /* A same-date end earlier than the start is the next morning: the event routes refuse an end at or
         before the start, so the next-day instant is sent (sendableEnd), with the end even when only the
         start, the zone or the all-day switch changed. */
      if (section === "schedule" && !allDay && ("start" in o || "end" in o || "timezone" in o || "allDay" in o)) {
        const end = (o.end ?? r0.end) as string | undefined;
        const sent = sendableEnd((o.start ?? r0.start) as string | undefined, end, String(o.timezone ?? schedZone));
        if (sent && sent !== end) o.end = sent;
      }
      return o;
    };
    /* Settled per section: a refused section keeps its held edits (and says why, with the
       server's code) while the sections that saved are cleared (CC, 2026-10-06). */
    const results = await Promise.allSettled(sections.map((k) => {
      const fn = EVENT_PATCHERS[k];
      const body = toContract(k, pending[k]);
      return fn ? (Object.keys(body).length ? fn(id, body) : Promise.resolve(null)) : Promise.resolve(null);
    }));
    const failed: { section: string; code: string; message: string; err: unknown }[] = [];
    results.forEach((r, i) => {
      const k = sections[i]!;
      if (r.status === "fulfilled") {
        // A section edited again while its save was in flight stays held.
        if (ePending.current[k] === pending[k]) delete ePending.current[k];
        return;
      }
      const err = r.reason as { code?: unknown; status?: unknown; message?: unknown } | null;
      failed.push({
        section: k,
        code: String(err?.code ?? (err?.status != null ? `HTTP ${err.status}` : "ERROR")),
        message: String(err?.message ?? "").trim(),
        err: r.reason,
      });
    });
    const stillHeld = Object.keys(ePending.current).length > 0;
    setEDirty(stillHeld);
    setESaveErrors(failed.map(({ section, code, message }) => ({ section, code, message })));
    if (!failed.length) {
      setEAudienceErrors(null);
      markSaved();
      /* Read the record back, so what the screen shows is what the server stored. */
      loadEventDoc(id).then((res) => {
        if (!res || Object.keys(ePending.current).length) return;
        eBase.current[id] = res.record;
        setRecords((m) => ({ ...m, [id]: { ...(m[id] ?? res.record), ...res.record } }));
      }).catch(() => { /* the save stands; the next open reads it */ });
      return true;
    }
    markFailed();
    const perCondition = failed.map((f) => audienceErrorsFrom(i18nT, f.err)).find(Boolean) ?? null;
    if (perCondition) {
      setEAudienceErrors(perCondition);
      showToast(i18nT("audience.error.saveInvalid", "Check the highlighted audience conditions. Nothing was saved."));
      return false;
    }
    /* 403 EVENT_LIVE_LOCKED: the event is live, so its dates, times and core details
       are locked. Say so; "try again" would only be refused again. */
    const locked = failed.find((f) => isEventLiveLocked(f.err));
    if (locked) {
      const paths = ((locked.err as { fields?: { path?: unknown }[] } | null)?.fields ?? []).map((x) => String(x?.path ?? "")).filter(Boolean);
      const words = [...new Set(paths.map((pth) => t("journeys.newJourneys.liveLockedField", "{field, select, title {title} startsAt {dates} endsAt {dates} timezone {time zone} locationId {location} locationName {location} locationAddress {location} location {location} meetingLink {join link} capacity {capacity} tags {tags} imageUrl {image} format {format} description {description} tickets {tickets} ticketTypeIds {tickets} ticketTypeConfigs {tickets} other {{field}}}", { field: pth })))];
      showToast(words.length
        ? t("journeys.newJourneys.toast.eventLiveLockedFields", "This event is live: {fields} can't change while it runs. Those changes weren't saved.", { fields: njListAnd(words) })
        : t("journeys.newJourneys.toast.eventLiveLocked", "This event is live, so its dates, times and core details can't be changed. Those changes weren't saved."));
      return false;
    }
    showToast(t("journeys.newJourneys.toast.saveEventFailed", "Couldn't save onto the event — your changes are still here. Press Save to try again."));
    return false;
  };

  /* ── Queue record — the queue editor's config, same ops (2026-08-24) ──
   * A `queue:` journey OWNS a queue, so every Setup field writes onto that
   * queue through updateQueue (debounced, exactly as the wizard autosaved).
   * A template-created queue journey has no record yet, so its values are
   * held in the document's `qsetup` until one exists. */
  const queueOriginId = D?.origin?.kind === "queue" ? D.origin.id : null;
  const qSummary: QueueSummary | null = queueOriginId ? (((queues as any[]).find((x) => x.id === queueOriginId) as QueueSummary) ?? null) : null;
  const qKey = queueOriginId ?? ui.curDoc;
  const [queueCfgs, setQueueCfgs] = useState<Record<string, QueueConfigLike>>({});
  const qSaveRef = useRef<number | null>(null);
  /* Setup save state — the autosave made VISIBLE (2026-09-16, CD report).
     The write worked; only the feedback was missing. Every Setup control routes
     through `patchQueue` or `patchRecord`, both of which debounce and then
     write, and the ONLY user-visible signal was a failure toast — so
     silence-on-success was indistinguishable from a screen that cannot save,
     and authors abandoned edits that had in fact landed. That misreading has a
     real cost downstream: empty queue hours mean "follows location hours"
     (queue-hours.ts), so an author who does not believe an hours edit saved
     leaves the queue on the location's hours, and the public join page then
     refuses visitors at the location's opening time.

     Deliberately NOT a Save button: the autosave is the right model, and an
     explicit Save would make the two paths disagree about what is committed.
     `error` is STICKY — it stays until the next write succeeds, because an
     author who misses a transient toast otherwise has no way to discover the
     edit did not land. */
  const [saveState, setSaveState] = useState<{ phase: "idle" | "saving" | "saved" | "error"; at: number | null }>({ phase: "idle", at: null });
  /** Queue Setup has held, uncommitted edits (2026-09-16, PP — explicit Save). */
  const [qDirty, setQDirty] = useState(false);
  /** The pending commit for the held queue edits; the Save button calls it. */
  const qCommitRef = useRef<(() => void) | null>(null);
  const markSaving = () => setSaveState((s) => (s.phase === "saving" ? s : { phase: "saving", at: s.at }));
  const markSaved = () => setSaveState({ phase: "saved", at: Date.now() });
  const markFailed = () => setSaveState({ phase: "error", at: Date.now() });
  /**
   * Run any pending Setup write immediately.
   *
   * Setup fields debounce by 700ms before they reach the document (unattached
   * journey) or the record (attached one). Leaving the designer inside that
   * window dropped the last edit — and the header said "Saved", because the
   * DOCUMENT had saved earlier for something else. A queue named and then closed
   * within 700ms lost its name and reported success (operator, 2026-09-02).
   *
   * The pending timer's own closure is what runs, so an early flush writes
   * exactly what the debounce would have — there is no second copy of the
   * payload here to drift from the real one.
   */
  const flushPendingRef = useRef<(() => void) | null>(null);
  const flushSetup = () => { flushPendingRef.current?.(); };
  /* Unmount too: the rail, the browser back button and a closed tab never touch
     the designer's own controls, and a pending write is just as lost. */
  useEffect(() => () => { flushPendingRef.current?.(); }, []);
  const storeCats = ServicesStore.categories() as { id: string; name: string; hue: string }[];
  const storeSvcs = ServicesStore.services() as { id: string; name: string; category?: string | null; listedDuration?: string }[];
  /* The queue editor picked from the studio services store. When that store is
   * empty but the tenant HAS a bookable catalogue (line-of-business shaped),
   * fall back to it so the picker offers the tenant's real services instead of
   * an empty state. */
  const catalogueSvcs = storeSvcs.length ? storeSvcs : ((cat?.services ?? []) as any[]).map((s) => ({ id: String(s.id), name: String(s.name), category: s.lob ? String(s.lob) : null, listedDuration: s.durationMin ? t("journeys.newJourneys.durationMin", "{count} min", { count: s.durationMin }) : undefined }));
  /* Queue-only services: names a queue already offers that no catalogue object
   * expresses. They are carried by NAME (`name:<name>` ids) so an older queue's
   * list round-trips through the picker instead of being dropped — an edit to
   * an unrelated field must never strip a live queue's services. */
  const QNAME_ID = "name:";
  const queueOnlyNames = [...new Set((queues as any[]).flatMap((qq) => ((qq.services ?? []) as string[])))]
    .filter((n) => n && !catalogueSvcs.some((s) => s.name === n));
  const QUEUE_ONLY_CAT = "__queue_only__";
  const svcCatServices = [
    ...catalogueSvcs,
    ...queueOnlyNames.map((n) => ({ id: QNAME_ID + n, name: n, category: QUEUE_ONLY_CAT as string | null, listedDuration: undefined })),
  ];
  const svcCatCategories = [
    ...(storeCats.length ? storeCats : ((cat?.lobs ?? []) as any[]).map((l, i) => ({ id: String(l.id), name: String(l.name), hue: ["#2D60EB", "#0AA396", "#D61A55", "#916D0F", "#7C3AED", "#1F8A5B"][i % 6]! }))),
    ...(queueOnlyNames.length ? [{ id: QUEUE_ONLY_CAT, name: t("journeys.newJourneys.queueOnlyServices", "Queue-only services"), hue: "#8A5B12" }] : []),
  ];
  const qFromSummary = (q: QueueSummary): QueueConfigLike => ({
    id: q.id, name: q.name, typeId: q.typeId, status: q.status,
    locationId: q.locationId, locationName: q.locationName,
    ticketPrefix: q.ticketPrefix ?? "", slaTargetMins: q.slaTargetMins, needed: (q as any).needed ?? 2,
    resolvesHolds: readResolvesHolds((q as any).resolvesHolds),
    audience: (q as any).audience ?? null,
    hours: JSON.parse(JSON.stringify(q.hours ?? [])),
    hoursAuthored: q.hoursAuthored,
    ignoreHours: q.ignoreHours,
    assignedStaff: [...((q as any).assignedStaff ?? [])],
    /* Selection when saved; else map legacy service NAMES onto catalogue ids —
     * the wizard's own hydration rule. A name with no catalogue object becomes
     * a `name:` entry rather than being dropped, so nothing is lost. */
    serviceSelection: q.serviceSelection?.length
      ? [...q.serviceSelection]
      : ((q.services ?? []).filter(Boolean).map((n) => { const hit = svcCatServices.find((s) => s.name === n); return { type: "service" as const, id: hit ? hit.id : QNAME_ID + n }; })),
    services: [...(q.services ?? [])], resources: [...((q as any).resources ?? [])],
    heroImage: q.heroImage ?? "", journeySource: q.journeySource ?? "", joinCode: q.joinCode,
    messagingFlowId: (q as any).messagingFlowId,
  });
  const qBase = (): QueueConfigLike => queueCfgs[qKey]
    ?? (qSummary ? qFromSummary(qSummary) : null)
  /* NO PREFILLED HOURS on a new queue (CD, 2026-09-17). It was
     `[{ days: [0,1,2,3,4], start: "09:00", end: "17:00" }]`, and with the
     author-intent bit that default became dangerous rather than merely untrue:
     the first touch of ANYTHING in the hours step — toggling Saturday on,
     nudging one time — writes `hoursAuthored: true` and enforces the WHOLE
     prefilled schedule, including the four evenings nobody chose. A desk in a
     building open until 6 would start closing at 5 because someone added
     Saturday.

     From an empty list the only hours that exist are the ones the author
     typed, so what is enforced is exactly what they meant. Empty already reads
     "Follows location hours" everywhere and readiness already accepts it. */
    ?? ({ id: "", name: D?.name ?? "", typeId: "quick", ticketPrefix: "", slaTargetMins: 10, needed: 2, resolvesHolds: [], hours: [], assignedStaff: [], serviceSelection: [], services: [], resources: [], heroImage: "", journeySource: "", ...(D?.qsetup ?? {}) } as QueueConfigLike);
  const q0 = qBase();
  /** Ordered selection → flat service names (a category resolves to its
   *  catalogue services, live) — the wizard's rule, unchanged. */
  const resolveServiceNames = (sel: QueueConfigLike["serviceSelection"]): string[] => {
    const names: string[] = [];
    (sel ?? []).forEach((it) => {
      if (it.type === "category") svcCatServices.forEach((s) => { if (s.category === it.id && s.name && !names.includes(s.name)) names.push(s.name); });
      else if (it.id.startsWith(QNAME_ID)) { const n = it.id.slice(QNAME_ID.length); if (n && !names.includes(n)) names.push(n); }
      else { const s = svcCatServices.find((x) => x.id === it.id); if (s?.name && !names.includes(s.name)) names.push(s.name); }
    });
    return names;
  };
  const patchQueue = (fn: (q: QueueConfigLike) => QueueConfigLike, opts?: { userEdit?: boolean }) => {
    const base = qBase();
    const next = fn(base);
    const resolved = resolveServiceNames(next.serviceSelection);
    /* Guard: an empty selection only clears the record's services when there was
     * something selected to remove. Otherwise an unrelated edit (a staff row, an
     * hours toggle) could strip a live queue's list. */
    next.services = resolved.length || (next.serviceSelection ?? []).length || (base.serviceSelection ?? []).length
      ? resolved
      : (base.services ?? []);
    setQueueCfgs((m) => ({ ...m, [qKey]: next }));
    /* HELD until Save (2026-09-16, PP) — but only for an actual AUTHOR EDIT.
       `userEdit: false` is for writes the app makes on the author's behalf, and
       exists because `qLifecycle` (Pause / Close / Resume) also goes through
       here: marking the queue dirty after a lifecycle write that just LANDED
       left the header reading "Unsaved changes" about a saved queue, with
       nothing left to clear it now the debounce is gone (CD, 2026-09-16 — and
       their best candidate for the stuck-badge report, which it explains: it
       sticks on DIRTY, not on saving).

       Author edits sit in component state, the header badge says "Unsaved
       changes", and Save commits them. Autosave stays for every other journey
       type (`patchRecord`).

       `flushPendingRef` still holds the commit, so leaving the designer with
       unsaved edits writes them rather than dropping them: an explicit Save is
       about giving the author control of WHEN, not about making a wrong
       keystroke unrecoverable. */
    const userEdit = opts?.userEdit !== false;
    if (userEdit) setQDirty(true);
    if (qSaveRef.current) { window.clearTimeout(qSaveRef.current); qSaveRef.current = null; }
    /* Clearing the pending state belongs to the SUCCESS path only (CD,
       2026-09-16). It used to run synchronously here, before the write settled,
       so a REJECTED save left "Not saved" on screen with Save disabled by
       `!qDirty` and `flushPendingRef` null — the author could not retry, and
       navigating away then dropped the edits without even attempting a write.
       Now a failure keeps the queue dirty, keeps Save live, and keeps the
       pending commit armed, so both retry routes still work. */
    const settle = () => {
      qSaveRef.current = null;
      flushPendingRef.current = null;
      if (userEdit) setQDirty(false);
    };
    const run = () => {
      markSaving();
      if (queueOriginId) {
        void patchQueueConfig(queueOriginId, {
          name: next.name, ticketPrefix: next.ticketPrefix, slaTargetMins: next.slaTargetMins,
          resolvesHolds: readResolvesHolds(next.resolvesHolds),
          needed: next.needed, hours: next.hours, hoursAuthored: next.hoursAuthored, ignoreHours: next.ignoreHours, assignedStaff: next.assignedStaff,
          services: next.services, serviceSelection: next.serviceSelection, resources: next.resources,
          heroImage: next.heroImage || null, journeySource: next.journeySource, messagingFlowId: next.messagingFlowId,
          /* JAI-2020: only when it changed from the stored queue — omitted keeps it. */
          ...(audienceChanged((qSummary as any)?.audience, next.audience) ? { audience: readAudience(next.audience) } : {}),
        } as Partial<QueueConfigLike>).then(() => { setQAudienceErrors(null); markSaved(); settle(); }, (err: unknown) => {
          markFailed();
          const perCondition = audienceErrorsFrom(i18nT, err);
          if (perCondition) {
            setQAudienceErrors(perCondition);
            showToast(i18nT("audience.error.saveInvalid", "Check the highlighted audience conditions. Nothing was saved."));
            return;
          }
          /* The old text said "the next edit retries", which was true under the
             debounce and is not now — nothing retries on its own. Say what is
             actually true: the edits are still here and Save is still live. */
          showToast(t("journeys.newJourneys.toast.saveQueueFailed", "Couldn't save onto the queue — your changes are still here. Press Save to try again."));
        });
      } else {
        /* The journey's DISPLAY NAME follows the Setup name (operator,
           2026-09-02: "I do not see my queue journey in the list").
           It was there — titled "Untitled queue journey", because `doc.name` and
           `qsetup.name` are different fields and only the second one has an
           input. An author who names a queue "Financial Aid walk-ins" and then
           scans the list for it finds a row called Untitled, which reads exactly
           like the save failed.
           Only while UNATTACHED and only from an untitled name: once a record
           exists the row is the record's, and an author who deliberately renamed
           the journey must not have it overwritten by the queue's name. */
        patchDoc((d) => {
          const typed = (next.name ?? "").trim();
          const own = (d.name ?? "").trim();
          const rename = typed && (!own || /^untitled/i.test(own));
          return { ...d, qsetup: next, ...(rename ? { name: typed } : {}) };
        });
        markSaved();
        settle();
      }
    };
    flushPendingRef.current = run;
    qCommitRef.current = run;
  };
  /** Commit the held queue Setup edits now — the Save button. */
  const saveQueueNow = () => { qCommitRef.current?.(); };
  /** An opening-hours set with no days chosen. The door refuses it (400), so
   *  Save is held until every set names a day — see the Save button. */
  const qHoursIncomplete = (q0.hours ?? []).some((h) => !h.days || h.days.length === 0);
  /* Switching documents drops any held queue edit state (CD note, 2026-09-16).
     Without this, the safety of the unbounded commit closure rested entirely on
     "every route into Publish flushes first" — true today, but stated nowhere
     and tested nowhere, which is exactly the kind of invariant that survives
     until someone adds a route. Resetting here makes it structural: a commit
     armed against one document can never fire against the next, and a stale
     "Unsaved changes" badge cannot follow you onto a different journey. */
  useEffect(() => {
    if (qSaveRef.current) { window.clearTimeout(qSaveRef.current); qSaveRef.current = null; }
    flushPendingRef.current = null;
    qCommitRef.current = null;
    setQDirty(false);
    setSaveState({ phase: "idle", at: null });
  }, [ui.curDoc]);
  /** The journey's name as an author would recognise it — `doc.name` unless it
   *  is still the template's "Untitled …", in which case whatever they typed in
   *  Setup. Used by the designer header so it agrees with the list row, which
   *  applies the same fallback. */
  const docDisplayName = (() => {
    const own = (D?.name ?? "").trim();
    if (own && !/^untitled/i.test(own)) return own;
    const held = String(
      (D?.setup as { title?: unknown } | undefined)?.title
      ?? (D?.qsetup as { name?: unknown } | undefined)?.name
      ?? (D?.boardCfg as { name?: unknown } | undefined)?.name
      ?? "",
    ).trim();
    return held || own || t("journeys.newJourneys.untitled.journey", "Untitled journey");
  })();
  const isQueueJourney = D?.origin?.kind === "queue" || D?.type === "Queue";
  /** What the header calls this screen — named after the OBJECT being edited,
   *  not the tool (2026-09-16, PP). Derived from the document's own origin/type
   *  so it holds on a deep link and a reload, with the generic designer name as
   *  the fallback for a document that is not about a record yet.
   *
   *  The VERB follows whether the record exists (2026-09-17, PP): "Add Queue"
   *  opens this same screen on a draft whose queue has not been created —
   *  Publish creates it — so titling that "Edit Queue" named an object the
   *  author had not made yet. Attachment is tested with `needsAttach` — the
   *  app's own definition — and NOT with `origin.kind`, which was the first
   *  attempt and read "Edit" on every draft: a template-created document
   *  carries `origin.kind === "local"`, so the key is present and truthy long
   *  before any record exists. "Add" also matches the button they pressed to
   *  get here, so the two read as one act rather than two. */
  const designerTitle = (() => {
    const kind = D?.origin?.kind ?? (D?.type ?? "").toLowerCase();
    const attached = !needsAttach(D);
    const verb = attached ? i18nT("shell.newJourneys.verbEdit", "Edit") : i18nT("shell.newJourneys.verbAdd", "Add");
    const noun = (kind === "queue" || D?.type === "Queue") ? i18nT("shell.newJourneys.nounQueue", "Queue")
      : (kind === "event" || D?.type === "Event") ? i18nT("shell.newJourneys.nounEvent", "Event")
      : kind === "appointment" ? i18nT("shell.newJourneys.nounAppointmentJourney", "Appointment journey")
      : (kind === "board" || D?.type === "Board") ? i18nT("shell.newJourneys.nounDisplayBoard", "Display Board")
      : kind === "onestop" ? i18nT("shell.newJourneys.nounOneStop", "One-Stop")
      : "";
    return noun ? `${verb} ${noun}` : i18nT("shell.newJourneys.journeyDesigner", "Journey Designer");
  })();
  /* HIDDEN FOR NOW (2026-09-15, PP) — a queue's PAGES on the Queue Details
     page: the Pages strip (Services · Wait time · Details · Waiting · How it
     works), the Pages/Flow toggle and the Desktop/Mobile toggle that only
     applies to a page canvas. Nothing is removed: the pages are still seeded,
     still in the document, and every renderer (`pagesView`, `flowView`, the
     inspector) is untouched — flip this to false and the whole surface returns.
     With the pages hidden there is no "no tab selected" state left, so the
     designer opens on Setup and stays on a setup tab. */
  const QUEUE_PAGES_HIDDEN = true;
  /* EVENTS TOO (2026-09-17, PP). Same withholding, same reasons, same one-flag
     reversal: an event journey's Setup is the whole editing surface an author
     needs, and the Pages strip / Pages–Flow toggle / page canvas were a second
     place to be that nothing in the current flow sends anyone to. Nothing is
     removed — the pages are still seeded, still in the document, and every
     renderer is untouched; flip this to false and the surface returns.

     TENANT-SCOPED (2026-09-17, KP): a tenant listed below gets the page surface
     BACK — an event's pages, the Pages/Flow toggle, Preview and undo/redo all
     return there, because the condition below is the only thing any of them read. Every
     other tenant keeps the withholding. The list is the tenant SLUG (the same
     value `slug` above resolves from the active tenant), so adding a tenant is
     one entry and returning to a global hide is emptying the set. */
  /* Empty by ruling (2026-09-21): no tenant slug literal ships in this file.
     The set stays as the seam — adding a tenant is one entry — but a shipped
     build hides event pages for every tenant rather than naming one. */
  const EVENT_PAGES_SHOWN_TENANTS = new Set<string>();
  const EVENT_PAGES_HIDDEN = !EVENT_PAGES_SHOWN_TENANTS.has(slug);
  const queuePagesHidden = isQueueJourney && QUEUE_PAGES_HIDDEN;
  const eventPagesHidden = !!isEventJourney && EVENT_PAGES_HIDDEN;
  /** Any journey whose page surface is withheld — the condition the strip, the
   *  Pages/Flow toggle, Preview and the undo stack all read. */
  const pagesHidden = queuePagesHidden || eventPagesHidden;
  /** Journeys that hold Setup edits behind an explicit Save. */
  const explicitSave = isQueueJourney || !!isEventJourney;
  /** The one dirty flag the header reads, whichever record this journey owns. */
  const setupDirty = isQueueJourney ? qDirty : eDirty;
  const saveSetupNow = () => { if (isQueueJourney) saveQueueNow(); else void saveEventNow(); };
  /** The setup tab actually being shown. Same as `ui.setupView` everywhere
   *  except a journey with its pages hidden, where nothing selected means the
   *  first setup tab — Setup for a queue, Basics for an event. */
  const setupViewEff = pagesHidden
    ? (ui.setupView ?? (isQueueJourney ? "qsetup" : "basics"))
    : ui.setupView;
  /* HIDDEN FOR NOW (2026-09-15, PP) — setup tabs withheld from the strip. The
     step itself is still built and still routable (`setupQResources`); removing
     an id here brings its tab back. */
  const HIDDEN_SETUP_TABS = new Set<string>(["qresources"]);
  const qType = queueTypes.find((t) => t.id === q0.typeId) ?? null;
  const qChecks = queueReadiness(q0, D?.pages.length ?? 0);
  const qReady = qChecks.every((c) => c.done);

  /* ── Display board record ─────────────────────────────────────────── */
  const boardOriginId = D?.origin?.kind === "board" ? D.origin.id : null;
  const bSummary: MessageBoard | null = boardOriginId ? (boards.find((b) => b.id === boardOriginId) ?? null) : null;
  const bKey = boardOriginId ?? ui.curDoc;
  const [boardCfgs, setBoardCfgs] = useState<Record<string, BoardConfigLike>>({});
  const bSaveRef = useRef<number | null>(null);
  const bFromSummary = (b: MessageBoard): BoardConfigLike => ({
    id: b.id, name: b.name, queueIds: [...(b.queueIds ?? [])], locationId: b.locationId ?? null,
    layout: b.layout, theme: b.theme, showWaitEstimates: b.showWaitEstimates ?? true,
    nameFormat: (b.nameFormat ?? "first-initial") as BoardNameFormat,
    message: b.message ?? "", blocks: b.blocks?.length ? JSON.parse(JSON.stringify(b.blocks)) : defaultBoardBlocks(b.layout ?? "now-serving"),
    updatedAt: b.updatedAt,
  });
  const bBase = (): BoardConfigLike => boardCfgs[bKey]
    ?? (bSummary ? bFromSummary(bSummary) : null)
    ?? ({
      id: `mb_${(ui.curDoc || "new").replace(/[^a-z0-9]/gi, "").slice(-12)}`,
      name: D?.name && D.name !== t("journeys.newJourneys.untitled.displayBoard", "Untitled display board") ? D.name : "",
      queueIds: [], locationId: null, theme: "dark", showWaitEstimates: true,
      nameFormat: "first-initial", message: t("journeys.newJourneys.defaultBoardMessage", "Welcome — take a seat, we'll text you when it's your turn."),
      blocks: defaultBoardBlocks("now-serving"), ...(D?.boardCfg ?? {}),
    } as BoardConfigLike);
  const b0 = bBase();
  const patchBoard = (fn: (b: BoardConfigLike) => BoardConfigLike) => {
    const next = fn(bBase());
    setBoardCfgs((m) => ({ ...m, [bKey]: next }));
    if (bSaveRef.current) window.clearTimeout(bSaveRef.current);
    const run = () => {
      if (boardOriginId) {
        void saveBoardConfig(next)
          .then(() => listMessageBoards().then((r: any) => setBoards(r?.boards ?? r?.data ?? [])))
          .catch(() => showToast(t("journeys.newJourneys.toast.saveBoardFailed", "Couldn't save the board — the next edit retries")));
      } else {
        patchDoc((d) => {
          const typed = (next.name ?? "").trim();
          const own = (d.name ?? "").trim();
          const rename = typed && (!own || /^untitled/i.test(own));
          return { ...d, boardCfg: next, ...(rename ? { name: typed } : {}) };
        });
      }
      bSaveRef.current = null;
      flushPendingRef.current = null;
    };
    flushPendingRef.current = () => { if (bSaveRef.current) { window.clearTimeout(bSaveRef.current); run(); } };
    bSaveRef.current = window.setTimeout(run, 700);
  };
  /* ── Appointment funnel (2026-08-31) ──────────────────────────────
     The type with no record: apptSetup on the DOCUMENT is the store, not a
     holding bag waiting for one. Everything service-scoped stays on the
     catalogue and is referenced. */
  const isApptJourney = D?.origin?.kind === "appointment" || D?.type === "Appointment";
  const a0 = { ...APPT_SETUP_DEFAULTS, ...(D?.apptSetup ?? {}) };
  const patchAppt = (patch: Partial<typeof a0>) =>
    patchDoc((d) => ({ ...d, apptSetup: { ...APPT_SETUP_DEFAULTS, ...(d.apptSetup ?? {}), ...patch } }));
  const aChecks = D ? apptReadiness(a0, D) : [];
  const aReady = aChecks.every((c) => c.done);

  const onestopOriginId = D?.origin?.kind === "onestop" ? D.origin.id : null;
  const isBoardJourney = D?.origin?.kind === "board" || D?.type === "Board";
  /** Does this journey type have a FLOW? A Display Board and a One-Stop page are
   *  single surfaces — nobody walks through them, they arrive and choose — so
   *  there is no sequence to model and no Pages/Flow toggle to show. */
  const isOneStopJourney = D?.origin?.kind === "onestop" || D?.type === "One-Stop";
  const hasFlow = !isBoardJourney && !isOneStopJourney;
  const bChecks = boardReadiness(b0);
  const bReady = bChecks.every((c) => c.done);

  /* ── All Setup/Publish hooks, hoisted together (never split by code that
   *  could throw before render completes — keeps the hook list identical
   *  across renders regardless of what D/rec/readiness turn out to be). ── */  const [addrVerify, setAddrVerify] = useState<"idle" | "checking" | "valid" | "invalid">("idle");
  const [guestDraft, setGuestDraft] = useState<GuestEntry>({ firstName: "", email: "" });
  const [guestSearch, setGuestSearch] = useState("");
  const [guestResults, setGuestResults] = useState<{ firstName: string; lastName?: string; email: string }[]>([]);
  const [guestSearching, setGuestSearching] = useState(false);
  const [segments, setSegments] = useState<{ id: string; name: string; count: number }[]>([]);
  const [publishBusy, setPublishBusy] = useState<null | "publish" | "offline" | "cancel" | "submit" | "pause" | "close">(null);
  /* Busy CTAs SHOW they are working (2026-09-16, PP): on a slow tenant a
     label-only change is easy to miss, and two of these buttons previously
     changed nothing at all while their request was in flight — Pause joins and
     Close the queue only went disabled, and both set the same busy key, so an
     author could not tell which of the two was running or whether their click
     had registered at all.

     `busyLabel` is the one shape every CTA on this screen uses: the design
     system's Spinner (prefers-reduced-motion, on-brand ink on a filled button,
     role="status" for screen readers — a hand-rolled ring would carry none of
     that, per the ruling in ConfirmationPages) plus the present-tense verb, so
     the button states which act is underway rather than just looking inert. */
  const busyLabel = (label: string, onColor = false) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
      {/* `label=""` on purpose: Spinner renders its label as visually-hidden
          text, and the verb beside it is already the button's accessible name —
          passing the same string twice makes a screen reader announce
          "Saving… Saving…". The ring is decoration here; the word carries the
          meaning. */}
      <Spinner size="sm" onColor={onColor} label="" />{label}
    </span>
  );
  const setupSaveRef = useRef<number | null>(null);
  /** Synchronous guard against a second Publish press in the same frame (the busy state lags one render). */
  const publishLock = useRef(false);

  const [tagDraft, setTagDraft] = useState("");
  const [addrSuggestions, setAddrSuggestions] = useState<{ label: string }[]>([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [heroDrop, setHeroDrop] = useState(false);
  const [newCustomDate, setNewCustomDate] = useState("");
  /* Queue/board local UI state (live board poll, message modal). The service
     drag/drop cursors moved into CatalogPicker with the reorder it now owns. */
  const [qHeroDrag, setQHeroDrag] = useState(false);
  const [qBoard, setQBoard] = useState<QueueBoardState | null>(null);
  const [msgTargets, setMsgTargets] = useState<QueueEntry[] | null>(null);
  const [msgText, setMsgText] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  const [bDragType, setBDragType] = useState<string | null>(null);
  /** Auto-scroll while a drag hovers near the canvas's top or bottom edge —
   *  without it a block at the bottom of a long page cannot reach the top, since
   *  getting there means leaving the viewport and ending the drag.
   *
   *  Declared UP HERE with the other hooks, not beside the canvas it attaches
   *  to: everything below the `if (loading) return` is conditional, and a hook
   *  down there runs on some renders and not others — React error #310. */
  const canvasScrollRef = useDragAutoScroll();
  /* Board canvas + Preview measurement (2026-09-02). Callback refs, not effects
     — see useFitBox for why the effect version never attached inside the portal.
     Two separate boxes because both can be mounted at once. */
  const { fit: boardFit, attach: boardFitRef } = useFitBox();
  const { fit: previewFit, attach: previewFitRef } = useFitBox();
  /* Board paging. Declared here with the other hooks because the board view is
     built past this screen's early returns, and a hook behind a return is React
     #310. Keyed off the board config's queue ids, which are known this early. */
  const bColCount = boardColumnsFor(b0.queueIds.length, ui.boardOrientation === "landscape");
  const bPager = useBoardPager(b0.queueIds, bColCount, ui.boardOrientation === "landscape" ? 20 : 16);
  useEffect(() => {
    if (!queueOriginId || q0.status !== "open") { setQBoard(null); return; }
    let live = true;
    const load = () => getQueueBoard(queueOriginId).then((b: any) => { if (live) setQBoard(b ?? null); }).catch(() => {});
    load();
    const t = window.setInterval(load, 10_000);
    return () => { live = false; window.clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueOriginId, q0.status]);
  const approvalsStore = useApprovals();
  const { locations: savedLocations, loading: locationsLoading, error: locationsError, reload: reloadLocations } = useLocations();
  useEffect(() => {
    const term = guestSearch.trim();
    if (!term) { setGuestResults([]); return; }
    let live = true;
    setGuestSearching(true);
    const t = window.setTimeout(() => {
      listGuests({ search: term, limit: 8 } as never).then((res: any) => {
        if (!live) return;
        const raw: Record<string, unknown>[] = res?.guests ?? res?.data ?? [];
        setGuestResults(raw.slice(0, 8).map((r) => ({ firstName: String(r.firstName ?? r.first_name ?? ""), lastName: r.lastName ? String(r.lastName) : undefined, email: String(r.email ?? "") })));
      }).catch(() => setGuestResults([])).finally(() => live && setGuestSearching(false));
    }, 300);
    return () => { live = false; window.clearTimeout(t); };
  }, [guestSearch]);
  useEffect(() => {
    let live = true;
    listGuestSegments().then((res: any) => {
      if (!live) return;
      const raw: Record<string, unknown>[] = res?.segments ?? res?.data ?? [];
      setSegments(raw.map((s) => ({ id: String(s.id ?? ""), name: String(s.name ?? t("journeys.newJourneys.untitled.segment", "Segment")), count: Number(s.count ?? 0) })));
    }).catch(() => setSegments([]));
    return () => { live = false; };
  }, []);

  /* The journey's flow, migrated from whatever shape it was stored in. Declared
     HERE, above every reader: the inspector and the library picker rows are
     evaluated earlier in this function body than the flow view is, so declaring
     it beside the flow view left both reading it inside its temporal dead zone
     and the screen threw on mount. */
  const qfFlow: QFFlow = D ? njFlowOf(D) : { trigger: { event: "screen_services" }, steps: [] };
  /** A win-back send anywhere in the tree — what the promo attachment row used
   *  to infer from "flowNo is non-empty", which was true of any branched flow. */
  const flowHasPromo = qfFlatten(qfFlow.steps).some((st) => st.kind === "comms" && st.promo);

  const patchPage = (patch: Partial<NJPage>) => patchDoc((d) => ({ ...d, pages: d.pages.map((pg, i) => (i === curIdx ? { ...pg, ...patch } : pg)) }));
  const patchContent = (fn: (c: Block[]) => Block[]) => patchDoc((d) => ({ ...d, pages: d.pages.map((pg, i) => (i === curIdx ? { ...pg, content: hoistHero(fn(pg.content)) } : pg)) }));

  /* ── Attachments — real library records ─────────────────────────── */
  const at = D?.attach ?? {};
  const attGroup = qGroups.find((g) => g.id === at.groupId) ?? qGroups[0] ?? null;
  /* An EVENT's survey is the event's, held on the record (2026-09-02). Other
     journey types still read the document until their own owner gains the field
     — a queue's survey is per service and already says so.
     No first-in-library fallback, matching the ticket group above: an unattached
     survey has to read as unattached everywhere rather than as the first row in
     the library, or an author sees a survey they never chose. */
  const surveyRefId = isEventJourney ? (r0.surveys ?? [])[0] ?? null : at.surveyId ?? null;
  const attSurvey = surveyRefId ? (surveys.find((s: any) => s.id === surveyRefId) ?? null) : null;
  /* No first-in-library fallback: the tickets page, the ATTACHMENTS chip and the
   * Setup · Tickets tab all read this one value, so an unattached event must say
   * so everywhere rather than inventing a group. */
  const attTickets = ticketGroups.find((g) => g.id === at.ticketGroupId) ?? null;
  /* An EVENT's coupons are the event's, held on the record (2026-09-02) — and
     there can be several, so this is a LIST rather than the single attachment the
     journey document used to hold. Queue and appointment journeys take theirs
     from the service, which the designer already says.
     No first-in-library fallback: an unattached coupon must read as unattached
     rather than as whichever row happens to be first. */
  const promoIds: string[] = isEventJourney
    ? [...(r0.coupons ?? []), ...(r0.promos ?? [])]
    : (at.promoId ? [at.promoId] : []);
  /* Resolved against the WHOLE library: the card summarises both incentives, and
     which kind each one is shows in its own row rather than in the heading. */
  const attPromos = promoIds
    .map((id) => allDiscounts.find((c: any) => c.id === id))
    .filter(Boolean) as any[];
  const attPromo = attPromos[0] ?? null;
  const surveyItemCount = (s: any) => (s?.items?.length ?? s?.questions?.length ?? 0);
  const qName = attGroup?.name || t("journeys.newJourneys.noQuestionGroup", "No question group attached");
  const qCount = attGroup?.items?.length ?? 0;
  const svName = attSurvey?.name || t("journeys.newJourneys.noSurvey", "No survey attached");
  const svCount = surveyItemCount(attSurvey);
  /* One coupon reads as its code; several read as a count, because listing three
     codes in a card this size truncates the third and implies there are only two. */
  const promoCode = attPromos.length === 0 ? t("journeys.newJourneys.noCoupon", "No coupon attached")
    : attPromos.length === 1 ? (attPromo?.code || attPromo?.name || t("journeys.newJourneys.untitled.coupon", "Untitled coupon"))
    : t("journeys.newJourneys.couponCount", "{count, plural, one {# coupon} other {# coupons}}", { count: attPromos.length });
  const promoOff = attPromos.length === 0
    ? (isEventJourney ? t("journeys.newJourneys.promoAddInSetup", "add one in Setup · Tickets") : t("journeys.newJourneys.promoPickFromLibrary", "pick one from the promos library"))
    : attPromos.length === 1
      ? ([attPromo.type, attPromo.value != null ? String(attPromo.value) : null].filter(Boolean).join(" ") || "coupon")
      : attPromos.map((c: any) => c.code || c.name).filter(Boolean).join(" · ");
  const tixName = attTickets?.name || t("journeys.newJourneys.noTicketGroup", "No ticket group attached");
  const tixTiers = (attTickets?.tiers ?? []) as any[];

  /* ── Setup → page mirrors ─────────────────────────────────────────
   *  Setup OWNS these values; pages read them from here and never keep a copy,
   *  so a page preview cannot disagree with what Setup holds. One derivation
   *  each, shared by the cores, the content blocks and the full-screen preview.
   *
   *  Audit 2026-08-31 found four silent breaks this section closes: the Welcome
   *  page's `eventdetails` block had NO renderer at all (the schedule never
   *  reached the page), Countdown counted down to a hardcoded "04d 02h 14m",
   *  the Tickets page showed only an attached GROUP and never the individual
   *  ticket types Setup selected, and the Contact/Register pages showed a COUNT
   *  of attached booking questions instead of the questions. */
  const setupLoc = (savedLocations as any[]).find((l) => l.id === r0.locationId) ?? null;
  /** The zone the Schedule step reads and writes times in (CC 0235 §2): the event's own,
   *  else its saved location's, else the workspace's; the viewer's browser only last. */
  const schedZone: string = r0.timezone || (setupLoc?.timezone ?? setupLoc?.timeZone ?? null) || tenantZone || tzContext().zone;
  /* The schedule's end against its start, the way the server reads it, in the schedule's zone: the same rule
     as the Publish readiness row (scheduleEndState). "next-day" is a hint; Save is held while "invalid" stands. */
  const scheduleEnd = scheduleEndState(r0.start, r0.end, { allDay: !!r0.allDay, timeZone: schedZone });
  const endBeforeStart = scheduleEnd === "invalid";
  const setupVenue = (r0.locationMode ?? "saved") === "custom" ? (r0.venueName ?? "") : (setupLoc?.name ?? "");
  const setupAddress = (r0.locationMode ?? "saved") === "custom" ? (r0.address ?? "") : (setupLoc?.address ?? r0.address ?? "");
  const asDate = (v?: string | null): Date | null => { if (!v) return null; const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d; };
  const startAt = asDate(r0.start);
  const endAt = asDate(r0.end);
  const sameDay = !!startAt && !!endAt && startAt.toDateString() === endAt.toDateString();
  const DFMT = { weekday: "short", day: "numeric", month: "long", year: "numeric" } as const;
  const TFMT = { hour: "numeric", minute: "2-digit" } as const;
  const setupDateLine = !startAt ? ""
    : (!endAt || sameDay)
      ? startAt.toLocaleDateString(undefined, DFMT)
      : `${startAt.toLocaleDateString(undefined, DFMT)} — ${endAt.toLocaleDateString(undefined, DFMT)}`;
  const setupTimeLine = !startAt ? "" : r0.allDay ? t("journeys.newJourneys.allDay", "All day")
    : [`${startAt.toLocaleTimeString(undefined, TFMT)}${endAt ? ` – ${endAt.toLocaleTimeString(undefined, TFMT)}` : ""}`,
       r0.timezone ? tzShortLabel(r0.timezone) : null].filter(Boolean).join(" · ");
  const setupRepeatLine = r0.repeatIndex ? (REPEAT_LABELS_ALL[r0.repeatIndex] ?? "") : "";

  /** Setup · Tickets in full: the attached GROUP's tiers AND the individual
   *  ticket types Setup selected, each carrying its own allocation. The Tickets
   *  page renders this one list, so what a customer would see IS the record. */
  const tixTypeRows = ((r0.ticketTypeIds ?? []) as string[]).map((id) => {
    const t = (allTicketTypes as any[]).find((x) => x.id === id);
    if (!t) return null;
    const cfg = (r0.ticketTypeConfigs ?? {})[id] ?? ({} as { qty?: number; kind?: string; price?: number });
    return { id, from: "type" as const, name: t.name || i18nT("journeys.newJourneys.untitled.ticket", "Untitled ticket"), kind: cfg.kind ?? t.kind ?? null, price: cfg.price ?? t.price, qty: cfg.qty || t.defaultAllocation || null }; // qty 0 = no per-type cap (sells within the event's capacity), not "0 allocated"
  }).filter(Boolean) as any[];
  const tixRows = [
    ...tixTiers.map((t) => ({ id: t.id, from: "group" as const, name: t.name || i18nT("journeys.newJourneys.untitled.tier", "Untitled tier"), kind: t.kind ?? null, price: t.price, qty: t.defaultAllocation ?? null })),
    ...tixTypeRows,
  ];
  /** One ticket row as the customer sees it — shared by the Tickets core and
   *  the ticket-group attachment block. */
  const tixRowView = (t: any) => (
    <div key={`${t.from}:${t.id}`} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", background: "#FFFFFF", borderRadius: 9, padding: "8px 11px" }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 12, fontWeight: 700 }}>{t.name}</span>
        {t.qty != null ? <span style={{ display: "block", fontSize: 10, color: "#697180" }}>{i18nT("journeys.newJourneys.qtyAllocated", "{qty} allocated", { qty: t.qty })}</span> : null}
      </span>
      <span style={{ fontSize: 11.5, fontWeight: 800, color: "#D01647", flex: "none" }}>{t.kind === "free" || t.kind === "invite" ? i18nT("journeys.newJourneys.priceFree", "Free") : tgMoney(t.price)}</span>
    </div>
  );

  /** The attached group's real questions, in group order. Group-level Required
   *  overrides the question's own, exactly as the group editor does. */
  const questionsOf = (groupId: string | null | undefined) => {
    const g = groupId ? qGroups.find((x: any) => x.id === groupId) : null;
    return ((g?.items ?? []) as any[]).map((it: any) => {
      const q = questions.getQ(it.qid);
      if (!q) return null;
      return { id: q.id, label: q.label || t("journeys.newJourneys.untitled.question", "Untitled question"), type: q.type, help: q.help ?? "", required: it.required ?? q.required, options: q.options ?? [] };
    }).filter(Boolean) as any[];
  };
  const attQuestions = questionsOf(attGroup?.id);
  /** One attached question, rendered as the customer sees it. Shared by the
   *  Contact/Register cores and the booking-questions attachment block. */
  const qFieldView = (q: any) => {
    const opts = ((q.options ?? []) as { id: string; label: string }[]).slice(0, 4);
    const plain = q.type === "heading" || q.type === "label";
    const body =
      q.type === "heading" ? <span style={{ display: "block", fontSize: 12.5, fontWeight: 800, color: "#333333" }}>{q.label}</span>
      : q.type === "label" ? <span style={{ display: "block", fontSize: 11, color: "#697180", lineHeight: 1.5 }}>{q.label}</span>
      : q.type === "long" ? <div style={{ ...FIELD, minHeight: 44 }}>{q.help || t('journeys.newJourneys.text27', "Long answer")}</div>
      : q.type === "radio" || q.type === "checks" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {(opts.length ? opts : [{ id: "a", label: t('journeys.newJourneys.label7', "Option A") }, { id: "b", label: t('journeys.newJourneys.label8', "Option B") }]).map((o) => (
            <span key={o.id} style={{ fontSize: 11, color: "#697180" }}>{q.type === "checks" ? "☐" : "◯"} {o.label}</span>
          ))}
        </div>
      )
      : q.type === "yesno" ? <span style={{ fontSize: 11, color: "#697180" }}>{t('journeys.newJourneys.text28', "◯── off")}</span>
      : q.type === "stepper" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 7, border: "1px solid #D1D4DC", borderRadius: 999, padding: "3px 9px", fontSize: 11, fontWeight: 700, color: "#697180" }}>− <span style={{ color: "#333333" }}>0</span> +</span>
      : q.type === "file" ? <div style={{ ...FIELD, border: "1px dashed #D1D4DC" }}>{t('journeys.newJourneys.text29', "Choose a file…")}</div>
      : <div style={FIELD}>{q.type === "select" || q.type === "multi" ? (opts[0]?.label ?? t("journeys.newJourneys.answerChoose", "Choose…")) : q.type === "datetime" ? t('journeys.newJourneys.text30', "dd / mm / yyyy") : q.type === "number" ? "0" : (q.help || t("journeys.newJourneys.answerPlaceholder", "Your answer"))}</div>;
    return (
      <div key={q.id}>
        {plain ? null : (
          <span style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#333333", marginBottom: 4 }}>
            {q.label}{q.required ? <span style={{ color: "#D01647" }}> *</span> : null}
          </span>
        )}
        {body}
        {q.help && !plain && q.type !== "long" ? <span style={{ display: "block", fontSize: 10, color: "#B5BAC6", marginTop: 3 }}>{q.help}</span> : null}
      </div>
    );
  };

  const setAttach = (patch: Record<string, string | null>) => patchDoc((d) => ({ ...d, attach: { ...(d.attach ?? {}), ...patch } }));
  /* AN EVENT'S QUESTION GROUP IS ALSO THE EVENT'S bookingQuestions (CC 0030
     item 17). The doc keeps attach.groupId for the canvas, and the event record
     gets the list, held for Save like every Setup write and sent only when it
     changed. Direct question refs already on the record are kept; the one group
     ref is swapped. */
  const attachQuestionGroup = (groupId: string | null) => {
    setAttach({ groupId });
    if (!isEventJourney) return;
    const cur = (((r0 as any).bookingQuestions ?? []) as { kind: string; id: string }[]);
    const next = [...cur.filter((x) => x && x.kind !== "group"), ...(groupId ? [{ kind: "group", id: groupId }] : [])];
    if (JSON.stringify(next) === JSON.stringify(cur)) return;
    patchRecord((x: any) => ({ ...x, bookingQuestions: next }));
    if (eventOriginId) holdEvent("basics", { bookingQuestions: next });
  };

  /* ── Catalogues behind the service pickers (real) ───────────────── */
  const catServices = (cat?.services ?? []) as any[];
  const lobName = (id: string) => ((cat?.lobs as any[] | undefined)?.find((l) => l.id === id)?.name ?? "");
  const GL = ["◰", "◳", "✦", "▣", "◭", "◒", "◓", "▤", "◈", "◐", "✎"];
  /* The service's persisted image, verbatim from the catalogue row: its own
     `image`, else its landing hero's. Guarded to http(s)/root-relative so a
     `blob:`/`data:` value can never render. "" keeps the row's glyph. */
  const svcRowImage = (s: any): string => {
    const pick = (v: unknown) => { const x = typeof v === "string" ? v.trim() : ""; return x && (/^https?:\/\//i.test(x) || /^\/(?!\/)/.test(x)) ? x : ""; };
    const hero = s && s.landing && typeof s.landing === "object" ? s.landing.hero : undefined;
    return pick(s && s.image) || pick(hero && hero.image);
  };
  const originQueue = D?.origin?.kind === "queue" ? queues.find((q: any) => q.id === D.origin!.id) : null;
  /* Setup · Services is the source of a queue's list — its own copy says "the
   * order here is the order on the Services page". Reading only the SAVED queue
   * record meant a service added in Setup didn't reach the page until the queue
   * was re-saved, so the designer's live config comes first. */
  const queueSetupNames = ((q0.services ?? []) as string[]).filter(Boolean);
  const queueSvcKey = queueSetupNames.join("|");
  const queueCat = useMemo(() => {
    const names: string[] = queueSetupNames.length ? queueSetupNames : (originQueue?.services?.length ? originQueue.services : catServices.map((s) => s.name));
    const wait = originQueue?.avgWaitMins ? t("journeys.newJourneys.waitApproxMin", "~{count} min", { count: originQueue.avgWaitMins }) : "";
    return names.map((n: string, i: number) => ({ id: n, glyph: GL[i % GL.length]!, name: n, dept: "", wait }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueSvcKey, originQueue, catServices]);
  const apptCat = useMemo(() => catServices.filter((s) => s.durationMin).map((s, i) => ({ id: s.id, glyph: ["◔", "◑", "◕", "◓", "◒"][i % 5]!, name: s.name, dept: lobName(s.lob), wait: t("journeys.newJourneys.durationMin", "{count} min", { count: s.durationMin }), image: svcRowImage(s) })), [catServices]);
  const isApptPage = p?.id === "apptsvc";
  const svcCatalogue = isApptPage ? apptCat : queueCat;
  /* The page MIRRORS Setup (operator, 2026-09-02). It used to hold its own
   * `p.svc` list, which is the drift this pass removes: a page could offer
   * something Setup did not, or omit something it did, and the only cure was a
   * warning band with a reconcile button. Setup's selection — in Setup's order
   * — is now the single answer, and a legacy `p.svc` is ignored rather than
   * migrated, because Setup already holds the same list. */
  const svcOn = svcCatalogue;

  /* ── Live availability for the Pick-a-time core (never invented) ── */
  const availSlug = D?.origin?.slug ?? null;
  const grid = useJourneyAvailability({ slug: p?.id === "appttime" ? availSlug : null, serviceId: svcOn[0]?.id ?? null, days: 14 });

  /* ── Canonical editors (ruling ④) ───────────────────────────────── */
  const goRoute = (route: string, params?: Record<string, string>) => { set({ lib: null }); nav.go(route, params as any); };

  /* The rows the One-Stop scope pickers select from — taken from the JOURNEYS
     the operator can already see in the list, because that is what they are
     picking: "the advising desk" is a queue journey, and its `origin` carries the
     real queue id the block's scope needs. Deriving them here rather than reading
     the underlying stores again means the names in the picker are the names in
     the Journeys list, which is the whole point.

     Sources this screen genuinely does not have — locations, org nodes,
     categories, staff — are left UNDEFINED rather than passed as `[]`. The picker
     hides an unavailable source entirely; an empty array would claim the tenant
     has none. */
  const osLibs = {
    queues: Object.values(docs)
      .filter((d) => d.origin?.kind === "queue")
      .map((d) => ({ id: String(d.origin!.id), name: d.name, meta: d.sub })),
    events: Object.values(docs)
      .filter((d) => d.origin?.kind === "event")
      .map((d) => ({ id: String(d.origin!.id), name: d.name, meta: d.sub })),
    services: catServices.map((s) => ({
      id: s.id,
      name: s.name,
      meta: [s.durationMin ? t("journeys.newJourneys.durationMin", "{count} min", { count: s.durationMin }) : null, lobName(s.lob)].filter(Boolean).join(" · ") || undefined,
    })),
    createRoute: {
      events: { label: t('journeys.newJourneys.label9', "Create an event journey"), go: () => goRoute("newjourneys") },
      queues: { label: t('journeys.newJourneys.label10', "Create a queue journey"), go: () => goRoute("newjourneys") },
      services: { label: t('journeys.newJourneys.label11', "Open the service catalog"), go: () => goRoute("ops.catalogue") },
    },
  };

  /* ── Page / flow mutations ──────────────────────────────────────── */
  const pageLock = (d: Doc, i: number): string | null => {
    const pg = d.pages[i];
    if (!pg) return null;
    const r = INV[pg.id];
    if (!r) return null;
    return d.pages.filter((x) => x.id === pg.id).length <= 1 ? r : null;
  };
  const addPage = (typeId: string, flowAt: number | null) => {
    if (!D) return;
    const t = regOf(typeId);
    const n = D.pages.filter((x) => x.id === typeId).length;
    const uid = njUid("pg");
    const page = mkPage(typeId, t.label + (n ? ` ${n + 1}` : ""), typeId === "content" ? { uid, content: [{ uid: uid + "b", type: "text", text: i18nT("journeys.newJourneys.defaultContentBlock", "Add your copy here — pick more blocks from the palette.") }] } : { uid });
    const newIdx = D.pages.length;
    patchDoc((d) => {
      /* The new step joins the TRUNK after the last page step there. Pages
         inside a decision branch keep their place; a page added from the Pages
         view has no branch to belong to. */
      const m = (d.flow?.steps ?? []).slice();
      let pos = flowAt;
      if (pos == null) { pos = 0; m.forEach((st, i) => { if (st.kind === "page") pos = i; }); }
      m.splice((pos ?? 0) + 1, 0, { id: uid, kind: "page", page: String(newIdx), cfg: {} });
      return { ...d, pages: [...d.pages, page], flow: { ...d.flow, steps: m } };
    });
    set({ typePicker: null, cur: newIdx, sel: null, selStep: uid });
    showToast(i18nT("journeys.newJourneys.toast.pageAdded", "{page} page added — the flow now has a step for it", { page: t.label }));
  };
  const removePage = (i: number) => {
    if (!D || D.pages.length <= 1) return;
    const lock = pageLock(D, i);
    if (lock) { showToast(t("journeys.newJourneys.toast.blocked", "Blocked — {reason} Core invariants are enforced, not advised.", { reason: lock })); return; }
    /* One walk over the whole tree: drop the deleted page's step wherever it
       sits (trunk or any branch) and shift the indices above it down. The three
       flat lists needed three passes and still missed nested decisions. */
    const fix = (steps: QFStep[]): QFStep[] => qfMapAll(steps, (st) => {
      if (st.kind !== "page") return st;
      const idx = Number(st.page);
      if (idx === i) return null;
      return idx > i ? { ...st, page: String(idx - 1) } : st;
    });
    patchDoc((d) => ({ ...d, pages: d.pages.filter((_, x) => x !== i), flow: { ...d.flow, steps: fix(d.flow?.steps ?? []) } }));
    let c = curIdx; if (i < c) c -= 1;
    set({ cur: Math.max(0, Math.min(c, D.pages.length - 2)), sel: null });
  };
  const clearDrag = { dragPg: null, dragOver: null, dragStep: null, dragOverStep: null } as const;
  const movePage = (from: number | null, to: number | null) => {
    if (!D || from == null || to == null || from === to) { set({ ...clearDrag }); return; }
    const order = D.pages.map((_, i) => i);
    order.splice(to, 0, order.splice(from, 1)[0]!);
    const map: Record<number, number> = {};
    order.forEach((oldI, newI) => { map[oldI] = newI; });
    const preview = order.map((i) => D.pages[i]!);
    const ci = preview.map((x) => x.id).lastIndexOf("apptconfirm");
    if (ci !== -1 && ci !== preview.length - 1) {
      set({ ...clearDrag });
      showToast(t("journeys.newJourneys.toast.confirmationLast", "Blocked — Confirmation must stay the last page of the funnel. Core invariants are enforced, not advised."));
      return;
    }
    patchDoc((d) => {
      const pages = order.map((i) => d.pages[i]!);
      const remap = (steps: QFStep[]): QFStep[] => qfMapAll(steps, (st) =>
        st.kind === "page" ? { ...st, page: String(map[Number(st.page)] ?? Number(st.page)) } : st);
      const fm = remap(d.flow?.steps ?? []);
      /* Trunk page steps follow the new page order; branch pages keep theirs,
         because a branch's sequence is the operator's, not the page list's. */
      const slots: number[] = [];
      fm.forEach((st, i) => { if (st.kind === "page") slots.push(i); });
      const inOrder = slots.map((i) => fm[i]!).slice().sort((a, b) => Number((a as { page: string }).page) - Number((b as { page: string }).page));
      slots.forEach((pos, j) => { fm[pos] = inOrder[j]!; });
      return { ...d, pages, flow: { ...d.flow, steps: fm } };
    });
    set({ cur: map[curIdx] ?? 0, ...clearDrag });
    showToast(t("journeys.newJourneys.toast.pagesReordered", "Pages reordered — the flow follows"));
  };
  const insertStep = (idx: number, step: QFStep) => {
    patchDoc((d) => { const m = (d.flow?.steps ?? []).slice(); m.splice(idx + 1, 0, step); return { ...d, flow: { ...d.flow, steps: m } }; });
    set({ addMenu: null, selStep: step.id });
  };

  /* ── Block helpers ──────────────────────────────────────────────── */
  const bval = (c: Block, k: string): any => (c && c[k] !== undefined ? c[k] : (BLOCK_DEF[c.type] ?? {})[k]);
  const setBval = (uid: string, k: string, v: unknown) => patchContent((list) => mapContent(list, (x) => (x.uid === uid ? { ...x, [k]: v } : x)));
  const pval = (k: string): any => (p && p[k] !== undefined ? p[k] : (CORE_DEF[p?.id ?? ""] ?? {})[k]);
  const tg = (k: string) => { const t = p?.toggles.find((x) => x.k === k); return t ? t.on : false; };
  /* How the customer leaves this page. Derived from the page's position and the
     journey's commit point (nj-model/stepNavFor) — the canvas draws it, the
     inspector only ever edits the commit page's wording. */
  const pageNav = D ? stepNavFor(D as any, curIdx) : null;
  const commitLabelDefault = String(D?.commitLabel || COMMIT_LABEL_BY_TYPE[D?.type ?? ""] || t("journeys.newJourneys.commitLabelDefault", "Confirm"));
  const applyImage = (c: Block, urls: string[]) => {
    if (!urls.length) return;
    if (c.type === "gallery") {
      patchContent((list) => list.map((x) => {
        if (x.uid !== c.uid) return x;
        const have = String(x.urls ?? "").split("\n").filter(Boolean);
        const next = have.concat(urls);
        return { ...x, urls: next.join("\n"), count: String(Math.max(Number(x.count ?? 3), next.length)) };
      }));
      showToast(t("journeys.newJourneys.toast.imagesAdded", "{count, plural, one {# image added to the gallery} other {# images added to the gallery}}", { count: urls.length }));
      return;
    }
    const key = c.type === "hero" ? "img" : "src";
    patchContent((list) => list.map((x) => (x.uid === c.uid ? { ...x, [key]: urls[0], ...(c.type === "hero" ? { bg: "Photo" } : {}) } : x))); // ui-config: stored background kind, not copy
    showToast(c.type === "hero" ? t("journeys.newJourneys.toast.heroBackgroundSet", "Hero background set") : t("journeys.newJourneys.toast.imageSet", "Image set"));
  };
  const dropImage = (c: Block) => (e: any) => {
    e.preventDefault(); e.stopPropagation();
    const files = Array.from<File>(e.dataTransfer?.files ?? []).filter((f) => /^image\//.test(f.type));
    const url = e.dataTransfer?.getData ? (e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain")) : "";
    if (!files.length && url) { applyImage(c, [url]); return; }
    if (!files.length) { showToast(t("journeys.newJourneys.toast.dropImage", "Drop an image file, or paste a URL in the panel on the right")); return; }
    Promise.all(files.map((f) => new Promise<string>((res) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.readAsDataURL(f); }))).then((urls) => applyImage(c, urls));
  };
  const cfgFor = (c: Block): CfgRow[] => {
    if (c.type === "button") {
      const kind = bval(c, "linkKind");
      return (BLOCK_CFG.button ?? []).concat([["linkKind", t("journeys.newJourneys.cfg.linksTo", "Links to"), "sel", ["URL", "Content page"]]] as CfgRow[], // ui-config: the option values URL / Content page are stored and compared; only the field label is translated
        kind === "Content page" ? ([["page", t("journeys.newJourneys.cfg.page", "Page"), "sel", ["Pick a page…"].concat((D?.pages ?? []).map((x) => x.label))]] as CfgRow[]) : ([["href", t("journeys.newJourneys.cfg.url", "URL"), "line"]] as CfgRow[])); // ui-config: "Pick a page…" is a stored sentinel compared on the canvas; only the field labels are translated
    }
    if (c.type === "social") {
      const out: CfgRow[] = [];
      NETS.forEach((n) => { out.push([n[0], n[1], "tog"]); out.push([n[0] + "Url", t("journeys.newJourneys.cfg.networkLink", "{network} link", { network: n[1] }), "line", undefined, !bval(c, n[0])]); });
      return out;
    }
    return BLOCK_CFG[c.type] ?? [];
  };

  /* ── Experiences ────────────────────────────────────────────────── */
  const expJourney = (key: string) => {
    const x = exps[key];
    if (x) return { key, name: x.name, ty: "Experience", tint: TYPE_TINT.Experience![0], color: TYPE_TINT.Experience![1], pages: x.attached.length }; // ui-config: ty is a type key (TYPE_GLYPH / TYPE_TINT lookups), not copy
    const d = docs[key];
    if (!d) return { key, name: t("journeys.newJourneys.missingJourney", "Missing journey"), ty: "Pages", tint: "#F4F5F7", color: "#697180", pages: 0 }; // ui-config: ty is a type key (TYPE_GLYPH lookup), not copy
    const ty = docType(d);
    const c = TYPE_TINT[ty] ?? TYPE_TINT.Pages!;
    return { key, name: d.name, ty, tint: c[0], color: c[1], pages: d.pages.length };
  };
  const E: Exp | null = ui.curExp && exps[ui.curExp] ? exps[ui.curExp]! : null;
  const ownerKey = Object.keys(exps).find((k) => exps[k]!.anchor === ui.curDoc || exps[k]!.attached.some((a) => a.doc === ui.curDoc));
  const owner = ownerKey ? exps[ownerKey]! : null;

  /* ── List rows (real objects + experiences) ─────────────────────── */
  const openDoc = (k: string, fromExp = "") => {
    const d = docs[k];
    const ps = qfFlatten(d?.flow?.steps ?? []).filter((st) => st.kind === "page")[0];
    set({ route: "designer", curDoc: k, view: "pages", cur: 0, sel: null, simCalled: false, selStep: ps?.id ?? null, addMenu: null, fromExp });
  };
  const allRows = useMemo(() => {
    const rows: any[] = [];
    Object.keys(exps).forEach((k) => {
      const e = exps[k]!;
      const inside = (e.anchor ? 1 : 0) + e.attached.length;
      rows.push({
        key: k, glyph: "◈", pageCount: inside, name: e.name, type: "Experience", typeText: njTypeName("Experience"),
        sub: t("journeys.newJourneys.experienceSub", "Experience — {anchor} + {count, plural, one {# attached journey} other {# attached journeys}}, one check-in", { anchor: e.anchor ? (docs[e.anchor]?.name ?? t("journeys.newJourneys.missingAnchor", "missing anchor")) : t("journeys.newJourneys.noAnchorYet", "no anchor yet"), count: e.attached.length }),
        tint: TYPE_TINT.Experience![0], color: TYPE_TINT.Experience![1],
        status: e.status, statusText: njStatusText(e.status, false), stColor: e.status === "Live" ? "#027D47" : "#916D0F",
        pages: t("journeys.newJourneys.journeyCount", "{count, plural, one {# journey} other {# journeys}}", { count: inside }), updated: e.updated, isExp: true, edited: true,
        /* Experiences have no audit block — they are their own resource with its
           own store, and stamping them was not part of this ask. Empty cells. */
        created: { who: "", when: "", title: "" } as AuditCell, modified: { who: "", when: "", title: "" } as AuditCell, mts: 0,
        onOpen: () => set({ route: "experience", curExp: k, expPicker: null, fromExp: "" }),
      });
    });
    docKeys.forEach((k) => {
      const d = docs[k]!;
      const ty = docType(d);
      const TT = TYPE_TINT[ty] ?? TYPE_TINT.Pages!;
      rows.push({
        key: k, glyph: TYPE_GLYPH[ty] ?? "▤", pageCount: d.pages.length,
        /* The name an author would recognise. `doc.name` is the designer
           header's field; a journey named only in Setup has an untitled one, and
           the row read "Untitled queue journey" while the queue was called
           something. The mirror in patchQueue keeps them in step from now on —
           this covers documents SAVED BEFORE that, which would otherwise stay
           untitled in the list forever. */
        name: (() => {
          const own = (d.name ?? "").trim();
          if (own && !/^untitled/i.test(own)) return own;
          const held = String(
            (d.setup as { title?: unknown } | undefined)?.title
            ?? (d.qsetup as { name?: unknown } | undefined)?.name
            ?? (d.boardCfg as { name?: unknown } | undefined)?.name
            ?? "",
          ).trim();
          return held || own || t("journeys.newJourneys.untitled.journey", "Untitled journey");
        })(),
        sub: d.sub,
        type: ty, typeText: njTypeName(ty), tint: TT[0], color: TT[1],
        status: d.edited ? `${d.status} · edited` : d.status, // ui-config: status is the identity the Live/Draft filters and the CSV read; statusText is what shows
        statusText: njStatusText(d.status, !!d.edited),
        stColor: /^Live/.test(d.status) ? "#027D47" : /^Cancel/.test(d.status) ? "#D01647" : "#916D0F",
        /* The history the row can honestly tell. A journey saved in the designer
           carries its own stamps; a derived row nobody has opened falls back to
           the OWNING record's created/modified fields, and shows nothing at all
           when that record carries none (see nj-audit / the filed API request). */
        pages: String(d.pages.length), updated: d.edited ? t("journeys.newJourneys.updatedJustNow", "Just now") : d.updated === "—" ? t("journeys.newJourneys.notEdited", "Not edited") : d.updated, edited: d.edited,
        created: createdCell(d.audit), modified: modifiedCell(d.audit),
        /** Sort key for "Recently updated": the real instant when there is one. */
        mts: d.audit?.updatedAt ? (Date.parse(d.audit.updatedAt) || 0) : 0,
        origin: d.origin, isExp: false, onOpen: () => openDoc(k),
      });
    });
    return rows;
  }, [exps, docs, docKeys.join("|")]);

  const liveCount = allRows.filter((r) => /^Live/.test(r.status)).length;
  const draftCount = allRows.filter((r) => /^Draft/.test(r.status)).length;
  const expCount = allRows.filter((r) => r.type === "Experience").length;
  /* Folder organization — the shared Folder Library shell (family "journeys"),
   * the same one Messages, Booking questions, Surveys and Events mount. Items
   * are filed by journey key, so a queue, event, board or experience files the
   * same way. */
  const jlib = useFolderLibrary({
    family: "journeys",
    ids: allRows.map((r) => r.key),
    canWrite: true,
    allLabel: t("journeys.newJourneys.lib.allJourneys", "All journeys"),
    searchAllLabel: t("journeys.newJourneys.lib.searchAll", "Search all journeys"),
    query: ui.listQuery,
    failTitle: t("journeys.newJourneys.lib.fileFailed", "Couldn't file that journey"),
  });
  const jPool = new Set(jlib.pool);
  const selSet = Array.from(jlib.sel);
  const clearSel = () => jlib.setSel(new Set());
  /** Type counts are folder-scoped, like every other count on this screen. */
  const typeCounts = allRows.filter((r) => jPool.has(r.key)).reduce((m: Record<string, number>, r) => { m[r.type] = (m[r.type] ?? 0) + 1; return m; }, {});
  const TYPE_ORDER = ["Queue", "Event", "Appointment", "Board", "Experience", "Mixed", "Pages"]; // ui-config: type keys; the filter chips translate them for display
  const q = ui.listQuery.trim().toLowerCase();
  const filtering = !!q || ui.listFilter !== "all" || ui.listType !== "all";
  let rows = allRows.filter((r) => {
    if (!jPool.has(r.key)) return false;
    if (ui.listType !== "all" && r.type !== ui.listType) return false;
    if (ui.listFilter === "live" && !/^Live/.test(r.status)) return false;
    if (ui.listFilter === "draft" && !/^Draft/.test(r.status)) return false;
    if (ui.listFilter === "exp" && r.type !== "Experience") return false;
    if (!q) return true;
    return `${r.name} ${r.type} ${r.typeText} ${r.sub}`.toLowerCase().includes(q);
  });
  if (ui.listSort === "name") rows = rows.slice().sort((a, b) => a.name.localeCompare(b.name));
  /* Recently updated: real timestamps first (newest first), then the rows that
     only know they were edited here, then the rest — A–Z inside each band. */
  else if (ui.listSort === "updated") rows = rows.slice().sort((a, b) => (b.mts - a.mts) || Number(b.edited) - Number(a.edited) || a.name.localeCompare(b.name));
  else if (ui.listSort === "type") rows = rows.slice().sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name));
  else if (ui.listSort === "pages") rows = rows.slice().sort((a, b) => b.pageCount - a.pageCount);
  const allChecked = rows.length > 0 && rows.every((r) => jlib.sel.has(r.key));
  const someChecked = selSet.length > 0 && !allChecked;

  const exportCsv = (keys: string[]) => {
    const head = ["name", "type", "status", "pages", "created by", "created", "last modified by", "last modified", "source"]; // ui-config: CSV export header, a data format
    const body = keys.map((k) => {
      const r = allRows.find((x) => x.key === k);
      if (!r) return "";
      return [r.name, r.type, r.status, r.pages, r.created.who, r.created.when, r.modified.who, r.modified.when || r.updated, r.origin?.label ?? (r.isExp ? "experience" : "—")]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",");
    }).filter(Boolean);
    const blob = new Blob([[head.join(","), ...body].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `journeys-${slug}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    showToast(t("journeys.newJourneys.toast.exportedCsv", "{count, plural, one {Exported # journey as CSV} other {Exported # journeys as CSV}}", { count: body.length }));
  };
  const duplicateKeys = (keys: string[]) => {
    let n = 0;
    keys.forEach((k) => {
      const d = docs[k];
      if (!d) return;
      const key = `local:${njUid("nj")}`;
      /* A copy is a NEW journey: dropping the source's audit block lets saveDoc
         stamp whoever duplicated it as the creator, not the original author. */
      saveDoc(key, { ...JSON.parse(JSON.stringify(d)), name: t("journeys.newJourneys.copyName", "{name} (copy)", { name: d.name }), status: "Draft", updated: t("journeys.newJourneys.updatedJustNow", "Just now"), edited: false, audit: undefined, origin: { kind: "local", id: key, label: t("journeys.newJourneys.copiedFrom", "Copied from {name}", { name: d.name }) } });
      n += 1;
    });
    set({ listSel: [] });
    showToast(t("journeys.newJourneys.toast.duplicated", "{count, plural, one {Duplicated # journey as a draft} other {Duplicated # journeys as drafts}}", { count: n }));
  };
  const deleteKeys = (keys: string[]) => {
    const local = keys.filter((k) => k.startsWith("local:") || exps[k]);
    const owned = keys.filter((k) => !local.includes(k));
    local.forEach((k) => {
      if (exps[k]) { setExps((x) => { const n = { ...x }; delete n[k]; return n; }); void deleteExperience(slug, k); }
      else { setOverlays((o) => { const n = { ...(o ?? {}) }; delete n[k]; return n; }); void deleteJourneyDoc(slug, k); }
    });
    set({ listSel: [] });
    if (local.length && owned.length) showToast(t("journeys.newJourneys.toast.deletedSomeOwned", "Deleted {deleted} · {owned} belong to a queue or event — delete those in their own screen", { deleted: local.length, owned: owned.length }));
    else if (local.length) showToast(t("journeys.newJourneys.toast.deleted", "{count, plural, one {Deleted # journey} other {Deleted # journeys}}", { count: local.length }));
    else showToast(t("journeys.newJourneys.toast.allOwned", "These journeys belong to a queue or event — delete the object in its own screen"));
  };
  const renameRow = (key: string, name: string) => {
    const clean = name.trim();
    if (!clean) { set({ renameKey: "", rowMenu: null }); return; }
    if (exps[key]) {
      const next = { ...exps[key]!, name: clean, updated: t("journeys.newJourneys.updatedJustNow", "Just now") };
      setExps((x) => ({ ...x, [key]: next }));
      void putExperience(slug, key, next);
      set({ renameKey: "", rowMenu: null });
      showToast(t("journeys.newJourneys.toast.renamed", "Renamed to “{name}”", { name: clean }));
      return;
    }
    const d = docs[key];
    if (!d) return;
    /* saveDoc pushes the name onto the owning queue too (updateQueue owns `name`). */
    saveDoc(key, { ...d, name: clean, edited: true, updated: t("journeys.newJourneys.updatedJustNow", "Just now") });
    set({ renameKey: "", rowMenu: null });
    showToast(WRITES_BACK(d.origin?.kind) ? t("journeys.newJourneys.toast.renamedSaving", "Renamed to “{name}” — saving onto the {noun}", { name: clean, noun: njOwnerNoun(d.origin!.kind) }) : t("journeys.newJourneys.toast.renamed", "Renamed to “{name}”", { name: clean }));
  };

  const revertKeys = (keys: string[]) => {
    let n = 0;
    keys.forEach((k) => { if (!k.startsWith("local:") && (overlays ?? {})[k]) { setOverlays((o) => { const x = { ...(o ?? {}) }; delete x[k]; return x; }); void deleteJourneyDoc(slug, k); n += 1; } });
    set({ listSel: [] });
    showToast(n ? t("journeys.newJourneys.toast.reverted", "{count, plural, one {Reverted # journey to what its object says} other {Reverted # journeys to what its object says}}", { count: n }) : t("journeys.newJourneys.toast.nothingToRevert", "Nothing to revert — no unsaved designer edits in the selection"));
  };

  const loading = overlays === null || (!queuesLoaded && !docKeys.length);

  /* ── Template picker ──────────────────────────────────────────────
     THE FOUR v1 JOURNEY TYPES, in scope order (operator 2026-08-31): Queue,
     Appointment, Event, One-Stop. The picker is the product's statement of what
     a journey can be, so anything not in v1 does not appear here.

     Removed rather than disabled: `board` (a display board is a lobby SCREEN,
     not a journey a visitor travels — it is still created from Queues ▸ Boards
     and still opens in this designer when derived) and `blank` (a journey with
     no type cannot be attached to a record, so it dead-ends at the one thing
     every template exists to reach). Both template paths still exist in
     `nj-model.templateDoc` and `docFromBoard`, so derived and existing
     documents keep working — only the entry point is scoped.

     One-Stop keeps id `exp`: the record it creates IS the experience document
     (slug + header + ordered blocks), which is what a One-Stop page hosts. Only
     the operator-facing name changed. As of 2026-09-01 it creates a real
     experience PAGE rather than the old anchor-and-attach record. */
  const templates = [
    { id: "queue", name: t('journeys.newJourneys.name', "Queue"), sub: t("journeys.newJourneys.template.queue.sub", "Join → wait → contact → in line → CSAT, flow seeded"), glyph: "☰", tint: "#FAF1DE", color: "#8A5B12", tagLabel: "V1" },
    { id: "appt", name: t('journeys.newJourneys.name2', "Appointment"), sub: t("journeys.newJourneys.template.appt.sub", "Service → time → contact → confirm"), glyph: "◔", tint: "#E0F2F1", color: "#0AA396", tagLabel: "V1" },
    { id: "event", name: t('journeys.newJourneys.name3', "Event"), sub: t("journeys.newJourneys.template.event.sub", "Landing → register → tickets"), glyph: "◆", tint: "#FDEBF3", color: "#D61A55", tagLabel: "V1" },
    { id: "exp", name: t('journeys.newJourneys.name4', "One-Stop"), sub: t("journeys.newJourneys.template.exp.sub", "One page — the desks, services and events a student can reach in one visit"), glyph: "◈", tint: "#E4E1F7", color: "#262265", tagLabel: "V1" },
  ];
  const pickTemplate = (id: string, prefill?: { start?: string; end?: string }, opts?: { fresh?: boolean }) => {
    /* One-Stop is a journey DOCUMENT like every other type — same designer, same
       page rail, same canvas, same inspector, same preview, same undo stack, same
       autosave. It differs only in what its page holds: One-Stop registry blocks
       instead of the designer's copy blocks. Nothing about it needs a screen of
       its own, and giving it one was the mistake this replaces. */
    /* The `as any` pair was here only because `templateDoc`'s union omitted
       "onestop" while the function already had an "onestop" branch (2026-09-08).
       The union carries it now, so the first cast is gone; `id` stays cast
       because it arrives as a plain string from the picker. */
    /* Add Event resumes the author's own unfinished event draft (CC, 2026-10-06): one typed into
       but never saved as an event is kept on its journey, and is reopened here rather than
       left behind as an orphan while a second blank one starts. Save draft or Publish creates
       the event and removes it. */
    if (id === "event" && !prefill?.start && !prefill?.end && !opts?.fresh) {
      const me = currentActor();
      const mine = (d: Doc) => !!me && d.audit?.from === "designer" && !!d.audit.createdBy
        && ((me.id && d.audit.createdBy.id === me.id) || (!!me.email && d.audit.createdBy.email === me.email));
      const resumeKey = Object.keys(docs).find((k) => k.startsWith("local:") && !unsavedRef.current.has(k)
        && docs[k]?.type === "Event" && docs[k]?.origin?.kind === "local" && mine(docs[k]!));
      if (resumeKey) { setResumeAsk(resumeKey); return; }
    }
    const r = templateDoc(id === "exp" ? "onestop" : (id as Parameters<typeof templateDoc>[0]));
    if (id === "exp") {
      const skeleton = oneStopSkeletonBlocks({ locale: "en-US", timeZone: tzContext().zone });
      const page = r.doc.pages[0]!;
      page.osBlocks = skeleton.blocks.map((b) => ({ uid: njUid("os"), ...b })) as any;
      page.osSettings = { navLabel: null, locale: "en-US", supportedLocales: ["en-US"], timeZone: tzContext().zone };
    }
    // Held in memory, not written: the first edit persists it (CC 0122).
    unsavedRef.current.add(r.key);
    /* A start/end from the calendar click lands in the new event's Setup (the date picker's ISO wall-clock shape) (CC, 2026-10-05). */
    const seeded = r.doc.type === "Event" && (prefill?.start || prefill?.end)
      ? { ...r.doc, setup: { ...((r.doc as { setup?: object }).setup ?? { id: "", title: r.doc.name ?? "", status: "draft" }), ...(prefill.start ? { start: prefill.start } : {}), ...(prefill.end ? { end: prefill.end } : {}) } }
      : r.doc;
    setOverlays((o) => ({ ...(o ?? {}), [r.key]: { ...seeded, audit: stampSave(r.doc.audit) } }));
    const ps = qfFlatten(r.doc.flow?.steps ?? []).filter((st) => st.kind === "page")[0];
    const firstSetup = r.doc.type === "Event" ? "basics" : r.doc.type === "Queue" ? "qsetup" : r.doc.type === "Board" ? "boardcfg" : r.doc.type === "One-Stop" ? "onestopcfg" : null;
    set({ tplOpen: false, route: "designer", curDoc: r.key, view: "pages", cur: 0, sel: null, selStep: ps?.id ?? null, setupView: firstSetup as any });
    /* An event draft is held in memory, not written (CC 0122): the toast said "Event created" for a record
       that did not exist. It says what happened (CC, 2026-10-04). Other templates keep their message. */
    showToast(id === "event"
      ? t("journeys.newJourneys.toast.eventDraftStarted", "Draft started — Publish creates the event")
      : t("journeys.newJourneys.toast.templateCreated", "{name} created — attach it to a real object from its pages", { name: templates.find((t) => t.id === id)?.name ?? "" }));
  };

  /* ── Deep links (2026-08-25) ──────────────────────────────────────
     The Events list is the create/edit/preview entry point for events, so
     `newjourneys` has to accept being told what to open:

       ?new=event                       → start a new Event journey in the designer
       ?journey=event:<id>              → open that event's journey
       ?journey=event:<id>&preview=1    → open it and show the customer preview

     Before this, every one of those landed on the journeys list and the caller's
     intent was dropped. The params are consumed ONCE and then replaced out of the
     URL, so a refresh can't mint a second draft. A `journey` link waits for the
     document to exist — event rows arrive asynchronously from useEvents. */
  const linkRef = useRef("");
  useEffect(() => {
    if (overlays === null) return;
    const want = String(nav.params?.journey ?? "");
    const fresh = String(nav.params?.new ?? "");
    const preview = String(nav.params?.preview ?? "") === "1";
    const sig = `${want}|${fresh}|${preview}`;
    if ((!want && !fresh) || linkRef.current === sig) return;
    if (want) {
      if (!docs[want]) {
        /* An event the list does not carry (another scope, a later page, a list read
           that failed) is read on its own once the list has settled, and opens like any
           other. A refused read says so and opens nothing: never a blank draft, which a
           save would turn into a second event (CC, 2026-10-06). */
        const evId = want.startsWith("event:") ? want.slice("event:".length) : "";
        if (!evId || eventsLoading || linkRef.current === `${sig}|read`) return; // not loaded yet — try again next render
        linkRef.current = `${sig}|read`;
        loadEventDoc(evId).then((res) => {
          if (!res) throw new Error("not found");
          eBase.current[evId] = res.record;
          setRecords((r) => ({ ...r, [evId]: res.record }));
          setLinkedDocs((m) => ({ ...m, [res.key]: res.doc }));
        }).catch(() => {
          linkRef.current = sig;
          showToast(t("journeys.newJourneys.toast.eventOpenFailed", "Couldn't open this event — it may have been removed, or you may not have access to it."));
          nav.replace("newjourneys");
        });
        return;
      }
      linkRef.current = sig;
      openDoc(want);
      if (preview) set({ previewOpen: true });
    } else {
      const tpl = templates.some((t) => t.id === fresh) ? fresh : "blank";
      linkRef.current = sig;
      pickTemplate(tpl, { start: String(nav.params?.start ?? "") || undefined, end: String(nav.params?.end ?? "") || undefined });
    }
    nav.replace("newjourneys");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav.params?.journey, nav.params?.new, nav.params?.preview, overlays, docKeys.join("|"), eventsLoading]);

  /* Esc leaves the preview; leaving the designer closes it too. The listener is
     on the window in the CAPTURE phase, so a nested overlay that stops keydown
     propagation can't strand the operator inside the preview. */
  useEffect(() => {
    if (!ui.previewOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setUi((u) => ({ ...u, previewOpen: false })); };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [ui.previewOpen]);
  useEffect(() => {
    if (ui.previewOpen && ui.route !== "designer") setUi((u) => ({ ...u, previewOpen: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.route]);

  /* ── Library picker rows ────────────────────────────────────────── */
  const libRows = (kind: LibKind) => {
    if (kind === "questions") return qGroups.map((g) => ({ id: g.id, name: g.name || t("journeys.newJourneys.untitled.group", "Untitled group"), meta: t("journeys.newJourneys.meta.questionGroup", "{count, plural, one {# question} other {# questions}} · {status}", { count: g.items?.length ?? 0, status: g.status }), on: attGroup?.id === g.id, pick: () => { attachQuestionGroup(g.id); showToast(t("journeys.newJourneys.toast.attachedQuestions", "Attached \"{name}\" — the journey references it by id (ruling ④)", { name: g.name })); }, edit: () => goRoute("questions.group.edit", { id: g.id }) }));
    if (kind === "survey") return surveys.map((s: any) => ({ id: s.id, name: s.name || t("journeys.newJourneys.untitled.survey", "Untitled survey"), meta: t("journeys.newJourneys.meta.survey", "{count, plural, one {# question} other {# questions}}", { count: surveyItemCount(s) }), on: attSurvey?.id === s.id, pick: () => {
      /* Writes where that journey type keeps it — the event record for an event,
         the document for the rest. One picker, two homes, rather than a picker
         that quietly writes to the wrong one. */
      if (isEventJourney) {
        const next = [s.id];
        patchRecord((x) => ({ ...x, surveys: next }));
        if (eventOriginId) void holdEvent("messaging", { surveys: next });
      } else {
        setAttach({ surveyId: s.id });
      }
      showToast(t("journeys.newJourneys.toast.attachedSurvey", "Attached \"{name}\" — edits open the surveys library", { name: s.name }));
    }, edit: () => goRoute("surveys.edit", { id: s.id }) }));
    if (kind === "tickets") return ticketGroups.map((g) => ({ id: g.id, name: g.name || t("journeys.newJourneys.untitled.group", "Untitled group"), meta: g.description || t("journeys.newJourneys.meta.ticketGroupV2", "{count, plural, one {Used by # event} other {Used by # events}}", { count: g.eventCount }), on: v2SelGroupId === g.id, disabled: !!eventOriginId && v2.applying, pick: () => { if (eventOriginId && v2.applying) return; attachTicketGroup(g.id); showToast(eventOriginId ? t("journeys.newJourneys.toast.appliedTicketsV2", "Applying \"{name}\" to this event", { name: g.name }) : t("journeys.newJourneys.toast.chosenTicketsV2", "\"{name}\" will be applied when you publish", { name: g.name })); }, edit: () => { set({ lib: null }); goEventTickets(); } }));
    if (kind === "promo") return coupons.map((c: any) => ({ id: c.id, name: c.code || c.name || t("journeys.newJourneys.untitled.promo", "Untitled promo"), meta: [c.type, c.status].filter(Boolean).join(" · "), on: promoIds.includes(c.id), pick: () => {
      /* Writes where that journey type keeps it — the event record for an event,
         the document for anything that still has no owner field. Toggles rather
         than replaces: an event accepts several codes, so picking a second must
         not silently drop the first. */
      if (isEventJourney) {
        const cur = r0.coupons ?? [];
        const next = cur.includes(c.id) ? cur.filter((x: string) => x !== c.id) : [...cur, c.id];
        patchRecord((x) => ({ ...x, coupons: next }));
        if (eventOriginId) void holdEvent("tickets", { coupons: next });
        showToast(next.includes(c.id) ? t("journeys.newJourneys.toast.acceptingCoupon", "Accepting \"{name}\" on this event", { name: c.code ?? c.name }) : t("journeys.newJourneys.toast.removedCoupon", "Removed \"{name}\"", { name: c.code ?? c.name }));
        return;
      }
      setAttach({ promoId: c.id });
      showToast(t("journeys.newJourneys.toast.attachedPromo", "Attached \"{name}\" to the win-back message", { name: c.code ?? c.name }));
    }, edit: () => goRoute("promos.edit", { id: c.id }) }));
    /* The step being attached to is the one selected on the flow canvas. This
       row used to read an undefined `selStep` and write through `d.flowMain`,
       so it could only ever have touched a trunk step — now the tree walk
       reaches a step inside any branch. */
    const selId = ui.selStep;
    const selFlowStep = selId ? qfFlatten(qfFlow.steps).filter((st) => st.id === selId)[0] : null;
    const selMsgId = selFlowStep && selFlowStep.kind === "comms" ? selFlowStep.messageId : null;
    return messages.map((m: any) => ({ id: m.id, name: m.name || t("journeys.newJourneys.untitled.message", "Untitled message"), meta: `${(m.channels ?? []).map((ch: string) => t("journeys.newJourneys.messageChannel", "{channel, select, email {email} sms {sms} whatsapp {whatsapp} push {push} other {{channel}}}", { channel: ch })).join(" · ")} · ${t("journeys.newJourneys.messageStatus", "{status, select, draft {draft} live {live} other {{status}}}", { status: String(m.status ?? "") })}`, on: selMsgId === m.id, pick: () => { if (selId) patchDoc((d) => ({ ...d, flow: { ...d.flow, steps: qfMapAll(d.flow?.steps ?? [], (st) => (st.id === selId && st.kind === "comms" ? { ...st, messageId: m.id, hint: undefined } : st)) } })); showToast(t("journeys.newJourneys.toast.stepSends", "Step now sends \"{name}\" from the message library", { name: m.name })); }, edit: () => goRoute("messaging.compose", { id: m.id }) }));
  };

  /* ══════════════════════════════════════════════════════════════════
     Render
     ══════════════════════════════════════════════════════════════════ */
  const shell = (children: any, noTabs = false) => (
    /* Journeys-section tabs (2026-08-31): Journeys · Confirmation pages. The
       confirmation-page count is not loaded here — that screen owns it.

       WITHHELD IN THE DESIGNER (2026-09-16, PP): the strip is a single tab
       labelled "Journeys", and inside a document it points at the list you
       already came from — a section switcher with nothing to switch to, above a
       screen that is not the section's list. It still renders on the LIST,
       where it is the section's own tab bar and where a sibling tab can join
       it. `ui.route` is the whole condition; nothing else changed. */
    <Screen rail="new-journeys" tabs={ui.route === "home" && !noTabs ? journeysSectionTabs(nav, "newjourneys") : undefined} activeTab="newjourneys">
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "#F4F5F7", fontFamily: FONT, color: "#333333", fontSize: 13 }}>{children}</div>
    </Screen>
  );

  /* ════════════════════════════════════════════════════════════════════
   *  NO HOOKS BELOW THIS LINE.
   *
   *  The early return underneath is the reason. Everything after it runs only
   *  when `loading` is false, so a `useState` / `useEffect` / `useMemo` /
   *  `useRef` — or any custom hook that calls one — placed below runs on some
   *  renders and not others. React reports that as the unhelpful minified error
   *  #310 ("rendered more hooks than during the previous render"), pointing at
   *  the new hook rather than at this return.
   *
   *  This file is ~5,000 lines and its render body reads like top-to-bottom
   *  markup, so "declare it next to what uses it" is the natural instinct and
   *  the wrong one. THREE features have now tripped over it: the live-ops
   *  composer, the guest fields, and the drag auto-scroll. Every hook belongs in
   *  the block above; the value it returns can be used anywhere below.
   *  ═══════════════════════════════════════════════════════════════════ */
  /* An existing event's applied group is the record's ticket group in the designer
     (CC 0195; must sit ABOVE the loading guard — hooks after an early return crash with #310), so the readiness check and the step agree with Events → Tickets. */
  useEffect(() => {
    const gid = v2.ticketing?.ticketGroupId;
    if (gid && ((r0.ticketGroupIds ?? []) as string[])[0] !== gid) patchRecord((x) => ({ ...x, ticketGroupIds: [gid] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v2.ticketing?.ticketGroupId]);
  /* The event's Publish / Unpublish / Cancel dialog (CC 0305 item 3). Above the loading guard,
     like every hook here (CC 0319): a hook after the early return crashes the render. */
  const [eventAsk, setEventAsk] = useState<null | "publish" | "unpublish" | "cancel">(null);
  /** The editor's own layout as ghosts (JAI-2519): the Setup rail on desktop, the form, and on a wide screen the
   *  customer preview at the preview column's width, with no section tabs (the editor shows none). Drawn while the
   *  journey store loads for a link that opens a journey, and while that link has not opened yet. */
  const editorLoadingSkeleton = (label: string, testId: string) => {
    const skelColW = Math.min(pvColW, pvColMax());
    return (
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div aria-hidden="true" style={{ flex: "none", display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", background: "#FFFFFF", borderBottom: "1px solid #E6E8EC" }}>
          <SkeletonBox width={28} height={28} />
          <SkeletonBox variant="text" width={180} height={16} />
          <span style={{ flex: 1 }} />
          <SkeletonBox width={92} height={32} />
          <SkeletonBox width={92} height={32} />
        </div>
        <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
          {njDesktop ? (
            <div aria-hidden="true" style={{ flex: "0 0 236px", display: "flex", flexDirection: "column", gap: 14, padding: "16px 13px", background: "#FFFFFF", borderRight: "1px solid #E6E8EC" }}>
              {[62, 54, 70, 48, 58, 66].map((w, i) => <SkeletonBox key={i} variant="text" width={`${w}%`} height={12} />)}
            </div>
          ) : null}
          <LoadingRegion label={label} testId={testId} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 16, padding: "20px 24px", overflow: "hidden" }}>
            <SkeletonBox variant="text" width="34%" height={18} />
            <GhostFormFields count={5} />
          </LoadingRegion>
          {njDesktop && njWide ? (
            <aside aria-hidden="true" style={{ position: "relative", flex: `0 0 ${skelColW}px`, width: skelColW, minWidth: 0, background: "#FFFFFF", borderLeft: "1px solid #E6E8EC" }}>
              <GhostFramedJourney label={label} />
            </aside>
          ) : null}
        </div>
      </div>
    );
  };
  if (loading) {
    /* The journey store is still loading (JAI-2519). This used to be a bare text line for the whole screen,
       shown for 2-3 s before an event's own skeleton took over, so opening an event read as two different
       loads. Now the skeleton has the shape of what is opening: a deep link to a journey (?journey=, ?new=)
       gets the editor's own layout (the Setup rail, the form, and on a wide screen the customer preview at the
       preview column's width, and no section tabs, which the editor does not show), the same ghosts the
       event-loading phase draws; the list gets list rows.
       The sentence stays, announced once, for screen readers. The 8 s watchdog and the records-only fallback
       above are unchanged, so this never outlives the store read. */
    const storeLoadingLabel = t('journeys.newJourneys.text31', "Loading journeys from your queues, events and catalog…");
    const openingEditor = !!(nav.params?.journey || nav.params?.new);
    if (openingEditor) return shell(editorLoadingSkeleton(storeLoadingLabel, "nj-store-loading"), true);
    return shell(
      <LoadingRegion label={storeLoadingLabel} testId="nj-store-loading" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 12, padding: "20px 24px", overflow: "hidden" }}>
        <SkeletonBox variant="text" width={140} height={20} />
        <SkeletonBox variant="text" width={260} height={12} />
        {[0, 1, 2, 3, 4, 5].map((i) => <SkeletonBox key={i} width="100%" height={56} />)}
      </LoadingRegion>,
    );
  }

  /* The store has answered, but a link to a journey opens it from the effect above, after this render, and an
     event link may still be waiting for the events list or for its own read (JAI-2519). Until it opens, the editor's
     skeleton stays, so the list never flashes between the loading skeleton and the editor. Bounded: a refused read
     clears the link (the list then shows, with its toast), and a link to an unknown non-event journey is not waited for. */
  {
    const wantJourney = String(nav.params?.journey ?? "");
    const freshJourney = String(nav.params?.new ?? "");
    // The link effect's own signature: once it has acted on this link (opened it, offered a draft back, or refused it),
    // the link is not pending, whatever the URL still says.
    const linkSig = `${wantJourney}|${freshJourney}|${String(nav.params?.preview ?? "") === "1"}`;
    const linkPending = ui.route === "home" && linkRef.current !== linkSig && (Boolean(freshJourney)
      || (Boolean(wantJourney) && (Boolean(docs[wantJourney]) || wantJourney.startsWith("event:"))));
    if (linkPending) return shell(editorLoadingSkeleton(t("journeys.newJourneys.eventLoading", "Loading the event…"), "nj-link-loading"), true);
  }

  /* ── HOME ───────────────────────────────────────────────────────── */
  const home = (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "20px 24px 14px", borderBottom: "1px solid #E6E8EC", background: "#FFFFFF", flexShrink: 0 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: "-0.01em" }}>{t('journeys.newJourneys.text32', "Journeys")}</h1>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", background: "var(--jrni-color-primary-3, #EDE5FE)", color: "var(--jrni-color-primary-text, #5142C7)", borderRadius: 999, padding: "3px 10px" }}>{t('journeys.newJourneys.text33', "PHASE 5")}</span>
          </div>
          <p style={{ fontSize: 12.5, color: "#697180", margin: "4px 0 0", lineHeight: 1.5 }}>
            {t("journeys.newJourneys.home.summary", "{all, plural, one {# journey} other {# journeys}} · {live} live · {draft} draft · {exp, plural, one {# experience} other {# experiences}}", { all: allRows.length, live: liveCount, draft: draftCount, exp: expCount })}</p>
        </div>
        <button onClick={() => exportCsv(rows.map((r) => r.key))} style={GHOST}>{t('journeys.newJourneys.text38', "↓ Export")}</button>
        <button onClick={() => set({ tplOpen: true })} style={PRIMARY}>{t('journeys.newJourneys.text39', "+ New journey")}</button>
      </div>

      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, flexShrink: 0 }}>
        {[
          { glyph: "▤", tint: "var(--jrni-color-primary-3, #EDE5FE)", color: "var(--jrni-color-primary-text, #5142C7)", value: allRows.length, label: t('journeys.newJourneys.label12', "Journeys") },
          { glyph: "◉", tint: "#E6F4EE", color: "#027D47", value: liveCount, label: t('journeys.newJourneys.label13', "Live") },
          { glyph: "✎", tint: "#FFFAED", color: "#916D0F", value: draftCount, label: t('journeys.newJourneys.label14', "Drafts") },
          { glyph: "◈", tint: "#E4E1F7", color: "#262265", value: expCount, label: t('journeys.newJourneys.label15', "Experiences") },
        ].map((s) => (
          <div key={s.label} style={{ background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 11 }}>
            <span style={glyphBox(s.tint, s.color)}>{s.glyph}</span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 19, fontWeight: 700, lineHeight: 1.15 }}>{s.value}</span>
              <span style={{ display: "block", fontSize: 11, color: "#697180", whiteSpace: "nowrap" }}>{s.label}</span>
            </span>
          </div>
        ))}
      </div>

      <FolderShellFrame lib={jlib}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 0 10px", flexShrink: 0, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, flex: "1 1 200px", maxWidth: 280, minWidth: 180, boxSizing: "border-box", height: 32, padding: "0 11px", border: "1px solid #D1D4DC", borderRadius: 8, background: "#FFFFFF" }}>
          <span style={{ color: "#B5BAC6", fontSize: 12 }}>⌕</span>
          <input value={ui.listQuery} onChange={(e) => { set({ listQuery: e.target.value }); clearSel(); }} placeholder={t('journeys.newJourneys.placeholder', "Find a journey by name or type")} style={{ flex: 1, minWidth: 0, border: "none", outline: "none", fontSize: 12.5, background: "transparent", color: "#333333", fontFamily: FONT }} />
        </span>
        <span style={{ width: 1, height: 20, background: "#E6E8EC", margin: "0 4px" }} />
        {[{ k: "all", label: t("journeys.newJourneys.filter.all", "All · {count}", { count: allRows.length }) }, { k: "live", label: t("journeys.newJourneys.filter.live", "Live · {count}", { count: liveCount }) }, { k: "draft", label: t("journeys.newJourneys.filter.draft", "Drafts · {count}", { count: draftCount }) }, { k: "exp", label: t("journeys.newJourneys.filter.exp", "Experiences · {count}", { count: expCount }) }].map((c) => {
          const on = ui.listFilter === c.k;
          return <button key={c.k} onClick={() => { set({ listFilter: c.k }); clearSel(); }} style={{ height: 28, padding: "0 11px", border: `1px solid ${on ? "var(--jrni-color-primary-1, #5142C7)" : "#D1D4DC"}`, borderRadius: 999, background: on ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", color: on ? "var(--jrni-color-primary-text, #5142C7)" : "#333333", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flex: "none", fontFamily: FONT }}>{c.label}</button>;
        })}
        <span style={{ width: 1, height: 20, background: "#E6E8EC", margin: "0 4px" }} />
        {[{ k: "all", label: t("journeys.newJourneys.filter.allTypes", "All types · {count}", { count: jlib.pool.length }) }, ...TYPE_ORDER.filter((ty) => typeCounts[ty]).map((ty) => ({ k: ty, label: t("journeys.newJourneys.filter.type", "{type} · {count}", { type: njTypeName(ty), count: typeCounts[ty] }) }))].map((c) => {
          const on = ui.listType === c.k;
          return <button key={c.k} onClick={() => { set({ listType: c.k }); clearSel(); }} style={{ height: 28, padding: "0 11px", border: `1px solid ${on ? "#262265" : "#D1D4DC"}`, borderRadius: 999, background: on ? "#E4E1F7" : "#FFFFFF", color: on ? "#262265" : "#333333", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flex: "none", fontFamily: FONT }}>{c.label}</button>;
        })}
        <span style={{ width: 1, height: 20, background: "#E6E8EC", margin: "0 4px" }} />
        {/* The design system's own single-select, not a native <select>
            (operator, 2026-09-02). The browser control drew the OS menu — system
            highlight blue, system type, nothing to do with the rest of the row —
            and the tinted box around it was a hand-built imitation of the
            trigger Dropdown already owns, along with its keyboard model and its
            listbox roles. Dropdown had no compact tier until now; it does. */}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flex: "none" }}>
          <span style={{ fontSize: 12, color: "#697180", fontWeight: 500 }}>{t('journeys.newJourneys.text40', "Sort")}</span>
          <Dropdown
            size="sm"
            portal
            aria-label={t('journeys.newJourneys.ariaLabel', "Sort journeys")}
            value={ui.listSort}
            onChange={(v) => set({ listSort: v })}
            options={[
              { value: "updated", label: t('journeys.newJourneys.label16', "Recently updated") },
              { value: "name", label: t('journeys.newJourneys.label17', "Name A–Z") },
              { value: "type", label: t('journeys.newJourneys.label18', "Type") },
              { value: "pages", label: t('journeys.newJourneys.label19', "Most pages") },
            ]}
            style={{ width: 176 }}
          />
        </span>
        <span style={{ display: "inline-flex", marginLeft: "auto", flex: "none", border: "1px solid #D1D4DC", borderRadius: 8, overflow: "hidden" }}>
          <button onClick={() => set({ listView: "list" })} style={{ height: 28, padding: "0 11px", border: "none", background: ui.listView === "list" ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: ui.listView === "list" ? "#FFFFFF" : "#333333", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>{t('journeys.newJourneys.text41', "☰ List")}</button>
          <button onClick={() => set({ listView: "grid" })} style={{ height: 28, padding: "0 11px", border: "none", background: ui.listView === "grid" ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: ui.listView === "grid" ? "#FFFFFF" : "#333333", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>{t('journeys.newJourneys.text42', "▦ Cards")}</button>
        </span>
      </div>

      <FolderShellBars lib={jlib} />

      {selSet.length > 0 ? (
        <div style={{ margin: "0 0 10px", padding: "8px 14px", background: "#FFFFFF", border: "1px solid #DCCCFC", borderRadius: 8, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          {[{ label: t('journeys.newJourneys.label20', "⧉ Duplicate"), fn: () => { duplicateKeys(selSet); clearSel(); } }, { label: t('journeys.newJourneys.label21', "↺ Revert edits"), fn: () => { revertKeys(selSet); clearSel(); } }, { label: t('journeys.newJourneys.label22', "↓ Export"), fn: () => { exportCsv(selSet); clearSel(); } }].map((b) => (
            <button key={b.label} onClick={b.fn} style={{ border: "none", background: "transparent", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "4px 6px", fontFamily: FONT }}>{b.label}</button>
          ))}
          <button onClick={() => { deleteKeys(selSet); clearSel(); }} style={{ border: "none", background: "transparent", color: "#D01647", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "4px 6px", fontFamily: FONT }}>{t('journeys.newJourneys.text43', "✕ Delete")}</button>
        </div>
      ) : null}

      <div style={{ flex: 1, minHeight: 0 }}>
        {rows.length === 0 ? (
          <div style={{ background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 12, padding: "44px 24px", textAlign: "center" }}>
            <span style={glyphBox("#F4F5F7", "#697180", 40, 17)}>▤</span>
            <div style={{ fontSize: 14, fontWeight: 700, marginTop: 12 }}>{filtering ? t('journeys.newJourneys.text44', "No journeys match those filters") : t('journeys.newJourneys.text45', "No journeys yet")}</div>
            <div style={{ fontSize: 12.5, color: "#697180", marginTop: 4 }}>{filtering ? t('journeys.newJourneys.text46', "Try a different search or clear the filters to see everything.") : t('journeys.newJourneys.text47', "Your queues, events and appointment catalog appear here as journeys. Start one from a template too.")}</div>
            <button onClick={() => (filtering ? (set({ listQuery: "", listFilter: "all", listType: "all" }), jlib.setScope("all")) : set({ tplOpen: true }))} style={{ ...PRIMARY, marginTop: 14 }}>{filtering ? t('journeys.newJourneys.text48', "Clear filters") : t('journeys.newJourneys.text39', "+ New journey")}</button>
          </div>
        ) : ui.listView === "list" ? (
          <div style={{ background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: LIST_COLS, gap: 10, alignItems: "center", padding: "9px 16px", background: "#FAFAFD", borderRadius: "11px 11px 0 0", borderBottom: "1px solid #E6E8EC", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#697180" }}>
              <span onClick={() => jlib.setSel(allChecked ? new Set() : new Set(rows.map((r) => r.key)))} style={{ width: 15, height: 15, borderRadius: 4, border: `1px solid ${allChecked ? "var(--jrni-color-primary-text, #5142C7)" : "#D1D4DC"}`, background: allChecked ? "var(--jrni-color-primary-text, #5142C7)" : someChecked ? "#C18FE8" : "#FFFFFF", color: "#FFFFFF", fontSize: 10, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>{allChecked ? "✓" : someChecked ? "–" : ""}</span>
              <span>{t('journeys.newJourneys.text49', "Journey")}</span><span>{t('journeys.newJourneys.text50', "Type")}</span><span>{t('journeys.newJourneys.text51', "Status")}</span><span>{t('journeys.newJourneys.text16', "Pages")}</span><span>{t('journeys.newJourneys.text52', "Created by")}</span><span>{t('journeys.newJourneys.text53', "Updated")}</span><span style={{ textAlign: "right" }}>{t('journeys.newJourneys.text54', "Actions")}</span>
            </div>
            {rows.map((r, ri) => {
              const on = jlib.sel.has(r.key);
              const last = ri === rows.length - 1;
              /* The card can't clip (no overflow:hidden — a row menu has to be able
                 to hang past the last row), so the corners are rounded per edge row. */
              return (
                <div key={r.key} {...jlib.dragProps(r.key)} onClick={r.onOpen} style={{ display: "grid", gridTemplateColumns: LIST_COLS, gap: 10, alignItems: "center", padding: "13px 16px", borderBottom: last ? "none" : "1px solid #F4F5F7", borderRadius: last ? "0 0 11px 11px" : 0, cursor: "pointer", background: on ? "#FCFBFF" : "#FFFFFF" }}>
                  <span onClick={(e) => { e.stopPropagation(); jlib.toggleSel(r.key, !on); }} style={{ width: 15, height: 15, borderRadius: 4, border: `1px solid ${on ? "var(--jrni-color-primary-text, #5142C7)" : "#D1D4DC"}`, background: on ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: "#FFFFFF", fontSize: 10, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>{on ? "✓" : ""}</span>
                  <span style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={glyphBox(r.tint, r.color, 28, 12)}>{r.glyph}</span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "flex", alignItems: "center", fontSize: 13, fontWeight: 700, color: "#333333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}{jlib.folderChips(r.key)}</span>
                      <span style={{ display: "block", fontSize: 11.5, color: "#697180", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.sub}</span>
                    </span>
                  </span>
                  <span><span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: r.tint, color: r.color }}>{r.typeText}</span></span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: r.stColor }}><span style={{ width: 6, height: 6, borderRadius: 999, background: r.stColor }} />{r.statusText}</span>
                  <span style={{ fontSize: 12, color: "#697180" }}>{r.pages}</span>
                  {/* Created by / Updated — who above, when below. A row with no
                      recorded author renders an em dash and says why on hover,
                      rather than borrowing a name from the signed-in user.
                      Keyed with the rest of this screen (journeys.newJourneys.*). */}
                  <span style={{ minWidth: 0 }} title={r.created.title || t('journeys.newJourneys.title', "No author recorded — this journey hasn't been saved in the designer yet")}>
                    <span style={{ display: "block", fontSize: 12, color: r.created.who ? "#333333" : "#B5BAC6", fontWeight: r.created.who ? 600 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.created.who || "—"}</span>
                    {r.created.when ? <span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 1, whiteSpace: "nowrap" }}>{r.created.when}</span> : null}
                  </span>
                  <span style={{ minWidth: 0 }} title={r.modified.title || ""}>
                    <span style={{ display: "block", fontSize: 12, color: "#697180", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.modified.when || r.updated}</span>
                    {r.modified.who ? <span style={{ display: "block", fontSize: 11, color: "#B5BAC6", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t("journeys.newJourneys.modifiedBy", "by {who}", { who: r.modified.who })}</span> : null}
                  </span>
                  <span onClick={(e) => e.stopPropagation()} style={{ justifySelf: "end" }}>
                    <RowActions
                      testid="njourneys-row"
                      onView={() => { if (r.origin?.route) goRoute(r.origin.route, r.origin.params); else r.onOpen(); }}
                      viewLabel={r.origin?.route ? t("journeys.newJourneys.viewOrigin", "View {name}", { name: r.origin.label }) : r.isExp ? t("journeys.newJourneys.viewExperience", "View experience") : t("journeys.newJourneys.viewJourney", "View journey")}
                      onEdit={r.onOpen}
                      editLabel={t("journeys.newJourneys.editInDesigner", "Edit in the Journey Designer")}
                      onDuplicate={() => duplicateKeys([r.key])}
                      moreHeader={r.name}
                      moreItems={[
                        jlib.foldersMenuItem(r.key),
                        { icon: <Icon.edit size={14} />, label: t('journeys.newJourneys.label23', "Rename…"), onClick: () => set({ renameKey: r.key, renameVal: r.name }) },
                        ...(r.origin?.route ? [{ icon: <Icon.external size={14} />, label: t("journeys.newJourneys.openOrigin", "Open the {noun} →", { noun: njOwnerNoun(r.origin.kind) }), onClick: () => goRoute(r.origin.route, r.origin.params) }] : []),
                        ...(r.edited && !r.isExp ? [{ icon: <Icon.arrow dir="left" size={14} />, label: t('journeys.newJourneys.label24', "Revert designer edits"), onClick: () => revertKeys([r.key]) }] : []),
                        { icon: <Icon.download size={14} />, label: t('journeys.newJourneys.label25', "Export as CSV"), onClick: () => exportCsv([r.key]) },
                        "divider" as const,
                        { icon: <Icon.trash size={14} />, label: t('journeys.newJourneys.label26', "Delete"), danger: true, onClick: () => deleteKeys([r.key]) },
                      ]}
                    />
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
            {rows.map((r) => (
              <div key={r.key} onClick={r.onOpen} style={{ background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 12, overflow: "hidden", cursor: "pointer", display: "flex", flexDirection: "column" }}>
                <div style={{ height: 84, background: r.tint, display: "flex", alignItems: "flex-end", justifyContent: "space-between", padding: 12 }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: r.color }}>{r.glyph}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: "#FFFFFF", color: r.color }}>{r.typeText}</span>
                </div>
                <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</div>
                  <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5 }}>{r.sub}</div>
                </div>
                <div style={{ padding: "8px 14px", borderTop: "1px solid #F4F5F7", background: "#FAFAFD", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11.5, color: "#697180" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600, color: r.stColor }}><span style={{ width: 6, height: 6, borderRadius: 999, background: r.stColor }} />{r.statusText}</span>
                  <span title={r.modified.title || ""}>{r.pages} · {r.modified.when || r.updated}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        <p style={{ fontSize: 11.5, color: "#B5BAC6", margin: "12px 2px 0" }}>
          {t('journeys.newJourneys.text56', "Rows come from this tenant's queues, events, display boards and appointment catalog — the eye opens the object itself, the pencil opens its journey in the designer. Experiences compose them by reference (phase 5). Folders are labels: a journey can sit in several.")}{NJ_STUBBED ? <> {t('journeys.newJourneys.text57', "A queue's name, hero image and services save onto the queue itself.")}</> : ""}
        </p>
      </div>
      </FolderShellFrame>
      <FolderShellOverlays lib={jlib} />
    </div>
  );

  /* ── Canvas cores ───────────────────────────────────────────────── */
  const accent = "#14131F";
  const heroBlock = p?.content.find((c) => c.type === "hero") ?? null;
  /** Hero background: the block's own image, else the hero image Setup · Basics
   *  holds. An event with a hero image in Setup showed a bare gradient before. */
  const heroImg = heroBlock ? String(bval(heroBlock, "img") || r0.heroImage || "") : "";
  const waitLabel = originQueue?.avgWaitMins ? t("journeys.newJourneys.waitApproxMin", "~{count} min", { count: originQueue.avgWaitMins }) : "—";
  const aheadLabel = originQueue ? t("journeys.newJourneys.aheadOfYou", "{count} ahead of you{place, select, none {} other { · {place}}}", { count: originQueue.waiting ?? 0, place: originQueue.locationName || "none" }) : t("journeys.newJourneys.liveFromQueue", "live from the queue when this journey is attached");
  const firstSvc = svcOn[0] ?? null;
  const hubRows = owner && owner.attached.length
    ? owner.attached.map((a) => { const j = expJourney(a.doc); return { glyph: TYPE_GLYPH[j.ty] ?? "▤", tint: j.tint, color: j.color, name: j.name, meta: a.scope }; })
    : [{ glyph: "◈", tint: "#E4E1F7", color: "#262265", name: t("journeys.newJourneys.notInExperience", "Not in an experience yet"), meta: t("journeys.newJourneys.notInExperienceMeta", "add this page's journey to one") }];

  /* ── The journey end page (stage 3, 2026-09-01) ────────────────────
   *  `sections` is read through csMigrate on every render rather than trusted:
   *  a document may predate a section, or carry one its type stopped allowing
   *  when the journey was retyped. Declared here, above core(), the inspector
   *  and the left pane — all three read it. */
  const csType = csTypeOf(D?.type);
  const isConfirmPage = !!p && (p.id === "confirmation" || p.id === "apptconfirm");
  const csState: CSState | null = isConfirmPage && p ? csMigrate(p.sections, csType) : null;
  const setSections = (next: CSState) => patchPage({ sections: next });

  /* ── The One-Stop page, in the SAME three panes as everything else ──────
   *  Read exactly the way `isConfirmPage`/`csState` are, and for the same
   *  reason: the page's own parts are not the designer's copy blocks, but they
   *  belong in the designer's rail, canvas and inspector rather than in a screen
   *  of their own. Everything shared comes free — undo/redo over the document,
   *  autosave through saveDoc, the Preview overlay, the page rail, Setup.
   *
   *  `ui.selSection` carries the selected BLOCK id here, the same slot the
   *  confirmation page uses for its selected section: both are "a part of this
   *  page that is not a `content` block", and a second slot would have to be
   *  cleared in every place this one already is. */
  const isOneStopPage = p?.id === "onestop";
  type OSBlock = { uid: string; id: string; type: string; version: number; config: Record<string, unknown>; visibility?: "always" | "signed-in" | "signed-out" };
  const osBlocks: OSBlock[] = (p?.osBlocks ?? []) as OSBlock[];
  const setOsBlocks = (next: OSBlock[]) => patchPage({ osBlocks: next });

  /** The draft's blocks with their scopes resolved against the records the
   *  designer already holds, so a picked queue or service RENDERS on the canvas
   *  rather than showing the block's empty state.
   *
   *  Not a hook: the input is three arrays the designer keeps loaded and a block
   *  list that changes on every edit, so a memo would recompute anyway and would
   *  be one more thing to place correctly relative to the early return. */
  /* ── What a One-Stop page can offer ───────────────────────────────────
   *  The three rails themselves, NOT the journeys derived from them (operator,
   *  2026-09-02). Two reasons, and the second one was a live defect.
   *
   *  A One-Stop page offers a student a bookable service, a desk to join or an
   *  event to sign up for. Those are the objects on the Appointments, Queues and
   *  Events rails. A journey is how one of them is *conducted*, which is a
   *  different noun — and picking journeys meant a queue with no journey could
   *  never be offered at all.
   *
   *  THE DEFECT: an appointment journey's `origin.id` is a catalogue GROUP id,
   *  and the block's scope key is `services`, which the resolver and the canvas
   *  preview both read as SERVICE ids. Ticking one wrote a group id where a
   *  service id was expected, so appointments could never render. Reading the
   *  rails directly makes the id that is stored the id that is looked up, by
   *  construction rather than by care.
   */
  /* ONE BLOCK PER ITEM (operator ruling, 2026-09-02).
   *
   *  Ticking three services used to add three ids to one `appointments` block.
   *  It now adds three `book-appointment` blocks, each naming one service — so a
   *  Notice can sit between two of them, which a single list block made
   *  impossible.
   *
   *  These three types already existed in the registry and were sitting disabled
   *  in the palette. They are the right carriers: each holds exactly one object
   *  id, and the renderer reads it from a key list that accepts `queueId` as
   *  readily as `scope.queues`.
   *
   *  The plural list blocks stay available for the case they are actually good
   *  at — "every service in this category", where the author does not want to
   *  name each one. */
  const OS_ITEM_FOR: Record<string, { type: string; idKey: string }> = {
    service: { type: "book-appointment", idKey: "preselects.serviceId" },
    queue: { type: "join-queue", idKey: "queueId" },
    event: { type: "register-event", idKey: "eventId" },
  };

  /** Read a dotted key off a block config. */
  const osCfgId = (config: Record<string, unknown> | undefined, path: string): string | null => {
    let cur: any = config;
    for (const k of path.split(".")) { if (cur == null || typeof cur !== "object") return null; cur = cur[k]; }
    return typeof cur === "string" && cur ? cur : null;
  };

  const osJourneyRows: { key: string; kind: string; id: string; name: string; sub: string; group: string }[] = [
    ...catServices
      .filter((s: any) => s.durationMin)
      .map((s: any) => ({
        key: `svc:${s.id}`, kind: "service", id: String(s.id),
        name: String(s.name ?? s.id),
        sub: [s.durationMin ? t("journeys.newJourneys.durationMin", "{count} min", { count: s.durationMin }) : null, lobName(s.lob)].filter(Boolean).join(" · "),
        group: "Bookable services", // ui-config: picker group key, compared at the One-Stop picker; translated only where it is displayed
      })),
    ...(queues as any[]).map((q) => ({
      key: `q:${q.id}`, kind: "queue", id: String(q.id),
      name: String(q.name ?? q.id),
      sub: [q.locationName, q.status].filter(Boolean).join(" · "),
      group: "Queues", // ui-config: picker group key, compared at the One-Stop picker; translated only where it is displayed
    })),
    ...((events ?? []) as any[]).map((e) => ({
      key: `ev:${e.id}`, kind: "event", id: String(e.id),
      name: String(e.title ?? e.name ?? e.id),
      sub: [e.date, e.location].filter(Boolean).join(" · "),
      group: "Events", // ui-config: picker group key, compared at the One-Stop picker; translated only where it is displayed
    })),
  ];

  /** Which objects of this kind are on the page — one block each. */
  const osScopeIds = (kind: string): string[] => {
    const map = OS_ITEM_FOR[kind];
    if (!map) return [];
    return osBlocks
      .filter((b) => b.type === map.type)
      .map((b) => osCfgId(b.config, map.idKey))
      .filter((x): x is string => !!x);
  };

  /** Build one per-item block, named after the object. */
  const osItemBlock = (kind: string, id: string, taken: string[]) => {
    const map = OS_ITEM_FOR[kind]!;
    const row = osJourneyRows.find((r) => r.kind === kind && r.id === id);
    const locale = p?.osSettings?.locale ?? "en-US";
    const config: Record<string, unknown> = row?.name ? { title: { [locale]: row.name } } : {};
    const parts = map.idKey.split(".");
    let cursor: Record<string, unknown> = config;
    parts.forEach((k, i) => {
      if (i === parts.length - 1) cursor[k] = id;
      else { cursor[k] = cursor[k] ?? {}; cursor = cursor[k] as Record<string, unknown>; }
    });
    return { uid: njUid("os"), id: mintBlockId(map.type, taken), type: map.type, version: 1, config };
  };

  /**
   * What a drag from the palette drops.
   *
   * The operator's two answers only reconcile one way. Ticking in Configuration
   * places blocks immediately (answer 1), AND dragging an offer block brings the
   * ticked things on together (answer 2) — which is only consistent if dragging
   * inserts the ticked items that are NOT already on the page, and falls back to
   * one unconfigured block when there are none left to catch up.
   *
   * So the palette entry means "put what I picked here", and when there is
   * nothing outstanding it means "add one I did not pick" — which is the case
   * answer 1 called out. One control, and its behaviour is always the useful one.
   */
  const osInsertAt = (type: string, at: number) => {
    const kind = Object.keys(OS_ITEM_FOR).find((k) => OS_ITEM_FOR[k]!.type === type);
    const taken = osBlocks.map((b) => b.id);
    const next = osBlocks.slice();
    const where = Math.max(0, Math.min(at, next.length));

    if (kind) {
      const onPage = osScopeIds(kind);
      const missing = osJourneyRows.filter((r) => r.kind === kind).map((r) => r.id).filter((id) => !onPage.includes(id));
      if (missing.length > 0) {
        const added = missing.map((id) => { const blk = osItemBlock(kind, id, taken); taken.push(blk.id); return blk; });
        next.splice(where, 0, ...added);
        setOsBlocks(next);
        set({ selSection: added[0]!.id, sel: null });
        showToast(added.length === 1 ? t("journeys.newJourneys.toast.addedOne", "Added {name}", { name: osLabel(type) }) : t("journeys.newJourneys.toast.addedMany", "Added {count} — everything picked in Configuration", { count: added.length }));
        return;
      }
      /* Nothing outstanding: an unconfigured block, chosen in the editor. */
      const blk = { uid: njUid("os"), id: mintBlockId(type, taken), type, version: 1, config: {} };
      next.splice(where, 0, blk);
      setOsBlocks(next);
      set({ selSection: blk.id, sel: null });
      showToast(t("journeys.newJourneys.toast.addedPick", "Added — pick which one in the editor on the right"));
      return;
    }

    /* Content block: seeded so it is visible the moment it lands. */
    const blk = {
      uid: njUid("os"),
      id: mintBlockId(type, taken),
      type,
      version: 1,
      config: osSeedConfig(type, p?.osSettings?.locale ?? "en-US"),
    };
    next.splice(where, 0, blk);
    setOsBlocks(next);
    set({ selSection: blk.id, sel: null });
  };

  const osToggleJourney = (kind: string, id: string) => {
    const map = OS_ITEM_FOR[kind];
    if (!map) return;
    const existing = osBlocks.find((b) => b.type === map.type && osCfgId(b.config, map.idKey) === id);
    if (existing) {
      setOsBlocks(osBlocks.filter((b) => b.uid !== existing.uid));
      if (ui.selSection === existing.id) set({ selSection: null });
      return;
    }
    /* Same builder the palette drag uses — one definition of "a block for this
       object", so a ticked block and a dragged block cannot differ. */
    const blk = osItemBlock(kind, id, osBlocks.map((b) => b.id));
    setOsBlocks([...osBlocks, blk]);
    set({ selSection: blk.id, sel: null });
  };

  const osPage = D?.pages.find((pg) => pg.id === "onestop") ?? null;
  const osCfg = osPage?.osSettings ?? {};
  const patchOsSettings = (patch: Record<string, unknown>) => {
    const idx = (D?.pages ?? []).findIndex((pg) => pg.id === "onestop");
    if (idx < 0) return;
    patchDoc((d) => ({ ...d, pages: d.pages.map((pg, i) => (i === idx ? { ...pg, osSettings: { ...(pg.osSettings ?? {}), ...patch } } : pg)) }));
  };

  const osPicked = osJourneyRows.filter((r) => osScopeIds(r.kind).includes(r.id)).length;
  const osChecks: { label: string; done: boolean }[] = [
    { label: t('journeys.newJourneys.label27', "Page name"), done: !!(D?.name || "").trim() && !/^untitled/i.test((D?.name || "").trim()) },
    { label: t('journeys.newJourneys.label28', "Time zone"), done: !!osCfg.timeZone },
    { label: t('journeys.newJourneys.label29', "What this page offers"), done: osPicked > 0 },
  ];
  const osReady = osChecks.every((c) => c.done);

  const osRendered = withPreviewData(
    osBlocks,
    { queues: queues as never, services: catServices as never, events: (events ?? []) as never },
    p?.osSettings?.timeZone ?? tzContext().zone,
  );

  /** The services this journey offers and what each would ask — the rows behind
   *  the sample-service switcher. Lifted out of `blockView` in stage 4 so the
   *  runtime slot on Contact and the end page's Answers section resolve the
   *  SAME list and share `ui.sampleByPage`: two switchers offering different
   *  services on one journey would be a defect only the operator would catch. */
  const sampleSvcRows: { id: string; name: string; groupId: string | null }[] = (isQueueJourney || isApptJourney)
    ? (isApptJourney ? (a0.serviceSelection ?? []) : (q0.services ?? []).map(String)).map((id: any) => {
        const sv = ((cat?.services ?? []) as any[]).filter((x: any) => x.id === id || x.name === id)[0];
        const method = isApptJourney ? "appointments" : "queueing";
        return sv
          ? { id: String(sv.id), name: String(sv.name), groupId: (sv.questionGroups?.[method] ?? null) as string | null }
          : { id: String(id), name: String(id), groupId: null };
      })
    : [];
  /** The switcher's pick for THIS page, remembered per page like the Contact
   *  page's (operator ruling 2026-08-31). */
  const csPageUid = p ? String(p.uid ?? p.id) : "";
  const csSample = sampleSvcRows.filter((r) => r.id === (ui.sampleByPage[csPageUid] ?? ""))[0] ?? null;
  const csSampleQs = csSample ? questionsOf(csSample.groupId) : [];
  /** The sample booking the sections render against. Every field is a value
   *  the designer ALREADY holds — the event record, the queue, the catalogue —
   *  so the preview cannot disagree with Setup. Nothing here is authored, and
   *  a value that isn't set stays undefined so the section shows its empty
   *  state naming the step that sets it. Answers is the one section whose
   *  content depends on a choice the customer has not made yet, so on a queue
   *  or an appointment it follows the sample service and says which one. */
  const csBooking: CSBooking = {
    brandName: tenant?.name ?? undefined,
    serviceName: csType === "event" ? undefined : (csSample?.name ?? firstSvc?.name),
    eventName: D?.type === "Event" ? D?.name : undefined,
    dateLine: setupDateLine || undefined,
    timeLine: setupTimeLine || undefined,
    durationLine: firstSvc?.wait,
    advisor: ((cat?.advisors as any[] | undefined)?.[0]?.name) ?? undefined,
    ticketLine: csType === "event" ? (tixRows[0]?.name ?? undefined) : undefined,
    placeName: csType === "event" ? (setupVenue || undefined) : (originQueue?.locationName ?? D?.origin?.label ?? undefined),
    address: setupAddress || undefined,
    virtual: csType === "event" ? !!(r0.method?.virtual && !r0.method?.inPerson) : false,
    answers: csType === "event"
      ? attQuestions.map((q: any) => ({ label: q.label, value: "—" }))
      : csSampleQs.map((q: any) => ({ label: q.label, value: "—" })),
    /* Only ever set where the answers came from a service the DESIGNER picked;
       an event's group is the real one and needs no disclaimer. */
    answersBadge: csType === "event" || !csSample ? undefined : t("journeys.newJourneys.sampleBadge", "Sample · {name}", { name: csSample.name }),
  };

  /* The step footer — FIXED FURNITURE (operator, 2026-09-02). It is in the
     canvas because an author has to see the page the customer sees, and it is
     not selectable, editable or deletable because it is derived: the only thing
     an author owns here is the commit button's wording, which the inspector
     holds. Back sits left, the forward button right; on the first page the
     forward button keeps its slot and the left one is simply absent.

     Sticky to the bottom only when the content overflows, which is what
     `position: sticky` already means — no viewport test, no second rule. */
  const stepNavFooter = () => {
    /* A Display Board and a One-Stop page are single surfaces — same predicate
       as the Pages/Flow toggle and "+ Add page". Nothing to navigate. */
    if (!hasFlow || !pageNav || (!pageNav.back && !pageNav.next)) return null;
    return (
      <div className="nj-stepnav" title={t('journeys.newJourneys.title2', "Back and Next follow the page's position in the flow — the commit button's wording is editable, the buttons themselves aren't")}
        style={{ position: "sticky", bottom: 0, display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderTop: "1px solid #F4F5F7", background: "#FFFFFF", cursor: "default" }}>
        {pageNav.back ? (
          <span style={{ height: 38, padding: "0 18px", display: "inline-flex", alignItems: "center", borderRadius: 10, border: "1px solid #D1D4DC", background: "#FFFFFF", color: "#333333", fontSize: 13, fontWeight: 600 }}>{t('journeys.newJourneys.text58', "Back")}</span>
        ) : null}
        <span style={{ flex: 1 }} />
        <span style={{ height: 38, padding: "0 22px", display: "inline-flex", alignItems: "center", borderRadius: 10, background: accent, color: "#FFFFFF", fontSize: 13, fontWeight: 700 }}>{pageNav.label}</span>
      </div>
    );
  };

  const core = () => {
    if (!p || !p.core) return null;
    const box = { margin: "12px 14px", borderRadius: 12, padding: 13, cursor: "pointer", position: "relative" as const, border: `2px solid ${ui.sel === "core" ? "var(--jrni-color-primary-1, #5142C7)" : p.tint}`, background: "#FFFFFF" };
    const cta = (label: string, wide = false) => <button style={{ marginTop: 12, height: 38, padding: wide ? undefined : "0 22px", width: wide ? "100%" : undefined, border: "none", borderRadius: 10, background: accent, color: "#FFFFFF", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{label}</button>;
    const svcRow = (s: any, tint: string, color: string, meta?: string | null, dept?: string | null) => (
      <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 12px" }}>
        <span style={{ ...glyphBox(tint, color), overflow: "hidden" }}>{s.image ? <img src={s.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : s.glyph}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{s.name}</span>
          {dept ? <span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{dept}</span> : null}
        </span>
        {meta ? <span style={{ fontSize: 11, fontWeight: 700, color, background: tint, borderRadius: 999, padding: "3px 9px", flex: "none" }}>{meta}</span> : null}
      </div>
    );
    const emptySvc = (where: string) => <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "18px 12px", textAlign: "center", fontSize: 11.5, color: "#697180", marginTop: 6 }}>{t('journeys.newJourneys.text59', "No services selected yet — pick them in")}{" "}<b>{where}</b> {t('journeys.newJourneys.text60', "on the right.")}</div>;

    return (
      <div onClick={() => set({ sel: ui.sel === "core" ? null : "core", selSection: null })} className="nj-core" style={box}>
        <span style={{ position: "absolute", top: -9, left: 10, background: p.color, color: "#FFFFFF", fontSize: 9, fontWeight: 800, letterSpacing: "0.05em", borderRadius: 4, padding: "1px 7px" }}>{t("journeys.newJourneys.coreBadge", "CORE · {core}", { core: p.core.toUpperCase() })}</span>

        {p.id === "services" ? (<>
          {svcOn.length === 0 ? emptySvc(t('journeys.newJourneys.text137', "Services offered")) : null}
          <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 6 }}>{svcOn.map((s) => svcRow(s, "#ECF1FE", "#2D60EB", tg("est") ? (s.wait || null) : null))}</div>
        </>) : null}

        {p.id === "wait" ? (
          <div style={{ textAlign: "center", padding: "10px 0 4px" }}>
            <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: "-0.02em" }}>{waitLabel}</div>
            <div style={{ fontSize: 12, color: "#697180", marginTop: 2 }}>{aheadLabel}</div>
            {/* No button here: the footer's forward button IS this action
                (operator, 2026-09-02). A core action beside Next was two
                controls for one step, and the core one could be pressed before
                the page was valid. */}
            {tg("book") ? (<>
              <div style={{ fontSize: 11.5, color: "var(--jrni-color-primary-text, #5142C7)", fontWeight: 600, marginTop: 9 }}>{String(pval("altText") ?? "")}</div>
              <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 3, ...MONO }}>→ {String(pval("altUrl") || t("journeys.newJourneys.noLinkSet", "no link set"))}</div>
            </>) : null}
          </div>
        ) : null}

        {p.id === "contact" || p.id === "register" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}><div style={FIELD}>{t('journeys.newJourneys.text62', "First name")}</div><div style={FIELD}>{t('journeys.newJourneys.text63', "Last name")}</div></div>
            {p.id === "contact" || (r0.standardFields ?? ["email"]).includes("email") ? <div style={FIELD}>{t('journeys.newJourneys.text64', "Email address")}</div> : null}
            {p.id === "contact" || (r0.standardFields ?? ["email"]).includes("phone") ? <div style={FIELD}>{t('journeys.newJourneys.text65', "Mobile number")}</div> : null}
            {p.id === "contact" && tg("party") ? <div style={FIELD}>{t('journeys.newJourneys.text66', "Party size")}</div> : null}
            {p.id === "register" && tg("guest") ? <div style={FIELD}>{t('journeys.newJourneys.text67', "+1 guest name (optional)")}{r0.maxGuests ? <> {t('journeys.newJourneys.text68', "· up to {maxGuests}", { maxGuests: r0.maxGuests })}</> : ""}</div> : null}
            {p.id === "contact" && tg("sms") ? <div style={{ fontSize: 11, color: "#697180" }}>{t('journeys.newJourneys.text69', "☑ Text me updates about my place in line")}</div> : null}
            {p.id === "register" && r0.consent?.marketing ? <div style={{ fontSize: 11, color: "#697180" }}>{t('journeys.newJourneys.text70', "☐ Send me offers and updates")}</div> : null}
            {p.id === "register" && r0.consent?.contactSharing ? <div style={{ fontSize: 11, color: "#697180" }}>{t('journeys.newJourneys.text71', "☐ Share my details with the event host")}</div> : null}
            {/* The attached group's REAL questions, in group order. This page is
                where a customer answers them, so it shows the fields — the count
                lives on the attachment chip. */}
            {attQuestions.length ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 9, borderTop: "1px solid #F4F5F7", paddingTop: 11, marginTop: 2 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em", color: "#7C3AED", textTransform: "uppercase" }}>{qName}</span>
                  <span style={{ fontSize: 10, color: "#B5BAC6" }}>{t("journeys.newJourneys.questionCount", "{count, plural, one {# question} other {# questions}}", { count: attQuestions.length })}</span>
                </div>
                {attQuestions.map(qFieldView)}
              </div>
            ) : null}
            {/* Submit lives in the footer — see the note on the wait page. */}
            {p.id === "register" && r0.approvalMode ? <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", ...MONO }}>{t('journeys.newJourneys.text74', "submits for approval — doesn't confirm immediately")}</div> : null}
          </div>
        ) : null}

        {p.id === "waiting" ? (ui.simCalled ? (
          <div style={{ textAlign: "center", padding: "12px 0 6px" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#027D47" }}>{t('journeys.newJourneys.text75', "It's your turn!")}</div>
            <div style={{ fontSize: 12.5, color: "#697180", marginTop: 4 }}>{t('journeys.newJourneys.text76', "The next available server is ready for you.")}</div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "8px 0 2px" }}>
            {tg("pos") ? (<>
              <div style={{ fontSize: 30, fontWeight: 800 }}>{originQueue ? t('journeys.newJourneys.text77', "#{arg1}", { arg1: (originQueue.waiting ?? 0) + 1 }) : "—"}</div>
              <div style={{ fontSize: 11.5, color: "#697180" }}>{originQueue?.avgWaitMins ? t('journeys.newJourneys.text78', "in line · about {avgWaitMins} min", { avgWaitMins: originQueue.avgWaitMins }) : t('journeys.newJourneys.text79', "in line")}</div>
            </>) : null}
            <div style={{ height: 6, borderRadius: 999, background: "#E6E8EC", margin: "12px 6px" }}><span style={{ display: "block", width: "55%", height: 6, borderRadius: 999, background: accent }} /></div>
            {tg("msg") ? (
              <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
                {[t("journeys.newJourneys.chip.runningLate", "Running late"), t("journeys.newJourneys.chip.messageHost", "Message the host")].map((x) => <span key={x} style={{ fontSize: 11, fontWeight: 600, border: "1px solid #D1D4DC", borderRadius: 999, padding: "5px 12px", color: "#333333" }}>{x}</span>)}
                <span style={{ fontSize: 11, fontWeight: 600, border: "1px solid #D1D4DC", borderRadius: 999, padding: "5px 12px", color: "#D01647" }}>{t('journeys.newJourneys.text80', "Leave the line")}</span>
              </div>
            ) : null}
          </div>
        )) : null}

        {p.id === "apptsvc" ? (<>
          {svcOn.length === 0 ? emptySvc(t('journeys.newJourneys.text136', "Appointment services offered")) : null}
          <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 6 }}>{svcOn.map((s) => svcRow(s, "#E0F2F1", "#0AA396", tg("dur") ? s.wait : null, tg("dept") ? s.dept : null))}</div>
        </>) : null}

        {p.id === "appttime" ? (
          <div style={{ marginTop: 6 }}>
            {grid.days.length ? (<>
              <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                {grid.days.slice(0, 4).map((d, i) => (
                  <span key={d.iso} style={{ width: 44, padding: "6px 0", borderRadius: 8, border: `1px solid ${i === 0 ? "#0AA396" : "#D1D4DC"}`, background: i === 0 ? "#0AA396" : "#FFFFFF", textAlign: "center" }}>
                    <span style={{ display: "block", fontSize: 9.5, fontWeight: 700, color: i === 0 ? "#FFFFFF" : "#333333", opacity: 0.75 }}>{d.dow}</span>
                    <span style={{ display: "block", fontSize: 13, fontWeight: 800, color: i === 0 ? "#FFFFFF" : "#333333" }}>{d.date.d}</span>
                  </span>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 10 }}>
                {grid.times.slice(0, 6).map((t, i) => (
                  <span key={t} style={{ padding: "8px 0", borderRadius: 8, border: `1px solid ${i === 0 ? "#0AA396" : "#D1D4DC"}`, background: i === 0 ? "#0AA396" : "#FFFFFF", color: i === 0 ? "#FFFFFF" : "#333333", textAlign: "center", fontSize: 12, fontWeight: 700 }}>{t}</span>
                ))}
              </div>
              {tg("adv") && (cat?.advisors as any[] | undefined)?.length ? <div style={{ fontSize: 11, color: "#697180", textAlign: "center", marginTop: 9 }}>{t('journeys.newJourneys.text81', "with")}{" "}<b>{(cat!.advisors as any[])[0].name}</b>{firstSvc ? t('journeys.newJourneys.text82', "· {wait}", { wait: firstSvc.wait }) : ""}</div> : null}
            </>) : (
              <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "18px 12px", textAlign: "center", fontSize: 11.5, color: "#697180" }}>
                {grid.loading ? t('journeys.newJourneys.text83', "Loading availability…") : availSlug ? t('journeys.newJourneys.text84', "No bookable slots in the next fortnight for this service.") : t('journeys.newJourneys.text85', "Attach this journey to a booking catalog with a public link to show live slots here.")}
              </div>
            )}
            <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", marginTop: 7, ...MONO }}>{t('journeys.newJourneys.text86', "slots via the journey availability contract (2026-08-14)")}</div>
          </div>
        ) : null}

        {isConfirmPage && csState ? (
          <CSCanvas
            state={csState}
            type={csType}
            booking={csBooking}
            fields={{ bannerTitle: p.bannerTitle, cancelHours: p.cancelHours, note: p.note }}
            sel={ui.selSection}
            onSelect={(id) => set({ selSection: id, sel: id ? null : ui.sel })}
            onChange={setSections}
            accent={accent}
            answersPick={csType === "event" ? null : {
              options: sampleSvcRows.map((r) => ({ id: r.id, name: r.name })),
              selected: ui.sampleByPage[csPageUid] ?? "",
              onPick: (id: string) => set({ sampleByPage: { ...ui.sampleByPage, [csPageUid]: id } }),
            }}
          />
        ) : null}

        {p.id === "eventlanding" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 12px" }}>
              <span style={glyphBox("#FDEBF3", "#D61A55")}>▦</span>
              <span><span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{(events ?? []).find((e: any) => `event:${e.id}` === ui.curDoc)?.date ?? t('journeys.newJourneys.text87', "Date from the event")}</span><span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{(events ?? []).find((e: any) => `event:${e.id}` === ui.curDoc)?.time ?? "—"}</span></span>
            </div>
            {tg("mapcard") ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 12px" }}>
                <span style={glyphBox("#FDEBF3", "#D61A55")}>◉</span>
                <span><span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{(events ?? []).find((e: any) => `event:${e.id}` === ui.curDoc)?.location ?? t('journeys.newJourneys.text88', "Location from the event")}</span><span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{t('journeys.newJourneys.text89', "map & parking")}</span></span>
              </div>
            ) : null}
            {tg("cta") ? <button style={{ height: 38, border: "none", borderRadius: 10, background: accent, color: "#FFFFFF", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{String(pval("cta") ?? t("journeys.newJourneys.ctaRegister", "Register"))}</button> : null}
          </div>
        ) : null}

        {p.id === "tickets" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 6 }}>
            {tixRows.length ? (<>
              {attTickets ? <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em", color: "#D61A55", textTransform: "uppercase" }}>{attTickets.name || t('journeys.newJourneys.text92', "Ticket group")}</div> : null}
              {tixRows.map((t) => (
                <div key={`${t.from}:${t.id}`} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 12px" }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{t.name}</span>
                    {tg("sold") && t.qty != null ? <span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{i18nT("journeys.newJourneys.qtyAllocated", "{qty} allocated", { qty: t.qty })}</span> : null}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "#D01647", flex: "none" }}>{t.kind === "free" || t.kind === "invite" ? i18nT("journeys.newJourneys.priceFree", "Free") : tgMoney(t.price)}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7, border: "1px solid #D1D4DC", borderRadius: 999, padding: "3px 9px", fontSize: 12, fontWeight: 700, color: "#697180", flex: "none" }}>− <span style={{ color: "#333333" }}>0</span> +</span>
                </div>
              ))}
            </>) : <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "18px 12px", textAlign: "center", fontSize: 11.5, color: "#697180" }}>{t('journeys.newJourneys.text90', "No tickets yet — attach a ticket group or pick ticket types in")}{" "}<b>{t('journeys.newJourneys.text91', "Setup · Tickets")}</b>.</div>}
            {tg("promo2") ? <div style={{ ...FIELD, border: "1px dashed #D1D4DC" }}>{t('journeys.newJourneys.text93', "Promo code")}</div> : null}
            {/* Checkout is the footer's forward button on this page. */}
            <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", ...MONO }}>{t('journeys.newJourneys.text94', "checkout stays the fixed payment component — presentation only is composable")}</div>
            {r0.waitlist ? <div style={{ fontSize: 11, color: "#697180", textAlign: "center" }}>{t('journeys.newJourneys.text95', "Sold out? Join the waitlist — we'll email you if a ticket frees up.")}</div> : null}
          </div>
        ) : null}

        {p.id === "exphub" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
            {tg("badge") ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E4E1F7", background: "#FAFAFE", borderRadius: 10, padding: "10px 12px" }}>
                <span style={{ ...glyphBox("#E6F4EE", "#027D47"), borderRadius: 999, fontWeight: 800 }}>✓</span>
                <span><span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{t('journeys.newJourneys.text96', "Checked in")}</span><span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{t('journeys.newJourneys.text97', "your QR works everywhere at this event")}</span></span>
              </div>
            ) : null}
            {hubRows.map((h, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 12px" }}>
                <span style={glyphBox(h.tint, h.color)}>{h.glyph}</span>
                <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700 }}>{h.name}</span>
                {tg("waits") ? <span style={{ fontSize: 11, fontWeight: 700, color: h.color, background: h.tint, borderRadius: 999, padding: "3px 9px", flex: "none" }}>{h.meta}</span> : null}
              </div>
            ))}
            <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", ...MONO }}>{t('journeys.newJourneys.text98', "rows = the experience's attached journeys · finishing one returns here")}</div>
          </div>
        ) : null}

        {p.id === "csat" ? (
          <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
            <div style={{ fontSize: 20, letterSpacing: 4, color: "#F5C61E" }}>★★★★★</div>
            {/* Post-commit pages keep their own actions, as LINKS rather than
                buttons (operator, 2026-09-02): the journey is over, so nothing
                here should carry the weight of a step. */}
            <div style={{ marginTop: 12, fontSize: 12.5, fontWeight: 700, color: accent, textDecoration: "underline" }}>{String(pval("cta") ?? t("journeys.newJourneys.ctaTakeSurvey", "Take the survey"))}</div>
            {tg("review") ? <div style={{ fontSize: 11.5, color: "var(--jrni-color-primary-text, #5142C7)", fontWeight: 600, marginTop: 8 }}>{t('journeys.newJourneys.text99', "Leave us a review")}</div> : null}
          </div>
        ) : null}
      </div>
    );
  };

  const contentBlocks = (p?.content ?? []).filter((c) => c.type !== "hero");

  /* ── Drag and drop on the canvas ────────────────────────────────
   *  Two drags, one set of drop targets. A PALETTE drag carries a block type and
   *  inserts a new block; a MOVE drag carries a uid and relocates an existing
   *  one. Both land on the same insertion lines between top-level blocks, so an
   *  author learns one gesture.
   *
   *  Indices here are into `contentBlocks` — the page's content WITHOUT the hero.
   *  Writing back through `patchContent` re-attaches the hero and `hoistHero`
   *  pins it to the top, so no drop target can put anything above it and the
   *  "hero is always first" invariant is never a thing this code has to check.
   */
  const writeBlocks = (next: Block[]) => {
    const hero = (p?.content ?? []).filter((c) => c.type === "hero");
    patchContent(() => [...hero, ...next]);
  };

  const insertTypeAt = (type: string, at: number) => {
    const uid = njUid("c");
    const next = contentBlocks.slice();
    next.splice(Math.max(0, Math.min(at, next.length)), 0, { uid, type, text: CDEF[type] });
    writeBlocks(next);
    set({ sel: uid });
    if (type === "hero") showToast(t("journeys.newJourneys.toast.heroAdded", "Hero added — it always sits at the top of the page"));
  };

  /* `moveBlockTo` and `nudgeBlock` retired: BlockCanvas owns reordering for every
     journey type, and it calls `onMove(from, to)` with indices. Two copies of
     splice-and-write were two places for the no-op-drag guard to be forgotten. */
  const removeBlock = (uid: string) => {
    writeBlocks(contentBlocks.filter((c) => c.uid !== uid));
    if (ui.sel === uid) set({ sel: null });
  };

  const blockView = (c: Block, i: number, opts?: { inCell?: boolean }) => {
    const ring = ui.sel === c.uid ? "var(--jrni-color-primary-1, #5142C7)" : "transparent";
    const align = bval(c, "align") === "Center" ? "center" : bval(c, "align") === "Right" ? "right" : "left";
    const attKind = ATTACH_TYPES[c.type];
    /* Service-scoped on this journey type? Then the journey-wide attachment is
       a lie: the customer sees the value for the service THEY picked, which is
       unknowable at design time (operator ruling 2026-08-31). Events are exempt
       — one event, one set of answers, known now. Tickets are event-only and
       event-scoped, so they are never in this set. */
    const serviceScoped = (isQueueJourney || isApptJourney) && ["qgroup", "survey", "promo"].indexOf(c.type) > -1;
    const attMeta: Record<string, { name: string; meta: string; tint: string; bd: string; color: string; glyph: string }> = {
      qgroup: { name: qName, meta: t("journeys.newJourneys.meta.bookingQuestionsRender", "{count, plural, one {# booking question renders here} other {# booking questions render here}}", { count: attQuestions.length || qCount }), tint: "var(--jrni-color-primary-4, #F6F2FE)", bd: "#DCCCFC", color: "var(--jrni-color-primary-text, #5142C7)", glyph: "?" },
      survey: { name: svName, meta: t("journeys.newJourneys.meta.questionsRender", "{count, plural, one {# question renders here} other {# questions render here}}", { count: svCount }), tint: "#E6F4EE", bd: "#A7DACB", color: "#027D47", glyph: "✎" },
      promo: { name: promoCode, meta: promoOff, tint: "#FFFAED", bd: "#F3D9A4", color: "#916D0F", glyph: "⌾" },
      ticketgrp: { name: tixName, meta: t("journeys.newJourneys.meta.ticketTypesRender", "{count, plural, one {# ticket type renders here} other {# ticket types render here}}", { count: tixRows.length }), tint: "#FDF3F6", bd: "#E7A4B8", color: "#D61A55", glyph: "⬧" },
    };
    return (
      /* The wrapper belongs to BlockCanvas now (2026-09-02): drag, selection,
         hover, the ↑↓× controls and the drop geometry are shared with every other
         journey type, so this function is back to its one job — how a block LOOKS.
         A block inside a COLUMN keeps its own click-to-select wrapper: cells are
         nested lists that BlockCanvas does not own, and a nested block must not
         drag onto the page's gaps and change which array holds it. */
      <div
        key={c.uid}
        className="nj-block"
        onClick={opts?.inCell ? (e) => { e.stopPropagation(); set({ sel: ui.sel === c.uid ? null : c.uid, selSection: null }); } : undefined}
        style={opts?.inCell
          ? { margin: "0 0 8px", position: "relative", cursor: "pointer", outline: `2px solid ${ring}`, outlineOffset: 2, borderRadius: 8 }
          : { position: "relative" }}>
        {serviceScoped ? (() => {
          /* Which services this journey offers, and what each would ask —
             derived once above, shared with the end page's Answers section. */
          const rows = sampleSvcRows;
          const distinct = new Set(rows.map((r) => r.groupId).filter(Boolean)).size;
          /* `p` is nullable here (the same guard L2169 already makes). An
             unattached page has no uid to key the sample switcher on, so "" is
             the honest key rather than "undefined" (2026-09-08). */
          const pageUid = p ? String(p.uid ?? p.id) : "";
          const sampleId = ui.sampleByPage[pageUid] ?? "";
          const sample = rows.filter((r) => r.id === sampleId)[0] ?? null;
          const sampleQs = sample ? questionsOf(sample.groupId) : [];
          const pending = c.type !== "qgroup";
          return (
            <div style={{ border: "1px dashed #C9C2EE", background: "#FBFAFF", borderRadius: 9, padding: "11px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                <span style={glyphBox("var(--jrni-color-primary-4, #F6F2FE)", "var(--jrni-color-primary-1, #5142C7)")}>{c.type === "qgroup" ? "?" : c.type === "survey" ? "✎" : "⌾"}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--jrni-color-primary-text, #5142C7)", flex: 1, minWidth: 0 }}>
                  {t("journeys.newJourneys.attachForService", "{kind, select, qgroup {Booking questions for the selected service} survey {Survey for the selected service} other {Promo code for the selected service}}", { kind: c.type })}</span>
                <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--jrni-color-primary-text, #5142C7)", background: "var(--jrni-color-primary-3, #EDE5FE)", borderRadius: 999, padding: "2px 8px" }}>{t('journeys.newJourneys.text101', "Resolves at runtime")}</span>
              </div>
              <div style={{ fontSize: 10.5, color: "var(--jrni-color-primary-text, #5142C7)", opacity: 0.85, marginTop: 5, lineHeight: 1.5 }}>
                {pending
                  ? t("journeys.newJourneys.setPerService", "{appt, select, yes {Set per service once the catalog carries it — see Setup · Per-service settings.} other {Set per service once the catalog carries it — see Setup · Services.}}", { appt: isApptJourney ? "yes" : "no" })
                  : <>{t("journeys.newJourneys.servicesOffered", "{count, plural, one {# service offered} other {# services offered}} · {groups, plural, one {# distinct group} other {# distinct groups}}", { count: rows.length, groups: distinct })}{" · "}</>}
                {!pending ? <a href="#" onClick={(e) => { e.preventDefault(); set({ setupView: (isApptJourney ? "aperservice" : "qservices") as any }); }} style={{ color: "var(--jrni-color-primary-text, #5142C7)", fontWeight: 700 }}>{t('journeys.newJourneys.text104', "see them in Setup")}</a> : null}
              </div>
              {/* Sample preview (operator ruling): one service's REAL questions,
                  labelled as a sample so it is never mistaken for the page's
                  own content. The choice is remembered per page. */}
              {!pending && rows.length ? (
                <div style={{ marginTop: 9, borderTop: "1px solid var(--jrni-color-primary-3, #EDE5FE)", paddingTop: 9 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: sample ? 8 : 0 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#8A92A0" }}>{t('journeys.newJourneys.text105', "Preview as")}</span>
                    {rows.map((r) => {
                      const on = r.id === sampleId;
                      return (
                        <button key={r.id} onClick={(e) => { e.stopPropagation(); set({ sampleByPage: { ...ui.sampleByPage, [pageUid]: on ? "" : r.id } }); }}
                          style={{ padding: "3px 9px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 10.5, fontWeight: 700, border: `1px solid ${on ? "var(--jrni-color-primary-text, #5142C7)" : "#D9DCE3"}`, background: on ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: on ? "#FFFFFF" : "#4B5563", whiteSpace: "nowrap" }}>
                          {r.name}
                        </button>
                      );
                    })}
                  </div>
                  {sample ? (
                    <div style={{ border: "1px solid #DCCCFC", background: "#FFFFFF", borderRadius: 8, padding: "10px 11px" }}>
                      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#8A5A08", background: "#FFF7E8", borderRadius: 999, padding: "2px 8px", display: "inline-block", marginBottom: 8 }}>{t("journeys.newJourneys.sampleBadge", "Sample · {name}", { name: sample.name })}</div>
                      {sampleQs.length
                        ? <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>{sampleQs.map(qFieldView)}</div>
                        : <div style={{ ...NOTE, margin: 0 }}>{t('journeys.newJourneys.text107', "This service asks no questions — the page shows only the standard fields.")}</div>}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })() : attKind ? (() => {
          const m = attMeta[c.type]!;
          return (
            <div style={{ border: `1px dashed ${m.bd}`, background: m.tint, borderRadius: 8, padding: "10px 11px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ ...glyphBox(m.color, "#FFFFFF", 22, 11), borderRadius: 6, fontWeight: 800 }}>{m.glyph}</span>
                <span style={{ flex: 1, fontSize: 11.5, fontWeight: 700, color: m.color }}>{m.name}</span>
                <span style={{ fontSize: 8.5, fontWeight: 800, background: m.color, color: "#FFFFFF", borderRadius: 3, padding: "1px 5px" }}>{t("journeys.newJourneys.attachedBadge", "ATTACHED")}</span>
              </div>
              <div style={{ fontSize: 10.5, color: m.color, opacity: 0.85, marginTop: 5 }}>
                {m.meta} · <a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); set({ lib: attKind as LibKind }); }} style={{ color: m.color, fontWeight: 700 }}>{t('journeys.newJourneys.text108', "Change")}</a>
              </div>
              {/* An attachment renders what IS attached, not a description of it:
                  a booking-questions or tickets page shows the customer's actual
                  fields and tickets. */}
              {c.type === "qgroup" && attQuestions.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 9, background: "#FFFFFF", border: `1px solid ${m.bd}`, borderRadius: 8, padding: "11px 12px" }}>{attQuestions.map(qFieldView)}</div>
              ) : null}
              {c.type === "ticketgrp" && tixRows.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 9 }}>{tixRows.map(tixRowView)}</div>
              ) : null}
            </div>
          );
        })() : null}

        {c.type === "notice" ? <div style={{ border: `1px solid ${({ Neutral: "#E6E8EC", Info: "#C9DAFB", Warning: "#F3D9A4", Success: "#A7DACB" } as any)[bval(c, "tone")] ?? "#E6E8EC"}`, background: ({ Neutral: "#F4F5F7", Info: "#ECF1FE", Warning: "#FFFAED", Success: "#E6F4EE" } as any)[bval(c, "tone")] ?? "#F4F5F7", borderRadius: 8, padding: "9px 12px", fontSize: 11.5, textAlign: align as any, color: ({ Neutral: "#697180", Info: "#2D60EB", Warning: "#916D0F", Success: "#027D47" } as any)[bval(c, "tone")] ?? "#697180" }}>{bval(c, "icon") ? "◈ " : ""}{c.text}</div> : null}
        {c.type === "text" ? <div style={{ fontSize: bval(c, "size") === "Large" ? 15 : bval(c, "size") === "Small" ? 11 : 12.5, textAlign: align as any, color: "#333333", lineHeight: 1.55, padding: "2px 4px" }}>{c.text}</div> : null}
        {c.type === "button" ? (
          <div style={{ textAlign: "center" }}>
            <span style={{ display: bval(c, "full") ? "block" : "inline-block", padding: "9px 20px", border: `1.5px solid ${bval(c, "style") === "Text link" ? "transparent" : accent}`, background: bval(c, "style") === "Primary" ? accent : "transparent", borderRadius: 10, fontSize: 12.5, fontWeight: 700, color: bval(c, "style") === "Primary" ? "#FFFFFF" : accent }}>{c.text}</span>
            <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 4, ...MONO }}>→ {bval(c, "linkKind") === "Content page" ? (bval(c, "page") && bval(c, "page") !== "Pick a page…" ? bval(c, "page") : t("journeys.newJourneys.noPagePicked", "no page picked")) : (bval(c, "href") || t("journeys.newJourneys.noLinkSet", "no link set"))}</div>
          </div>
        ) : null}
        {/* ui-config: "Solid" is the divider's CSS border-style default, lowercased below */}
        {c.type === "divider" ? <hr style={{ border: "none", borderTop: `1px ${String(bval(c, "style") ?? "Solid").toLowerCase()} #D1D4DC`, margin: ({ Tight: "0 8px", Normal: "4px 8px", Loose: "14px 8px" } as any)[bval(c, "space")] ?? "4px 8px" }} /> : null}
        {c.type === "spacer" ? <div style={{ height: ({ Small: 12, Medium: 24, Large: 48 } as any)[bval(c, "height")] ?? 24 }} /> : null}
        {c.type === "image" ? (
          <div>
            <div onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }} onDrop={dropImage(c)} style={{ height: ({ "Wide 16:9": 96, Square: 150, "Tall 4:5": 180, "Banner 3:1": 62 } as any)[bval(c, "ratio")] ?? 96, borderRadius: bval(c, "round") ? 8 : 0, background: bval(c, "src") ? `url(${bval(c, "src")}) center/cover` : "repeating-linear-gradient(45deg, #EDEDF2, #EDEDF2 8px, #F6F6FA 8px, #F6F6FA 16px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, color: "#697180", textAlign: "center", padding: "0 10px", ...MONO }}>{bval(c, "src") ? "" : t('journeys.newJourneys.text109', "drop an image here, or add a URL on the right")}</div>
            {bval(c, "caption") ? <div style={{ fontSize: 10.5, color: "#697180", marginTop: 5, textAlign: "center" }}>{bval(c, "caption")}</div> : null}
          </div>
        ) : null}
        {c.type === "video" ? (
          <div>
            <div style={{ height: 110, borderRadius: 10, background: "#1F2430", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <span style={{ ...glyphBox("rgba(255,255,255,0.18)", "#FFFFFF", 34, 13), borderRadius: 999 }}>▶</span>
              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", ...MONO }}>{bval(c, "url") || t('journeys.newJourneys.text110', "video — paste URL")}</span>
            </div>
            {bval(c, "caption") ? <div style={{ fontSize: 10.5, color: "#697180", marginTop: 5, textAlign: "center" }}>{bval(c, "caption")}</div> : null}
          </div>
        ) : null}
        {c.type === "gallery" ? (
          <div onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }} onDrop={dropImage(c)}>
            <div style={{ display: "grid", gridTemplateColumns: bval(c, "layout") === "Carousel" ? `repeat(${bval(c, "count") ?? 3}, 96px)` : `repeat(${Math.min(3, Number(bval(c, "count") ?? 3))}, 1fr)`, gap: 6, overflow: "hidden" }}>
              {Array.from({ length: Number(bval(c, "count") ?? 3) }).map((_, n) => {
                const u = String(bval(c, "urls") ?? "").split("\n").map((x) => x.trim()).filter(Boolean)[n] ?? "";
                return <span key={n} style={{ height: 54, borderRadius: 8, background: u ? `url(${u}) center/cover` : "repeating-linear-gradient(45deg, #EDEDF2, #EDEDF2 8px, #F6F6FA 8px, #F6F6FA 16px)" }} />;
              })}
            </div>
            <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 5, textAlign: "center", ...MONO }}>{t('journeys.newJourneys.text111', "drop images here, or list URLs on the right")}</div>
          </div>
        ) : null}
        {c.type === "map" ? (() => {
          /* Falls back to the venue Setup · Details holds, so a map block never
             sits empty next to a page that already knows the address. */
          const authored = String(bval(c, "address") ?? "");
          const shown = authored || setupAddress || setupVenue;
          return (
            <div>
              <div style={{ height: 90, borderRadius: 10, border: "1px solid #E6E8EC", background: "repeating-linear-gradient(45deg, #EDEDF2, #EDEDF2 8px, #F6F6FA 8px, #F6F6FA 16px)", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "0 10px" }}>
                <span style={{ color: "#D01647", fontSize: 14 }}>◉</span>
                <span style={{ fontSize: 10.5, color: "#697180", ...MONO }}>{shown || t('journeys.newJourneys.text112', "address — set it on the right, or in Setup · Details")}</span>
              </div>
              {!authored && shown ? <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 4, textAlign: "center", ...MONO }}>{t('journeys.newJourneys.text113', "from Setup · Details")}</div> : null}
              {bval(c, "directions") ? <div style={{ fontSize: 11, color: "var(--jrni-color-primary-text, #5142C7)", fontWeight: 700, marginTop: 5, textAlign: "center" }}>{t('journeys.newJourneys.text114', "Get directions")}</div> : null}
            </div>
          );
        })() : null}
        {c.type === "faq" ? (
          <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "9px 12px", fontSize: 12, fontWeight: 600, borderBottom: "1px solid #F4F5F7" }}>{c.text}<span style={{ color: "#B5BAC6" }}>▾</span></div>
            {bval(c, "expand") ? <div style={{ padding: "0 12px 9px", fontSize: 11.5, color: "#697180", lineHeight: 1.5, borderBottom: "1px solid #F4F5F7" }}>{bval(c, "a1")}</div> : null}
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "9px 12px", fontSize: 12, fontWeight: 600 }}>{bval(c, "text2")}<span style={{ color: "#B5BAC6" }}>▾</span></div>
            {bval(c, "expand") ? <div style={{ padding: "0 12px 9px", fontSize: 11.5, color: "#697180", lineHeight: 1.5 }}>{bval(c, "a2")}</div> : null}
          </div>
        ) : null}
        {c.type === "quote" ? (
          <div style={{ borderLeft: "3px solid #C18FE8", padding: "2px 0 2px 12px" }}>
            <div style={{ fontSize: 13, fontStyle: "italic", color: "#333333", lineHeight: 1.5 }}>“{c.text}”</div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 6 }}>
              {bval(c, "photo") ? <span style={{ width: 22, height: 22, borderRadius: 999, background: "repeating-linear-gradient(45deg, #EDEDF2, #EDEDF2 8px, #F6F6FA 8px, #F6F6FA 16px)", flex: "none" }} /> : null}
              <span style={{ fontSize: 10.5, color: "#697180" }}>— {bval(c, "who")}</span>
            </div>
          </div>
        ) : null}
        {c.type === "stats" ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, textAlign: "center" }}>
            {[["s1v", "s1l"], ["s2v", "s2l"], ["s3v", "s3l"]].map(([v, l]) => (
              <span key={v} style={{ background: "#F4F5F7", borderRadius: 8, padding: "10px 4px" }}>
                <span style={{ display: "block", fontSize: 16, fontWeight: 800 }}>{bval(c, v!)}</span>
                <span style={{ display: "block", fontSize: 9.5, color: "#697180" }}>{bval(c, l!)}</span>
              </span>
            ))}
          </div>
        ) : null}
        {c.type === "countdown" ? (() => {
          /* Counts down to the event's real start. It used to print a fixed
             "04d 02h 14m" no matter what Setup · Schedule said. */
          const ms = startAt ? startAt.getTime() - Date.now() : 0;
          const d = ms > 0 ? Math.floor(ms / 86400000) : 0;
          const h = ms > 0 ? Math.floor((ms % 86400000) / 3600000) : 0;
          const mi = ms > 0 ? Math.floor((ms % 3600000) / 60000) : 0;
          const se = ms > 0 ? Math.floor((ms % 60000) / 1000) : 0;
          const pad = (n: number) => String(n).padStart(2, "0");
          const parts = bval(c, "units") === "Days / hours / min"
            ? [`${pad(d)}d`, `${pad(h)}h`, `${pad(mi)}m`]
            : [`${pad(d * 24 + h)}h`, `${pad(mi)}m`, `${pad(se)}s`];
          return (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", color: "#697180", textTransform: "uppercase", marginBottom: 6 }}>{bval(c, "label")}</div>
              {startAt ? (<>
                <div style={{ display: "inline-flex", gap: 6 }}>
                  {parts.map((x, n) => <span key={n} style={{ background: "#1F2430", color: "#FFFFFF", borderRadius: 6, padding: "6px 9px", fontSize: 14, fontWeight: 800, ...MONO }}>{x}</span>)}
                </div>
                <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 5, ...MONO }}>{ms > 0 ? t('journeys.newJourneys.text115', "to {setupDateLine}", { setupDateLine }) : t('journeys.newJourneys.text116', "the start date has passed")}</div>
              </>) : (
                <div style={{ border: "1px dashed #D1D4DC", borderRadius: 8, padding: "12px 10px", fontSize: 11.5, color: "#697180" }}>{t('journeys.newJourneys.text117', "Set a start date in")}{" "}<b>{t('journeys.newJourneys.text118', "Setup · Schedule")}</b> {t('journeys.newJourneys.text119', "— the countdown runs to it.")}</div>
              )}
            </div>
          );
        })() : null}
        {c.type === "social" ? (
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            {NETS.filter((s) => bval(c, s[0])).map((s) => (
              <span key={s[0]} style={{ width: 28, height: 28, borderRadius: 999, border: "1px solid #D1D4DC", color: "#697180", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, position: "relative" }}>
                {s[2]}{bval(c, s[0] + "Url") ? <span style={{ position: "absolute", bottom: -1, right: -1, width: 8, height: 8, borderRadius: 999, background: "#027D47", border: "1.5px solid #FFFFFF" }} /> : null}
              </span>
            ))}
          </div>
        ) : null}

        {/* Event details — the date/venue line. Reads the event record LIVE and
            is never authored here; the block's own config only chooses which
            pieces show. Until this pass the block existed in the palette, the
            schema AND on every seeded Welcome page, but had no renderer at all,
            so nothing entered in Setup · Schedule ever reached the page. */}
        {/* Container (stage 4). Cells render through blockView itself, so a
            nested block gets the same selection ring, the same inspector and the
            same move/delete controls as one on the page body. */}
        {c.type === "columns" ? (() => {
          const cells = blockCells(c) ?? [[], []];
          const ratio = String(bval(c, "ratio") ?? "1-1");
          const cols = ratio === "2-1" ? "2fr 1fr" : ratio === "1-2" ? "1fr 2fr" : "1fr 1fr";
          const gap = Number(bval(c, "gap")) || 20;
          return (
            <div style={{ display: "grid", gridTemplateColumns: renderWidth < 520 && bval(c, "stackMobile") !== false ? "1fr" : cols, gap }}>
              {cells.map((cell, ci) => {
                const over = ui.dropCell === `${c.uid}:${ci}`;
                return (
                  <div key={ci}
                    onDragOver={(e) => { if (e.dataTransfer?.types?.includes(BLOCK_ADD_DND)) { e.preventDefault(); e.stopPropagation(); if (!over) set({ dropCell: `${c.uid}:${ci}` }); } }}
                    onDragLeave={() => { if (over) set({ dropCell: null }); }}
                    onDrop={(e) => {
                      const type = e.dataTransfer?.getData(BLOCK_ADD_DND);
                      set({ dropCell: null });
                      if (!type) return;
                      e.preventDefault(); e.stopPropagation();
                      const uid = njUid("c");
                      patchContent((list) => addToCell(list, c.uid, ci, { uid, type, text: CDEF[type] }));
                      set({ sel: uid });
                      showToast(t("journeys.newJourneys.toast.blockDropped", "{type} block dropped into column {column}", { type, column: ci + 1 }));
                    }}
                    style={{ minHeight: 64, borderRadius: 8, border: `1px ${cell.length ? "solid" : "dashed"} ${over ? "var(--jrni-color-primary-1, #5142C7)" : cell.length ? "transparent" : "#D1D4DC"}`, background: over ? "var(--jrni-color-primary-4, #F6F2FE)" : "transparent", padding: cell.length ? 0 : "6px 4px", display: "flex", flexDirection: "column", justifyContent: cell.length ? "flex-start" : "center" }}>
                    {cell.length
                      ? cell.map((child, k) => blockView(child, k, { inCell: true }))
                      : <span style={{ fontSize: 10.5, color: over ? "var(--jrni-color-primary-text, #5142C7)" : "#B5BAC6", textAlign: "center", lineHeight: 1.5 }}>{over ? t('journeys.newJourneys.text120', "Drop to add") : t('journeys.newJourneys.text121', "Drag a block here")}</span>}
                  </div>
                );
              })}
            </div>
          );
        })() : null}

        {c.type === "eventdetails" ? (() => {
          const card = bval(c, "layout") === "Card";
          const showDate = bval(c, "showDate") !== false;
          const showLoc = bval(c, "showLocation") !== false;
          const rows = [
            showDate && (setupDateLine || setupTimeLine)
              ? { k: "when", glyph: "▦", title: setupDateLine || t("journeys.newJourneys.dateNotSet", "Date not set"), sub: setupTimeLine }
              : null,
            showLoc && (setupVenue || setupAddress || r0.virtualProvider)
              ? {
                  k: "where", glyph: "◉",
                  title: setupVenue || setupAddress || t("journeys.newJourneys.venueOnline", "Online"),
                  sub: setupVenue && setupAddress ? setupAddress : (!setupVenue && !setupAddress && r0.virtualProvider ? t("journeys.newJourneys.joinLinkSent", "Join link sent when you register") : ""),
                }
              : null,
          ].filter(Boolean) as { k: string; glyph: string; title: string; sub: string }[];
          if (!rows.length) return (
            <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "14px 12px", fontSize: 11.5, color: "#697180", textAlign: "center", lineHeight: 1.55 }}>
              {t('journeys.newJourneys.text122', "The date and venue appear here as soon as they exist — set them in")}{" "}<b>{t('journeys.newJourneys.text118', "Setup · Schedule")}</b> {t('journeys.newJourneys.text123', "and")}{" "}<b>{t('journeys.newJourneys.text124', "Setup · Details")}</b>.
            </div>
          );
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: card ? 7 : 5 }}>
              {rows.map((r) => card ? (
                <div key={r.k} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 12px" }}>
                  <span style={glyphBox("#FDEBF3", "#D61A55")}>{r.glyph}</span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{r.title}</span>
                    {r.sub ? <span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{r.sub}</span> : null}
                  </span>
                </div>
              ) : (
                <div key={r.k} style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, lineHeight: 1.5 }}>
                  <span style={{ color: "#D61A55", flex: "none" }}>{r.glyph}</span>
                  <span style={{ minWidth: 0 }}>
                    <b style={{ fontWeight: 700 }}>{r.title}</b>
                    {r.sub ? <span style={{ color: "#697180" }}> · {r.sub}</span> : null}
                  </span>
                </div>
              ))}
              {showDate && setupRepeatLine ? <div style={{ fontSize: 10.5, color: "#697180", paddingLeft: card ? 2 : 20 }}>{t("journeys.newJourneys.repeatsLine", "Repeats · {line}", { line: setupRepeatLine })}</div> : null}
              {bval(c, "showAddToCalendar") !== false ? (
                <span style={{ alignSelf: "flex-start", marginTop: 3, border: `1.5px solid ${accent}`, borderRadius: 10, padding: "7px 16px", fontSize: 12, fontWeight: 700, color: accent }}>{t('journeys.newJourneys.text126', "Add to calendar")}</span>
              ) : null}
              <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 1, ...MONO }}>{t('journeys.newJourneys.text127', "live from Setup · Schedule and Setup · Details — not authored here")}</div>
            </div>
          );
        })() : null}

        {ui.sel === c.uid ? (
          <span style={{ position: "absolute", top: -13, right: 0, display: "inline-flex", gap: 2, background: "var(--jrni-color-primary-text, #5142C7)", borderRadius: 6, padding: 2 }}>
            {!opts?.inCell && i > 0 ? <button onClick={(e) => { e.stopPropagation(); patchContent((list) => { const n = list.slice(); const a = n.findIndex((x) => x.uid === c.uid); const prev = n.slice(0, a).map((x, j) => [x, j] as [Block, number]).filter((pair) => pair[0].type !== "hero").pop(); if (!prev) return list; const pi = prev[1]; [n[pi], n[a]] = [n[a]!, n[pi]!]; return n; }); }} style={{ width: 20, height: 18, border: "none", background: "transparent", color: "#FFFFFF", fontSize: 10, cursor: "pointer" }}>↑</button> : null}
            {!opts?.inCell && i < contentBlocks.length - 1 ? <button onClick={(e) => { e.stopPropagation(); patchContent((list) => { const n = list.slice(); const a = n.findIndex((x) => x.uid === c.uid); if (a === n.length - 1) return list; [n[a + 1], n[a]] = [n[a]!, n[a + 1]!]; return n; }); }} style={{ width: 20, height: 18, border: "none", background: "transparent", color: "#FFFFFF", fontSize: 10, cursor: "pointer" }}>↓</button> : null}
            {!attKind ? <button onClick={(e) => {
              e.stopPropagation();
              const cells = blockCells(c);
              const inside = cells ? cells.reduce((n, cell) => n + cell.length, 0) : 0;
              if (inside && !window.confirm(t("journeys.newJourneys.confirmDeleteColumns", "{count, plural, one {Delete this columns block and the # block inside it?} other {Delete this columns block and the # blocks inside it?}}", { count: inside }))) return;
              /* mapContent, not filter: a nested block lives inside a cell and
                 a flat filter would never find it. */
              patchContent((list) => mapContent(list, (x) => (x.uid === c.uid ? null : x)));
              set({ sel: null });
            }} style={{ width: 20, height: 18, border: "none", background: "transparent", color: "#FFFFFF", fontSize: 10, cursor: "pointer" }}>✕</button> : null}
          </span>
        ) : null}
      </div>
    );
  };

  const selB = ui.sel ? findContentBlock(p?.content ?? [], ui.sel) : null;
  const lockReason = D ? pageLock(D, curIdx) : null;
  /* Queues and appointment funnels resolve questions, surveys and promos from
     the SERVICE, so the journey-wide pickers are withdrawn (operator ruling
     2026-08-31). Events keep theirs: one event, one set, known at design time. */

  const inspector = (
    /* The editor pane, as a slide-out over the canvas rather than a third column
       squeezed beside it (operator, 2026-09-02). Same posture as Edit a Service:
       it OVERLAYS, so the page underneath keeps the width the author was checking
       at and does not reflow every time a block is selected. 420px — wide enough
       for a link group or a chip list, which 264 never was. */
    <div
      style={{
        position: "absolute", top: 0, right: 0, bottom: 0, zIndex: 20,
        width: inspW, maxWidth: "92%",
        background: "#FFFFFF", borderLeft: "1px solid #E6E8EC",
        boxShadow: "-10px 0 28px rgba(20,19,31,0.10)",
        padding: 14, paddingTop: 0, overflow: "auto",
        transform: ui.inspOpen ? "translateX(0)" : "translateX(100%)",
        transition: "transform 170ms ease",
      }}
    >
      <div style={{ position: "sticky", top: 0, zIndex: 1, background: "#FFFFFF", display: "flex", alignItems: "center", gap: 8, padding: "12px 0 10px", borderBottom: "1px solid #F4F5F7", marginBottom: 12 }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, flex: 1 }}>{t('journeys.newJourneys.text128', "Editor")}</span>
        <button
          onClick={() => set({ inspOpen: false })}
          title={t('journeys.newJourneys.title3', "Minimize the editor")}
          style={{ width: 26, height: 26, border: "1px solid #D1D4DC", borderRadius: 6, background: "#FFFFFF", color: "#697180", fontSize: 13, lineHeight: 1, cursor: "pointer", fontFamily: FONT }}
        >
          ›
        </button>
      </div>
      {(ui.sel === "core" || ui.sel === "heading" || ui.sel === null) && p ? (<>
        <div style={SEC}>{t('journeys.newJourneys.text129', "Page text")}</div>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('journeys.newJourneys.text130', "Heading")}</div>
        <input value={p.h} onChange={(e) => patchPage({ h: e.target.value })} style={{ ...INPUT, marginBottom: 8 }} />
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('journeys.newJourneys.text131', "Subheading")}</div>
        <textarea value={p.s} onChange={(e) => patchPage({ s: e.target.value })} rows={2} style={{ ...INPUT, fontSize: 12, resize: "vertical", marginBottom: 12 }} />
        <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "0 0 12px" }} />
      </>) : null}

      {p?.core && (ui.sel === "core" || ui.sel === null) && !isConfirmPage ? (<>
        <div style={SEC}>{t("journeys.newJourneys.coreSettings", "{core} — settings", { core: p.core })}</div>
        {p.toggles.map((t) => (
          <div key={t.k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, marginBottom: 9 }}>{t.label}
            <Switch on={t.on} onClick={() => patchPage({ toggles: p.toggles.map((x) => (x.k === t.k ? { ...x, on: !x.on } : x)) })} />
          </div>
        ))}
        {(CORE_CFG[p.id] ?? []).map((f) => (
          <div key={f[0]} style={{ marginBottom: 9 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{f[1]}</div>
            <input value={String(pval(f[0]) ?? "")} onChange={(e) => patchPage({ [f[0]]: e.target.value } as any)} style={{ ...INPUT, fontSize: 12 }} />
          </div>
        ))}
      </>) : null}

      {/* The commit button's wording — the ONLY part of the footer an author
          owns. Whether a page shows Back or Next is derived; what the button
          that creates the booking says is a real editorial decision, and it is
          per-page because a journey can commit somewhere unusual. Empty falls
          back to the journey default from Setup, which falls back to the type's
          own wording. */}
      {p && pageNav?.isCommit && (ui.sel === "core" || ui.sel === "heading" || ui.sel === null) ? (<>
        <div style={SEC}>{t('journeys.newJourneys.text133', "Navigation")}</div>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('journeys.newJourneys.text134', "Button label")}</div>
        <input value={String(p.commitLabel ?? "")} placeholder={commitLabelDefault} onChange={(e) => patchPage({ commitLabel: e.target.value } as any)} style={{ ...INPUT, fontSize: 12, marginBottom: 6 }} />
        <div style={NOTE}>{t('journeys.newJourneys.text135', "This page's forward button is what creates the booking, so it says what that does rather than \"Next\". Leave it empty to use the journey's own default (")}{commitLabelDefault}).</div>
        <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "12px 0" }} />
      </>) : null}

      {p && (p.id === "services" || p.id === "apptsvc") && (ui.sel === "core" || ui.sel === null) ? (<>
        <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "4px 0 12px" }} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8 }}>
          <span style={{ ...SEC, marginBottom: 0 }}>{isApptPage ? t('journeys.newJourneys.text136', "Appointment services offered") : t('journeys.newJourneys.text137', "Services offered")}</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 10, color: "#B5BAC6" }}>{t("journeys.newJourneys.fromSetupCount", "{count} from Setup", { count: svcCatalogue.length })}</span>
        </div>
        {/* READ-ONLY MIRROR of Setup (operator, 2026-09-02). This used to be a
            second editable list, which is why it needed a drift warning and a
            reconcile button: a page could quietly offer something Setup did not.
            The page now shows what Setup offers, in Setup's order, and the only
            way to change it is the one picker on the setup step. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 10, maxHeight: 250, overflow: "auto" }}>
          {svcCatalogue.length ? svcCatalogue.map((s) => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid #EEF0F3", background: "#FBFBFC", borderRadius: 7, padding: "7px 9px" }}>
              <span style={{ flex: 1, minWidth: 0 }}><span style={{ display: "block", fontSize: 11.5, fontWeight: 600 }}>{s.name}</span>{s.dept ? <span style={{ display: "block", fontSize: 10, color: "#697180" }}>{s.dept}</span> : null}</span>
              <span style={{ fontSize: 10, color: "#B5BAC6", flex: "none" }}>{s.wait}</span>
            </div>
          )) : <div style={{ border: "1px dashed #D1D4DC", borderRadius: 7, padding: "12px 10px", fontSize: 11, color: "#697180" }}>{isApptPage ? t('journeys.newJourneys.text139', "No bookable services in the catalog yet.") : t('journeys.newJourneys.text140', "This journey isn't attached to a queue — its services come from the queue.")}</div>}
        </div>
        <button onClick={() => set({ setupView: isApptPage ? "aservices" : "qservices", sel: null })} style={{ ...GHOST, width: "100%" }}>{t('journeys.newJourneys.text141', "Change what's offered in Setup →")}</button>
      </>) : null}

      {/* Registration settings moved to Setup · Registration (2026-08-31). They
          are event RECORD fields — approval, guest limits, which contact fields
          are captured — and they were being edited from a page inspector, which
          is the duplicate authoring surface this pass removes. The page still
          RENDERS them; it no longer owns them. */}
      {p?.id === "register" && (ui.sel === "core" || ui.sel === null) ? (<>
        <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "4px 0 12px" }} />
        <div style={SEC}>{t('journeys.newJourneys.text142', "Registration")}</div>
        <div style={{ border: "1px dashed #C9C2EE", background: "#FBFAFF", borderRadius: 8, padding: "10px 11px", fontSize: 11, lineHeight: 1.55, color: "var(--jrni-color-primary-text, #5142C7)" }}>
          {(r0.standardFields ?? ["email"]).join(" · ")}{r0.approvalMode ? <> {t('journeys.newJourneys.text143', "· approval required")}</> : ""}{r0.maxGuests ? <> {t('journeys.newJourneys.text144', "· up to {maxGuests} guests", { maxGuests: r0.maxGuests })}</> : ""}
          <br />
          <a href="#" onClick={(e) => { e.preventDefault(); set({ setupView: "registration" }); }}>{t('journeys.newJourneys.text145', "Edit in Setup · Registration →")}</a>
        </div>
      </>) : null}

      {lockReason ? <div style={{ ...WARN, marginTop: 12, marginBottom: 12 }}>🔒 <b>{t('journeys.newJourneys.text146', "Required page.")}</b> {lockReason} {t('journeys.newJourneys.text147', "Invariants are enforced, not advised.")}</div> : null}

      {/* Section scope. A section is not a block, so it gets its own scope
          rather than being squeezed into the block inspector — what it renders
          comes from the booking, and the only things an author sets are the
          three page fields below. */}
      {isOneStopPage && ui.selSection ? (() => {
        const block = osBlocks.find((b) => b.id === ui.selSection);
        if (!block) return null;
        return (
          <OneStopInspector
            block={block as any}
            locale={p?.osSettings?.locale ?? "en-US"}
            libs={osLibs}
            takenIds={osBlocks.map((b) => b.id)}
            onPatchConfig={(config) => setOsBlocks(osBlocks.map((b) => (b.uid === block.uid ? { ...b, config } : b)))}
            onRename={(next) => {
              const trimmed = next.trim();
              if (!trimmed || trimmed === block.id) return;
              setOsBlocks(osBlocks.map((b) => (b.uid === block.uid ? { ...b, id: trimmed } : b)));
              set({ selSection: trimmed });
            }}
            onVisibility={(visibility) =>
              setOsBlocks(osBlocks.map((b) => (b.uid === block.uid ? { ...b, visibility: visibility === "always" ? undefined : visibility } : b)))
            }
          />
        );
      })() : null}

      {/* ui.selSection holds a section ROW UID (2026-09-01), not a section id —
          the inspector still keys off the registry, so the uid resolves first. */}
      {isConfirmPage && csState && ui.selSection && csAt(csState, ui.selSection) ? (
        <CSInspector
          id={csAt(csState, ui.selSection)!.type}
          type={csType}
          fields={{ bannerTitle: p?.bannerTitle, cancelHours: p?.cancelHours, note: p?.note, surveyPrompt: p?.surveyPrompt }}
          /* Spread rather than pass through: `NJPage` carries an index
             signature (`[k: string]: unknown`, nj-model.ts), so `Partial<NJPage>`
             wants one too — and TypeScript gives an implicit index signature to
             an anonymous object type but never to an `interface`, which is what
             `CSPageFields` is. Spreading keeps every field and its type; it is
             not a cast, and nothing is widened or dropped. */
          onPatch={(patch) => patchPage({ ...patch })}
          hidden={!!csAt(csState, ui.selSection)?.hidden}
          onToggle={() => setSections(csToggle(csState, ui.selSection!))}
        />
      ) : null}

      {isConfirmPage && csState && !ui.selSection ? (
        <div style={{ ...NOTE, marginTop: 12 }}>
          {t('journeys.newJourneys.text148', "This page is built from")}{" "}<b>{t('journeys.newJourneys.text149', "sections")}</b>{t('journeys.newJourneys.text150', ", not settings — pick one on the left to see what it renders and what you can set. Blocks you add sit below them.")}</div>
      ) : null}

      {selB ? (<>
        <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "4px 0 12px" }} />
        <div style={SEC}>{t("journeys.newJourneys.blockHeading", "{kind, select, qgroup {Booking questions block} survey {Survey block} promo {Promo block} ticketgrp {Ticket group block} other {{kind} block}}", { kind: selB.type })}</div>
        {blockVariant(selB.type, "page") ? (
          <div style={{ ...NOTE, marginTop: -4, marginBottom: 10 }}>{blockVariant(selB.type, "page")}</div>
        ) : null}
        {ATTACH_TYPES[selB.type] ? (
          <button onClick={() => set({ lib: ATTACH_TYPES[selB.type] as LibKind })} style={{ ...GHOST, width: "100%", marginBottom: 10 }}>{t('journeys.newJourneys.text152', "Pick from the library →")}</button>
        ) : null}
        {cfgFor(selB).map((f) => {
          const kind = f[2]; const k = f[0]; const v = bval(selB, k);
          if (kind === "tog") return <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, marginBottom: 9 }}>{f[1]}<Switch on={!!v} onClick={() => setBval(selB.uid, k, !v)} /></div>;
          if (kind === "area") return <div key={k} style={{ marginBottom: 9 }}><div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{f[1]}</div><textarea value={String(v ?? "")} onChange={(e) => setBval(selB.uid, k, e.target.value)} rows={2} style={{ ...INPUT, fontSize: 12, resize: "vertical" }} /></div>;
          // Block config picker: the design system's Dropdown, not the browser's
          // native select (operator, 2026-09-02) — same ruling as the list
          // toolbar's Sort. `portal` because the inspector body scrolls.
          if (kind === "sel") return <div key={k} style={{ marginBottom: 9 }}><div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{f[1]}</div><Dropdown size="sm" portal aria-label={String(f[1])} value={String(v ?? "")} onChange={(nv: string) => setBval(selB.uid, k, nv)} options={(f[3] ?? []).map((o) => ({ value: o, label: njCfgOptionText(o) }))} style={{ width: "100%" }} /></div>;
          return <div key={k} style={{ marginBottom: 9, opacity: f[4] ? 0.45 : 1 }}><div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{f[1]}</div><input value={String(v ?? "")} onChange={(e) => setBval(selB.uid, k, e.target.value)} placeholder={f[4] ? t('journeys.newJourneys.placeholder2', "switch it on first") : ""} style={{ ...INPUT, fontSize: 12 }} /></div>;
        })}
      </>) : null}

      {/* The ATTACHMENTS section is gone (operator ruling, 2026-09-02).
          It summarised booking questions, the survey, a coupon and the ticket
          group — but Setup owns every one of those, and three of the four are
          properties of the SERVICE or the EVENT rather than of the journey. A
          second place to read them was a second place for them to drift, and the
          section's own copy had already drifted: it described a journey-wide
          survey and a journey-wide coupon, neither of which is how they are set.
          Tickets were the one row that was still true, and Setup · Tickets says
          it better — that picker writes to the event record. */}
    </div>
  );

  /** PAGES row — always visible beside SETUP (2026-08-24): a page pill jumps
   *  straight to that page's canvas, whatever view you were in. */
  const pagesStrip = D && !pagesHidden ? (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#FFFFFF", borderBottom: "1px solid #E6E8EC", overflowX: "auto", flexShrink: 0 }}>
      <span style={{ ...SEC, marginBottom: 0, marginRight: 4, flex: "none" }}>{t('journeys.newJourneys.text16', "Pages")}</span>
      {D.pages.map((pg, i) => {
        const locked = !!pageLock(D, i);
        const on = i === curIdx && !setupViewEff && ui.view === "pages";
        return (
          <div key={String(pg.uid ?? i)} onClick={() => set({ cur: i, sel: null, selSection: null, simCalled: false, setupView: null, view: "pages" })} draggable onDragStart={(e) => { e.dataTransfer?.setData("text/plain", ""); set({ dragPg: i }); }} onDragOver={(e) => { e.preventDefault(); if (ui.dragOver !== i) set({ dragOver: i }); }} onDrop={(e) => { e.preventDefault(); movePage(ui.dragPg, i); }} onDragEnd={() => set({ dragPg: null, dragOver: null })}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 11px", borderRadius: 999, border: `1px solid ${ui.dragOver === i ? "#C18FE8" : on ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}`, background: ui.dragOver === i ? "var(--jrni-color-primary-3, #EDE5FE)" : on ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", cursor: "grab", flex: "none" }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: pg.color, flex: "none" }} />
            <span style={{ fontSize: 11.5, fontWeight: 700, color: on ? "var(--jrni-color-primary-text, #5142C7)" : "#333333", whiteSpace: "nowrap" }}>{pg.label}</span>
            {on && D.pages.length > 1 && !locked ? <button onClick={(e) => { e.stopPropagation(); removePage(i); }} style={{ border: "none", background: "transparent", color: "#697180", fontSize: 10, cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button> : null}
            {on && locked ? <button onClick={(e) => { e.stopPropagation(); showToast(t("journeys.newJourneys.toast.blocked", "Blocked — {reason} Core invariants are enforced, not advised.", { reason: pageLock(D, i) ?? "" })); }} style={{ border: "none", background: "transparent", fontSize: 9, cursor: "not-allowed", padding: 0, lineHeight: 1 }}>🔒</button> : null}
          </div>
        );
      })}
      {/* "+ Add page" only where the type's pages are the author's to choose.
          Three cases are not:
           · a Display Board and a One-Stop page are single surfaces — the whole
             experience IS that one page (`hasFlow`);
           · a QUEUE journey's pages are seeded on creation and are the set we
             want (operator, 2026-09-02) — same ruling that removed the starter
             picker, since both offered to change a page set the template owns.
          Removing the control does not lock the journey down: pages can still be
          removed, reordered and edited, and the Flow canvas still adds steps.
          What is gone is inventing a page the type has no place for. */}
      {hasFlow && !isQueueJourney ? (
        <button onClick={() => set({ typePicker: { at: null } })} style={{ height: 28, padding: "0 12px", border: "1px dashed #B9A8F0", borderRadius: 999, background: "#FCFBFF", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11.5, fontWeight: 700, cursor: "pointer", flex: "none", fontFamily: FONT }}>{t('journeys.newJourneys.text153', "+ Add page")}</button>
      ) : null}
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 10.5, color: "#B5BAC6", flex: "none", ...MONO }}>{!hasFlow ? t('journeys.newJourneys.text154', "one page — this type has no others") : isQueueJourney ? t('journeys.newJourneys.text155', "these pages come with the queue") : ""}</span>
    </div>
  ) : null;

  const pagesView = D && p ? (
    <>

      <div ref={designerRowRefCb} style={{ flex: 1, display: "flex", minHeight: 0, position: "relative" }}>
        <div style={{ width: 212, background: "#FFFFFF", borderRight: "1px solid #E6E8EC", padding: 14, overflow: "auto", flexShrink: 0 }}>
          {/* Sections sit ABOVE the block palette (operator ruling 2026-08-31):
              on a confirmation page they are the page, and blocks are the
              extra an author may add. */}
          {isOneStopPage ? (
            <OSRail blocks={osBlocks} />
          ) : null}
          {isConfirmPage && csState ? (
            <CSRail state={csState} type={csType} sel={ui.selSection}
              onSelect={(id) => set({ selSection: id, sel: id ? null : ui.sel })}
              onChange={setSections} />
          ) : null}
          {/* The designer's own copy-block palette and its Core block card are
              hidden on a One-Stop page: its content blocks (Notice, Button, FAQ,
              Quote, Figures, Social, Spacer) are already in the One-Stop palette
              above, typed by the registry the publish validates against, and a
              second palette offering near-identical names that save to a
              different array is the definition of redundant. There is no core
              block either — the page IS its blocks. */}
          {!isOneStopPage ? (<>
          <div style={SEC}>{t('journeys.newJourneys.text156', "Blocks")}</div>
          {/* One column, not two. Each entry now carries a checkbox, a label and
              a count; at two columns in a 212px rail that is ~95px per tile and
              the labels ellipsised away. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 18 }}>
            {blocksFor(p.id).map((bd) => {
              /* Top-level only: a Text block dropped into a column must not
                 make the palette's Text entry read as "already on the page",
                 and unchecking it then must not reach into the column. */
              const mine = contentBlocks.filter((c) => c.type === bd.type);
              const on = mine.length > 0;
              /* A CHECKBOX, checked ⇄ unchecked (operator, 2026-09-03). Click-to-add
                 alone left no way back from the palette — the tile went checked and
                 stayed checked, so the control could be set but never unset.
                 Unchecking removes the LAST block of that type rather than every
                 one at once: a repeatable type can hold several blocks of authored
                 copy, and one click should never take more than one of them. The
                 count says how many are there, so the consequence is legible
                 before the press. Drag still places a block somewhere other than
                 the end; the × on the block is still there too. */
              const toggle = () => {
                if (!on) { insertTypeAt(bd.type, contentBlocks.length); return; }
                const last = mine[mine.length - 1]!;
                removeBlock(last.uid);
              };
              return (
                <button key={bd.type}
                  draggable
                  onClick={toggle}
                  onDragStart={(e) => { e.dataTransfer?.setData(BLOCK_ADD_DND, bd.type); if (e.dataTransfer) e.dataTransfer.effectAllowed = "copy"; }}
                  onDragEnd={() => set({ dropCell: null })}
                  title={on
                    ? t('journeys.newJourneys.title4', "{label} · {length} on this page. Click to take the last one off, or drag to add another where you choose.", { label: bd.label, length: mine.length })
                    : t('journeys.newJourneys.title5', "Click to add {label} at the end, or drag onto the page to place it.", { label: bd.label })}
                  style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 8px", border: `1px solid ${on ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}`, background: on ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", color: on ? "var(--jrni-color-primary-text, #5142C7)" : "#333333", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: FONT, textAlign: "left" }}>
                  <span style={{ width: 14, height: 14, borderRadius: 4, border: `1px solid ${on ? "var(--jrni-color-primary-text, #5142C7)" : "#C2C7D2"}`, background: on ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: "#FFFFFF", fontSize: 9, lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{on ? "✓" : ""}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{bd.label}</span>
                  {mine.length > 1 ? <span style={{ fontSize: 9.5, fontWeight: 800, color: "var(--jrni-color-primary-text, #5142C7)", flex: "none", ...MONO }}>{mine.length}</span> : null}
                </button>
              );
            })}
          </div>
          <div style={SEC}>{t('journeys.newJourneys.text157', "Core block")}</div>
          {p.core
            ? <div style={{ border: "1px solid #DCCCFC", background: "var(--jrni-color-primary-4, #F6F2FE)", borderRadius: 8, padding: 10, fontSize: 11, lineHeight: 1.5, color: "#333333", marginBottom: 18 }}><b style={{ color: "var(--jrni-color-primary-text, #5142C7)" }}>{p.core}</b><br />{t('journeys.newJourneys.text158', "Seeded with the page — one per page, restorable if deleted. Carries the page's function.")}</div>
            : <div style={{ border: "1px dashed #D1D4DC", background: "#FAFAFD", borderRadius: 8, padding: 10, fontSize: 11, lineHeight: 1.5, color: "#697180", marginBottom: 18 }}>{t('journeys.newJourneys.text159', "Content page — no core. Compose it entirely from blocks.")}</div>}
          </>) : null}
          <div style={SEC}>{t('journeys.newJourneys.text160', "Attached to")}</div>
          <div style={{ border: "1px solid #E6E8EC", borderRadius: 8, padding: "9px 10px", fontSize: 11, lineHeight: 1.5, color: "#333333" }}>
            {D.origin?.kind === "local" ? <span style={{ color: "#697180" }}>{D.origin.label}</span> : (<>
              <b>{D.origin?.label}</b><br />
              <a href="#" onClick={(e) => { e.preventDefault(); if (D.origin?.route) goRoute(D.origin.route, D.origin.params); }}>{t("journeys.newJourneys.openOrigin", "Open the {noun} →", { noun: njOwnerNoun(D.origin?.kind) })}</a>
            </>)}
            <span style={{ display: "block", marginTop: 6, color: "#697180" }}>
              {WRITES_BACK(D.origin?.kind)
                ? t('journeys.newJourneys.text162', "Renaming this journey, its hero image and its services save onto the queue itself.")
                : t('journeys.newJourneys.text163', "This journey's own pages and flow are saved here.")}
            </span>
          </div>
        </div>

        <div ref={canvasScrollRef} style={{ flex: 1, background: "#F1F2F6", overflow: "auto", padding: "18px 20px 30px", paddingRight: ui.inspOpen && reserveInsp ? INSP_MAX + 20 : 20, minWidth: 0, position: "relative", display: "flex", flexDirection: "column" }}>
          {p.id === "waiting" ? (
            <button onClick={() => set({ simCalled: !ui.simCalled })} style={{ position: "absolute", top: 14, right: 20, zIndex: 5, height: 32, padding: "0 14px", borderRadius: 999, border: `1.5px solid ${ui.simCalled ? "var(--jrni-color-primary-1, #5142C7)" : "#D1D4DC"}`, background: ui.simCalled ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", color: ui.simCalled ? "var(--jrni-color-primary-text, #5142C7)" : "#333333", fontSize: 12, fontWeight: 700, cursor: "pointer", boxShadow: "0 1px 3px rgba(20,19,31,0.1)", fontFamily: FONT }}>{ui.simCalled ? t('journeys.newJourneys.text164', "‹ Back to waiting") : t('journeys.newJourneys.text165', "Preview: It's your turn")}</button>
          ) : null}
          {!isBoardJourney ? <CanvasWidthBar width={pageWidth} presets={WIDTH_PRESETS} /> : null}
          {/* minWidth 0: without it the centering row's `auto` minimum lets the
              flex algorithm drive it below its children, and the two drag
              handles then squeeze the column out. */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", flex: 1, minWidth: 0 }}>
          {!isBoardJourney ? <CanvasEdge width={pageWidth} side={-1} /> : null}
          <CanvasColumn width={pageWidth}>
          <div className="nj-page" style={{ width: "100%", margin: "0 auto", background: "#FFFFFF", borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 10px rgba(20,19,31,0.08)" }}>
            {missingChips.length ? (
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, padding: "10px 14px", background: "#FBFAFF", borderBottom: "1px dashed #C9C2EE" }}>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", color: "var(--jrni-color-primary-text, #5142C7)", textTransform: "uppercase" }}>{t('journeys.newJourneys.text166', "Still needed")}</span>
                {missingChips.map((c) => (
                  <button key={c.label} onClick={() => set({ setupView: c.to, pagesOpen: true })} title={t("journeys.newJourneys.setThisIn", "{to, select, basics {Set this in Setup · Basics} other {Set this in Setup · Schedule}}", { to: c.to })} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, border: "1px dashed #A9A0DE", background: "#FFFFFF", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{c.label} →</button>
                ))}
              </div>
            ) : null}
            {heroBlock ? (
              <div onClick={() => set({ sel: ui.sel === heroBlock.uid ? null : heroBlock.uid })} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }} onDrop={dropImage(heroBlock)} className="nj-block nj-hero" style={{ margin: "14px 14px 12px", position: "relative", cursor: "pointer", outline: `2px solid ${ui.sel === heroBlock.uid ? "var(--jrni-color-primary-1, #5142C7)" : "transparent"}`, outlineOffset: 2, borderRadius: 10 }}>
                <div style={{ borderRadius: 10, background: heroImg ? `linear-gradient(rgba(20,19,31,0.45), rgba(20,19,31,0.45)), url(${heroImg}) center/cover` : (({ Gradient: "linear-gradient(135deg, #75ADFA, var(--jrni-color-primary-text, #5142C7))", "Solid brand": accent, Photo: "repeating-linear-gradient(45deg, #3B3660, #3B3660 8px, #464070 8px, #464070 16px)" } as any)[bval(heroBlock, "bg")] ?? "linear-gradient(135deg, #75ADFA, var(--jrni-color-primary-text, #5142C7))"), padding: ({ Compact: "14px 16px", Standard: "18px 16px", Tall: "34px 16px" } as any)[bval(heroBlock, "height")] ?? "18px 16px", color: "#FFFFFF" }}>
                  <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.01em" }}>{heroBlock.text}</div>
                  {bval(heroBlock, "sub") ? <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 5, lineHeight: 1.45 }}>{bval(heroBlock, "sub")}</div> : null}
                  {bval(heroBlock, "cta") ? <span style={{ display: "inline-block", marginTop: 10, background: "#FFFFFF", color: "var(--jrni-color-primary-text, #5142C7)", borderRadius: 999, padding: "6px 14px", fontSize: 11, fontWeight: 700 }}>{bval(heroBlock, "cta")}</span> : null}
                  {!heroImg ? <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.6)", marginTop: 9, ...MONO }}>{t('journeys.newJourneys.text167', "drop an image here for the background")}</div> : null}
                </div>
                {ui.sel === heroBlock.uid ? (
                  <span style={{ position: "absolute", top: -13, right: 0, display: "inline-flex", gap: 2, background: "var(--jrni-color-primary-text, #5142C7)", borderRadius: 6, padding: 2 }}>
                    <span style={{ padding: "0 6px", height: 18, color: "#FFFFFF", fontSize: 8.5, fontWeight: 800, letterSpacing: "0.05em", display: "inline-flex", alignItems: "center" }}>{t('journeys.newJourneys.text168', "PINNED TO TOP")}</span>
                    <button onClick={(e) => { e.stopPropagation(); patchContent((list) => list.filter((x) => x.type !== "hero")); set({ sel: null }); }} style={{ width: 20, height: 18, border: "none", background: "transparent", color: "#FFFFFF", fontSize: 10, cursor: "pointer" }}>✕</button>
                  </span>
                ) : null}
              </div>
            ) : null}
            <div onClick={() => set({ sel: ui.sel === "heading" ? null : "heading", selSection: null })} className="nj-heading" style={{ padding: "18px 20px 6px", cursor: "pointer", outline: `2px solid ${ui.sel === "heading" ? "var(--jrni-color-primary-1, #5142C7)" : "transparent"}`, outlineOffset: -2, borderRadius: 4 }}>
              <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.01em" }}>{p.h}</div>
              {p.s ? <div style={{ fontSize: 12.5, color: "#697180", marginTop: 4 }}>{p.s}</div> : null}
            </div>
            {core()}
            {/* A One-Stop page IS its blocks — no core, and NOT the designer's block
                vocabulary either. Its canvas REPLACES the shared one rather than
                sitting beside it: both listen for the same drag payload, so two
                sets of insertion lines on one page would race for every drop and
                the loser would silently write into the wrong array. */}
            {isOneStopPage ? (
              <div style={{ padding: "0 14px 8px" }}>
                <OSCanvas
                  blocks={osBlocks}
                  rendered={osRendered}
                  locale={p.osSettings?.locale ?? "en-US"}
                  timeZone={p.osSettings?.timeZone ?? tzContext().zone}
                  subtitle={p.s}
                  sel={ui.selSection}
                  onSelect={(id) => set({ selSection: id, sel: id ? null : ui.sel })}
                  onChange={setOsBlocks}
                  onInsert={osInsertAt}
                />
              </div>
            ) : (
            <BlockCanvas
              blocks={contentBlocks.map((c) => ({ uid: String(c.uid), id: String(c.uid), block: c }))}
              sel={ui.sel}
              labelOf={(b) => String(BLOCK_DEF[b.block.type]?.label ?? b.block.type)}
              onSelect={(id) => set({ sel: id, selSection: id ? null : ui.selSection })}
              onMove={(from, to) => {
                const next = contentBlocks.slice();
                const [moved] = next.splice(from, 1);
                next.splice(to, 0, moved!);
                writeBlocks(next);
              }}
              onInsert={insertTypeAt}
              onRemove={(uid) => removeBlock(uid)}
              renderBlock={(b, i) => blockView(b.block, i)}
              emptyState={
                <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "18px 14px", textAlign: "center", fontSize: 11.5, color: "#8A92A0", lineHeight: 1.55 }}>
                  {t("journeys.newJourneys.noBlocksYet", "No blocks yet. Drag one from the palette to place it.")}
                </div>
              }
            />
            )}
            <div style={{ height: 6 }} />
            {stepNavFooter()}
          </div>
          <div style={{ margin: "10px auto 0", textAlign: "center", fontSize: 10.5, color: "#B5BAC6", ...MONO }}>
            {[
              t("journeys.newJourneys.previewBrandNote", "a preview — the published page uses your brand's styles"),
            ].filter(Boolean).join(" · ")}
          </div>
          </CanvasColumn>
          {!isBoardJourney ? <CanvasEdge width={pageWidth} side={1} /> : null}
          </div>
        </div>

        {inspector}
        {/* The way back in. A tab rather than a re-opening of the whole pane on
            selection: an author who minimized it did so on purpose, and having it
            spring back at the next click is the behaviour that made them close
            it. */}
        {!ui.inspOpen ? (
          <button
            onClick={() => set({ inspOpen: true })}
            title={t('journeys.newJourneys.title7', "Open the editor")}
            style={{ position: "absolute", top: 14, right: 0, zIndex: 19, height: 34, padding: "0 12px 0 10px", border: "1px solid #E6E8EC", borderRight: "none", borderRadius: "8px 0 0 8px", background: "#FFFFFF", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11.5, fontWeight: 700, cursor: "pointer", boxShadow: "-4px 0 12px rgba(20,19,31,0.08)", fontFamily: FONT }}
          >
            {t('journeys.newJourneys.text170', "‹ Editor")}</button>
        ) : null}
      </div>
    </>
  ) : null;

  /* ── Flow view ─────────────────────────────────────────────────────
   *  The ONE modeler (2026-08-25). This used to be a bespoke vertical strip
   *  with the single decision's branches in two fixed columns. It now mounts
   *  JourneyFlowBuilder — the canvas promoted out of screens/queues — so the
   *  Journey Designer and the queue flows page are the same editor, not two
   *  that resemble each other.
   *
   *  Three seams keep it honest:
   *    · vocab  — qfVocabFor(doc.type) picks this interaction type's triggers,
   *               decision conditions, end outcomes and wait timings.
   *    · pages  — journeyPages() supplies the page-type registry, a miniature
   *               that renders the document's real page, and a callback that
   *               switches to the Pages view (journey pages are built there,
   *               not in a modal).
   *    · flow   — the document IS the tree now (Doc.flow). njFlowOf only
   *               migrates a document stored in the old three-list shape, so
   *               there is no round-trip left to lose a third path in.
   */
  const flowVocab = qfVocabFor(D?.type);
  const flowServices = [
    ...queueCat.map((s: any) => ({ id: s.id, name: s.name, wait: s.wait })),
    ...apptCat.map((s: any) => ({ id: s.id, name: s.name, wait: s.wait, appt: true })),
  ];
  const flowPages = D
    ? journeyPages(D, (idx) => set({ view: "pages", cur: Math.max(0, idx), sel: null }), flowServices, {
        /* Same setup mirrors the Pages view reads, so a flow miniature can't
           show tickets, questions or a date the record doesn't have. */
        tickets: tixRows.map((t) => ({ name: t.name, price: t.kind === "free" || t.kind === "invite" ? i18nT("journeys.newJourneys.priceFree", "Free") : tgMoney(t.price) })),
        questions: attQuestions.map((q) => q.label),
        when: [setupDateLine, setupTimeLine].filter(Boolean).join(" · "),
        where: [setupVenue, setupAddress].filter(Boolean).join(" · "),
      })
    : null;
  const flowView = D && flowPages ? (
    <div style={{ flex: 1, display: "flex", minHeight: 0, padding: 12, background: "#F4F5F7" }}>
      <JourneyFlowBuilder
        flow={qfFlow}
        vocab={flowVocab}
        pages={flowPages}
        heroImage={(D as { heroImage?: string }).heroImage || undefined}
        onFlow={(next) => patchDoc((d) => ({ ...d, flow: next }))}
      />
    </div>
  ) : null;

  /* ── Setup views (stage 3) — jrni-ui controls inside the ported chrome,
   *  per the build-plan ruling: chrome stays as ported, form controls don't. */
  const SETUP_LABEL: Record<string, string> = {
    basics: t("journeys.newJourneys.setupStep.basics", "Basics"), details: t("journeys.newJourneys.setupStep.details", "Details"), schedule: t("journeys.newJourneys.setupStep.schedule", "Schedule"), tickets: t("journeys.newJourneys.setupStep.tickets", "Tickets"), guests: t("journeys.newJourneys.setupStep.guests", "Guests"), messaging: t("journeys.newJourneys.setupStep.messaging", "Messaging"), publish: t("journeys.newJourneys.setupStep.publish", "Publish"),
    qsetup: t("journeys.newJourneys.setupStep.qsetup", "Setup"), qhours: t("journeys.newJourneys.setupStep.qhours", "Opening hours"), qstaffing: t("journeys.newJourneys.setupStep.qstaffing", "Staffing"), qresources: t("journeys.newJourneys.setupStep.qresources", "Resources"), qservices: t("journeys.newJourneys.setupStep.qservices", "Services"), boardcfg: t("journeys.newJourneys.setupStep.configuration", "Configuration"), onestopcfg: t("journeys.newJourneys.setupStep.configuration", "Configuration"),
    registration: t("journeys.newJourneys.setupStep.registration", "Registration"),
    ...APPT_SETUP_LABEL,
  };
  const setupFieldLabel = { fontSize: 12, fontWeight: 600, color: "#333333", display: "block" as const, marginBottom: 5 };


  /** What a Setup step requires, and whether each one is met — the single
   *  answer to "which fields turn this dot green". Reads the SAME readiness
   *  rows the dots and the Publish gate read (eventReadiness / queueReadiness /
   *  boardReadiness), so the three surfaces cannot disagree. An empty array
   *  means the step requires nothing and carries no dot.
   *
   *  Declared HERE, above the setup panels, because those panels now read it to
   *  mark their own fields — they are built eagerly, so a definition further
   *  down the render would be a temporal-dead-zone error rather than a lint. */
  const requiredForView = (view: string): { label: string; done: boolean }[] => {
    if (isApptJourney) return aChecks.filter((c) => c.tab === view).map((c) => ({ label: c.label, done: c.done }));
    if (isBoardJourney) return view === "boardcfg" ? bChecks.map((c) => ({ label: c.label, done: c.done })) : [];
    if (isOneStopJourney) return view === "onestopcfg" ? osChecks : [];
    if (isQueueJourney) return qChecks.filter((c) => c.tab === view).map((c) => ({ label: c.label, done: c.done }));
    return readiness.filter((c) => c.step === view).map((c) => ({ label: c.label, done: c.done }));
  };

  /** Is THIS field the reason the step's dot is red? Matched on the readiness
   *  row's label, which is the same string the field's own label carries — so a
   *  renamed field that stops matching goes quiet rather than marking the wrong
   *  control. */
  /** Field labels are identities (reqMiss matches readiness rows by the English label); this is only
   *  what they DISPLAY as. */
  const NJ_FIELD_TEXT: Record<string, string> = {
    "Event title": t("journeys.newJourneys.field.eventTitle", "Event title"),
    "Description": t("journeys.newJourneys.field.description", "Description"),
    "Format": t("journeys.newJourneys.field.format", "Format"),
    "Visibility": t("journeys.newJourneys.field.visibility", "Visibility"),
    "Location": t("journeys.newJourneys.field.location", "Location"),
    "Meeting provider / join link": t("journeys.newJourneys.field.meetingProviderJoinLink", "Meeting provider / join link"),
    "Time zone": t("journeys.newJourneys.field.timeZone", "Time zone"),
    "Page name": t("journeys.newJourneys.field.pageName", "Page name"),
    "What this page offers": t("journeys.newJourneys.field.whatThisPageOffers", "What this page offers"),
    "Fixed content": t("journeys.newJourneys.field.fixedContent", "Fixed content"),
  };
  const njFieldText = (label: string) => NJ_FIELD_TEXT[label] ?? label;
  /** Dropdown options whose stored value is their English label: only the label is translated. */
  const NJ_OPTION_TEXT: Record<string, string> = {
    "Public · Anyone with link": t("journeys.newJourneys.option.visPublicLink", "Public · Anyone with link"),
    "Invite · RSVP": t("journeys.newJourneys.option.visInvite", "Invite · RSVP"),
    "Public & Invite": t("journeys.newJourneys.option.visPublicInvite", "Public & Invite"),
    "Private · Internal": t("journeys.newJourneys.option.visPrivate", "Private · Internal"),
    "Open on publish": t("journeys.newJourneys.option.opensOnPublish", "Open on publish"),
    "1d before": t("journeys.newJourneys.option.oneDayBefore", "1d before"),
    "1h before": t("journeys.newJourneys.option.oneHourBefore", "1h before"),
    "Close at start": t("journeys.newJourneys.option.closesAtStart", "Close at start"),
    "Keep open": t("journeys.newJourneys.option.keepOpen", "Keep open"),
  };
  const reqMiss = (label: string): boolean =>
    requiredForView(ui.setupView ?? "").some((c) => c.label === label && !c.done);

  /** A field label that shows its own error. Replaces the summary block that
   *  used to sit at the top of the panel listing what was missing: a list one
   *  scroll above the fields is a second place to read the same fault, and it
   *  pulled the eye AWAY from where the fix happens (operator, 2026-09-02). */
  const reqLabel = (label: string, why?: string) => {
    const bad = reqMiss(label);
    return (
      <label style={{ ...setupFieldLabel, color: bad ? "#C0392B" : "#333333" }}>
        {njFieldText(label)}
        <span title={why ?? t('journeys.newJourneys.title8', "Required — this step's dot stays red until it's filled")} style={{ color: bad ? "#C0392B" : "#D01647", fontWeight: 700, whiteSpace: "nowrap" }}>{"\u00A0*"}</span>
        {bad ? <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "#C0392B" }}>{t('journeys.newJourneys.text171', "Needs a value")}</span> : null}
      </label>
    );
  };

  /** Marks the whole field — label and control — when it is the missing one, so
   *  the eye lands on the input rather than only on its wording. A rule down the
   *  left edge rather than a border on the control itself: the controls are
   *  shared components, and re-bordering them from outside fights their own
   *  focus ring. */
  const reqField = (label: string, node: ReactNode) =>
    reqMiss(label)
      ? <div style={{ borderLeft: "3px solid #C0392B", paddingLeft: 11, marginLeft: -14, borderRadius: 2 }}>{node}</div>
      : <div>{node}</div>;

  const setupCard = { maxWidth: 560, display: "flex", flexDirection: "column" as const, gap: 16 };
  /** Required-field marker. A field wearing this is one the step's readiness
   *  row reads — fill every marked field in a step and its dot turns green.
   *  Same red as the queue/board labels that already carried an asterisk. */
  const req = (why?: string, label?: string) => {
    /* When a label is given, the marker also reports whether THIS field is the
       one holding the step back. Without one it is only the asterisk — which is
       right for a rule spanning two fields ("a ticket type OR a ticket group"),
       where reddening both would name the wrong number of faults. */
    const bad = !!label && reqMiss(label);
    return (
      <span title={why ?? t('journeys.newJourneys.title8', "Required — this step's dot stays red until it's filled")} style={{ color: bad ? "#C0392B" : "#D01647", fontWeight: 700, whiteSpace: "nowrap" }}>{"\u00A0*"}</span>
    );
  };

  /* A live event's registered-against fields, read-only with the reason (CC, 2026-10-06): the
     worker refuses any change to them (403 EVENT_LIVE_LOCKED), so the author is told before
     editing rather than after saving. `note` puts the reason above the first locked group. */
  const lockLive = (node: ReactNode, note = false): ReactNode => (!isLocked || node == null || node === false ? node : (
    <>
      {note ? (isEnded
        ? <div role="note" data-testid="nj-ended-locked" style={NOTE}>{t("journeys.newJourneys.endedLocked", "This event has ended, so these details can no longer be changed.")}</div>
        : <div role="note" data-testid="nj-live-locked" style={NOTE}>{t("journeys.newJourneys.liveLocked", "Locked while the event is live; unpublish to change.")}</div>) : null}
      <fieldset disabled style={{ border: "none", margin: 0, padding: 0, minWidth: 0, display: "contents" }}>{node}</fieldset>
    </>
  ));

  const tags = r0.tags ?? [];
  const addTag = () => {
    const v = tagDraft.trim();
    if (!v || tags.includes(v)) { setTagDraft(""); return; }
    const next = [...tags, v];
    patchRecord((x) => ({ ...x, tags: next }));
    if (eventOriginId) holdEvent("basics", { tags: next });
    setTagDraft("");
  };
  const setHeroImage = (v: string) => {
    patchRecord((x) => ({ ...x, heroImage: v }));
    holdEvent("basics", { heroImage: v });
  };
  /** Take the file itself. Inlined as a data URL, exactly as the queue's hero
   *  is — there is no upload endpoint to post it to (see the drop note). */
  const heroFile = (f: File | undefined) => {
    if (!f) return;
    if (!/^image\//.test(f.type)) { showToast(t("journeys.newJourneys.toast.notAnImage", "That isn't an image file")); return; }
    const rd = new FileReader();
    rd.onload = () => setHeroImage(String(rd.result));
    rd.readAsDataURL(f);
  };
  const setupBasics = (
    <div style={setupCard}>      {lockLive(<div>{reqLabel("Event title")}<Input value={r0.title ?? ""} onChange={(e: any) => { const v = e.target.value; patchRecord((x) => ({ ...x, title: v })); if (!eventOriginId) patchDoc((d) => ({ ...d, name: v })); else holdEvent("basics", { title: v }); }} placeholder={t('journeys.newJourneys.placeholder3', "Give your event a clear, scannable title")} /></div>, true)}
      <div>{reqLabel("Description")}<textarea value={r0.description ?? ""} onChange={(e) => { const v = e.target.value; patchRecord((x) => ({ ...x, description: v })); holdEvent("basics", { description: v }); }} rows={4} style={{ ...INPUT, resize: "vertical" as const }} placeholder={t('journeys.newJourneys.placeholder4', "What should people know before they register?")} /></div>
      {/* JAI-1767 — the hold categories attending this event clears. Rides the
          event update body already used by every other field on this pane.

          HIGH IN THE PANE (placement ruling, 2026-09-21): it shipped under the
          tag list and the card preview, where an author never scrolled to it.
          The ruling says "under the location field"; on this pane there is no
          location field — an event's location lives in the Details pane, and
          moving the control there would move its write group with it. So it
          sits under Description, above the hero and the tag list, which is the
          same intent: never below a long list. */}
      {portalAbsent ? null : <div>
        <label style={setupFieldLabel}>{holdsResolvesLabel(i18nT)}</label>
        <ResolvesHoldsPicker
          value={r0.resolvesHolds} subject="event" t={i18nT}
          onChange={(v) => { patchRecord((x) => ({ ...x, resolvesHolds: v })); if (eventOriginId) holdEvent("basics", { resolvesHolds: v }); }}
        />
      </div>}
      {/* JAI-2020 — Audience, beside Resolves holds. Data: partial (lib/audience.tsx):
          attributes read bound; the value joins holdEvent("basics") on edit and is
          written by Save through creation.updateEvent. */}
      {portalAbsent ? null : <div>
        <label style={setupFieldLabel}>{i18nT("audience.section.title", "Audience")}</label>
        <AudienceSection value={r0.audience} subject="event" t={i18nT} errors={eAudienceErrors}
          onChange={(v) => { setEAudienceErrors(null); patchRecord((x) => ({ ...x, audience: v })); if (eventOriginId) holdEvent("basics", { audience: v }); }} />
      </div>}
      {/* HERO IMAGE — an upload, not a URL to type (2026-09-17, PP).
          The field was a bare "Hero image URL" text input, which asked an
          author to have already hosted the picture somewhere and to paste its
          address. Queues and services take the file itself; an event now does
          the same, with the same control: drop on the card, or Browse.
          ⚠ No upload endpoint exists — see the drop note. Like the queue's
          hero, the file is inlined as a data URL and travels inside the record,
          so a large image inflates the event document. That is the shared
          behaviour today, not something new here; it wants a real
          POST /v1/admin/uploads returning a hosted URL. */}
      <div>
        <label style={setupFieldLabel}>{t('journeys.newJourneys.text172', "Hero image")}</label>
        <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
          <div style={{ width: 92, height: 62, borderRadius: 8, flex: "none", border: "1px solid #E6E8EC", background: r0.heroImage ? `url(${r0.heroImage}) center/cover` : "#F6F7F9", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {!r0.heroImage ? <span style={{ fontSize: 10, color: "#B5BAC6", ...MONO }}>{t('journeys.newJourneys.text173', "none")}</span> : null}
            {r0.heroImage ? <button onClick={() => setHeroImage("")} title={t('journeys.newJourneys.title9', "Remove the hero image")} style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: 999, border: "1px solid #E6E8EC", background: "#FFFFFF", color: "#697180", cursor: "pointer", lineHeight: 1, fontSize: 11 }}>✕</button> : null}
          </div>
          <label onDragOver={(e) => { e.preventDefault(); setHeroDrop(true); }} onDragLeave={() => setHeroDrop(false)}
            onDrop={(e) => { e.preventDefault(); setHeroDrop(false); heroFile(e.dataTransfer?.files?.[0]); }}
            style={{ flex: 1, border: `1px dashed ${heroDrop ? "var(--jrni-color-primary-1, #5142C7)" : "#D1D4DC"}`, background: heroDrop ? "var(--jrni-color-primary-4, #F6F2FE)" : "transparent", borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, cursor: "pointer", fontSize: 11.5, color: "#697180", textAlign: "center" as const }}>
            <span style={{ fontSize: 15 }}>↥</span>
            <span>{r0.heroImage ? t('journeys.newJourneys.text174', "Replace the image — drop it here or") : t('journeys.newJourneys.text175', "Drop an image here or")}{" "}<b style={{ color: "var(--jrni-color-primary-text, #5142C7)" }}>{t('journeys.newJourneys.text176', "browse")}</b></span>
            <input type="file" accept="image/*" onChange={(e) => { heroFile(e.target.files?.[0]); e.currentTarget.value = ""; }} style={{ display: "none" }} />
          </label>
        </div>
        <div style={{ fontSize: 10.5, color: "#B5BAC6", marginTop: 6 }}>{t('journeys.newJourneys.text177', "Shown on the landing page, in comms and on the card grid. A hosted image URL can also be dropped onto the card below.")}</div>
      </div>
      <div>
        <label style={setupFieldLabel}>{t('journeys.newJourneys.text178', "Tags")}</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {tags.map((tag) => (
            <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, border: "1px solid #D1D4DC", fontSize: 11.5, color: "#333333" }}>{tag}
              <button onClick={() => { const next = tags.filter((x) => x !== tag); patchRecord((x) => ({ ...x, tags: next })); if (eventOriginId) holdEvent("basics", { tags: next }); }} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
            </span>
          ))}
        </div>
        <Input value={tagDraft} onChange={(e: any) => setTagDraft(e.target.value)} onKeyDown={(e: any) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder={t('journeys.newJourneys.placeholder5', "Add tag — press Enter")} />
      </div>
      {/* JAI-1767 — the hold categories attending this event clears. Rides the
          event update body already used by every other field on this pane. */}
      <div>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>{t('journeys.newJourneys.text179', "Card preview")}</div>
        <div
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setHeroDrop(true); }}
          onDragLeave={() => setHeroDrop(false)}
          onDrop={(e) => {
            e.preventDefault(); e.stopPropagation(); setHeroDrop(false);
            const files = Array.from<File>(e.dataTransfer?.files ?? []).filter((f) => /^image\//.test(f.type));
            const url = e.dataTransfer?.getData ? (e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain")) : "";
            if (!files.length && url) { setHeroImage(url); return; }
            if (!files.length) { showToast(t("journeys.newJourneys.toast.dropImageOrBrowse", "Drop an image file, or use Browse below")); return; }
            heroFile(files[0]);
          }}
          style={{ width: 220, border: `${heroDrop ? 2 : 1}px dashed ${heroDrop ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}`, borderRadius: 10, overflow: "hidden", background: "#FFFFFF", cursor: "copy" }}>
          <div style={{ height: 90, background: r0.heroImage ? `url(${r0.heroImage}) center/cover` : "linear-gradient(135deg, #75ADFA, var(--jrni-color-primary-text, #5142C7))", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {!r0.heroImage ? <span style={{ fontSize: 10.5, color: "#FFFFFF", opacity: 0.9, ...MONO }}>{t('journeys.newJourneys.text180', "no image yet")}</span> : null}
          </div>
          <div style={{ padding: 10 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: r0.title ? "#333333" : "#B5BAC6" }}>{r0.title || t('journeys.newJourneys.text181', "Untitled event")}</div>
            <div style={{ fontSize: 10.5, color: "#697180", marginTop: 3 }}>{r0.start ? new Date(r0.start).toLocaleDateString() : t('journeys.newJourneys.text182', "Date not set")}</div>
          </div>
        </div>
      </div>
      <div style={NOTE}>{t('journeys.newJourneys.descriptionHint', "Describe the event in your own words. This is what guests read on the event page.")}</div>
    </div>
  );

  const geoOpts = studioRequestOptions("geo") as { baseUrl: string; surface?: string };
  const verifyAddress = (q: string) => {
    if (q.trim().length < 4) { setAddrVerify("idle"); return; }
    setAddrVerify("checking");
    validateAddress({ address: q.trim() }, geoOpts).then((res: any) => setAddrVerify(res?.valid ? "valid" : "invalid")).catch(() => setAddrVerify("invalid"));
  };
  const onAddressType = (v: string) => {
    patchRecord((x) => ({ ...x, address: v }));
    holdEvent("details", { locationAddress: v });
    setAddrVerify("idle");
    if (v.trim().length < 4) { setAddrSuggestions([]); setAddrOpen(false); return; }
    suggestAddresses({ q: v.trim(), limit: 5 }, geoOpts).then((res: any) => {
      setAddrSuggestions(res?.suggestions ?? []);
      setAddrOpen(true);
    }).catch(() => setAddrSuggestions([]));
  };
  const pickAddress = (label: string) => {
    patchRecord((x) => ({ ...x, address: label }));
    setAddrOpen(false);
    if (eventOriginId) holdEvent("details", { locationAddress: label });
    setAddrVerify("valid");
  };
  const setupDetails = (
    <div style={setupCard}>
      {lockLive(reqField("Format", (<>
        {reqLabel("Format")}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
          {[{ v: "in", l: t("journeys.newJourneys.format.inPerson", "In person"), i: <Icon.loc size={14} />, d: t("journeys.newJourneys.format.inPersonHint", "A physical venue"), m: { inPerson: true, virtual: false } },
            { v: "virtual", l: t("journeys.newJourneys.format.virtual", "Virtual"), i: <Icon.video size={14} />, d: t("journeys.newJourneys.format.virtualHint", "Online only"), m: { inPerson: false, virtual: true } },
            { v: "hybrid", l: t("journeys.newJourneys.format.hybrid", "Hybrid"), i: <Icon.globe size={14} />, d: t("journeys.newJourneys.format.hybridHint", "Venue + online"), m: { inPerson: true, virtual: true } }].map((o) => {
            const active = !!r0.method && !!r0.method.inPerson === !!o.m.inPerson && !!r0.method.virtual === !!o.m.virtual;
            return <ChoiceTile key={o.v} leading={o.i} label={o.l} sublabel={o.d} selected={active} disabled={isLocked} onSelect={() => { patchRecord((x) => ({ ...x, method: o.m })); if (eventOriginId) holdEvent("basics", { method: o.m }); }} />;
          })}
        </div>
      </>)), true)}
      {reqField("Visibility", (<>
        {reqLabel("Visibility")}
        {/* ui-config: stored option values; the labels come from NJ_OPTION_TEXT */}
        <Dropdown options={["Public · Anyone with link", "Invite · RSVP", "Public & Invite", "Private · Internal"].map((v) => ({ value: v, label: NJ_OPTION_TEXT[v] ?? v }))} value={r0.visibility || null} onChange={(v: string) => { patchRecord((x) => ({ ...x, visibility: v })); if (eventOriginId) holdEvent("details", { visibility: v }); }} placeholder={t('journeys.newJourneys.placeholder6', "Choose visibility…")} />
      </>))}
      {lockLive(r0.method?.inPerson ? (<>
        <div>
          {reqLabel("Location", t("journeys.newJourneys.why.location", "Required for in-person and hybrid events — a saved location, or a venue name or address"))}
          <div style={{ display: "inline-flex", border: "1px solid #D1D4DC", borderRadius: 8, overflow: "hidden", marginBottom: 8 }}>
            {(["saved", "custom"] as const).map((m) => (
              <button key={m} onClick={() => patchRecord((x) => ({ ...x, locationMode: m }))} style={{ padding: "5px 12px", border: "none", fontSize: 11.5, fontWeight: 700, cursor: "pointer", background: (r0.locationMode ?? "saved") === m ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: (r0.locationMode ?? "saved") === m ? "#FFFFFF" : "#333333", fontFamily: FONT }}>{m === "saved" ? t('journeys.newJourneys.text184', "Saved location") : t('journeys.newJourneys.text185', "New address")}</button>
            ))}
          </div>
          {(r0.locationMode ?? "saved") === "saved" ? (
            <Dropdown disabled={isLocked} options={savedLocations.map((l) => ({ value: l.id, label: l.name }))} value={r0.locationId || null} onChange={(v: string) => { const loc = savedLocations.find((l) => l.id === v); patchRecord((x) => ({ ...x, locationId: v, venueName: loc?.name, address: loc?.address })); if (eventOriginId) holdEvent("details", { locationId: v, locationName: loc?.name, locationAddress: loc?.address }); }} placeholder={savedLocations.length ? t('journeys.newJourneys.placeholder7', "Select a location…") : t('journeys.newJourneys.placeholder8', "No saved locations yet")} />
          ) : null}
        </div>
        {(r0.locationMode ?? "saved") === "custom" ? (<>
          <div><label style={setupFieldLabel}>{t('journeys.newJourneys.text186', "Venue name")}</label><Input value={r0.venueName ?? ""} onChange={(e: any) => { const v = e.target.value; patchRecord((x) => ({ ...x, venueName: v })); holdEvent("details", { locationName: v }); }} placeholder={t('journeys.newJourneys.placeholder9', "e.g. Westside Branch · Community Room")} /></div>
          <div style={{ position: "relative" }}>
            <label style={setupFieldLabel}>{t('journeys.newJourneys.text187', "Address")}</label>
            <Input value={r0.address ?? ""} onChange={(e: any) => onAddressType(e.target.value)} onFocus={() => addrSuggestions.length && setAddrOpen(true)} onBlur={() => { window.setTimeout(() => setAddrOpen(false), 150); verifyAddress(r0.address ?? ""); }} placeholder={t('journeys.newJourneys.placeholder10', "Street, city, region, postal code")} />
            {addrOpen && addrSuggestions.length ? (
              <div style={{ position: "absolute", zIndex: 5, top: "100%", left: 0, right: 0, background: "#FFFFFF", border: "1px solid #D1D4DC", borderRadius: 8, marginTop: 4, boxShadow: "0 4px 12px rgba(20,19,31,0.12)" }}>
                {addrSuggestions.map((s, i) => (
                  <div key={i} onMouseDown={() => pickAddress(s.label)} style={{ padding: "8px 11px", fontSize: 12, cursor: "pointer", borderBottom: i < addrSuggestions.length - 1 ? "1px solid #F4F5F7" : "none" }}>{s.label}</div>
                ))}
              </div>
            ) : null}
            {addrVerify === "checking" ? <div style={{ fontSize: 11, color: "#697180", marginTop: 4 }}>{t('journeys.newJourneys.text190', "Verifying address…")}</div>
              : addrVerify === "valid" ? <div style={{ fontSize: 11, color: "#1F8A5B", marginTop: 4 }}>{t('journeys.newJourneys.text189', "✓ Verified")}</div>
              : addrVerify === "invalid" ? <div style={{ fontSize: 11, color: "#C0392B", marginTop: 4 }}>{t('journeys.newJourneys.text188', "Couldn't verify this address, but it's saved as entered.")}</div> : null}
          </div>
        </>) : null}
      </>) : null)}
      {r0.method?.virtual ? (
        <div>{reqLabel("Meeting provider / join link", t("journeys.newJourneys.why.meetingProvider", "Required for virtual and hybrid events"))}<Input value={r0.virtualProvider ?? ""} onChange={(e: any) => { const v = e.target.value; patchRecord((x) => ({ ...x, virtualProvider: v })); holdEvent("details", { virtualProvider: v }); }} placeholder={t("journeys.newJourneys.joinLinkPlaceholder", "https://…")} /></div>
      ) : null}
      {/* Registration window HIDDEN (CC 0137): the endpoint stores instants, the dropdowns
          offer relative choices, and their old keys are refused. They come back bound to
          the relative fields once CC sends that packet. Flip to show them again. */}
      {REGISTRATION_WINDOW_ENABLED ? (
      <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 14 }}>
        <div style={{ ...setupFieldLabel, marginBottom: 4 }}>{t('journeys.newJourneys.text191', "Registration window")}</div>
        <div style={{ fontSize: 11.5, color: "#697180", marginBottom: 8 }}>{t('journeys.newJourneys.text192', "When registration opens and closes.")}</div>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('journeys.newJourneys.text193', "Opens")}</div>
            {/* ui-config: stored option values and default; the labels come from NJ_OPTION_TEXT */}
            <Dropdown options={["Open on publish", "1d before", "1h before"].map((o) => ({ value: o, label: NJ_OPTION_TEXT[o] ?? o }))} value={r0.registrationOpens ?? "Open on publish"} onChange={(v: string) => { patchRecord((x) => ({ ...x, registrationOpens: v })); if (eventOriginId) holdEvent("registration", { registrationOpens: v }); }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('journeys.newJourneys.text194', "Closes")}</div>
            {/* ui-config: stored option values and default; the labels come from NJ_OPTION_TEXT */}
            <Dropdown options={["Close at start", "1h before", "1d before", "Keep open"].map((o) => ({ value: o, label: NJ_OPTION_TEXT[o] ?? o }))} value={r0.registrationCloses ?? "Close at start"} onChange={(v: string) => { patchRecord((x) => ({ ...x, registrationCloses: v })); if (eventOriginId) holdEvent("registration", { registrationCloses: v }); }} />
          </div>
        </div>
      </div>
      ) : null}
      {lockLive(<>
      <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 14 }}>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>{t('journeys.newJourneys.text195', "Consent & marketing")}</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, marginBottom: 9 }}>{t('journeys.newJourneys.text196', "Request marketing opt-in")}<Toggle on={!!r0.consent?.marketing} onChange={() => { const next = { ...r0.consent, marketing: !r0.consent?.marketing }; patchRecord((x) => ({ ...x, consent: next })); if (eventOriginId) holdEvent("registration", { consent: next }); }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, marginBottom: 9 }}>{t('journeys.newJourneys.text197', "Request contact-sharing consent")}<Toggle on={!!r0.consent?.contactSharing} onChange={() => { const next = { ...r0.consent, contactSharing: !r0.consent?.contactSharing }; patchRecord((x) => ({ ...x, consent: next })); if (eventOriginId) holdEvent("registration", { consent: next }); }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('journeys.newJourneys.text198', "Terms & conditions copy")}</div>
          <textarea value={r0.termsCopy ?? ""} onChange={(e) => { const v = e.target.value; patchRecord((x) => ({ ...x, termsCopy: v })); holdEvent("registration", { termsCopy: v }); }} rows={3} style={{ ...INPUT, resize: "vertical" as const }} placeholder={t('journeys.newJourneys.placeholder11', "Shown beside the consent checkboxes at registration.")} />
        </div>
      </div>
      <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 14 }}>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>{t('journeys.newJourneys.text199', "After registration")}</div>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('journeys.newJourneys.text200', "Redirect URL")}</div>
        <Input value={r0.redirectUrl ?? ""} onChange={(e: any) => { const v = e.target.value; patchRecord((x) => ({ ...x, redirectUrl: v })); holdEvent("registration", { redirectUrl: v }); }} placeholder={t("journeys.newJourneys.redirectPlaceholder", "https://… (leave blank to show the confirmation page)")} />
      </div>
      </>, true)}
    </div>
  );

  /* ── Schedule helpers: event days + recurrence occurrences ── */
  const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const eventDays = (() => {
    if (!r0.start || !r0.end) return [] as Date[];
    const a = new Date(r0.start), b = new Date(r0.end);
    if (isNaN(+a) || isNaN(+b)) return [];
    const out: Date[] = [];
    const cur = new Date(a.getFullYear(), a.getMonth(), a.getDate());
    const last = new Date(b.getFullYear(), b.getMonth(), b.getDate());
    while (cur <= last && out.length < 60) { out.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    return out;
  })();
  const multiDay = eventDays.length > 1;
  const dayHours = r0.dayHours ?? {};
  const setDayHour = (key: string, part: "start" | "end", v: string) => {
    const next = { ...dayHours, [key]: { ...(dayHours[key] ?? {}), [part]: v } };
    patchRecord((x) => ({ ...x, dayHours: next }));
    if (eventOriginId) holdEvent("schedule", { dayHours: next });
  };
  const repeatIdx = r0.repeatIndex ?? 0;
  const isCustomRepeat = repeatIdx === REPEAT_LABELS_ALL.length - 1;
  const customDates = r0.customDates ?? [];
  /** Occurrence dates for the next 3 months, per the chosen recurrence. */
  const occurrences = (() => {
    if (!r0.start) return [] as string[];
    const s0 = new Date(r0.start);
    if (isNaN(+s0)) return [];
    if (isCustomRepeat) return customDates.slice();
    const horizon = new Date(s0.getFullYear(), s0.getMonth() + 3, s0.getDate());
    const out: string[] = [];
    const push = (d: Date) => { if (d <= horizon) out.push(dayKey(d)); };
    const nthWeekday = (d: Date) => Math.ceil(d.getDate() / 7);
    if (repeatIdx === 0) { push(s0); return out; }
    if (repeatIdx === 1) { const d = new Date(s0); while (d <= horizon) { push(new Date(d)); d.setDate(d.getDate() + 7); } return out; }
    const monthStep = repeatIdx === 2 || repeatIdx === 3 ? 1 : 3;
    const byWeekday = repeatIdx === 3 || repeatIdx === 5;
    const wd = s0.getDay(), nth = nthWeekday(s0);
    for (let m = 0; m < 12; m++) {
      const base = new Date(s0.getFullYear(), s0.getMonth() + m * monthStep, 1);
      if (base > horizon) break;
      if (!byWeekday) { const d = new Date(base.getFullYear(), base.getMonth(), s0.getDate()); if (d.getMonth() === base.getMonth()) push(d); }
      else { const d = new Date(base); let count = 0; while (d.getMonth() === base.getMonth()) { if (d.getDay() === wd) { count++; if (count === nth) { push(new Date(d)); break; } } d.setDate(d.getDate() + 1); } }
    }
    return out;
  })();
  const occSet = new Set(occurrences);
  const monthGrid = (offset: number) => {
    const anchor = r0.start ? new Date(r0.start) : new Date();
    const base = new Date(anchor.getFullYear(), anchor.getMonth() + offset, 1);
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const lead = first.getDay();
    const days = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    const cells: (Date | null)[] = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= days; d++) cells.push(new Date(base.getFullYear(), base.getMonth(), d));
    return { label: base.toLocaleDateString(undefined, { month: "long", year: "numeric" }), cells };
  };
  const setupSchedule = lockLive(
    <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
      <div style={{ ...setupCard, flex: "1 1 420px", minWidth: 320 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 220px", minWidth: 0 }}><DatePicker label={<>{t('journeys.newJourneys.text201', "Start")}{req(undefined, "Start")}</>} value={r0.start} timeZone={schedZone} onChange={(v: string | null) => { const next = v ?? ""; patchRecord((x) => ({ ...x, start: next, ...(x.timezone ? {} : { timezone: schedZone }) })); if (eventOriginId) holdEvent("schedule", { start: next }); }} leading={<Icon.calendar size={14} />} showTime={!r0.allDay} /></div>
          <div style={{ flex: "1 1 220px", minWidth: 0 }}><DatePicker label={<>{t('journeys.newJourneys.text202', "End")}{req(undefined, "End")}</>} value={r0.end} timeZone={schedZone} onChange={(v: string | null) => { const next = v ?? ""; patchRecord((x) => ({ ...x, end: next, ...(x.timezone ? {} : { timezone: schedZone }) })); if (eventOriginId) holdEvent("schedule", { end: next }); }} leading={<Icon.calendar size={14} />} showTime={!r0.allDay} minDate={r0.start ? new Date(r0.start) : undefined} /></div>
        </div>
        {endBeforeStart ? <div role="alert" data-testid="nj-end-before-start" style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: "#B42318" }}>{t("journeys.newJourneys.endBeforeStart", "The end must be after the start.")}</div> : null}
        {scheduleEnd === "next-day" ? <div role="status" data-testid="nj-ends-next-day" style={{ marginTop: 6, fontSize: 12, color: "#697180" }}>{t("journeys.newJourneys.endsNextDay", "Ends the next day")}</div> : null}
        <TimeZonePicker disabled={isLocked} label={t('journeys.newJourneys.label28', "Time zone")} value={schedZone} onChange={(v: string) => { patchRecord((x) => ({ ...x, timezone: v })); if (eventOriginId) holdEvent("schedule", { timezone: v }); }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={setupFieldLabel}>{t('journeys.newJourneys.text203', "All-day event")}</span>
          <Toggle disabled={isLocked} on={!!r0.allDay} onChange={() => { const next = !r0.allDay; patchRecord((x) => ({ ...x, allDay: next })); if (eventOriginId) holdEvent("schedule", { allDay: next }); }} />
        </div>
        {multiDay && !r0.allDay ? (
          <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 14 }}>
            <div style={{ ...setupFieldLabel, marginBottom: 4 }}>{t('journeys.newJourneys.text204', "Hours for each day")}</div>
            <div style={{ fontSize: 11.5, color: "#697180", marginBottom: 8 }}>{t("journeys.newJourneys.eventSpansDays", "{count, plural, one {This event spans # day. Each day can run different hours — leave a day blank to use the event's own start and end.} other {This event spans # days. Each day can run different hours — leave a day blank to use the event's own start and end.}}", { count: eventDays.length })}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {eventDays.map((d) => {
                const k = dayKey(d);
                return (
                  <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid #E6E8EC", borderRadius: 8, padding: "8px 10px" }}>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#333333" }}>{d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
                    <input type="time" value={dayHours[k]?.start ?? ""} onChange={(e) => setDayHour(k, "start", e.target.value)} style={{ ...INPUT, width: 110 }} />
                    <span style={{ fontSize: 11, color: "#B5BAC6" }}>{t('journeys.newJourneys.text207', "to")}</span>
                    <input type="time" value={dayHours[k]?.end ?? ""} onChange={(e) => setDayHour(k, "end", e.target.value)} style={{ ...INPUT, width: 110 }} />
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
        <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 14 }}>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text208', "Repeats")}</label>
          <Dropdown options={REPEAT_LABELS_ALL.map((label, i) => ({ value: String(i), label }))} value={String(repeatIdx)} onChange={(v: string) => { const next = Number(v); patchRecord((x) => ({ ...x, repeatIndex: next })); if (eventOriginId) holdEvent("schedule", { repeatIndex: next }); }} />
          {isCustomRepeat ? (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('journeys.newJourneys.text209', "Custom dates")}</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input type="date" value={newCustomDate} onChange={(e) => setNewCustomDate(e.target.value)} style={{ ...INPUT, flex: 1 }} />
                <button onClick={() => { const v = newCustomDate; if (!v || customDates.includes(v)) return; const next = [...customDates, v].sort(); patchRecord((x) => ({ ...x, customDates: next })); if (eventOriginId) holdEvent("schedule", { customDates: next }); setNewCustomDate(""); }} style={PRIMARY}>{t('journeys.newJourneys.text210', "Add date")}</button>
              </div>
              {customDates.length ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {customDates.map((cd) => (
                    <span key={cd} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, border: "1px solid #D1D4DC", fontSize: 11.5 }}>{cd}
                      <button onClick={() => { const next = customDates.filter((x) => x !== cd); patchRecord((x) => ({ ...x, customDates: next })); if (eventOriginId) holdEvent("schedule", { customDates: next }); }} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
                    </span>
                  ))}
                </div>
              ) : <div style={{ fontSize: 11.5, color: "#B5BAC6" }}>{t('journeys.newJourneys.text211', "No dates added yet.")}</div>}
            </div>
          ) : null}
        </div>
      </div>
      <div style={{ flex: "0 0 300px", minWidth: 280 }}>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>{t('journeys.newJourneys.text212', "Next 3 months")}</div>
        {!r0.start ? <div style={{ fontSize: 12, color: "#B5BAC6" }}>{t('journeys.newJourneys.text213', "Set a start date to preview occurrences.")}</div> : (<>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[0, 1, 2].map((off) => {
              const { label, cells } = monthGrid(off);
              return (
                <div key={off} style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: 12, background: "#FFFFFF" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{label}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, fontSize: 9.5, color: "#B5BAC6", marginBottom: 4 }}>
                    {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <span key={i} style={{ textAlign: "center" }}>{d}</span>)}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
                    {cells.map((d, i) => {
                      const on = d ? occSet.has(dayKey(d)) : false;
                      return (
                        <span key={i} style={{ height: 26, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, borderRadius: 6, fontWeight: on ? 800 : 400, background: on ? "var(--jrni-color-primary-text, #5142C7)" : "transparent", color: on ? "#FFFFFF" : d ? "#333333" : "transparent" }}>{d ? d.getDate() : ""}</span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: "#697180", marginTop: 8 }}>{t("journeys.newJourneys.occurrencesMarked", "{count, plural, one {# occurrence marked — {repeat}.} other {# occurrences marked — {repeat}.}}", { count: occurrences.length, repeat: REPEAT_LABELS_ALL[repeatIdx] })}</div>
        </>)}
      </div>
    </div>
  , true);

  const selGroupIds = r0.ticketGroupIds ?? [];
  const totalCap = v2.ticketing?.capacity ?? 0;
  /* One group per event: attaching replaces, so the tickets page has a single
   * source to mirror. */
  const attachTicketGroup = (id: string | null) => {
    /* One apply at a time (CC 0221): a second pick while the first is still on its
       way is ignored, so two applies can never overlap. */
    if (eventOriginId && v2.applying) return;
    const prev: string | null = (selGroupIds[0] as string | undefined) ?? at.ticketGroupId ?? null;
    const next = id ? [id] : [];
    /* The document's `attach.ticketGroupId` is a MIRROR of the record's first
     * selected group, not a second store — it exists so the tickets page's
     * preview and attachment chip render the same thing this picker chose.
     * Both entry points (here and the page's picker) write the pair. */
    const write = (ids: string[]) => {
      patchRecord((x) => ({ ...x, ticketGroupIds: ids }));
      setAttach({ ticketGroupId: ids[0] ?? null });
    };
    write(next);
    /* An existing event applies through the v2 path at once (CC 0195), the same call
       Event tickets makes, so its tiers are really on the event. The pick shows
       straight away, but the screen only KEEPS a group the server applied (CC 0221):
       on a refusal both writes go back to the previous group and the event's
       ticketing is read again, so the card matches Events → Tickets and the public
       page. A 409 is a normal answer (the event already has sales) and says so.
       A draft has no event yet: Publish applies the stored choice. Detaching is not
       an operation in v2: the event keeps the tiers it has until they are edited in
       Events → Tickets. */
    if (eventOriginId && id) {
      v2.apply(eventOriginId, id).catch((e) => {
        write(prev ? [prev] : []);
        v2.reloadTicketing();
        const sold = (e as { status?: number })?.status === 409;
        showToast(sold
          ? i18nT("journeys.newJourneys.toast.ticketGroupAlreadySold", "This event already has ticket sales, so its ticket group can't be swapped here. Change tiers in Events → Tickets.")
          : i18nT("journeys.newJourneys.toast.ticketGroupApplyFailed", "Couldn't apply the ticket group: {msg}", { msg: e instanceof Error ? e.message : String(e) }));
      });
    }
  };
  const PickRow = ({ selected, onClick, title, sub, right }: { selected: boolean; onClick: () => void; title: string; sub?: string; right?: string }) => (
    <button onClick={onClick} style={{ display: "flex", alignItems: "flex-start", gap: 10, width: "100%", textAlign: "left", padding: "10px 12px", border: `${selected ? 2 : 1}px solid ${selected ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}`, background: selected ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", borderRadius: selected ? "9px 9px 0 0" : 9, cursor: "pointer", fontFamily: FONT }}>
      <span style={{ width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${selected ? "var(--jrni-color-primary-text, #5142C7)" : "#D1D4DC"}`, background: selected ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: "#FFFFFF", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1, fontSize: 11 }}>{selected ? "✓" : ""}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: "#333333" }}>{title}</span>
        {sub ? <span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 2 }}>{sub}</span> : null}
      </span>
      {right ? <span style={{ fontSize: 12, fontWeight: 800, color: "#D01647", flexShrink: 0 }}>{right}</span> : null}
    </button>
  );
  /* Attached group: the record's selection, falling back to the document's
   * attachment so this tab can never claim "no group" for an event whose
   * tickets page is already rendering one. No implicit first-in-library
   * fallback here — an empty pair means genuinely nothing attached. */
  const attachedGroup = (ticketGroups as any[]).find((g) => g.id === (selGroupIds[0] ?? at.ticketGroupId)) ?? null;
  const secLabel = { ...SEC, color: "#333333", marginBottom: 0 };
  const linkBtn = { border: "none", background: "transparent", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT, padding: 0 } as const;
  const dashedNote = { border: "1px dashed #D1D4DC", borderRadius: 9, padding: "11px 13px", fontSize: 11.5, color: "#697180", lineHeight: 1.5 } as const;
  /* (CC 0195 effect moved above the loading guard — 2026-10-05, Nick: below it, React counted one
     more hook after loading finished and Edit Event crashed with error #310.) */
  const setupTickets = (
    <div style={setupCard}>
      <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>{t('journeys.newJourneys.ticketsV2Intro', "Choose the ticket group this event sells. Its tiers are copied onto the event; prices, seats and waitlists are edited in Events → Tickets.")}</div>

      <div style={{ height: 1, background: "#E6E8EC" }} />

      <div>
        <div style={{ ...secLabel, marginBottom: 8 }}>{t('journeys.newJourneys.text225', "Capacity & waitlist")}</div>
        {/* v2: capacity is the sum of the tiers' seats (server-computed) and waitlists are per tier. */}
        {v2.ticketing && v2.ticketing.tiers.length ? (
          <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.55 }}>{t('journeys.newJourneys.ticketsV2Capacity', "{count, plural, one {# seat} other {# seats}} across this event's tiers. Seats and waitlists are set per tier in Events → Tickets.", { count: v2.ticketing.capacity })}</div>
        ) : (
          <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.55 }}>{t('journeys.newJourneys.ticketsV2CapacityNone', "Set by the ticket group's tiers once one is applied.")}</div>
        )}
      </div>

      {lockLive(<>
      <div style={{ height: 1, background: "#E6E8EC" }} />

      {/* Moved off the Tickets page inspector (2026-08-31): an event's promo is
          event-scoped and belongs with the rest of the event's commerce. */}
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={secLabel}>{t('journeys.newJourneys.text93', "Promo code")}</span>
          <button onClick={() => nav.go("promos")} style={linkBtn}>{t('journeys.newJourneys.text234', "Manage promos →")}</button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {coupons.length === 0 ? <span style={{ fontSize: 11.5, color: "#697180" }}>{t('journeys.newJourneys.text235', "No promo codes in the library yet.")}</span> : coupons.map((c: any) => {
            const on = (D?.attach ?? {}).promoId === c.id;
            return (
              <button key={c.id} onClick={() => setAttach({ promoId: on ? null : c.id })}
                style={{ padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 11.5, fontWeight: 700, border: `1.5px solid ${on ? "var(--jrni-color-primary-text, #5142C7)" : "#E6E8EC"}`, background: on ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: on ? "#FFFFFF" : "#4B5563" }}>
                {c.code || c.name || t("journeys.newJourneys.untitled.promo", "Untitled promo")}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={secLabel}>{t('journeys.newJourneys.text237', "Ticket groups")}</span>
          <button onClick={goEventTickets} style={linkBtn}>{eventOriginId ? t('journeys.newJourneys.ticketsV2EditEvent', "Edit in Events → Tickets →") : t('journeys.newJourneys.text238', "Manage groups →")}</button>
        </div>
        {attachedGroup ? (
          <>
            <div style={{ border: "1px solid #DCCCFC", background: "var(--jrni-color-primary-4, #F6F2FE)", borderRadius: 10, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 9.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--jrni-color-primary-text, #5142C7)", marginBottom: 3 }}>{t('journeys.newJourneys.text239', "Ticket group · attached")}</span>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 800, color: "#333333" }}>{attachedGroup.name || t('journeys.newJourneys.text240', "Untitled group")}</span>
                  <span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 2 }}>{t("journeys.newJourneys.tierCount", "{count, plural, one {# tier} other {# tiers}}", { count: (attachedGroup.tiers ?? []).length })}{attachedGroup.description ? <> {t('journeys.newJourneys.text243', "· {description}", { description: attachedGroup.description })}</> : ""}</span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 10, flex: "none" }}>
                  <button onClick={() => set({ lib: "tickets" })} style={linkBtn}>{t('journeys.newJourneys.text108', "Change")}</button>
                  {/* v2 has no "remove a group from an event": once applied, the tiers are the event's. Detach only undoes a draft's choice. */}
                  {!v2.ticketing?.ticketGroupId ? <button onClick={() => attachTicketGroup(null)} style={{ ...linkBtn, color: "#697180" }}>{t('journeys.newJourneys.text244', "Detach")}</button> : null}
                </span>
              </div>
              {(attachedGroup.tiers ?? []).length ? (
                <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
                  {(attachedGroup.tiers ?? []).map((tier: any, i: number) => (
                    <div key={tier.id ?? i} style={{ background: "#FFFFFF", border: "1px solid var(--jrni-color-primary-3, #EDE5FE)", borderRadius: 8, padding: "9px 11px" }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: "#333333" }}>{tier.name || t('journeys.newJourneys.text245', "Untitled tier")}</div>
                      <div style={{ fontSize: 12.5, fontWeight: 800, color: "var(--jrni-color-primary-text, #5142C7)", marginTop: 3 }}>{tier.kind === "free" || !tier.price ? t('journeys.newJourneys.text26', "Free") : tgMoney(tier.price)}</div>
                      {tier.defaultAllocation ? <div style={{ fontSize: 10.5, color: "#697180", marginTop: 2 }}>{tier.defaultAllocation} {t('journeys.newJourneys.text25', "allocated")}</div> : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            <div style={{ fontSize: 11, color: "#697180", lineHeight: 1.55, marginTop: 8 }}>{t('journeys.newJourneys.ticketsV2Reference', "These are the tiers on this event. Change names, prices, seats and waitlists in Events → Tickets.")}</div>
          </>
        ) : (
          <>
            <div style={dashedNote}>{t('journeys.newJourneys.ticketsV2NoGroup', "No ticket group yet. This event has nothing to sell until one is applied.")}</div>
            <button onClick={() => set({ lib: "tickets" })} style={{ ...GHOST, marginTop: 8 }}>{t('journeys.newJourneys.text248', "Attach a ticket group →")}</button>
          </>
        )}
      </div>

      <div style={NOTE}>{t('journeys.newJourneys.tickets.allocNote', "Ticket types and groups come from your library; edit their names and prices there. You don't have to give out every ticket now. Leave some unassigned and add more later when you edit the event.")}</div>

      {/* Coupons and promotions — two different incentives, event-scoped, on the
          RECORD (operator, 2026-09-02). The promos library distinguishes them:
          a COUPON is a code the student enters at checkout, a PROMO applies
          automatically. They are separate fields because they behave
          differently at runtime, and one list would hide which is which.
          Under Tickets because both discount a price, and the prices are here.
          Neither is per method: an event has one way in. */}
      <div style={{ height: 1, background: "#E6E8EC" }} />
      {([
        { key: "coupons" as const, label: t('journeys.newJourneys.label30', "Coupons"), hint: t("journeys.newJourneys.lib.couponsHintGuest", "Codes a guest can enter when they register. They are saved on the event, so every page that sells it accepts the same codes."), rows: coupons, empty: t("journeys.newJourneys.lib.couponsEmpty", "No coupons in the library yet — create one under Coupons and it will appear here.") },
        { key: "promos" as const, label: t('journeys.newJourneys.label31', "Promotions"), hint: t("journeys.newJourneys.lib.promosHint", "Applied automatically, with no code to enter."), rows: autoPromos, empty: t("journeys.newJourneys.lib.promosEmpty", "No automatic promotions in the library yet.") },
      ]).map((grp) => (
        <div key={grp.key}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={setupFieldLabel}>{grp.label}</span>
            <button onClick={() => goRoute("promos")} style={linkBtn}>{t("journeys.newJourneys.manageLibrary", "Manage {name} →", { name: grp.label.toLowerCase() })}</button>
          </div>
          <div style={{ ...NOTE, marginBottom: 8 }}>{grp.hint}</div>
          {grp.rows.length === 0 ? (
            <span style={{ fontSize: 11.5, color: "#697180" }}>{grp.empty}</span>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {grp.rows.map((c: any) => {
                const cur = (r0[grp.key] ?? []) as string[];
                const on = cur.includes(c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => {
                      const next = on ? cur.filter((x) => x !== c.id) : [...cur, c.id];
                      patchRecord((x) => ({ ...x, [grp.key]: next }));
                      if (eventOriginId) void holdEvent("tickets", { [grp.key]: next });
                    }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 11.5, fontWeight: 700, border: `1.5px solid ${on ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}`, background: on ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", color: on ? "var(--jrni-color-primary-text, #5142C7)" : "#4B5563" }}
                  >
                    <span style={{ fontSize: 10 }}>{on ? "✓" : "+"}</span>
                    {/* A coupon is known by its CODE, a promo has none — it is
                        known by its name. Showing "code || name" for both would
                        print an empty string for every automatic promo. */}
                    {grp.key === "coupons" ? (c.code || c.name || t("journeys.newJourneys.untitled.coupon", "Untitled coupon")) : (c.name || t("journeys.newJourneys.untitled.promotion", "Untitled promotion"))}
                    {c.kind && c.value != null ? <span style={{ color: "#8A92A0", fontWeight: 500 }}>· {c.kind === "percent" ? t('journeys.newJourneys.text251', "{value}%", { value: c.value }) : t('journeys.newJourneys.text252', "{value} off", { value: c.value })}</span> : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
      </>, true)}
    </div>
  );

  const guests = r0.guestList ?? [];
  const addGuest = (entry?: GuestEntry) => {
    const g = entry ?? guestDraft;
    if (!entry && (!guestDraft.firstName.trim() || !guestDraft.email.trim())) return;
    const next = [...guests, g];
    patchRecord((x) => ({ ...x, guestList: next }));
    if (eventOriginId) holdEvent("guestList", { entries: next });
    if (!entry) setGuestDraft({ firstName: "", email: "" });
  };
  /* An existing event's guests are managed in its Registrations (CC, 2026-10-06): no read of the
     stored list exists, so this view cannot show it, and an add here would replace it. */
  const openEventRegistrations = () => {
    const ev = events.find((e) => e.id === eventOriginId);
    if (ev) nav.openEvent(ev); else nav.go("events");
  };
  const setupGuests = guestListManagedElsewhere(eventOriginId) ? (
    <div style={setupCard} data-testid="nj-guests-managed">
      <div style={NOTE}>{t("journeys.newJourneys.guestsManagedInRegistrations", "Manage this event's guests in Registrations")}</div>
      <div>
        <Button size="sm" variant="link" testid="nj-guests-open-registrations" onClick={openEventRegistrations}>{t("journeys.newJourneys.openRegistrations", "Open Registrations")}</Button>
      </div>
    </div>
  ) : (
    <div style={setupCard}>
      <div>
        <Input value={guestSearch} onChange={(e: any) => setGuestSearch(e.target.value)} placeholder={t('journeys.newJourneys.placeholder13', "Search by name, email or company…")} />
        {guestSearching ? <div style={{ fontSize: 11, color: "#697180", marginTop: 4 }}>{t('journeys.newJourneys.text253', "Searching…")}</div> : null}
        {guestResults.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
            {guestResults.map((r, i) => (
              <button key={i} onClick={() => { addGuest({ firstName: r.firstName, lastName: r.lastName, email: r.email }); setGuestSearch(""); setGuestResults([]); }} style={{ textAlign: "left", border: "1px solid #E6E8EC", borderRadius: 8, padding: "6px 10px", fontSize: 12, background: "#FFFFFF", cursor: "pointer", fontFamily: FONT }}>{r.firstName} {r.lastName ?? ""} — {r.email}</button>
            ))}
          </div>
        ) : null}
      </div>
      <div>
        <div style={{ ...setupFieldLabel, marginBottom: 6 }}>{t('journeys.newJourneys.text254', "Add manually")}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <Input value={guestDraft.firstName} onChange={(e: any) => setGuestDraft((g) => ({ ...g, firstName: e.target.value }))} placeholder={t('journeys.newJourneys.placeholder14', "First name")} />
          <Input value={guestDraft.email} onChange={(e: any) => setGuestDraft((g) => ({ ...g, email: e.target.value }))} placeholder={t('journeys.newJourneys.placeholder15', "Email")} />
          <button onClick={() => addGuest()} style={PRIMARY}>{t('journeys.newJourneys.text255', "Add")}</button>
        </div>
      </div>
      {segments.length ? (
        <div>
          <div style={{ ...setupFieldLabel, marginBottom: 6 }}>{t('journeys.newJourneys.text256', "Add a segment")}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {segments.map((s) => (
              <button key={s.id} onClick={() => addGuest({ firstName: s.name, email: "", kind: "segment", count: s.count, id: s.id })} style={{ padding: "6px 12px", borderRadius: 999, border: "1px solid #D1D4DC", background: "#FFFFFF", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>{s.name} · {s.count}</button>
            ))}
          </div>
        </div>
      ) : null}
      {guests.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {guests.map((g, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #E6E8EC", borderRadius: 8, padding: "7px 10px", fontSize: 12 }}>
              <span>{g.kind === "segment" ? t('journeys.newJourneys.text258', "Segment — {firstName} ({arg2})", { firstName: g.firstName, arg2: g.count ?? 0 }) : t("journeys.newJourneys.guestLine", "{first} {last} — {email}", { first: g.firstName, last: g.lastName ?? "", email: g.email })}</span>
              <button onClick={() => { const next = guests.filter((_, j) => j !== i); patchRecord((x) => ({ ...x, guestList: next })); if (eventOriginId) holdEvent("guestList", { entries: next }); }} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer" }}>✕</button>
            </div>
          ))}
        </div>
      ) : <div style={{ fontSize: 12, color: "#B5BAC6" }}>{t('journeys.newJourneys.text257', "No guests added yet.")}</div>}
      <div style={NOTE}>{t('journeys.newJourneys.text260', "CSV import isn't wired to this view yet — use Guests → Import for bulk upload.")}</div>
    </div>
  );

  /* ── Stage 5 — Publish view: one verb, ordered, honest on partial failure ──
     `isLive` / `isPublishedEvent` are derived beside the record (above), from the worker's status. */
  /* The approvals reads (approvalsGetSettings / ListProcesses / ListSubmissions) require events.approvals.read, not
     settings.read (CC, 2026-10-06). Gating on settings.read hid "Submit for approval" from the Event Manager, who
     publishes events but holds no settings capability, so publishing went straight past an enabled approval. */
  /* Approvals are Coming soon (CC 0224): nothing here reads them, Publish is always
     offered and there is no "Submit for approval", whatever the server has stored. */
  const approvalsReadable = !APPROVALS_COMING_SOON && can("events.approvals.read");
  const approvalsOn = approvalsReadable && approvalsStore.enabled();
  const approvalSub = approvalsReadable && eventOriginId ? approvalsStore.submission("events", eventOriginId) : null;
  const approvalBlocked = approvalsReadable && !!eventOriginId && approvalsStore.isBlocked("events", eventOriginId);
  const approvalBlockReason = approvalsReadable && eventOriginId ? approvalsStore.blockReason("events", eventOriginId) : "";
  const approvalProcess = approvalsReadable ? approvalsStore.processes("events")[0] : undefined;
  /* With approvals on, an event goes live only once approved (CC, 2026-10-06): the server refuses publish for a
     never-submitted or unapproved event (409 EVENT_APPROVAL_REQUIRED). So until approval the designer offers
     "Submit for approval" (creating the event first when needed) and no Publish; once approved, Publish returns. */
  const needsApproval = approvalsOn && !!approvalProcess && approvalSub?.status !== "approved";
  const allReady = readiness.every((c) => c.done);
  const runPublish = async () => {
    if (!allReady || (approvalsOn && approvalBlocked) || publishBusy || publishLock.current) return;
    publishLock.current = true;
    setPublishBusy("publish");
    setPublishFail(null);
    /* When this call attaches (creates the event), the closure's `ui.curDoc`, `docs` and `eventOriginId` are the
       PRE-attach values: patchDoc/patchRecord would write the old local key back and re-arm the setup autosave.
       So after an attach the post-publish writes go to the NEW key and record id directly. */
    let attached: AttachResult | null = null;
    let liveDoc: Doc | null = null;
    const patchLive = (fn: (d: Doc) => Doc) => {
      if (attached?.doc) { liveDoc = { ...fn(liveDoc ?? attached.doc), edited: false }; saveDoc(attached.key, liveDoc); }
      else patchDoc(fn);
    };
    try {
      /* Create the record if this journey has none (operator, 2026-09-02).
         Publish used to require it to exist already, with a separate "Create the
         event" card doing that first — two steps for one intention, and the
         first one read as a save prompt. `runAttach` re-keys the document onto
         the new event, so `eventOriginId` is only trustworthy after it. */
      let id = eventOriginId;
      if (!id) {
        const r = await runAttach();
        /* The create failed as part of Publish: "Try again" re-runs Publish, which creates first. */
        if (!r) { setPublishFail((f) => (f ? { ...f, action: "publish" } : f)); return; }
        attached = r;
        id = r.origin.id;
      }
      // Ordered: the record's own fields already save per-field as Setup is
      // edited (each onBlur above); the document (pages/blocks/flow) already
      // saves on every canvas edit via saveDoc/putJourneyDoc. Publish is the
      // one remaining step — flip the record live — and it is NOT assumed to
      // have succeeded until the call resolves.
      /* Tickets v2 (CC 0195): the chosen group is applied before going live, so the
         published event really has its tiers. A just-created event always needs it; an
         existing one only when its ticketing doesn't already carry that group. */
      if (v2SelGroupId && (attached || v2.ticketing?.ticketGroupId !== v2SelGroupId)) {
        await v2.apply(id!, v2SelGroupId);
      }
      await retryWarming(() => publishEventRecord(id!));
      /* Published: upcoming until its start, then live (the worker's derived status). */
      const pubStatus = r0.start && Date.parse(r0.start) > Date.now() ? "scheduled" : "live";
      if (attached) { const evId = id!; setRecords((m) => ({ ...m, [evId]: { ...(m[evId] ?? r0), id: evId, status: pubStatus } })); }
      else patchRecord((x) => ({ ...x, status: pubStatus }));
      patchLive((d) => ({ ...d, status: "Live", edited: false }));
      /* Stage 5's residual, closed: the record going live is not the same as
         the public page serving this journey. The pages/blocks are mapped onto
         the landing envelope and published as the second ordered step — and if
         it fails, the toast says the record is live but the page is not, rather
         than implying both landed. */
      const dNow = liveDoc ?? attached?.doc ?? docs[ui.curDoc] ?? D!;
      try {
        /* `id`, not `eventOriginId` (2026-09-08). `runAttach` above mints the
           event and re-keys the document onto it; `eventOriginId` is the value
           from BEFORE that and is null on a first publish — so a
           create-and-publish sent the pages to no record while the record itself
           went live. The comment 12 lines up says exactly this and the call did
           not follow it. */
        const pg = await publishJourneyPages(id, dNow, dNow.publish?.landingPageId ?? null, true, {
          /* The live record, not the journey's setup bag: an attached event's
             schedule and venue live on the record, and the published page must
             carry the same values the designer showed. */
          start: r0.start, end: r0.end, allDay: !!r0.allDay, timezone: r0.timezone,
          venue: setupVenue, address: setupAddress,
        });
        patchLive((d) => ({ ...d, status: "Live", edited: false, publish: { ...(d.publish ?? {}), landingPageId: pg.landingPageId } }));
        showToast(pg.dropped.length
          ? t("journeys.newJourneys.toast.publishedDropped", "{count, plural, one {Published — the public page now serves these pages. {pages} has no public equivalent and wasn't sent.} other {Published — the public page now serves these pages. {pages} have no public equivalent and wasn't sent.}}", { count: pg.dropped.length, pages: pg.dropped.join(" and ") })
          : t("journeys.newJourneys.toast.published", "Published — registration is open, comms have started, and the public page serves this journey's pages"));
      } catch (pageErr) {
        showToast(t("journeys.newJourneys.toast.publishPageFailed", "Event is live, but the public page didn't update — press Publish again. The record is unchanged by the failure."));
      }
    } catch (e) {
      /* A refused publish says why (CC, 2026-10-04): the server's reason (an end before the start, say) read
         "Retry in a moment" before, which no retry fixes. */
      const why = (e instanceof Error ? e.message : "").trim();
      /* 409 EVENT_APPROVAL_REQUIRED: the tenant has event approvals on and this event has none yet. */
      if ((e as { code?: unknown } | null)?.code === "EVENT_APPROVAL_REQUIRED") {
        showToast(t("journeys.newJourneys.toast.approvalRequired", "This event needs approval before it can be published — submit it for approval from Publish."));
        return;
      }
      /* Say what did happen (CC 0250): when this Publish created the event first, the create
         stands and only going live failed. */
      const outcome = attached ? "It was saved as a draft, but not published." : "The event is unchanged.";
      const whyText = why ? (/[.!?]$/.test(why) ? why : why + ".") : "";
      setPublishFail({ text: whyText ? `Couldn't publish: ${whyText} ${outcome}` : `Couldn't publish. ${outcome}`, retry: true, action: "publish", doc: attached ? attached.key : ui.curDoc });
      showToast(attached
        ? (whyText ? `Couldn't publish — ${whyText} ${outcome}` : `Couldn't publish — ${outcome}`)
        : whyText
          ? t("journeys.newJourneys.toast.publishFailedWhy", "Couldn't publish — {error} The event is unchanged.", { error: whyText })
          : t("journeys.newJourneys.toast.publishFailed", "Couldn't publish — the event is unchanged. Retry in a moment."));
    } finally { publishLock.current = false; setPublishBusy(null); }
  };
  /** The header's Publish (CC, 2026-10-06): held Setup edits are saved first, then the event is
   *  published (a new one is created as a draft and then published). A refused save publishes nothing. */
  const publishNow = async () => {
    if (eventOriginId && Object.keys(ePending.current).length) {
      const ok = await saveEventNow();
      if (!ok) return;
    }
    await runPublish();
  };
  /* Publish, Unpublish and Cancel event ask in the screen's own dialog (CC 0305 item 3, 0318),
     not the browser's confirm box. Cancel in the dialog changes nothing. */
  const confirmUnpublish = () => setEventAsk("unpublish");
  const confirmCancelEvent = () => setEventAsk("cancel");
  const runTakeOffline = async () => {
    if (!eventOriginId || publishBusy) return;
    setPublishBusy("offline");
    try {
      await takeEventRecordOffline(eventOriginId);
      patchRecord((x) => ({ ...x, status: "draft" }));
      patchDoc((d) => ({ ...d, status: "Draft" }));
      showToast(t("journeys.newJourneys.toast.takenOffline", "Taken offline — registration is closed and the journey is editable"));
    } catch (e) { showToast(t("journeys.newJourneys.toast.offlineFailed", "Couldn't take the event offline. Retry in a moment.")); }
    finally { setPublishBusy(null); }
  };
  const runCancel = async () => {
    if (!eventOriginId || publishBusy) return;
    setPublishBusy("cancel");
    try {
      await cancelEventRecord(eventOriginId);
      patchRecord((x) => ({ ...x, status: "cancelled" }));
      patchDoc((d) => ({ ...d, status: "Cancelled" }));
      showToast(t("journeys.newJourneys.toast.eventCancelled", "Event canceled — registered guests will be notified"));
    } catch (e) { showToast(t("journeys.newJourneys.toast.cancelFailed", "Couldn't cancel the event. Retry in a moment.")); }
    finally { setPublishBusy(null); }
  };
  const submitForApproval = async () => {
    if (!approvalProcess || publishBusy || attachBusy) return;
    let id = eventOriginId;
    if (!id) { const r = await attachWithGroup(); id = r && r.origin.kind === "event" ? (r.origin.id ?? null) : null; if (!id) return; }
    setPublishBusy("submit");
    try { await approvalsStore.submitItem("events", id, approvalProcess.id, getUser().name || undefined); showToast(t("journeys.newJourneys.toast.submittedForApproval", "Submitted for approval")); }
    catch (e) { showToast(t("journeys.newJourneys.toast.submitFailed", "Couldn't submit for approval. Retry in a moment.")); }
    finally { setPublishBusy(null); }
  };

  /* ── Live event operations (operator 2026-08-24: "events needs to do it how
     queuing does it today"). The queue's Publish view carries the live list and
     both messaging modes; an event's live surface lived only in the wizard's
     Live step, so it came here — same placement, same two verbs, real ops:
     registrations from the record's ticket rows, sends through messageGuest
     (POST /events/:id/live-messages), which is the op the wizard uses. */
  /* The event's own v2 tiers with their live availability (CC 0195). */
  const liveTickets = (v2.ticketing?.tiers ?? []).map((tt) => ({
    id: String(tt.id), name: tt.name || t("journeys.newJourneys.untitled.type", "Untitled type"), color: tt.color || "var(--jrni-color-primary-text, #5142C7)",
    capacity: tt.defaultAllocation, sold: tt.sold ?? 0, waitlist: tt.waitlisted ?? 0,
  }));
  const liveSold = liveTickets.reduce((n, x) => n + x.sold, 0);
  const liveCap = liveTickets.reduce((n, x) => n + x.capacity, 0) || totalCap;
  const liveWait = liveTickets.reduce((n, x) => n + x.waitlist, 0);
  const evMsgCount = evMsg?.tierId ? (liveTickets.find((x) => x.id === evMsg.tierId)?.sold ?? 0) : liveSold;
  const sendEventMessage = async () => {
    if (!eventOriginId || !evMsg || evMsgSending || !evMsgText.trim()) return;
    setEvMsgSending(true);
    try {
      const res: any = await messageGuest(eventOriginId, {
        audience: evMsg.tierId ? { type: "tier", tierId: evMsg.tierId } : { type: "all" },
        channel: evMsgChannel,
        subject: evMsgChannel === "email" ? (evMsgSubject.trim() || undefined) : undefined,
        body: evMsgText.trim(),
      } as Parameters<typeof messageGuest>[1]);
      const n = typeof res?.queued === "number" ? res.queued : evMsgCount;
      showToast(t("journeys.newJourneys.toast.messageQueued", "{count, plural, one {Message queued for # registrant via {channel}} other {Message queued for # registrants via {channel}}}", { count: n, channel: evMsgChannel.toUpperCase() }));
      setEvMsg(null); setEvMsgText(""); setEvMsgSubject("");
    } catch (e) { showToast(t("journeys.newJourneys.toast.messageFailed", "Couldn't queue the message — {error}", { error: e instanceof Error ? e.message : String(e) })); }
    finally { setEvMsgSending(false); }
  };
  const eventLiveOps = isLive && eventOriginId ? (
    <>
      <div style={{ height: 1, background: "#E6E8EC" }} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        {([[t("journeys.newJourneys.stat.registered", "Registered"), liveSold.toLocaleString(), liveCap ? t("journeys.newJourneys.stat.ofCapacity", "of {cap} · {pct}%", { cap: liveCap.toLocaleString(), pct: Math.round((liveSold / liveCap) * 100) }) : t("journeys.newJourneys.stat.noCapacity", "no capacity set")],
           [t("journeys.newJourneys.stat.capacity", "Capacity"), liveCap.toLocaleString(), liveCap ? t("journeys.newJourneys.stat.left", "{count} left", { count: Math.max(0, liveCap - liveSold).toLocaleString() }) : "—"],
           [t("journeys.newJourneys.stat.waitlist", "Waitlist"), liveWait.toLocaleString(), liveWait ? t("journeys.newJourneys.stat.waitingRelease", "waiting on a release") : t("journeys.newJourneys.stat.nobodyWaiting", "nobody waiting")]] as [string, string, string][]).map(([k, v, sub]) => (
          <div key={k} style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: "12px 14px", background: "#FFFFFF" }}>
            <div style={{ ...SEC, marginBottom: 6 }}>{k}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#333333", letterSpacing: "-0.02em" }}>{v}</div>
            <div style={{ fontSize: 11, color: "#697180", marginTop: 2 }}>{sub}</div>
          </div>
        ))}
      </div>
      <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#333333" }}>{t('journeys.newJourneys.text261', "Registered by ticket type")}</div>
            <div style={{ fontSize: 11.5, color: "#697180", marginTop: 2 }}>{t('journeys.newJourneys.text262', "Live from the event record. Message a single type, or everyone at once.")}</div>
          </div>
          <button onClick={() => { setEvMsg({ tierId: null }); setEvMsgText(""); }} disabled={!liveSold} style={{ ...PRIMARY, opacity: liveSold ? 1 : 0.5 }}>{t('journeys.newJourneys.text263', "Message everyone")}</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {liveTickets.map((tk) => (
            <div key={tk.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", border: "1px solid #E6E8EC", borderRadius: 9 }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: tk.color, flex: "none" }} />
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "#333333", flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tk.name}</span>
              <span style={{ fontSize: 11.5, color: "#333333", flex: "none", ...MONO }}>{tk.sold}<span style={{ color: "#697180" }}>/{tk.capacity || "—"}</span></span>
              {tk.waitlist ? <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: "#FAF1DE", color: "#916D0F", flex: "none" }}>{tk.waitlist} {t('journeys.newJourneys.text264', "waiting")}</span> : null}
              <button onClick={() => { setEvMsg({ tierId: tk.id }); setEvMsgText(""); }} disabled={!tk.sold} style={{ ...GHOST, opacity: tk.sold ? 1 : 0.5 }}>{t('journeys.newJourneys.text265', "Message")}</button>
            </div>
          ))}
          {!liveTickets.length ? <div style={{ padding: "14px 12px", border: "1px dashed #D1D4DC", borderRadius: 9, fontSize: 12, color: "#697180", textAlign: "center" }}>{t('journeys.newJourneys.text266', "No ticket types selected — registrations aren't split by type.")}</div> : null}
        </div>
      </div>
    </>
  ) : null;
  /** Appointment Setup — the rail lives in appt-setup.tsx; this hands it the
   *  live libraries and the two patchers. Services come from the SAME catalogue
   *  read the pages use, so Setup and the page cannot disagree about what the
   *  funnel offers. */
  const apptLibs: ApptLibs = {
    services: (cat?.services ?? [])
      .filter((sv: any) => sv?.modules?.appointments !== false)
      .map((sv: any) => ({
        id: sv.id, name: sv.name, category: sv.category, lob: sv.lob,
        durationMin: sv.durationMin, preMin: sv.preMin, postMin: sv.postMin,
        channels: sv.channels,
        questionGroupId: sv.questionGroups?.appointments ?? null,
        minAdvance: sv.minAdvance, maxAdvance: sv.maxAdvance, minCancel: sv.minCancel,
        disableBefore: sv.disableBefore, disableAfter: sv.disableAfter,
      })),
    categories: (cat?.categories ?? []).map((c: any) => ({ id: c.id, name: c.name })),
    locations: (savedLocations as any[]).map((l) => ({ id: l.id, name: l.name, address: l.address })),
    questionGroups: qGroups.map((g: any) => ({ id: g.id, name: g.name || t("journeys.newJourneys.untitled.group", "Untitled group"), count: g.items?.length ?? 0 })),
    surveys: (surveys as any[]).map((sv: any) => ({ id: sv.id, name: sv.name || t("journeys.newJourneys.untitled.survey", "Untitled survey") })),
    promos: coupons.map((c: any) => ({ id: c.id, label: c.code || c.name || t("journeys.newJourneys.untitled.promo", "Untitled promo") })),
    flows: flows.all().map((f: any) => ({ id: f.id, name: f.name || t("journeys.newJourneys.untitled.flow", "Untitled flow") })),
    availability: null,
  };
  const setupApptPanel = D ? (
    <ApptSetupPanel
      view={(ui.setupView ?? "aservices") as ApptView}
      doc={D}
      setup={D.apptSetup ?? {}}
      libs={apptLibs}
      onPatch={patchAppt}
      onPublish={(patch) => patchDoc((d) => ({ ...d, publish: { ...(d.publish ?? {}), ...patch } }))}
      goRoute={goRoute}
    />
  ) : null;

  const setupPublish = (
    <div style={setupCard}>
      <div>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>{t('journeys.newJourneys.text267', "Readiness")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {readiness.map((c) => (
            <div key={c.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12.5, padding: "6px 2px" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: c.done ? "#1F8A5B" : "#C0392B" }}>{c.done ? "✓" : "○"}</span>{c.label}
              </span>
              {!c.done && c.step ? <button onClick={() => set({ setupView: c.step as typeof ui.setupView })} style={{ border: "none", background: "transparent", color: "var(--jrni-color-primary-text, #5142C7)", fontWeight: 700, fontSize: 11.5, cursor: "pointer", fontFamily: FONT }}>{t('journeys.newJourneys.text268', "Fix →")}</button> : null}
            </div>
          ))}
          {!readiness.length || rec === null ? <div style={{ fontSize: 12, color: "#B5BAC6" }}>{rec === null ? t('journeys.newJourneys.text269', "Nothing set yet — fill in Setup to see what's outstanding.") : t('journeys.newJourneys.text270', "Loading the event record…")}</div> : null}
        </div>
      </div>
      {approvalsOn ? (
        <div>
          <div style={{ ...setupFieldLabel, marginBottom: 8 }}>{t('journeys.newJourneys.text271', "Approval")}</div>
          {approvalSub ? (
            <div style={{ fontSize: 12.5, marginBottom: 8 }}>{t('journeys.newJourneys.text273', "Status:")}{" "}<b>{t("journeys.newJourneys.approvalStatus", "{status, select, submitted {submitted} approved {approved} rejected {rejected} feedback {feedback} draft {draft} other {{status}}}", { status: approvalSub.status })}</b></div>
          ) : <div style={{ fontSize: 12.5, color: "#697180", marginBottom: 8 }}>{t('journeys.newJourneys.text272', "Not yet submitted.")}</div>}
          {approvalBlocked ? <div style={WARN}>{approvalBlockReason}</div> : null}
          {!isPublishedEvent && (!approvalSub || approvalSub.status === "rejected") ? (
            <button onClick={submitForApproval} disabled={publishBusy === "submit"} style={{ ...PRIMARY, opacity: publishBusy === "submit" ? 0.6 : 1 }}>{publishBusy === "submit" ? busyLabel(t("journeys.newJourneys.busy.submitting", "Submitting…"), true) : t('journeys.newJourneys.text274', "Submit for approval")}</button>
          ) : null}
        </div>
      ) : null}
      {/* Publish, Save, Unpublish and Cancel live in the header (CC, 2026-10-06): Publish for a
          draft, Save changes for a published event, and Unpublish / Cancel event in its ⋯ menu. */}
      {isLive ? <div style={NOTE}>{t('journeys.newJourneys.liveNote', "This event is live. Registration is open and comms have started. What people registered against is read-only; everything else saves with Save changes.")}</div> : null}
      {eventLiveOps}
      {!eventOriginId ? (
        /* No button (operator, 2026-09-02): Publish creates the event itself.
           The card stays as an explanation of what Publish will do, because
           "everything in Setup becomes the record's opening values" is worth
           knowing BEFORE pressing it and is stated nowhere else. */
        <div style={{ border: "1px dashed #DCCCFC", background: "#FCFBFF", borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#333333", marginBottom: 4 }}>{t('journeys.newJourneys.text280', "Not an event yet")}</div>
          <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.55 }}>{t('journeys.newJourneys.text281', "Everything in Setup is held on this journey and saved as you work. Publishing creates the event from those values, keeps every page exactly as designed, and takes it live in one step.")}</div>
        </div>
      ) : null}
    </div>
  );

  /** Setup · Registration (2026-08-31) — the event-record fields that used to be
   *  edited from the Register page's inspector. Same controls, same ops, one
   *  owner: reference where it acts, edit where it lives. */
  const setupRegistration = (
    <div style={setupCard}>{lockLive(<>
      <div>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>{t('journeys.newJourneys.text282', "Who can register, and what you ask them")}</div>
        <div style={NOTE}>{t('journeys.newJourneys.text283', "These are fields on the event record — the Register page shows their effect and links back here. Booking questions are attached separately, in the Registration section below.")}</div>
      </div>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5, marginBottom: 12 }}>
          <span>{t('journeys.newJourneys.text284', "Require approval before a place is confirmed")}</span>
          <Toggle on={!!r0.approvalMode} onChange={() => { const next = !r0.approvalMode; patchRecord((x) => ({ ...x, approvalMode: next })); if (eventOriginId) holdEvent("registration", { approvalMode: next }); }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <span style={setupFieldLabel}>{t('journeys.newJourneys.text285', "Max guests per registration")}</span>
          <Input type="number" value={r0.maxGuests ?? ""} onChange={(e: any) => { const v = e.target.value ? Number(e.target.value) : null; patchRecord((x) => ({ ...x, maxGuests: v })); holdEvent("registration", { maxGuests: v }); }} placeholder={t('journeys.newJourneys.placeholder16', "e.g. 2")} />
          <div style={{ ...NOTE, marginTop: 5 }}>{t('journeys.newJourneys.text286', "Only applies when the Register page has +1 guests switched on.")}</div>
        </div>
        <span style={setupFieldLabel}>{t('journeys.newJourneys.text287', "Standard attendee fields")}</span>
        <div style={{ ...NOTE, marginBottom: 8 }}>{t('journeys.newJourneys.text288', "First and last name are always collected.")}</div>
        {([["email", t("journeys.newJourneys.channel.email", "Email")], ["phone", t("journeys.newJourneys.channel.phone", "Phone number")]] as const).map(([k, label]) => {
          const on = (r0.standardFields ?? ["email"]).includes(k);
          return (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5, marginBottom: 10 }}>{label}
              <Toggle on={on} onChange={() => { const cur = r0.standardFields ?? ["email"]; const next = on ? cur.filter((x) => x !== k) : cur.concat(k); patchRecord((x) => ({ ...x, standardFields: next })); if (eventOriginId) holdEvent("registration", { standardFields: next }); }} />
            </div>
          );
        })}
      </div>
      <div style={{ height: 1, background: "#E6E8EC" }} />
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={setupFieldLabel}>{t('journeys.newJourneys.text289', "Booking questions")}</span>
          <button onClick={() => goRoute("questions")} style={linkBtn}>{t('journeys.newJourneys.text238', "Manage groups →")}</button>
        </div>
        <div style={{ ...NOTE, marginBottom: 8 }}>{t('journeys.newJourneys.questions.oneGroup', "One set of questions for the whole event. Every guest is asked the same questions.")}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {qGroups.length === 0 ? <span style={{ fontSize: 11.5, color: "#697180" }}>{t('journeys.newJourneys.text291', "No question groups yet.")}</span> : qGroups.map((g: any) => {
            const on = (D?.attach ?? {}).groupId === g.id;
            return (
              <button key={g.id} onClick={() => attachQuestionGroup(on ? null : g.id)}
                style={{ padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 11.5, fontWeight: 700, border: `1.5px solid ${on ? "var(--jrni-color-primary-text, #5142C7)" : "#E6E8EC"}`, background: on ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: on ? "#FFFFFF" : "#4B5563" }}>
                {g.name || t("journeys.newJourneys.untitled.group", "Untitled group")} <span style={{ opacity: 0.7 }}>· {g.items?.length ?? 0}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>, true)}</div>
  );

  const setupMessaging = (
    <div style={setupCard}>
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text292', "Message flow")}</label>
          <button onClick={() => nav.go("messaging")} style={{ border: "none", background: "transparent", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{t('journeys.newJourneys.text293', "Manage flows →")}</button>
        </div>
        <div style={{ fontSize: 11.5, color: "#697180", marginBottom: 8 }}>{t('journeys.newJourneys.text294', "Pick an existing flow from the message library — it runs for this event. Designing a new one happens in the library, not here.")}</div>
        {flows.loading ? <div style={{ fontSize: 12, color: "#B5BAC6" }}>{t('journeys.newJourneys.text296', "Loading flows…")}</div>
          : allFlows.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {allFlows.map((f: any) => {
              const st = flowStats(f);
              const on = r0.messagingFlowId === f.id;
              return (
                <PickRow key={f.id} selected={on}
                  onClick={() => { const next = on ? undefined : f.id; patchRecord((x) => ({ ...x, messagingFlowId: next })); if (eventOriginId) holdEvent("messaging", { messagingFlowId: next ?? null }); }}
                  title={f.name || t("journeys.newJourneys.untitled.flow", "Untitled flow")}
                  sub={t("journeys.newJourneys.flowStepsMessages", "{steps, plural, one {# step} other {# steps}} · {messages, plural, one {# message} other {# messages}}", { steps: st.steps, messages: st.messages }) + (f.desc ? ` · ${f.desc}` : "")}
                  right={f.eventKind || undefined} />
              );
            })}
          </div>
        ) : <div style={{ fontSize: 12, color: "#B5BAC6" }}>{t('journeys.newJourneys.text295', "No flows in the library yet — create one under Messaging.")}</div>}
      </div>
      <div style={NOTE}>{t('journeys.newJourneys.text297', "Selecting a flow references it by id; the flow itself stays in the library, so edits there apply everywhere it's used. Per-event customization lives on the Flow canvas.")}</div>
      <div style={{ height: 1, background: "#E6E8EC" }} />
      {/* Moved off the CSAT page inspector (2026-08-31). An event's survey is
          event-scoped, so it belongs with the event's other sends.
          2026-09-02: it now writes to the EVENT RECORD (`surveys`) instead of the
          journey document's `attach.surveyId`. It was in the right screen and the
          wrong store — two journeys against one event could each hold a different
          survey, and the event itself knew about neither. Same ruling that put
          surveys on the service definition. */}
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={setupFieldLabel}>{t('journeys.newJourneys.text298', "Post-event survey")}</span>
          <button onClick={() => goRoute("surveys")} style={linkBtn}>{t('journeys.newJourneys.text299', "Manage surveys →")}</button>
        </div>
        <div style={{ ...NOTE, marginBottom: 8 }}>{t('journeys.newJourneys.text300', "Sent after the event and shown on the How did we do? page. Saved on the event, so every journey that runs it sends the same one.")}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {(surveys as any[]).length === 0 ? <span style={{ fontSize: 11.5, color: "#697180" }}>{t('journeys.newJourneys.text301', "No surveys in the library yet — create one under Surveys and it will appear here.")}</span> : (surveys as any[]).map((sv: any) => {
            /* One survey, held in the record's `surveys` list. The field is an
               array because the contract allows several; the UI offers one
               because "the post-event survey" is how an organiser thinks of it,
               and a second would just race the first onto the same page. */
            const on = (r0.surveys ?? [])[0] === sv.id;
            return (
              <button key={sv.id} onClick={() => {
                const next = on ? [] : [sv.id];
                patchRecord((x) => ({ ...x, surveys: next }));
                if (eventOriginId) void holdEvent("messaging", { surveys: next });
              }}
                style={{ padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 11.5, fontWeight: 700, border: `1.5px solid ${on ? "var(--jrni-color-primary-text, #5142C7)" : "#E6E8EC"}`, background: on ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: on ? "#FFFFFF" : "#4B5563" }}>
                {sv.name || t("journeys.newJourneys.untitled.survey", "Untitled survey")}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════════════
     Queue Setup views — the queue editor's steps, ported in full
     (2026-08-24). Type & journey · Opening hours · Staffing · Resources ·
     Services · Messaging · Publish. Every field writes through
     patchQueue → updateQueue, the op the wizard autosaved with.
     ══════════════════════════════════════════════════════════════════ */
  const qHours = q0.hours ?? [];
  /** The LOCATION's clock — the one a desk's hours are always kept on. Off the
   *  queue row when the worker surfaces it, else the location record the picker
   *  already loaded. "" when unknown, and the label then names no zone rather
   *  than guessing the reader's own (CD, 2026-09-18). */
  const qLocationZone = String(
    (q0 as any).locationTimezone
    || (savedLocations as any[]).find((l) => l.id === q0.locationId)?.timezone
    || "",
  );
  /** "Open now" / "Closed now · opens Thu 9:00 AM (Chicago time)".
   *
   *  Computed from the desk's OWN authored hours on the location's clock — not
   *  from a status read, which this admin screen does not make. So it answers
   *  "do this desk's hours admit right now?" and NOT "will the join door take
   *  someone?": the door also intersects the building's hours, which are not on
   *  this record. It is the same question the label beside it answers, one step
   *  further, and it is silent whenever it cannot be sure — no hours, no zone,
   *  unauthored hours, or the override on (then the pill already says the hours
   *  are ignored). Deliberately not shown as authoritative anywhere. */
  const qLiveHoursState = (() => {
    if (q0.ignoreHours || q0.hoursAuthored !== true || !qHours.length || !qLocationZone) return "";
    try {
      const now = new Date();
      const parts = new Intl.DateTimeFormat("en-GB", { timeZone: qLocationZone, weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(now);
      const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
      const dayIdx = Q_DAYS.indexOf(get("weekday"));
      if (dayIdx < 0) return "";
      const mins = Number(get("hour")) * 60 + Number(get("minute"));
      const toMins = (hm: string) => { const [h, m] = String(hm).split(":").map((x) => parseInt(x, 10)); return (h || 0) * 60 + (m || 0); };
      const openNow = qHours.some((h) => (h.days ?? []).includes(dayIdx) && mins >= toMins(h.start) && mins < toMins(h.end));
      if (openNow) return t("journeys.newJourneys.queueStatus.openNow", "Open now");
      /* The next window, searching today-onwards across a week. A desk with
         hours only on Tuesdays must still get a real answer on a Friday. */
      for (let ahead = 0; ahead < 8; ahead++) {
        const day = (dayIdx + ahead) % 7;
        const starts = qHours
          .filter((h) => (h.days ?? []).includes(day))
          .map((h) => toMins(h.start))
          .filter((s) => ahead > 0 || s > mins)
          .sort((a, b) => a - b);
        if (starts.length) {
          const when = hmLabel12(`${String(Math.floor(starts[0]! / 60)).padStart(2, "0")}:${String(starts[0]! % 60).padStart(2, "0")}`);
          return t("journeys.newJourneys.queueStatus.closedOpens", "{day, select, today {Closed now · opens {when} ({zone})} tomorrow {Closed now · opens tomorrow {when} ({zone})} other {Closed now · opens {day} {when} ({zone})}}", { day: ahead === 0 ? "today" : ahead === 1 ? "tomorrow" : qDayLabel(day), when, zone: zoneLabel(qLocationZone) });
        }
      }
      return t("journeys.newJourneys.queueStatus.closedNow", "Closed now");
    } catch { return ""; }
  })();
  /* AUTHOR INTENT, not data shape (CD, 2026-09-17). `hoursAuthored` is set by
     these four mutations and by nothing else, because the queue worker uses it
     to decide whether a desk's own hours narrow its location's.

     It cannot be derived from `hours` being non-empty: `qBase()` hands every
     new queue a default Mon–Fri 9–5 whether or not the author ever opens this
     step, so a derived bit would be true for every queue in the fleet and
     would clip a Saturday desk to Mon–Fri on the next deploy. `locations`
     derives its `hoursSet` that way and is the precedent NOT to follow here.

     Deliberately not set by anything outside this step — not by publish, not by
     a save that happens to carry hours. Touching the step is the intent. */
  const qAuthorHours = (fn: (q: QueueConfigLike) => QueueConfigLike) =>
    patchQueue((q) => ({ ...fn(q), hoursAuthored: true }));
  const qPatchHours = (i: number, patch: Partial<{ days: number[]; start: string; end: string }>) =>
    qAuthorHours((q) => ({ ...q, hours: (q.hours ?? []).map((h, hi) => (hi === i ? { ...h, ...patch } : h)) }));
  const qToggleDay = (i: number, d: number) =>
    qAuthorHours((q) => ({ ...q, hours: (q.hours ?? []).map((h, hi) => (hi === i ? { ...h, days: h.days.includes(d) ? h.days.filter((x) => x !== d) : [...h.days, d].sort((a, b) => a - b) } : h)) }));
  const clonePages = (pgs: NJPage[]): NJPage[] => pgs.map((pg) => ({ ...pg, uid: njUid("pg"), content: pg.content.map((c) => ({ ...c, uid: njUid("bl") })) }));
  /** Fresh ids for a copied flow, page steps keyed to the cloned pages. Walks
   *  the whole tree, so a copied journey's branches come with it. */
  const remapSteps = (steps: QFStep[], pages: NJPage[]): QFStep[] => qfMapAll(steps, (st) =>
    st.kind === "page"
      ? { ...st, id: String(pages[Number(st.page)]?.uid ?? njUid("st")) }
      : { ...st, id: njUid("st") });
  /** Per-type starter flows (jf-model's qfTypeDefaultFlow), in
   *  journey-document terms: the same pages, messages, waits and decision. */
  /* `buildQueueStarter` / `applyQueueStarter` / `seedFromQueueDoc` removed
     (2026-09-02). They existed to seed or replace a queue's journey from the
     Setup tab's starter tiles; the tiles are gone because a queue journey arrives
     with its pages already made. Copy-a-journey survives them, inverted, on the
     journeys list. */
  const qFlowStats = (() => {
    let messages = 0, decisions = 0, waits = 0;
    qfFlatten(qfFlow.steps).forEach((st) => {
      if (st.kind === "comms" || st.kind === "survey") messages += 1;
      else if (st.kind === "wait") waits += 1;
      else if (st.kind === "decision") decisions += 1;
    });
    return { messages, decisions, waits };
  })();
  const qHeroFile = (f: File | undefined) => {
    if (!f || !/^image\//.test(f.type)) return;
    const rd = new FileReader();
    rd.onload = () => patchQueue((q) => ({ ...q, heroImage: String(rd.result) }));
    rd.readAsDataURL(f);
  };

  const setupQSetup = (
    <div style={setupCard}>
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 12 }}>
        <div>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text303', "Queue name")}{" "}<span style={{ color: "#D01647" }}>*</span></label>
          <Input value={q0.name ?? ""} onChange={(e: any) => patchQueue((q) => ({ ...q, name: e.target.value }))} placeholder={t('journeys.newJourneys.placeholder17', "e.g. Advising walk-ins")} />
        </div>
        <div>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text304', "Location")}<span style={{ color: "#D01647" }}>*</span></label>
          <Dropdown options={(savedLocations as any[]).map((l) => ({ value: l.id, label: l.name }))} value={q0.locationId ?? null}
            onChange={(v: any) => patchQueue((q) => ({ ...q, locationId: v, locationName: (savedLocations as any[]).find((l) => l.id === v)?.name }))}
            placeholder={locationsLoading ? t('journeys.newJourneys.placeholder18', "Loading locations…") : locationsError ? t('journeys.newJourneys.placeholder19', "Couldn't load locations") : ((savedLocations as any[]).length ? t("journeys.newJourneys.pickLocation", "Pick a location") : t("journeys.newJourneys.noLocationsYet", "No locations yet"))} disabled={!!queueOriginId || locationsLoading} style={{ gap: 0 }} />
          {/* THREE STATES, not one (CD, 2026-09-17). This read only
             `locations` off `useLocations`, so "No locations yet" was shown
             while the list was still loading AND when the read had failed — on
             Add Queue that is the first thing an author sees, and it says the
             tenant has no locations when it has twenty. Publish then blocks on
             "✕ Location" with nothing to pick, so a queue could not be created
             from the UI at all.

             Loading says loading; a failure says so and offers a retry;
             "No locations yet" now means only what it says. Editing an
             existing queue was unaffected because the control is disabled
             there — which is why this only ever bit the create flow. */}
          {locationsError && !queueOriginId ? (
            <div style={{ fontSize: 10.5, color: "#8C1D18", marginTop: 4, lineHeight: 1.5, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span>{t('journeys.newJourneys.text305', "The location list couldn't be loaded, so there is nothing to choose from.")}</span>
              <button onClick={() => reloadLocations()} style={GHOST}>{t('journeys.newJourneys.text306', "Try again")}</button>
            </div>
          ) : null}
          {queueOriginId ? <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>{t('journeys.newJourneys.text307', "A queue belongs to its location's branch company, so it can't move after it exists.")}</div> : null}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text308', "Ticket prefix")}</label>
          <Input value={q0.ticketPrefix ?? ""} onChange={(e: any) => patchQueue((q) => ({ ...q, ticketPrefix: String(e.target.value).toUpperCase().replace(/\s/g, "").slice(0, 4) }))} placeholder={t('journeys.newJourneys.placeholder20', "e.g. ADV")} />
          <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>{t('journeys.newJourneys.text309', "Prefix + the customer's join number is their ticket")}{" "}{q0.ticketPrefix ? t('journeys.newJourneys.text310', "— tickets like {ticketPrefix}-104", { ticketPrefix: q0.ticketPrefix }) : t('journeys.newJourneys.text311', "— leave blank for numbers only")}.</div>
        </div>
        <div>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text312', "Wait target (minutes)")}</label>
          <NumberField value={Number(q0.slaTargetMins ?? 10)} min={1} onChange={(n: number) => patchQueue((q) => ({ ...q, slaTargetMins: n }))} ariaLabel={t('journeys.newJourneys.ariaLabel2', "Wait target in minutes")} style={INPUT as any} />
        </div>
      </div>
      {/* JAI-1767 — the hold categories joining this desk clears. Rides the
          queue update body, beside ticket prefix and wait target. */}
      {portalAbsent ? null : <div>
        <label style={setupFieldLabel}>{holdsResolvesLabel(i18nT)}</label>
        <ResolvesHoldsPicker value={q0.resolvesHolds} subject="queue" t={i18nT}
          onChange={(v) => patchQueue((q) => ({ ...q, resolvesHolds: v }))} />
      </div>}
      {/* JAI-2020 — Audience, beside Resolves holds. Data: partial (lib/audience.tsx):
          sent by patchQueueConfig only when it changed from the stored queue. */}
      {portalAbsent ? null : <div>
        <label style={setupFieldLabel}>{i18nT("audience.section.title", "Audience")}</label>
        <AudienceSection value={q0.audience} subject="queue" t={i18nT} errors={qAudienceErrors}
          onChange={(v) => { setQAudienceErrors(null); patchQueue((q) => ({ ...q, audience: v })); }} />
      </div>}
      <div>
        <label style={setupFieldLabel}>{t('journeys.newJourneys.text172', "Hero image")}</label>
        <div style={{ fontSize: 11, color: "#697180", margin: "0 0 8px", lineHeight: 1.5 }}>{t('journeys.newJourneys.text313', "Tops every page of this queue's journey. Empty falls back to the brand gradient.")}</div>
        <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
          <div style={{ width: 200, aspectRatio: "16 / 9", borderRadius: 8, background: q0.heroImage ? `url(${q0.heroImage}) center/cover` : "linear-gradient(135deg, #75ADFA, var(--jrni-color-primary-text, #5142C7))", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFFFFF", fontSize: 20, fontWeight: 800, flex: "none" }}>
            {!q0.heroImage ? ((q0.name ?? "").trim() ? (q0.name ?? "").trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase() : "Q") : null}
            {q0.heroImage ? <button onClick={() => patchQueue((q) => ({ ...q, heroImage: "" }))} title={t('journeys.newJourneys.title9', "Remove the hero image")} style={{ position: "absolute", top: 6, right: 6, width: 22, height: 22, borderRadius: 999, border: "none", background: "rgba(0,0,0,0.55)", color: "#FFFFFF", fontSize: 10, cursor: "pointer" }}>✕</button> : null}
          </div>
          <label onDragOver={(e) => { e.preventDefault(); setQHeroDrag(true); }} onDragLeave={() => setQHeroDrag(false)}
            onDrop={(e) => { e.preventDefault(); setQHeroDrag(false); qHeroFile(e.dataTransfer?.files?.[0]); }}
            style={{ flex: 1, border: `1px dashed ${qHeroDrag ? "var(--jrni-color-primary-1, #5142C7)" : "#D1D4DC"}`, background: qHeroDrag ? "var(--jrni-color-primary-4, #F6F2FE)" : "transparent", borderRadius: 8, padding: 16, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 12, color: "#697180", cursor: "pointer", textAlign: "center" }}>
            <span style={{ fontSize: 16 }}>↥</span>
            <span>{q0.heroImage ? t('journeys.newJourneys.text174', "Replace the image — drop it here or") : t('journeys.newJourneys.text175', "Drop an image here or")}{" "}<b style={{ color: "var(--jrni-color-primary-text, #5142C7)" }}>{t('journeys.newJourneys.text176', "browse")}</b></span>
            <span style={{ fontSize: 10.5 }}>{t('journeys.newJourneys.text314', "PNG or JPG · 16:9 works best")}</span>
            <input type="file" accept="image/*" onChange={(e) => { qHeroFile(e.target.files?.[0]); (e.target as HTMLInputElement).value = ""; }} style={{ display: "none" }} />
          </label>
        </div>
      </div>
      {/* The "Type of queue & starting point" picker is gone (operator,
          2026-09-02: "I do not need the Type of Queue & Starting point content at
          all. The queue pages are already created automatically").
          It offered four starters, a copy-another-queue option and Blank, each
          of which REPLACED the journey's pages and flow. Creating a queue journey
          already seeds them, so the picker's only remaining use was to throw away
          what the template had just made — an action that looks like a choice and
          is really a reset. The pages are edited in Pages and the flow in Flow.
          "Copy another queue's journey" lived here too and is DROPPED (operator,
          2026-09-02) rather than re-sited. It only ever seeded a journey by
          replacing its pages and flow, which is the same reset the starter tiles
          performed — a queue's pages arrive with it, so there is nothing a copy
          adds that is not a discard first. `clonePages`/`remapSteps` stay: the
          journeys list's Duplicate uses them.
          `queueTypes`/`qType` stay: `typeId` still drives the resource-tracking
          preset and the Publish summary's Type row. */}
    </div>
  );

  const setupQHours = (
    <div style={setupCard}>
      {/* The intro no longer demands a set (CD, 2026-09-17). It read "At least
          one set, with days chosen, is required" in red, which stopped being
          true when readiness started accepting an empty list — and the empty
          list is now the DEFAULT, so that sentence would have been the first
          thing every author read and the first thing that misled them. */}
      <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>{t('journeys.newJourneys.text315', "When can customers join this queue? Add a set per pattern — weekdays and weekends usually differ.")}</div>
      {/* What EMPTY means, said where the emptiness is. A new queue now starts
          with no sets, so without this the step is a blank card with an Add
          button and no way to know that blank is a working answer — or that a
          desk cannot outlast its building. */}
      {qHours.length === 0 ? (
        <div style={{ fontSize: 12, color: "#333333", padding: "9px 11px", background: "var(--jrni-color-primary-4, #F6F2FE)", border: "1px solid #DCCCFC", borderRadius: 8, lineHeight: 1.55 }}>
          {t('journeys.newJourneys.text316', "Follows the location's hours. Add a set to give this queue its own hours; they can narrow the location's hours, never extend them.")}</div>
      ) : null}
      {qHours.map((h, i) => (
        <div key={i} style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
            {Q_DAYS.map((d, di) => {
              const on = h.days.includes(di);
              return (
                <button key={d} onClick={() => qToggleDay(i, di)} style={{ width: 42, height: 32, borderRadius: 8, border: `1.5px solid ${on ? "var(--jrni-color-primary-text, #5142C7)" : "#E6E8EC"}`, background: on ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: on ? "#FFFFFF" : "#333333", fontSize: 11.5, fontWeight: 700, fontFamily: FONT, cursor: "pointer" }}>{qDayLabel(di)}</button>
              );
            })}
            <span style={{ flex: 1 }} />
            <button onClick={() => qAuthorHours((q) => ({ ...q, hours: (q.hours ?? []).filter((_, hi) => hi !== i) }))} style={GHOST}>{t('journeys.newJourneys.text317', "Remove")}</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, maxWidth: 400 }}>
            <Dropdown options={TIME_OPTS.map((v) => ({ value: v, label: hmLabel12(v) }))} value={h.start} onChange={(v: any) => qPatchHours(i, { start: v })} style={{ gap: 0, width: 140 }} />
            <span style={{ fontSize: 12.5, color: "#697180" }}>{t('journeys.newJourneys.text207', "to")}</span>
            <Dropdown options={TIME_OPTS.filter((v) => v > h.start).map((v) => ({ value: v, label: hmLabel12(v) }))} value={h.end} onChange={(v: any) => qPatchHours(i, { end: v })} style={{ gap: 0, width: 140 }} />
          </div>
          {h.days.length === 0 ? <div style={{ fontSize: 11, color: "#C0392B" }}>{t('journeys.newJourneys.text318', "Pick at least one day for this set.")}</div> : null}
        </div>
      ))}
      <div><button onClick={() => qAuthorHours((q) => ({ ...q, hours: [...(q.hours ?? []), { days: [], start: "09:00", end: "17:00" }] }))} style={GHOST}>{t('journeys.newJourneys.text319', "+ Add another set")}</button></div>
      {/* TAKE JOINS OUTSIDE OPENING HOURS (CD, 2026-09-18). Lifts BOTH the
          building's hours and the desk's own; the desk's status — open, paused,
          closed — still governs, which is what the helper says, because "ignores
          hours" could otherwise be read as "always on".

          Routed through `patchQueue`, NOT `qAuthorHours`: this is not a
          schedule, it is the absence of one, and writing `hoursAuthored` here
          would claim the author had authored hours they never touched — the
          same conflation the removed Mon–Fri prefill made. It is still a real
          author edit, so it marks the queue dirty and Save commits it. */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, borderTop: "1px solid #E6E8EC", paddingTop: 14 }}>
        <span style={{ fontSize: 12.5, color: "#333333", lineHeight: 1.5 }}>
          {t('journeys.newJourneys.text320', "Take joins outside opening hours")}<span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 2 }}>{t('journeys.newJourneys.text321', "For a demo or an exceptional day. The desk still respects its own status — open / paused / closed.")}</span>
        </span>
        <Toggle on={!!q0.ignoreHours} onChange={() => patchQueue((q) => ({ ...q, ignoreHours: !q.ignoreHours }))} />
      </div>
      {/* The "Open:" summary is for a list that exists \u2014 with none, the panel
          above already says follows-location, and two lines saying the same
          thing read as a disagreement waiting to happen. */}
      {qHours.length || q0.ignoreHours ? (
        <div style={{ fontSize: 12, color: "#333333", padding: "9px 11px", background: "var(--jrni-color-primary-4, #F6F2FE)", border: "1px solid #DCCCFC", borderRadius: 8 }}><b>{t('journeys.newJourneys.text322', "Open:")}</b> {qHoursLabel(qHours as any, q0.hoursAuthored, { timeZone: qLocationZone, ignoreHours: q0.ignoreHours })}{qLiveHoursState ? <span style={{ color: "#697180" }}> · {qLiveHoursState}</span> : null}</div>
      ) : null}
    </div>
  );

  const qStaff = q0.assignedStaff ?? [];
  /** Staff through the same picker, grouped by line of business — `department`
   *  where the record carries one, else the member's first skill, which is the
   *  nearest thing to a line of business the staff record has. */
  const staffLobId = (m: any) => String(m.department ?? m.category ?? (m.skills ?? [])[0] ?? "").trim();
  const staffPickerItems: PickerItem[] = (staffMembers as any[]).map((m) => ({
    id: m.id,
    name: m.name ?? m.id,
    group: staffLobId(m) || null,
    meta: [m.title ?? null, (m.skills ?? []).slice(0, 2).join(", ") || null],
    keywords: [(m.skills ?? []).join(" "), (m.languages ?? []).join(" "), m.department ?? ""].join(" "),
  }));
  const staffPickerGroups: PickerGroup[] = [...new Set((staffMembers as any[]).map(staffLobId).filter(Boolean))]
    .map((id, i) => ({ id: String(id), name: String(id), hue: ["#2D60EB", "#0AA396", "#D61A55", "#916D0F", "#7C3AED", "#1F8A5B"][i % 6]! }));
  const setupQStaffing = (
    <div style={setupCard}>
      <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>{t('journeys.newJourneys.text323', "Who serves this queue. Add whole lines of business or single people — staff can be added or removed while it is live.")}</div>
      <CatalogPicker
        items={staffPickerItems}
        groups={staffPickerGroups}
        value={(q0.staffSelection ?? qStaff.map((s) => ({ type: "item" as const, id: s.id }))) as PickerSel[]}
        onChange={(next) => patchQueue((q) => ({
          ...q,
          staffSelection: next,
          /* `assignedStaff` stays the resolved truth the console reads, so a
             live line-of-business rule reaches the floor as real people. */
          assignedStaff: resolveSelection(next, staffPickerItems).map((it) => {
            const m = (staffMembers as any[]).find((x) => x.id === it.id);
            return { id: it.id, name: it.name, role: m?.title ?? null };
          }),
        }))}
        sourceLabel={t("journeys.newJourneys.picker.staff.source", "Your staff")}
        selectedLabel={t("journeys.newJourneys.picker.staff.selected", "Serving this queue")}
        noun="staff member"
        searchPlaceholder={t("journeys.newJourneys.picker.staff.search", "Search people, skills or lines of business…")}
        emptySource={t("journeys.newJourneys.picker.staff.emptySource", "No staff in the directory yet.")}
        emptySelection={t("journeys.newJourneys.picker.staff.emptySelection", "Pick who serves this queue — the order here is the order the console offers them.")} />
    </div>
  );

  const qRes = q0.resources ?? [];
  /** Resources through the same picker, grouped by resource type ("Room",
   *  "Desk", "Equipment"), which is the resource record's own department. */
  const resPickerItems: PickerItem[] = ((resourceRecs as any[]) ?? []).map((r) => ({
    id: r.name, name: r.name, group: (r.type || r.kind || "") || null,
    meta: [r.type || null, r.location || null], keywords: [r.description ?? "", (r.groups ?? []).join(" ")].join(" "),
  })).filter((r) => r.name);
  const resPickerGroups: PickerGroup[] = [...new Set(((resourceRecs as any[]) ?? []).map((r) => (r.type || r.kind || "")).filter(Boolean))]
    .map((id, i) => ({ id: String(id), name: String(id), hue: ["#0AA396", "#2D60EB", "#916D0F", "#D61A55", "#7C3AED"][i % 5]! }));
  const setupQResources = (
    <div style={setupCard}>
      <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>{t('journeys.newJourneys.text324', "Rooms, desks or equipment this queue consumes. Availability is tracked per resource while the queue runs.")}</div>
      <CatalogPicker
        items={resPickerItems}
        groups={resPickerGroups}
        value={(q0.resourceSelection ?? (qRes as string[]).map((r) => ({ type: "item" as const, id: r }))) as PickerSel[]}
        onChange={(next) => patchQueue((q) => ({
          ...q,
          resourceSelection: next,
          resources: resolveSelection(next, resPickerItems).map((it) => it.name),
        }))}
        sourceLabel={t("journeys.newJourneys.picker.resources.source", "Your resources")}
        selectedLabel={t("journeys.newJourneys.picker.resources.selected", "Used by this queue")}
        noun="resource"
        searchPlaceholder={t("journeys.newJourneys.picker.resources.search", "Search resources or types…")}
        emptySource={t("journeys.newJourneys.picker.resources.emptySource", "Create resources under Resources and they'll show up here.")}
        emptySelection={t("journeys.newJourneys.picker.resources.emptySelection", "Pick the rooms, desks or equipment this queue consumes.")} />
      {qType && !(qType.presets as any)?.resourceTracking ? <div style={{ fontSize: 11.5, color: "#697180" }}>{qType.label} {t('journeys.newJourneys.text325', "queues don't track resources by default — anything added here is still enforced.")}</div> : null}
    </div>
  );

  /* The queue's own selection helpers (qSelHas / qSelAdd / qSelRemove /
     qSelMove / qSelDropOn / qSvcRow / qSelLabel / uncategorisedSvcs) are gone
     with the hand-rolled picker they served — CatalogPicker owns add, remove,
     reorder and labelling now, and qPickerCommit is the only writer. */
  /** The per-service matrix, shared by the queue Services panel and reused in
   *  shape by the appointment rail. What each offered service asks and sends —
   *  the surface the withdrawn page pickers were standing in for. */
  const perServiceMatrix = (method: "queueing" | "appointments", svcIds: string[]) => {
    const catSvcs = ((cat?.services ?? []) as any[]);
    const rows = svcIds.map((id) => {
      const sv = catSvcs.filter((x: any) => x.id === id || x.name === id)[0];
      const gid = sv?.questionGroups?.[method] ?? null;
      const g = gid ? qGroups.find((x: any) => x.id === gid) : null;
      return { id: String(id), name: sv?.name ?? String(id), svcId: sv?.id ?? null, group: g ?? null, gid };
    });
    return (
      <div>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>{t('journeys.newJourneys.text326', "What each service asks and sends")}</div>
        {rows.length === 0 ? (
          <div style={{ border: "1px dashed #D1D4DC", borderRadius: 9, padding: "16px 12px", textAlign: "center", fontSize: 11.5, color: "#697180" }}>{t('journeys.newJourneys.text327', "No services selected yet.")}</div>
        ) : (
          <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.4fr 1fr", gap: 10, padding: "8px 12px", background: "#F7F7FA", fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#697180" }}>
              <span>{t('journeys.newJourneys.text328', "Service")}</span><span>{t('journeys.newJourneys.text289', "Booking questions")}</span><span>{t('journeys.newJourneys.text329', "Survey · promo · flow")}</span>
            </div>
            {rows.map((r) => (
              <div key={r.id} style={{ display: "grid", gridTemplateColumns: "1.2fr 1.4fr 1fr", gap: 10, padding: "10px 12px", borderTop: "1px solid #EEF0F3", alignItems: "baseline", fontSize: 11.5 }}>
                <span style={{ fontWeight: 700 }}>{r.name}</span>
                <span>
                  {r.group
                    ? <>{r.group.name} <span style={{ color: "#697180" }}>· {r.group.items?.length ?? 0} {t('journeys.newJourneys.text330', "questions")}</span><br /><a href="#" onClick={(e) => { e.preventDefault(); goRoute("questions.group.edit", { id: r.group.id }); }} style={{ fontSize: 11 }}>{t('journeys.newJourneys.text331', "Edit group →")}</a></>
                    : <span style={{ color: "#916D0F" }}>{t('journeys.newJourneys.text332', "None set")}{r.svcId ? <> — <a href="#" onClick={(e) => { e.preventDefault(); goRoute("ops.catalogue", { service: r.svcId! }); }}>{t('journeys.newJourneys.text333', "set one on the service")}</a></> : null}</span>}
                </span>
                <span style={{ color: "#8A92A0", fontSize: 10.5, lineHeight: 1.5 }}>{t('journeys.newJourneys.text334', "Pending the catalog fields")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ── The one picker (2026-09-02, operator) ──────────────────────────────
     Every "what does this journey offer" list on every setup step now runs
     through CatalogPicker: departments with an Add-all that stays live, search
     across name / department / keywords, and an ordered selection. These
     adapters are the only per-kind code left — items in, selection out. */
  /** Queue services. The queue's own `{type:"category"|"service"}` shape is
   *  translated to the picker's `{type:"group"|"item"}` here rather than
   *  migrating the stored shape, so an older queue round-trips untouched. */
  const qPickerItems: PickerItem[] = svcCatServices.map((s) => {
    /* Queue-only names are no longer OFFERED (operator: stop offering them,
       migrate on next save) — but they stay in `items` so an existing queue's
       selection still RESOLVES and shows its real name. Dropping them from the
       array entirely made every legacy pick read "No longer in the catalogue"
       and reported the queue as offering nothing. */
    const legacy = s.id.startsWith(QNAME_ID);
    return {
      id: s.id,
      name: s.name,
      group: legacy ? null : (s.category ?? null),
      meta: legacy ? [t("journeys.newJourneys.legacyService", "Not in your catalog — migrates when you next save")] : [s.listedDuration],
      keywords: (s as any).description ?? "",
      offerable: !legacy,
    };
  });
  const qPickerGroups: PickerGroup[] = svcCatCategories.filter((c) => c.id !== QUEUE_ONLY_CAT);
  const qPickerValue: PickerSel[] = (q0.serviceSelection ?? []).map((s) => ({ type: s.type === "category" ? "group" : "item", id: s.id } as PickerSel));
  /** Write back, migrating queue-only entries as we go: a `name:` id whose name
   *  now exists in the catalogue becomes that service; one that does not is
   *  dropped rather than carried forward for ever. */
  const qPickerCommit = (next: PickerSel[]) => patchQueue((q) => ({
    ...q,
    /* Return type annotated (2026-09-08). Without it TypeScript infers the
       union from the FIRST arm — `{type:"category"}` — and the two `"service"`
       arms below then fail to assign. Naming the element type up front makes all
       three arms members of the same union rather than candidates to widen it. */
    serviceSelection: next.flatMap((s): { type: "category" | "service"; id: string }[] => {
      if (s.type === "group") return [{ type: "category" as const, id: s.id }];
      if (s.id.startsWith(QNAME_ID)) {
        const hit = svcCatServices.find((x) => !x.id.startsWith(QNAME_ID) && x.name === s.id.slice(QNAME_ID.length));
        return hit ? [{ type: "service" as const, id: hit.id }] : [];
      }
      return [{ type: "service" as const, id: s.id }];
    }),
  }));

  const setupQServices = (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 900 }}>
      {/* The per-service matrix (2026-08-31): the surface the withdrawn page
          pickers were standing in for. Placed FIRST so an operator changing the
          selection below sees immediately what each service brings with it. */}
      {perServiceMatrix("queueing", (q0.services ?? []).map(String))}
      <div style={{ height: 1, background: "#E6E8EC" }} />
      <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>{t('journeys.newJourneys.text335', "What can customers queue for? Add whole departments or single services — the order here is the order on the Services page.")}{" "}<span style={{ color: "#D01647" }}>{t('journeys.newJourneys.text336', "At least one is required.")}</span></div>
      <CatalogPicker
        items={qPickerItems}
        groups={qPickerGroups}
        value={qPickerValue}
        onChange={qPickerCommit}
        sourceLabel={t("journeys.newJourneys.picker.services.source", "Your catalog")}
        selectedLabel={t("journeys.newJourneys.picker.services.selected", "In this queue")}
        noun="service"
        searchPlaceholder={t("journeys.newJourneys.picker.services.search", "Search services or departments…")}
        emptySource={t("journeys.newJourneys.picker.services.emptySource", "Your services catalog is empty — create services first.")}
        emptySelection={t("journeys.newJourneys.picker.services.emptySelection", "Add services from your catalog — they appear here in customer order.")} />
    </div>
  );

  /* Setup · Messaging removed (operator, 2026-09-02: "Messaging is also not
     necessary, as messages are now added and defined in the flow itself").
     The panel picked a library message flow and then linked to the Flow canvas
     for its steps — two places to answer one question, and the canvas is the one
     that can actually change what a customer receives. `messagingFlowId` stays
     on the record and is still written by the flow; only the duplicate picker
     is gone. */
  const reloadQueues = () => listQueues().then((p: any) => setQueues(p?.data ?? [])).catch(() => {});
  /* LOCAL PATCH (platform-side): `queueOriginId`
     is read from the rendered document, so on the Publish click that CREATES the
     queue it is still null when `runAttach` returns — the doc is re-keyed by a
     state update this closure has not seen. The call then toasted "This journey
     isn't attached to a queue yet." and the setup (hours, staff, services) and
     the open transition only landed on a SECOND click. The attach result carries
     the new origin id; the publish handler passes it in. */
  const qLifecycle = async (status: "open" | "paused" | "closed", verb: string, originId: string | null = queueOriginId) => {
    if (!originId) { showToast(t("journeys.newJourneys.toast.notAttachedQueue", "This journey isn't attached to a queue yet.")); return; }
    setPublishBusy(status === "open" ? "publish" : status === "paused" ? "pause" : "close");
    try {
      await patchQueueConfig(originId, {
        name: q0.name, ticketPrefix: q0.ticketPrefix, slaTargetMins: q0.slaTargetMins, needed: q0.needed,
        resolvesHolds: readResolvesHolds(q0.resolvesHolds),
        ...(audienceChanged((qSummary as any)?.audience, q0.audience) ? { audience: readAudience(q0.audience) } : {}),
        hours: q0.hours, hoursAuthored: q0.hoursAuthored, ignoreHours: q0.ignoreHours, assignedStaff: q0.assignedStaff, services: q0.services,
        serviceSelection: q0.serviceSelection, resources: q0.resources,
        heroImage: q0.heroImage || null, journeySource: q0.journeySource, messagingFlowId: q0.messagingFlowId,
      });
      await setQueueLifecycle(originId, status);
      /* Only reflect the status into the held setup when this render owns the
         record. On the click that created the queue this closure's `patchQueue`
         still carries the pre-attach `local:` key and a null `queueOriginId`, so
         its debounced run would take the document branch and re-save the draft
         `runAttach` just deleted — a ghost duplicate journey. The `reloadQueues`
         below re-derives the status from the record on that path. */
      if (originId === queueOriginId) patchQueue((q) => ({ ...q, status }), { userEdit: false });
      await reloadQueues();
      showToast(verb);
    } catch (e) {
      showToast(t("journeys.newJourneys.toast.queueUpdateFailed", "{action, select, publish {Couldn't publish the queue — {error}} other {Couldn't update the queue — {error}}}", { action: status === "open" ? "publish" : "update", error: e instanceof Error ? e.message : String(e) }));
    } finally { setPublishBusy(null); }
  };
  /** The customer's link: the tenant's SERVED join authority, root path, with
   *  this desk in the query string — `?queue=<queue id>`, the parameter the
   *  journey already reads. Composed by `queueJoinUrl`, shared with the Queues
   *  list's "Copy join link" so the copied link and this QR are one link.
   *
   *  The join-CODE requirement added on 2026-09-16 is gone (2026-09-17, CD):
   *  the worker-rendered `/<code>` pages are being removed from every public
   *  host and the journey owns every path, so a path segment carrying the code
   *  resolves to its not-found. The link now carries `queueOriginId`, so the
   *  "no join code by default" case — which that gate existed for — now HAS a
   *  working link, and withholding it would be the regression.
   *
   *  `publicHost("join")` is served, never composed, so an empty host still
   *  means no resolved join surface: empty string here, and the card says so
   *  rather than display, copy and encode a dead `https:///?queue=…`. */
  const qJoinUrl = () => queueJoinUrl(queueOriginId);
  const qActive: QueueEntry[] = qBoard ? [
    ...(qBoard.entries ?? []).filter((e) => e.status === "serving"),
    ...(qBoard.entries ?? []).filter((e) => e.status === "called"),
    ...(qBoard.entries ?? []).filter((e) => e.status === "waiting").sort((a, b) => a.position - b.position),
  ] : [];
  const setupQPublish = (
    <div style={setupCard}>
      <div style={{ border: `1px solid ${qReady ? "#A7DACB" : "#F3D0CB"}`, background: qReady ? "#F1FAF6" : "#FDECEA", borderRadius: 10, padding: "12px 14px" }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: qReady ? "#1F8A5B" : "#C0392B", marginBottom: 8 }}>
          {qReady ? (q0.status === "open" ? t("journeys.newJourneys.queueReady.running", "Everything required is set — edits apply to the running queue immediately") : t("journeys.newJourneys.queueReady.canOpen", "Everything required is set — this queue can open")) : t("journeys.newJourneys.queueReady.remaining", "{count, plural, one {# required field still to complete} other {# required fields still to complete}}", { count: qChecks.filter((c) => !c.done).length })}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {qChecks.map((c) => (
            <div key={c.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
              <span style={{ color: c.done ? "#1F8A5B" : "#C0392B", flex: "none" }}>{c.done ? "✓" : "✕"}</span>
              <span style={{ flex: 1, color: c.done ? "#333333" : "#C0392B", fontWeight: c.done ? 500 : 600 }}>{c.label}</span>
              {!c.done ? <button onClick={() => {
                /* Services lost their Setup pill (operator 2026-08-24) — they're
                   picked on the services PAGE now, so that's where Fix goes. */
                if (c.tab === "qservices") {
                  const idx = Math.max(0, (D?.pages ?? []).findIndex((pg) => pg.id === "services"));
                  /* With the pages hidden there is no canvas to send an author
                     to — the tab that owns the missing thing is the honest
                     destination. Queue: Services. Event: the page checks all
                     belong to Basics. */
                  if (pagesHidden) set({ setupView: isQueueJourney ? "qservices" : "basics" });
                  else set({ setupView: null, view: "pages", cur: idx, sel: null });
                } else set({ setupView: c.tab as any });
              }} style={{ border: "none", background: "transparent", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{t('journeys.newJourneys.text268', "Fix →")}</button> : null}
            </div>
          ))}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#333333", marginBottom: 10 }}>{q0.name || t('journeys.newJourneys.text338', "Untitled queue")}</div>
        {([
          [t("journeys.newJourneys.summary.type", "Type"), qType?.label ?? q0.typeId ?? "—"],
          /* `||`, not `??` (2026-09-17, PP). A queue record carries
             `locationName: ""` when the name was never denormalised onto it,
             and `??` treats the empty string as a PRESENT value — so the
             summary rendered blank while Setup, which reads `locationId`, showed
             the location correctly. The id lookup is the reliable source; the
             denormalised name is only a shortcut. */
          [t("journeys.newJourneys.summary.location", "Location"), q0.locationName || (savedLocations as any[]).find((l) => l.id === q0.locationId)?.name || "—"],
          [t("journeys.newJourneys.summary.ticketPrefix", "Ticket prefix"), q0.ticketPrefix ? t("journeys.newJourneys.summary.ticketPrefixValue", "{prefix} — tickets like {prefix}-104", { prefix: q0.ticketPrefix }) : "—"],
          [t("journeys.newJourneys.summary.waitTarget", "Wait target"), t("journeys.newJourneys.summary.minutes", "{count, plural, one {# minute} other {# minutes}}", { count: q0.slaTargetMins ?? 10 })],
          [t("journeys.newJourneys.summary.openingHours", "Opening hours"), qHoursLabel(qHours as any, q0.hoursAuthored, { timeZone: qLocationZone, ignoreHours: q0.ignoreHours })],
          [t("journeys.newJourneys.summary.staff", "Staff"), qStaff.length ? qStaff.map((s) => s.name).join(", ") : t("journeys.newJourneys.summary.noOneAssigned", "No one assigned yet")],
          /* What the selection RESOLVES TO today, not just that a department is
             live (operator ruling, 2026-09-02): a live rule is the right default
             but Publish is where you check what is actually being offered. */
          [t("journeys.newJourneys.summary.services", "Services"), describeSelection(qPickerValue, qPickerItems, qPickerGroups, "service")],
          /* "Resources" and "Message flow" removed from the summary (2026-09-16,
             PP). Both named things this screen no longer lets an author set: the
             Resources setup tab is withheld, and Setup · Messaging was retired
             in 2026-09-02 when messages moved into the flow. A summary row you
             cannot act on is worse than an absent one — it reads as a setting
             someone forgot to fill in. `resources` and `messagingFlowId` are
             still carried on the record and still written by patchQueue; only
             these two rows are gone. */
          [t("journeys.newJourneys.summary.journey", "Journey"), t("journeys.newJourneys.summary.pageCount", "{count, plural, one {# page} other {# pages}}", { count: D?.pages.length ?? 0 })],
        ] as [string, string][]).map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 12.5, borderBottom: "1px solid #E6E8EC", padding: "8px 0" }}>
            <span style={{ color: "#697180", flex: "none" }}>{k}</span><span style={{ fontWeight: 600, color: "#333333", textAlign: "right" }}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {q0.status === "open" ? (
          <>
            <button onClick={() => qLifecycle("paused", t("journeys.newJourneys.lifecycle.paused", "Queue paused — no new joins, the line keeps its place"))} disabled={!!publishBusy} style={{ ...GHOST, opacity: publishBusy ? 0.6 : 1 }}>{publishBusy === "pause" ? busyLabel(t("journeys.newJourneys.busy.pausing", "Pausing…")) : t('journeys.newJourneys.text339', "Pause joins")}</button>
            <button onClick={() => qLifecycle("closed", t("journeys.newJourneys.lifecycle.closed", "Queue closed"))} disabled={!!publishBusy} style={{ ...GHOST, opacity: publishBusy ? 0.6 : 1 }}>{publishBusy === "close" ? busyLabel(t("journeys.newJourneys.busy.closing", "Closing…")) : t('journeys.newJourneys.text340', "Close the queue")}</button>
            <span style={{ flex: 1 }} />
            {/* COMING SOON (2026-09-16, PP) — the console jump-off is disabled
                rather than removed: the route exists and works, so this is a
                presentation hold, and deleting the button would lose the only
                pointer to it. Disabled + a badge says "not yet" honestly, where
                a live button that opens a surface we are not ready to show
                would not. Drop the `disabled` and the badge to restore it. */}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", background: "#F1F2F6", color: "#697180", border: "1px solid #E6E8EC", borderRadius: 999, padding: "3px 8px", whiteSpace: "nowrap" }}>{t('journeys.newJourneys.text341', "Coming soon")}</span>
              <button disabled title={t('journeys.newJourneys.title14', "Coming soon")} style={{ ...PRIMARY, opacity: 0.45, cursor: "default" }}>{t('journeys.newJourneys.text342', "Open the console →")}</button>
            </span>
          </>
        ) : (
          <>
            {/* ONE button (operator, 2026-09-02). Publish creates the record if
                it does not exist yet and then opens it — the same fold the board
                publish has always used. Two buttons made record-creation look
                like a separate decision, and it is not: a journey nobody can join
                is not published, whatever the record says. */}
            <button
              onClick={async () => {
                if (queueOriginId) { qLifecycle("open", t("journeys.newJourneys.lifecycle.published", "Queue published — it's live and taking customers")); return; }
                setPublishBusy("publish");
                try {
                  const r = await runAttach();
                  /* Only open it if the create succeeded. `runAttach` toasts its
                     own failure, so a refused create must not be followed by a
                     lifecycle call against a record that does not exist. */
                  if (r) qLifecycle("open", t("journeys.newJourneys.lifecycle.published", "Queue published — it's live and taking customers"), r.origin.id ?? null);
                } finally { setPublishBusy(null); }
              }}
              disabled={!qReady || !!publishBusy || attachBusy}
              style={{ ...PRIMARY, opacity: !qReady || publishBusy || attachBusy ? 0.55 : 1 }}
            >
              {publishBusy === "publish" ? busyLabel(q0.status === "paused" ? t("journeys.newJourneys.busy.resuming", "Resuming…") : t("journeys.newJourneys.busy.publishing", "Publishing…"), true) : q0.status === "paused" ? t('journeys.newJourneys.text343', "Resume the queue") : t('journeys.newJourneys.text344', "Publish the queue")}
            </button>
          </>
        )}
      </div>
      {q0.status === "open" && queueOriginId ? (
        <>
          <div style={{ height: 1, background: "#E6E8EC" }} />
          <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: 14, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#333333", marginBottom: 3 }}>{t('journeys.newJourneys.text345', "The customer's journey link")}</div>
              <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5, marginBottom: 8 }}>{t('journeys.newJourneys.text346', "Print it, share it, or put the QR on the desk — it opens this journey's first page.")}</div>
              {/* One link, one preview (2026-09-15, PP). Preview used to jump to
                  the in-Studio simulation of the journey pages — a different
                  surface from the link beside it, on the admin origin, which is
                  not what this card is about. It now opens the SAME url the card
                  shows and the QR encodes, so what you check is what you
                  published. Everything here is gated on the link existing: with
                  no resolved join host there is nothing to copy, open or encode. */}
              {qJoinUrl() ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ flex: 1, minWidth: 200, fontSize: 11.5, padding: "8px 10px", border: "1px solid #E6E8EC", borderRadius: 8, background: "#FBFAFF", ...MONO, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{qJoinUrl()}</span>
                  <button onClick={() => { void copyText(qJoinUrl()).then((ok) => showToast(ok ? t("journeys.newJourneys.toast.linkCopied", "Journey link copied") : t("journeys.newJourneys.toast.copyFailed", "Couldn't copy — select the link and copy it manually"))); }} style={GHOST}>{t('journeys.newJourneys.text347', "Copy link")}</button>
                  <button onClick={() => window.open(qJoinUrl(), "_blank", "noopener,noreferrer")} style={GHOST}>{i18nT("shell.newJourneys.preview", "Preview ↗")}</button>
                </div>
              ) : (
                <div style={{ fontSize: 11.5, color: "#916D0F", background: "#FAF1DE", border: "1px solid #EBD9AE", borderRadius: 8, padding: "8px 10px", lineHeight: 1.5 }}>
                  {i18nT("shell.newJourneys.noPublicLinkYetHost", "No public link yet — the tenant's join host hasn't resolved. Publish the queue's journey and the printable link and QR appear here.")}</div>
              )}
            </div>
            {qJoinUrl() ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center", flex: "none" }}>
                <div style={{ padding: 8, border: "1px solid #E6E8EC", borderRadius: 8, background: "#FFFFFF" }}><QrCode value={qJoinUrl()} size={112} /></div>
                <button onClick={() => { const ok = downloadQrPng(qJoinUrl(), `${(q0.name || "queue").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-qr`); showToast(ok ? t("journeys.newJourneys.toast.qrDownloaded", "QR downloaded — a print-ready PNG is in your downloads") : t("journeys.newJourneys.toast.qrFailed", "Couldn't generate the QR code")); }} style={GHOST}>{t('journeys.newJourneys.text348', "Download PNG")}</button>
              </div>
            ) : null}
          </div>
          {/* COMING SOON (2026-09-16, PP) — the live-line section is held, not
              removed. Its reads and sends are all real and verified
              (getQueueBoard / sendQueueMessage / broadcastQueueMessage), so
              this is a presentation hold: the roster still renders, so an
              author can see what the section is about, but every control is
              inert and the body is dimmed and pointer-inert so nothing can be
              half-used. Remove the wrapper's opacity/pointerEvents and the
              badge to restore it. */}
          <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#333333" }}>{t("journeys.newJourneys.inQueueNow", "In the queue now — {count}", { count: qActive.length })}</div>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", background: "#F1F2F6", color: "#697180", border: "1px solid #E6E8EC", borderRadius: 999, padding: "3px 8px", whiteSpace: "nowrap" }}>{t('journeys.newJourneys.text341', "Coming soon")}</span>
                </div>
                <div style={{ fontSize: 11.5, color: "#697180", marginTop: 2 }}>{t('journeys.newJourneys.text350', "Live from the queue board, refreshed every 10 seconds.")}</div>
              </div>
              <button disabled title={t('journeys.newJourneys.title14', "Coming soon")} style={{ ...PRIMARY, opacity: 0.45, cursor: "default" }}>{t('journeys.newJourneys.text263', "Message everyone")}</button>
            </div>
            <div style={{ opacity: 0.45, pointerEvents: "none", userSelect: "none" }} aria-disabled="true">
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {qActive.map((e) => (
                <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", border: "1px solid #E6E8EC", borderRadius: 9 }}>
                  {e.ticket ? <span style={{ fontSize: 11.5, fontWeight: 700, color: "#333333", flex: "none", ...MONO }}>{e.ticket}</span> : null}
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: "#333333", flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.name}{e.party > 1 ? <span style={{ color: "#697180", fontWeight: 500 }}> +{e.party - 1}</span> : null}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: e.status === "serving" ? "#E4F5EF" : e.status === "called" ? "#ECF1FE" : "#F4F5F7", color: e.status === "serving" ? "#1F8A5B" : e.status === "called" ? "#2D60EB" : "#697180", flex: "none" }}>{e.status === "serving" ? t('journeys.newJourneys.text351', "Serving") : e.status === "called" ? t('journeys.newJourneys.text352', "Called") : t('journeys.newJourneys.text353', "#{position}", { position: e.position })}</span>
                  <span style={{ fontSize: 11, color: "#697180", flex: "none", ...MONO }}>{t("journeys.newJourneys.waitMinutesShort", "{count}m", { count: e.waitMins })}</span>
                  <button disabled style={{ ...GHOST, cursor: "default" }}>{t('journeys.newJourneys.text265', "Message")}</button>
                </div>
              ))}
              {!qActive.length ? <div style={{ padding: "14px 12px", border: "1px dashed #D1D4DC", borderRadius: 9, fontSize: 12, color: "#697180", textAlign: "center" }}>{t('journeys.newJourneys.text355', "Nobody in the line right now.")}</div> : null}
            </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );

  /* ══════════════════════════════════════════════════════════════════
     Display board — Setup · Configuration (the board builder's options)
     ══════════════════════════════════════════════════════════════════ */
  const bDark = b0.theme === "dark";
  const bC = boardColors(bDark);
  const bUsedTypes = new Set(b0.blocks.map((x) => x.type));
  const bSelected = b0.blocks.find((x) => x.uid === ui.boardSel) ?? null;
  const bSelectedDef = bSelected ? BOARD_BLOCKS.find((d) => d.id === bSelected.type) ?? null : null;
  const bAddBlock = (type: string, beforeUid?: string | null) => {
    const def = BOARD_BLOCKS.find((d) => d.id === type);
    if (!def || (!def.multi && bUsedTypes.has(type))) return;
    const nb: BoardBlockRec = { uid: boardBlockUid(), type, cfg: { ...(def.defaults ?? {}) } };
    patchBoard((b) => {
      const i = beforeUid ? b.blocks.findIndex((x) => x.uid === beforeUid) : -1;
      return { ...b, blocks: i < 0 ? [...b.blocks, nb] : [...b.blocks.slice(0, i), nb, ...b.blocks.slice(i)] };
    });
    set({ boardSel: nb.uid });
  };
  const bRemoveBlock = (uid: string) => { patchBoard((b) => ({ ...b, blocks: b.blocks.filter((x) => x.uid !== uid) })); if (ui.boardSel === uid) set({ boardSel: null }); };
  const bMoveBlock = (uid: string, dir: number) => patchBoard((b) => {
    const i = b.blocks.findIndex((x) => x.uid === uid); const j = i + dir;
    if (i < 0 || j < 0 || j >= b.blocks.length) return b;
    const next = b.blocks.slice(); const [it] = next.splice(i, 1); next.splice(j, 0, it!);
    return { ...b, blocks: next };
  });
  /* ── One-Stop · Configuration ──────────────────────────────────────────
   *  The Setup step a One-Stop journey was missing. A Display Board has one for
   *  the same reason: the page itself is composed on the canvas, but the things
   *  that are true of the WHOLE page — its name, its URL, the language and zone
   *  it renders in, and which of the tenant's journeys it offers — are not
   *  blocks and have nowhere on a canvas to live.
   *
   *  The journey picker is the part worth explaining. It does not store a list.
   *  Ticking a journey writes its origin id into the SCOPE of the matching live
   *  block — a queue journey into `queues`, an appointment journey into
   *  `appointments`, an event journey into `events` — creating that block if the
   *  page has none. So the page keeps exactly one source of truth (the blocks),
   *  and the picker is a faster way to reach it than selecting each block and
   *  filling its scope by hand. Untick and the id comes out; the block stays,
   *  because removing a block an author placed is not what unticking a journey
   *  asked for.
   */

  const setupOneStopCfg = (
    <div style={{ display: "flex", gap: 16, maxWidth: 1120, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>
          {t('journeys.newJourneys.text356', "What is true of the whole page: what it is called, where it lives, and which of your journeys a student can reach from it. The page itself is composed on the canvas.")}</div>

        <div>
          {reqLabel("Page name")}
          <Input value={D?.name ?? ""} onChange={(e: any) => patchDoc((d) => ({ ...d, name: e.target.value }))} placeholder={t('journeys.newJourneys.placeholder21', "e.g. Student Center One-Stop")} />
          <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>{t('journeys.newJourneys.text357', "Shown in the journeys list, and used as the page heading unless a block overrides it.")}</div>
        </div>

        <div>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text358', "Web address")}</label>
          <div style={{ display: "flex", alignItems: "center", gap: 0, border: "1px solid #D1D4DC", borderRadius: 8, overflow: "hidden", background: onestopOriginId ? "#FAFAFD" : "#FFFFFF" }}>
            <span style={{ padding: "8px 0 8px 10px", fontSize: 12, color: "#8A92A0", flex: "none", ...MONO }}>{t('journeys.newJourneys.text359', "onestop/")}</span>
            <input
              value={osCfg.slug ?? slugFromTitle(D?.name || "")}
              readOnly={!!onestopOriginId}
              onChange={(e) => patchOsSettings({ slug: slugFromTitle(e.target.value) })}
              style={{ flex: 1, minWidth: 0, border: "none", outline: "none", padding: "8px 10px 8px 0", fontSize: 12.5, fontFamily: FONT, color: onestopOriginId ? "#697180" : "#333333", background: "transparent" }}
            />
          </div>
          <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>
            {onestopOriginId
              ? t('journeys.newJourneys.text360', "Fixed once the page exists — it is the URL students have, so changing it would break every link to it.")
              : t('journeys.newJourneys.text361', "Taken from the name if you leave it. It becomes fixed when the page is created.")}
          </div>
        </div>

        <div>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text362', "Navigation label")}</label>
          <Input value={osCfg.navLabel ?? ""} onChange={(e: any) => patchOsSettings({ navLabel: e.target.value || null })} placeholder={t('journeys.newJourneys.placeholder22', "Leave empty to use the page name")} />
          <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>{t('journeys.newJourneys.text363', "How this page is named in the portal's own navigation, where it sits beside your other One-Stop pages.")}</div>
        </div>


        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={setupFieldLabel}>{t('journeys.newJourneys.text364', "Language")}</label>
            <Dropdown
              options={["en-US", "es-US", "fr-CA", "de-DE", "it-IT"].map((v) => ({ value: v, label: v }))}
              value={osCfg.locale ?? "en-US"}
              onChange={(v: string) => patchOsSettings({ locale: v, supportedLocales: [...new Set([v, ...(osCfg.supportedLocales ?? [])])] })}
            />
            <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>{t('journeys.newJourneys.text365', "The language your text is written in. Students see their own where a translation exists.")}</div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {reqLabel("Time zone")}
            <TimeZonePicker value={osCfg.timeZone ?? tzContext().zone} onChange={(v: string) => patchOsSettings({ timeZone: v })} />
            <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>{t('journeys.newJourneys.text366', "Every opening time and date on the page is shown in this zone.")}</div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 16 }}>
          {reqLabel("What this page offers")}
          <div style={{ fontSize: 11, color: "#697180", margin: "0 0 12px", lineHeight: 1.5 }}>
            {t('journeys.newJourneys.text367', "Three lists, one per block. Tick what this page should offer — each list feeds the matching block on the page, and adds that block if the page has none yet.")}</div>
          {osJourneyRows.length === 0 ? (
            <div style={{ border: "1px dashed #D1D4DC", borderRadius: 8, padding: "16px 12px", textAlign: "center", fontSize: 11.5, color: "#697180", lineHeight: 1.55 }}>
              {t('journeys.newJourneys.text368', "Nothing to offer yet. Create a bookable service, a queue or an event and it will appear here.")}</div>
          ) : (
            /* Three CatalogPickers, one per block (operator, 2026-09-02). Same
               component as every other picker in the designer, with grouping
               OFF: a One Stop page's three lists are each one flat kind by
               design, and the ruling was that this is the exception to the
               department breakdown. Each is ordered — the order here is the
               order the blocks sit on the page. */
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, alignItems: "start" }}>
              {/* ui-config: picker group keys (compared below); displayed through the lookup at sourceLabel */}
              {["Bookable services", "Queues", "Events"].map((group) => {
                const rows = osJourneyRows.filter((r) => r.group === group);
                const kind = rows[0]?.kind ?? (group === "Queues" ? "queue" : group === "Events" ? "event" : "service");
                const picked = osScopeIds(kind);
                return (
                  <CatalogPicker
                    key={group}
                    dense
                    grouped={false}
                    allowGroups={false}
                    /* `r.label` is not a field on these rows — they carry `name`
                 (2026-09-08). The `??` chain read as a tolerant fallback while
                 the first term could never be anything but undefined. */
              items={rows.map((r) => ({ id: r.id, name: r.name ?? r.id, meta: [r.sub ?? null], keywords: r.sub ?? "" }))}
                    value={picked.map((id) => ({ type: "item" as const, id }))}
                    onChange={(next) => {
                      const want = next.filter((s) => s.type === "item").map((s) => s.id);
                      /* osToggleJourney owns block creation and removal, so the
                         diff is applied through it rather than rebuilding the
                         block list here. */
                      picked.filter((id) => !want.includes(id)).forEach((id) => osToggleJourney(kind, id));
                      want.filter((id) => !picked.includes(id)).forEach((id) => osToggleJourney(kind, id));
                    }}
                    sourceLabel={({ "Bookable services": t("journeys.newJourneys.osGroup.bookable", "Bookable services"), Queues: t("journeys.newJourneys.osGroup.queues", "Queues"), Events: t("journeys.newJourneys.osGroup.events", "Events") } as Record<string, string>)[group] ?? group}
                    selectedLabel={t("journeys.newJourneys.osPicker.selected", "On this page")}
                    noun={group === "Queues" ? "queue" : group === "Events" ? "event" : "service"}
                    searchPlaceholder={t("journeys.newJourneys.osPicker.search", "Search…")}
                    emptySource={t("journeys.newJourneys.osPicker.emptySource", "None yet.")}
                    emptySelection={t("journeys.newJourneys.osPicker.emptySelection", "Nothing from this list yet.")} />
                );
              })}
            </div>
          )}
          <div style={{ fontSize: 10.5, color: "#697180", marginTop: 10, lineHeight: 1.5 }}>
            {t('journeys.newJourneys.text369', "The page stores what to show, never a copy of it — rename a queue or add a time to a service and this page follows without being republished.")}</div>
        </div>
      </div>

      <div style={{ width: 260, flex: "none", border: "1px solid #E6E8EC", borderRadius: 10, padding: 14, background: "#FFFFFF" }}>
        <div style={{ ...SEC, marginBottom: 9 }}>{t('journeys.newJourneys.text370', "On this page")}</div>
        {osBlocks.length === 0 ? (
          <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.55 }}>{t('journeys.newJourneys.text371', "Nothing on this page yet — that is the right place to start. Tick what it should offer below, and a block appears for each one.")}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {osBlocks.map((b) => (
              <div key={b.uid} style={{ display: "flex", alignItems: "baseline", gap: 7, fontSize: 11.5 }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: "var(--jrni-color-primary-1, #5142C7)", flex: "none" }} />
                <span style={{ flex: 1, minWidth: 0, color: "#333333" }}>{osLabel(b.type)}</span>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => set({ setupView: null, view: "pages" })} style={{ ...GHOST, width: "100%", marginTop: 12 }}>{t('journeys.newJourneys.text372', "Open the page canvas →")}</button>
        {/* Start over (operator, 2026-09-02: "start with nothing selected, let me
            make all selections from the beginning").
            A NEW One-Stop journey has started empty since this morning, but a page
            created before that still carries the seven blocks the old seed made —
            including three types the palette no longer offers, so they can be seen
            and removed but not put back. This empties the page in one act instead
            of asking for seven deletions.
            No confirmation dialog: it is one undo away, and the toast says so.
            Guarding a reversible action with a modal trains people to dismiss
            modals. */}
        {osBlocks.length > 0 ? (
          <button
            onClick={() => {
              const n = osBlocks.length;
              setOsBlocks([]);
              set({ selSection: null });
              showToast(t("journeys.newJourneys.toast.clearedBlocks", "{count, plural, one {Cleared # block — undo brings them back} other {Cleared # blocks — undo brings them back}}", { count: n }));
            }}
            style={{ width: "100%", marginTop: 8, height: 30, border: "1px solid #F3D0CB", borderRadius: 8, background: "#FFFFFF", color: "#C0392B", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
          >
            {t('journeys.newJourneys.text373', "Clear the page and start over")}</button>
        ) : null}
      </div>
    </div>
  );

  const bPatchCfg = (uid: string, patch: Record<string, unknown>) => patchBoard((b) => ({ ...b, blocks: b.blocks.map((x) => (x.uid === uid ? { ...x, cfg: { ...x.cfg, ...patch } } : x)) }));
  const bRadio = (on: boolean) => ({ flex: 1, minHeight: 32, borderRadius: 8, border: `1.5px solid ${on ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}`, background: on ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", color: on ? "var(--jrni-color-primary-text, #5142C7)" : "#333333", fontSize: 11.5, fontWeight: 600, fontFamily: FONT, cursor: "pointer" } as const);
  const setupBoardCfg = (
    <div style={{ display: "flex", gap: 16, maxWidth: 900, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>{t('journeys.newJourneys.text374', "Everything the lobby screen shows: which queues, what content, how queuers are named, and the welcome line underneath.")}</div>
        <div>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text375', "Board name")}{" "}<span style={{ color: "#D01647" }}>*</span></label>
          <Input value={b0.name ?? ""} onChange={(e: any) => patchBoard((b) => ({ ...b, name: e.target.value }))} placeholder={t('journeys.newJourneys.placeholder23', "e.g. Student Center — lobby TV")} />
        </div>
        <div>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text376', "Queues shown")}<span style={{ color: "#D01647" }}>*</span></label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(queues as any[]).map((qq) => {
              const on = b0.queueIds.includes(qq.id);
              return (
                <button key={qq.id} onClick={() => patchBoard((b) => ({ ...b, queueIds: on ? b.queueIds.filter((x) => x !== qq.id) : [...b.queueIds, qq.id] }))}
                  style={{ minHeight: 30, padding: "0 11px", borderRadius: 999, border: `1.5px solid ${on ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}`, background: on ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", color: on ? "var(--jrni-color-primary-text, #5142C7)" : "#333333", fontSize: 11.5, fontWeight: 600, fontFamily: FONT, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
                  {on ? "✓" : "+"} {qq.name}
                </button>
              );
            })}
            {!(queues as any[]).length ? <span style={{ fontSize: 12, color: "#B5BAC6" }}>{t('journeys.newJourneys.text377', "No queues yet — create one first.")}</span> : null}
          </div>
        </div>
        <div>
          {reqLabel("Fixed content", t("journeys.newJourneys.why.fixedContent", "At least one block is required"))}
          <div style={{ fontSize: 11, color: "#697180", margin: "0 0 8px", lineHeight: 1.5 }}>{t('journeys.newJourneys.text378', "These blocks compose each queue's card, top to bottom. Click one in the list to configure it.")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
            {BOARD_BLOCKS.map((bb) => {
              const used = !bb.multi && bUsedTypes.has(bb.id);
              return (
                <button key={bb.id} onClick={() => bAddBlock(bb.id)} disabled={used} title={bb.blurb}
                  style={{ textAlign: "left", padding: "9px 10px", border: `1px solid ${used ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}`, background: used ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", borderRadius: 8, cursor: used ? "default" : "pointer", fontFamily: FONT }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: used ? "var(--jrni-color-primary-text, #5142C7)" : "#333333" }}>{bb.l}{used ? " ✓" : ""}</div>
                  <div style={{ fontSize: 10.5, color: "#697180", marginTop: 2, lineHeight: 1.4 }}>{bb.blurb}</div>
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {b0.blocks.map((bb, i) => {
              const def = BOARD_BLOCKS.find((d) => d.id === bb.type);
              const on = ui.boardSel === bb.uid;
              return (
                <div key={bb.uid} onClick={() => set({ boardSel: on ? null : bb.uid })}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `1px solid ${on ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}`, background: on ? "#FCFBFF" : "#FFFFFF", borderRadius: 8, cursor: "pointer" }}>
                  <span style={{ fontSize: 10.5, color: "#B5BAC6", flex: "none", ...MONO }}>{i + 1}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 700, color: "#333333" }}>{def?.l ?? bb.type}</span>
                  <button onClick={(e) => { e.stopPropagation(); bMoveBlock(bb.uid, -1); }} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", fontSize: 11 }}>▲</button>
                  <button onClick={(e) => { e.stopPropagation(); bMoveBlock(bb.uid, 1); }} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", fontSize: 11 }}>▼</button>
                  <button onClick={(e) => { e.stopPropagation(); bRemoveBlock(bb.uid); }} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", fontSize: 11 }}>✕</button>
                </div>
              );
            })}
            {!b0.blocks.length ? <div style={{ padding: "14px 12px", border: "1px dashed #D1D4DC", borderRadius: 9, fontSize: 12, color: "#697180", textAlign: "center" }}>{t('journeys.newJourneys.text379', "No content yet — add a block above.")}</div> : null}
          </div>
        </div>
        <div>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text380', "Theme")}</label>
          <div style={{ display: "flex", gap: 8, maxWidth: 300 }}>
            <button onClick={() => patchBoard((b) => ({ ...b, theme: "dark" }))} style={bRadio(bDark)}>{t('journeys.newJourneys.text381', "Dark")}</button>
            <button onClick={() => patchBoard((b) => ({ ...b, theme: "light" }))} style={bRadio(!bDark)}>{t('journeys.newJourneys.text382', "Light")}</button>
          </div>
        </div>
        <div>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text383', "Queuer display")}</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, maxWidth: 360 }}>
            {([["full", t("journeys.newJourneys.nameFormat.full", "Full name")], ["first-initial", t("journeys.newJourneys.nameFormat.firstInitial", "First + initial")], ["first", t("journeys.newJourneys.nameFormat.first", "First only")], ["ticket", t("journeys.newJourneys.nameFormat.ticket", "Ticket only")]] as [BoardNameFormat, string][]).map(([id, lbl]) => (
              <button key={id} onClick={() => patchBoard((b) => ({ ...b, nameFormat: id }))} style={{ ...bRadio(b0.nameFormat === id), minHeight: 30 }}>{lbl}</button>
            ))}
          </div>
          {b0.nameFormat === "ticket" ? <div style={{ fontSize: 11, color: "#697180", marginTop: 5, lineHeight: 1.5 }}>{t('journeys.newJourneys.text384', "Tickets only — no names on screen. Queuers without a ticket fall back to a shortened name.")}</div> : null}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, padding: "10px 12px", border: "1px solid #E6E8EC", borderRadius: 9, maxWidth: 360 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: "#333333" }}>{t('journeys.newJourneys.text385', "Show wait estimates")}</span>
            <Toggle on={!!b0.showWaitEstimates} onChange={() => patchBoard((b) => ({ ...b, showWaitEstimates: !b.showWaitEstimates }))} />
          </div>
        </div>
        <div>
          <label style={setupFieldLabel}>{t('journeys.newJourneys.text386', "Welcome message")}</label>
          <textarea value={b0.message} onChange={(e) => patchBoard((b) => ({ ...b, message: e.target.value }))} rows={2} style={{ ...INPUT, resize: "vertical" as const, minHeight: 54 }} placeholder={t('journeys.newJourneys.placeholder24', "Shown under the board, across the full width")} />
        </div>
        <div style={NOTE}>{t('journeys.newJourneys.text387', "The board is a library object: this configuration saves through the boards library, so the lobby display and this journey always show the same thing.")}</div>
      </div>
      {bSelected && bSelectedDef ? (
        <div style={{ width: 240, flex: "none", background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ ...SEC, marginBottom: 0, flex: 1 }}>{t("journeys.newJourneys.boardBlockHeading", "Block · {name}", { name: bSelectedDef.l })}</span>
            <button onClick={() => set({ boardSel: null })} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", fontSize: 11 }}>✕</button>
          </div>
          {"label" in bSelected.cfg || ["now-serving", "called", "up-next"].includes(bSelected.type) ? (
            <div><label style={setupFieldLabel}>{t('journeys.newJourneys.text130', "Heading")}</label><Input value={String(bSelected.cfg.label ?? "")} onChange={(e: any) => bPatchCfg(bSelected.uid, { label: e.target.value })} /></div>
          ) : null}
          {bSelected.type === "called" || bSelected.type === "up-next" ? (
            <div><label style={setupFieldLabel}>{t('journeys.newJourneys.text389', "People shown")}</label><NumberField value={Number(bSelected.cfg.max ?? (bSelected.type === "called" ? 3 : 6))} min={1} onChange={(n: number) => bPatchCfg(bSelected.uid, { max: n })} ariaLabel={t('journeys.newJourneys.ariaLabel3', "People shown")} style={INPUT as any} /></div>
          ) : null}
          {bSelected.type === "hours" ? (
            <div><label style={setupFieldLabel}>{t('journeys.newJourneys.text390', "Prefix")}</label><Input value={String(bSelected.cfg.prefix ?? t("queues.boardBlocks.text9", "Open"))} onChange={(e: any) => bPatchCfg(bSelected.uid, { prefix: e.target.value })} /></div>
          ) : null}
          {bSelected.type === "text" ? (
            <div><label style={setupFieldLabel}>{t('journeys.newJourneys.text391', "Text")}</label><textarea value={String(bSelected.cfg.text ?? "")} onChange={(e) => bPatchCfg(bSelected.uid, { text: e.target.value })} rows={3} style={{ ...INPUT, resize: "vertical" as const, minHeight: 62 }} /></div>
          ) : null}
          {bSelected.type === "stats" ? <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5 }}>{t("journeys.newJourneys.boardStatsNote", "{wait, select, yes {Shows the waiting count and the estimated wait — both come from the live queue.} other {Shows the waiting count — both come from the live queue.}}", { wait: b0.showWaitEstimates ? "yes" : "no" })}</div> : null}
          {bSelected.type === "services" ? <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5 }}>{t('journeys.newJourneys.text395', "Per-service waiting counts, live from each queue's own service list.")}</div> : null}
        </div>
      ) : null}
    </div>
  );

  const setupBoardPublish = (
    <div style={setupCard}>
      <div style={{ border: `1px solid ${bReady ? "#A7DACB" : "#F3D0CB"}`, background: bReady ? "#F1FAF6" : "#FDECEA", borderRadius: 10, padding: "12px 14px" }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: bReady ? "#1F8A5B" : "#C0392B", marginBottom: 8 }}>{bReady ? t('journeys.newJourneys.text396', "This board is ready for the lobby screen") : t('journeys.newJourneys.text397', "Finish the configuration first")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {bChecks.map((c) => (
            <div key={c.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
              <span style={{ color: c.done ? "#1F8A5B" : "#C0392B", flex: "none" }}>{c.done ? "✓" : "✕"}</span>
              <span style={{ flex: 1, color: c.done ? "#333333" : "#C0392B", fontWeight: c.done ? 500 : 600 }}>{c.label}</span>
              {!c.done ? <button onClick={() => set({ setupView: "boardcfg" })} style={{ border: "none", background: "transparent", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{t('journeys.newJourneys.text268', "Fix →")}</button> : null}
            </div>
          ))}
        </div>
      </div>
      {boardOriginId ? (
        <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#333333", marginBottom: 3 }}>{t('journeys.newJourneys.text398', "Display link")}</div>
          <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5, marginBottom: 8 }}>{t('journeys.newJourneys.text399', "Open it full-screen on the lobby screen. The link is token-authorized — copying mints a fresh token.")}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ flex: 1, minWidth: 200, fontSize: 11.5, padding: "8px 10px", border: "1px solid #E6E8EC", borderRadius: 8, background: "#FBFAFF", ...MONO, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{boardPublicUrl(boardOriginId)}</span>
            <button onClick={async () => {
              try {
                const minted: any = await mintBoardDisplayUrl(boardOriginId);
                const url = `${boardPublicUrl(boardOriginId)}${boardPublicUrl(boardOriginId).includes("?") ? "&" : "?"}t=${encodeURIComponent(minted.token)}`;
                showToast((await copyText(url)) ? t("journeys.newJourneys.toast.displayLinkCopied", "Display link copied") : url);
              } catch { showToast(t("journeys.newJourneys.toast.displayLinkFailed", "Couldn't create a display link — publishing one needs the queues publish permission")); }
            }} style={GHOST}>{t('journeys.newJourneys.text400', "Copy display link")}</button>
            <button onClick={async () => {
              try { const minted: any = await mintBoardDisplayUrl(boardOriginId); nav.go("queue.display", { b: boardOriginId, t: minted.token }); }
              catch { showToast(t("journeys.newJourneys.toast.displayOpenFailed", "Couldn't open the display — publishing a link needs the queues publish permission")); }
            }} style={PRIMARY}>{t('journeys.newJourneys.text401', "Open the display →")}</button>
          </div>
        </div>
      ) : (
        <div style={{ border: "1px dashed #B9A8F0", background: "#FCFBFF", borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#333333", marginBottom: 4 }}>{t('journeys.newJourneys.text402', "Not in the boards library yet")}</div>
          <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.55, marginBottom: 10 }}>{t('journeys.newJourneys.text403', "Saving it creates the board and its display link. Its configuration is already held on this journey, so nothing is lost either way.")}</div>
          <button disabled={!bReady || !!publishBusy || attachBusy} onClick={async () => {
            setPublishBusy("publish");
            try {
              /* Goes through the shared attach path so the journey is re-keyed
                 onto the board it just created — otherwise the board exists in
                 the library while its journey still reads as unattached. */
              const r = await runAttach();
              if (r) { const list: any = await listMessageBoards(); setBoards(list?.boards ?? list?.data ?? []); }
            } finally { setPublishBusy(null); }
          }} style={{ ...PRIMARY, opacity: !bReady || publishBusy || attachBusy ? 0.55 : 1 }}>{publishBusy ? busyLabel(t("journeys.newJourneys.busy.saving", "Saving…"), true) : t('journeys.newJourneys.text404', "Save to the boards library")}</button>
        </div>
      )}
      <div>
        {([
          [t("journeys.newJourneys.boardSummary.queues", "Queues shown"), b0.queueIds.map((id) => (queues as any[]).find((qq) => qq.id === id)?.name ?? id).join(", ") || "—"],
          [t("journeys.newJourneys.boardSummary.theme", "Theme"), b0.theme === "dark" ? t("journeys.newJourneys.boardSummary.dark", "Dark") : t("journeys.newJourneys.boardSummary.light", "Light")],
          [t("journeys.newJourneys.boardSummary.content", "Content"), b0.blocks.map((x) => BOARD_BLOCKS.find((d) => d.id === x.type)?.l ?? x.type).join(" · ") || "—"],
          [t("journeys.newJourneys.boardSummary.queuerDisplay", "Queuer display"), (({ full: t("journeys.newJourneys.nameFormat.full", "Full name"), "first-initial": t("journeys.newJourneys.nameFormat.firstInitial", "First + initial"), first: t("journeys.newJourneys.nameFormat.first", "First only"), ticket: t("journeys.newJourneys.nameFormat.ticket", "Ticket only") } as any)[b0.nameFormat] ?? "") + (b0.showWaitEstimates ? " · " + t("journeys.newJourneys.boardSummary.waitOn", "wait estimates on") : "")],
          [t("journeys.newJourneys.boardSummary.welcome", "Welcome message"), b0.message || "—"],
        ] as [string, string][]).map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 12.5, borderBottom: "1px solid #E6E8EC", padding: "8px 0" }}>
            <span style={{ color: "#697180", flex: "none" }}>{k}</span><span style={{ fontWeight: 600, color: "#333333", textAlign: "right" }}>{v}</span>
          </div>
        ))}
      </div>
      {boardOriginId ? (
        <button onClick={async () => {
          if (!window.confirm(t("journeys.newJourneys.confirmDeleteBoard", "Delete this display board? The lobby link stops working immediately."))) return;
          try { await deleteBoardConfig(boardOriginId); const r: any = await listMessageBoards(); setBoards(r?.boards ?? r?.data ?? []); set({ route: "home" }); showToast(t("journeys.newJourneys.toast.boardDeleted", "Board deleted")); }
          catch (e) { showToast(t("journeys.newJourneys.toast.boardDeleteFailed", "Couldn't delete the board — {error}", { error: e instanceof Error ? e.message : String(e) })); }
        }} style={{ ...GHOST, color: "#C0392B", alignSelf: "flex-start" }}>{t('journeys.newJourneys.text405', "Delete this board")}</button>
      ) : null}
    </div>
  );

  /** Board canvas — the lobby display itself, fed by the real queue config
   *  plus sample customers (never data: a preview can't call people forward). */
  const BOARD_SAMPLE = ["Maria Gonzales", "Marcus Lee", "Priya Shah", "Jordan Ellis", "Sofia Marino", "Devon Clark", "Amara Diallo", "Theo Brandt"]; // ui-config: sample customers in the board preview, not copy
  const sampleBoardQueues = () => b0.queueIds.map((id, i) => {
    const qq = (queues as any[]).find((x) => x.id === id);
    if (!qq) return null;
    const pfx = qq.ticketPrefix || String(qq.name ?? "Q").charAt(0).toUpperCase();
    const person = (n: number, position: number) => ({ name: BOARD_SAMPLE[(i * 3 + n) % BOARD_SAMPLE.length]!, ticket: `${pfx}-${104 + i * 20 + n}`, position });
    return {
      id: qq.id, name: qq.name, hoursLabel: qHoursLabel(qq.hours, qq.hoursAuthored),
      nowServing: person(0, 0), called: [person(1, 1), person(2, 2)],
      upNext: [person(3, 1), person(4, 2), person(5, 3)],
      services: ((qq.services ?? []).length ? qq.services : ["Walk-in"]).slice(0, 4).map((s: string, si: number) => ({ name: s, waiting: 1 + ((i + si) % 3) })), // ui-config: sample service in the board preview when a queue has none
      waiting: 4 + i * 2, estWaitMins: 12 + i * 4,
    };
  }).filter(Boolean) as any[];
  /** Orientation of the board canvas (2026-09-02, operator). A board is always
   *  full screen, so the canvas is a real SCREEN BOX at true pixel size —
   *  1280×720 landscape, 720×1280 portrait — laid out at that size and then
   *  scaled as a whole to fit the pane. Sizing the box to the pane instead (an
   *  aspect-ratio frame with 1fr rows) squeezed the queue cards below their
   *  natural height and every card's content overflowed across its neighbours;
   *  laying out at true size and scaling keeps type at the proportions the
   *  lobby screen actually renders.
   *
   *  The measuring hooks live at the top of the component with the other state,
   *  NOT here: this point is past the screen's early returns, and a hook behind
   *  a return is React error #310 on the first render that takes the short path. */
  const bLand = ui.boardOrientation === "landscape";
  const B_W = bLand ? 1280 : 720;
  const B_H = bLand ? 720 : 1280;
  /* Fit the whole screen box into the pane, with a LEGIBILITY FLOOR. Pure
     fit-both put the board at 32% in a short window, where the 17px card title
     renders at about 5px and the ticket lines go sub-pixel — complete but
     unreadable, which is not a preview. Below the floor the board fits its WIDTH
     instead and the pane scrolls, so the type stays legible and the rest of the
     screen is one scroll away rather than shrunk into illegibility. */
  const B_FLOOR = 0.5;
  const bFitBoth = boardFit.w > 0 && boardFit.h > 0 ? Math.min(boardFit.w / B_W, boardFit.h / B_H) : 1;
  const bFitsPane = bFitBoth >= B_FLOOR;
  const bScale = bFitsPane ? bFitBoth : (boardFit.w > 0 ? Math.max(B_FLOOR, boardFit.w / B_W) : 1);
  const bCols = bColCount;
  const bPageQueues = sampleBoardQueues().slice(bPager.page * bPager.perPage, bPager.page * bPager.perPage + bPager.perPage);
  /** The board screen itself at true pixel size, scaled as a whole. Shared by
   *  the canvas and the Preview overlay so the two cannot drift — Preview used
   *  to fall through to the page-card renderer, which a board has no content
   *  for, and showed an empty white card (operator, 2026-09-02). */
  const boardScreen = (scale: number) => (
    <div style={{ width: B_W * scale, height: B_H * scale, borderRadius: 14 * Math.min(1, scale), overflow: "hidden", boxShadow: "0 2px 14px rgba(20,19,31,0.14)", flex: "none" }}>
      <div style={{ width: B_W, height: B_H, transform: `scale(${scale})`, transformOrigin: "top left", background: bC.bg, color: bC.fg, fontFamily: bC.font, padding: bLand ? "30px 34px 24px" : "34px 26px 26px", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
        {/* Portrait stacks the masthead: at 9:16 the org name, board name and
            clock on one baseline crush each other, and the clock is the one
            thing a waiting customer looks for. */}
        <div style={{ display: "flex", alignItems: bLand ? "baseline" : "flex-start", flexDirection: bLand ? "row" : "column", gap: bLand ? 14 : 3, marginBottom: bLand ? 24 : 22, flexShrink: 0 }}>
          <span style={{ fontSize: bLand ? 26 : 30, fontWeight: 800, letterSpacing: -0.3 }}>{tenant?.name ?? t('journeys.newJourneys.text406', "Your organization")}</span>
          <span style={{ fontSize: bLand ? 15 : 17, color: bC.soft }}>{b0.name || t('journeys.newJourneys.text407', "Untitled board")}</span>
          <span style={{ marginLeft: bLand ? "auto" : 0, marginTop: bLand ? 0 : 4, fontSize: bLand ? 15 : 20, fontWeight: bLand ? 400 : 700, color: bC.soft }}>{new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
        </div>
        {sampleBoardQueues().length ? (
          /* One PAGE of queues, sized from the real grid and real card heights
             (useBoardPager) so nothing is cropped, with the remainder cycling.
             `gridRef` measures the space available; `cardRef` on the first card
             measures what a card actually costs. Rows take their natural
             height — forcing them to share the space made cards overlap. */
          <div ref={bPager.gridRef} style={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: `repeat(${bCols}, minmax(0, 1fr))`, gridAutoRows: "min-content", alignContent: "start", gap: bLand ? 20 : 16, overflow: "hidden" }}>
            {bPageQueues.map((qq, i) => (
              <div key={qq.id} ref={i === 0 ? bPager.cardRef : undefined} style={{ display: "grid", minWidth: 0 }}>
                <BoardQueueCard q={qq} blocks={b0.blocks as BoardBlockRec[]} C={bC} dark={bDark} nameFormat={b0.nameFormat as BoardNameFormat} showWait={!!b0.showWaitEstimates} />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ margin: "auto", fontSize: 18, color: bC.soft, textAlign: "center", lineHeight: 1.6 }}>{t('journeys.newJourneys.text408', "No queues picked yet.")}<br />{t('journeys.newJourneys.text409', "Choose which queues this board shows in Setup · Configuration.")}</div>
        )}
        {b0.message ? <div style={{ marginTop: bLand ? 20 : 18, textAlign: "center", fontSize: bLand ? 17 : 19, color: bC.soft, flexShrink: 0 }}>{b0.message}</div> : null}
        {/* Page dots, on the screen itself — a waiting customer needs to know
            another page of queues is coming, not wonder where theirs went. */}
        {bPager.pages > 1 ? (
          <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 7, flexShrink: 0 }}>
            {Array.from({ length: bPager.pages }).map((_, i) => (
              <span key={i} style={{ width: 8, height: 8, borderRadius: 999, background: i === bPager.page ? bC.fg : bC.border }} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
  /* No advisory strip and no "Configure this board" button (operator,
     2026-09-02): the Setup · Configuration chip directly above is the same
     destination, and a full-width purple row explaining where configuration
     lives cost the canvas a band of height on every board, every visit. */
  const boardPageView = (
    <div style={{ flex: 1, minHeight: 0, overflow: "hidden", background: "#F1F2F6", padding: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div ref={boardFitRef} style={{ flex: 1, minHeight: 0, width: "100%", overflow: bFitsPane ? "hidden" : "auto", display: "flex", alignItems: bFitsPane ? "center" : "flex-start", justifyContent: "center" }}>
        {boardScreen(bScale)}
      </div>
      <div style={{ textAlign: "center", fontSize: 10.5, color: "#B5BAC6", flexShrink: 0, ...MONO }}>{bLand ? t('journeys.newJourneys.text410', "16:9 · {B_W}×{B_H} — a wall-mounted screen", { B_W, B_H }) : t('journeys.newJourneys.text411', "9:16 · {B_W}×{B_H} — a screen turned on its side", { B_W, B_H })} · {Math.round(bScale * 100)}%{bFitsPane ? "" : <> {t('journeys.newJourneys.text412', "· scroll for the rest")}</>} · {bCols === 1 ? t('journeys.newJourneys.text413', "one column") : t('journeys.newJourneys.text414', "{bCols} columns", { bCols })}{bPager.pages > 1 ? <> {t('journeys.newJourneys.text415', "· {length} queues over {pages} pages, cycling every 10s — page {arg3}", { length: sampleBoardQueues().length, pages: bPager.pages, arg3: bPager.page + 1 })}</> : ""} {t('journeys.newJourneys.text416', "· sample customers with each queue's real services and hours")}</div>
    </div>
  );

  const setupPanel = setupViewEff ? (
    <div style={{ flex: 1, overflow: "auto", padding: "28px 32px", background: "#F1F2F6" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{SETUP_LABEL[setupViewEff]}</div>
        {/* The autosave, stated. Sits in the SHARED panel header so every Setup
            step inherits it — they all write through the same two debounced
            paths, so they all had the same silence. Idle shows nothing: before
            the first edit there is no save to report, and "Saved" on arrival
            would be a claim about a write that never happened. */}
        {saveState.phase === "idle" ? null : (
          <div style={{
            display: "flex", alignItems: "center", gap: 6, flex: "0 0 auto",
            fontSize: 11.5, lineHeight: 1.4,
            color: saveState.phase === "error" ? "#8C1D18" : saveState.phase === "saving" ? "#697180" : "#027D47",
            ...(saveState.phase === "error" ? { background: "#FCEEEE", border: "1px solid #F3C9C6", borderRadius: 7, padding: "5px 9px" } : {}),
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: 999, flex: "0 0 auto",
              background: saveState.phase === "error" ? "#8C1D18" : saveState.phase === "saving" ? "#B5BAC6" : "#027D47",
            }} />
            <span style={saveState.phase === "error" ? { fontWeight: 600 } : undefined}>
              {saveState.phase === "saving"
                ? t('journeys.newJourneys.text417', "Saving…")
                : saveState.phase === "saved"
                  ? t('journeys.newJourneys.text418', "Saved {arg1}", { arg1: new Date(saveState.at ?? Date.now()).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) })
                  : t('journeys.newJourneys.text419', "Not saved — your next edit retries")}
            </span>
          </div>
        )}
      </div>
      {/* No "where does this save" subtitle (operator, 2026-09-02). On a journey
          whose record does not exist yet it read "Held on this journey until you
          create the queue", which told an author nothing they could act on and
          repeated itself on every step of every setup. */}
      {/* No "nothing on this step is required" band either: a white box whose
          only content is the absence of requirements is a line to read on the
          way to the fields, every visit. */}
      {/* Journey-level navigation defaults. On the type's FIRST setup tab,
          because that is where the journey's own identity is set; the commit
          page can override the label, nothing overrides the switch. */}
      {eventLoadingPane ? eventLoadingPane : setupViewEff === "basics" ? setupBasics : setupViewEff === "details" ? setupDetails
        : setupViewEff === "schedule" ? setupSchedule : setupViewEff === "tickets" ? setupTickets
        : setupViewEff === "guests" ? setupGuests : setupViewEff === "messaging" ? setupMessaging
        : setupViewEff === "qsetup" ? setupQSetup : setupViewEff === "qhours" ? setupQHours
        : setupViewEff === "qstaffing" ? setupQStaffing : setupViewEff === "qresources" ? setupQResources
        : setupViewEff === "qservices" ? setupQServices
        : setupViewEff === "registration" ? setupRegistration
        : setupViewEff === "boardcfg" ? setupBoardCfg
        : setupViewEff === "onestopcfg" ? setupOneStopCfg
        : isApptJourney ? setupApptPanel
        : isBoardJourney ? setupBoardPublish : isQueueJourney ? setupQPublish : setupPublish}
      {/* BELOW the tab's own fields (operator, 2026-09-02). It used to sit above
          them, so Queue Setup opened on a panel about button wording with the
          required Queue name and Location beneath it — the tab's least-used
          control in front of its most-used ones.
          It stays on this tab rather than moving to the Flow canvas: the wording
          is a journey-wide DEFAULT, and the flow edits one step at a time. The
          per-page override lives in the page inspector, where the button is
          visible while you type. */}
      {D && hasFlow && !pagesHidden && (setupViewEff === "basics" || setupViewEff === "qsetup" || setupViewEff === "aservices") ? (
        /* No card around these (operator, 2026-09-02): they are setup fields
           like the ones above them and read as one column, not as a panel
           bolted onto the end of the step. */
        <div style={{ maxWidth: 520, marginTop: 22 }}>
          <div style={{ ...setupFieldLabel, marginBottom: 3 }}>{t('journeys.newJourneys.text420', "Moving through the journey")}</div>
          <div style={{ fontSize: 11.5, color: "#697180", marginBottom: 12, lineHeight: 1.5 }}>{t('journeys.newJourneys.text421', "Every page carries Back and Next, derived from its place in the flow. The page that creates the booking is the exception — its button says what it does.")}</div>
          <div style={{ ...setupFieldLabel, marginBottom: 4 }}>{t('journeys.newJourneys.text422', "Booking button label")}</div>
          <input value={String(D.commitLabel ?? "")} placeholder={COMMIT_LABEL_BY_TYPE[D.type ?? ""] ?? t('journeys.newJourneys.placeholder25', "Confirm")} onChange={(e) => patchDoc((d) => ({ ...d, commitLabel: e.target.value }))} style={{ ...INPUT, fontSize: 12, maxWidth: 260 }} />
          <div style={{ fontSize: 11, color: "#B5BAC6", marginTop: 4 }}>{t('journeys.newJourneys.text423', "Default for this journey. A page can override it in its own editor.")}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, fontSize: 12, marginTop: 18 }}>
            <span style={{ minWidth: 0 }}>
              <span style={setupFieldLabel}>{t('journeys.newJourneys.text424', "Advance automatically when something is picked")}</span>
              <span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 2, lineHeight: 1.5 }}>{t('journeys.newJourneys.text425', "Off by default — Next is the way forward, and a page that jumps on its own takes the choice back.")}</span>
            </span>
            <Switch on={D.autoAdvance === true} onClick={() => patchDoc((d) => ({ ...d, autoAdvance: !(d.autoAdvance === true) }))} />
          </div>
        </div>
      ) : null}
    </div>
  ) : null;

  /** SETUP row — one tab set per journey type. Always visible for a journey
   *  that owns configuration (events, queues, display boards). */
  const setupTabs: { k: string; label: string; dot: boolean | null; go?: () => void; active?: boolean }[] =
    isBoardJourney
      ? [{ k: "boardcfg", label: t('journeys.newJourneys.label32', "Configuration"), dot: bReady }]
      : isOneStopJourney
        ? [{ k: "onestopcfg", label: t('journeys.newJourneys.label32', "Configuration"), dot: osReady }]
      : isQueueJourney
        ? [
            /* Two checks now, not three — the seeded-pages assertion retired with
             the starter picker (2026-09-02). */
          { k: "qsetup", label: t('journeys.newJourneys.label33', "Setup"), dot: qChecks[0]!.done && qChecks[1]!.done },
            { k: "qhours", label: t('journeys.newJourneys.label34', "Opening hours"), dot: qChecks[2]!.done },
            /* Staffing carries a dot as of 2026-09-16 (PP): it is now a publish
               requirement, so there is a row that can turn it green or red. */
            { k: "qstaffing", label: t('journeys.newJourneys.label35', "Staffing"), dot: qChecks[4]!.done },
            { k: "qresources", label: t('journeys.newJourneys.label36', "Resources"), dot: null },
            /* Services carries a real readiness row (queueReadiness's fifth
               check) but had no tab — reachable only by clicking the row on
               Publish, so its red dot was invisible until then. */
            { k: "qservices", label: t('journeys.newJourneys.label37', "Services"), dot: qChecks[3]!.done },
            ]
        : isEventJourney
          ? [
              { k: "basics", label: t('journeys.newJourneys.label38', "Basics"), dot: eventLoadingPane ? null : setupDone.basics },
              { k: "details", label: t('journeys.newJourneys.label39', "Details"), dot: eventLoadingPane ? null : setupDone.details },
              { k: "schedule", label: t('journeys.newJourneys.label40', "Schedule"), dot: eventLoadingPane ? null : setupDone.schedule },
              { k: "tickets", label: t('journeys.newJourneys.label41', "Tickets"), dot: eventLoadingPane ? null : setupDone.tickets },
              { k: "registration", label: t('journeys.newJourneys.label42', "Registration"), dot: null },
              { k: "guests", label: t('journeys.newJourneys.label43', "Guests"), dot: null },
              /* No Messaging step (operator, 2026-09-02): an event's messages
                 are set up in the flow now, and a second place to configure
                 them is a second answer to the same question. */
            ]
          : isApptJourney
            ? [
                { k: "aservices", label: t('journeys.newJourneys.label37', "Services"), dot: aChecks[0]!.done },
                { k: "aplaces", label: t('journeys.newJourneys.label44', "Places & methods"), dot: aChecks[1]!.done },
                { k: "awindow", label: t('journeys.newJourneys.label45', "Booking window"), dot: null },
                { k: "aperservice", label: t('journeys.newJourneys.label46', "Per-service settings"), dot: null },
                { k: "adetails", label: t('journeys.newJourneys.label47', "Details & consent"), dot: aChecks[2]!.done },
                { k: "amessaging", label: t('journeys.newJourneys.label48', "Messaging"), dot: null },
              ]
            : [];
  const publishReady = isApptJourney ? aReady : isBoardJourney ? bReady : isQueueJourney ? qReady : allReady;
  const liveNow = isBoardJourney ? !!boardOriginId : isQueueJourney ? q0.status === "open" : isLive;
  const visibleSetupTabs = setupTabs.filter((tb) => !HIDDEN_SETUP_TABS.has(tb.k));
  const setupStrip = visibleSetupTabs.length ? (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#FFFFFF", borderBottom: "1px solid #E6E8EC", overflowX: "auto", flexShrink: 0 }}>
      <span style={{ ...SEC, marginBottom: 0, marginRight: 4, flex: "none" }}>{t('journeys.newJourneys.text426', "Setup")}</span>
      {visibleSetupTabs.map((tb) => {
        const active = tb.active ?? setupViewEff === tb.k;
        return (
          <button key={tb.k} onClick={() => (tb.go ? tb.go() : set({ setupView: tb.k as any }))} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 11px", borderRadius: 999, border: `1px solid ${active ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}`, background: active ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", cursor: "pointer", flex: "none" }}>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: active ? "var(--jrni-color-primary-text, #5142C7)" : "#333333", whiteSpace: "nowrap" }}>{tb.label}</span>
            {tb.dot !== null ? <span style={{ width: 6, height: 6, borderRadius: 999, background: tb.dot ? "#1F8A5B" : "#C0392B", flex: "none" }} /> : null}
          </button>
        );
      })}
      {/* No "Back to pages" — a page pill IS the way back, it sits one row below,
          and it says which page it goes to. A second control meaning only "not
          this" was noise (operator, 2026-09-02). */}
      <span style={{ flex: 1 }} />
      {liveNow ? <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", background: "#E4F5EF", color: "#1F8A5B", borderRadius: 999, padding: "3px 10px", flex: "none" }}>{t("journeys.newJourneys.badge.live", "LIVE")}</span> : null}
      {/* Flush before Publish (2026-09-02). Publish reads `q0`/`r0`, which are
          live, so the button itself is safe — but a pending write must land
          before the record is created from those values, or the record is made
          from the document's older copy. */}
      {/* STICKY to the strip's right edge (2026-09-03). This strip scrolls, and
          on an appointment journey its seven tabs plus the note came to ~1167px
          in a 708px bar — so Publish sat 459px off the end and an author had to
          discover a horizontal scroll to reach it. The tabs may scroll; the verb
          that ships the thing may not. */}
      <button onClick={() => { flushSetup(); set({ setupView: "publish" }); }} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 13px", borderRadius: 999, border: `1px solid ${setupViewEff === "publish" ? "var(--jrni-color-primary-1, #5142C7)" : "#D1D4DC"}`, background: setupViewEff === "publish" ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", cursor: "pointer", flex: "none", position: "sticky", right: 0, zIndex: 2, boxShadow: "-8px 0 10px -6px rgba(255,255,255,1), -14px 0 12px -10px rgba(20,19,31,0.10)" }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: setupViewEff === "publish" ? "var(--jrni-color-primary-text, #5142C7)" : "#333333" }}>{t('journeys.newJourneys.text427', "Publish")}</span>
        {!publishReady && !eventLoadingPane ? <span style={{ width: 6, height: 6, borderRadius: 999, background: "#C0392B", flex: "none" }} /> : null}
      </button>
      {/* The aside is the first thing to go when the strip is tight — it explains
          the strip, and the strip's own "Setup" label already does that. */}
      <span style={{ fontSize: 10.5, color: "#B5BAC6", flex: "0 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...MONO }}>{isApptJourney ? t('journeys.newJourneys.text428', "funnel + catalog settings, not page content") : isBoardJourney ? t('journeys.newJourneys.text429', "board config, not page content") : isQueueJourney ? t('journeys.newJourneys.text430', "queue data, not page content") : t('journeys.newJourneys.text431', "event data, not page content")}</span>
    </div>
  ) : null;

  /* EVENT SETUP RAIL (2026-10-05, layout only): on desktop an event's Setup steps sit in the
     Events tab's sectioned left rail (236px, white, SETUP / PUBLISH groups) instead of the pill
     strip. Same steps, same dots, same handlers — the strip is kept for phone/tablet and for
     every other journey type. */
  const railRowStyle = (active: boolean): CSSProperties => ({ display: "flex", alignItems: "center", gap: 12, width: "100%", boxSizing: "border-box", padding: "8px 12px", border: "1px solid transparent", textAlign: "left", background: active ? "var(--jrni-color-primary-4, #F6F2FE)" : "transparent", boxShadow: active ? "0 1px 2px rgba(51,51,51,0.08)" : "none", color: active ? "var(--jrni-color-primary-text, #5142C7)" : "#333333", borderRadius: 8, cursor: "pointer", fontFamily: FONT, fontSize: 14, fontWeight: active ? 600 : 500 });
  const railHeadStyle = (first: boolean): CSSProperties => ({ padding: first ? "0 13px 2px" : "14px 13px 2px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#697180" });
  const railDot = (dot: boolean | null | undefined) => (dot === null || dot === undefined ? null : <span style={{ width: 6, height: 6, borderRadius: 999, background: dot ? "#1F8A5B" : "#C0392B", flex: "none" }} />);
  const useSetupRail = njDesktop && isEventJourney && visibleSetupTabs.length > 0;
  const setupRail = useSetupRail ? (
    <nav aria-label={t('journeys.newJourneys.text426', "Setup")} style={{ flex: "0 0 236px", width: 236, minWidth: 236, overflowY: "auto", background: "#FFFFFF", borderRight: "1px solid #E6E8EC", padding: "16px 13px 20px", display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={railHeadStyle(true)}>{t('journeys.newJourneys.text426', "Setup")}</div>
      <div role="tablist" aria-orientation="vertical" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {visibleSetupTabs.map((tb) => {
          const active = tb.active ?? setupViewEff === tb.k;
          return (
            <button key={tb.k} type="button" role="tab" aria-selected={active} onClick={() => (tb.go ? tb.go() : set({ setupView: tb.k as any }))} style={railRowStyle(active)}>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tb.label}</span>
              {railDot(tb.dot)}
            </button>
          );
        })}
      </div>
      <div style={railHeadStyle(false)}>{t('journeys.newJourneys.text427', "Publish")}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <button type="button" onClick={() => { flushSetup(); set({ setupView: "publish" }); }} style={railRowStyle(setupViewEff === "publish")}>
          <span style={{ flex: 1, minWidth: 0 }}>{t('journeys.newJourneys.text427', "Publish")}</span>
          {liveNow ? <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", background: "#E4F5EF", color: "#1F8A5B", borderRadius: 999, padding: "3px 10px", flex: "none" }}>{t("journeys.newJourneys.badge.live", "LIVE")}</span> : null}
          {!publishReady && !eventLoadingPane ? railDot(false) : null}
        </button>
      </div>
      <span style={{ flex: 1 }} />
      <div style={{ padding: "0 13px", fontSize: 10.5, color: "#B5BAC6", ...MONO }}>{t('journeys.newJourneys.text431', "event data, not page content")}</div>
    </nav>
  ) : null;

  /* EVENT PREVIEW COLUMN (2026-10-05): the third column. The SAME customer page the public
     event route draws (AllJourneys Designer, draft mount — no reads), fed this draft record,
     on the customer step that matches the Setup step on screen. Inert: nothing in it clicks. */
  const pvStep = setupViewEff === "registration" ? "details" : setupViewEff === "guests" || setupViewEff === "publish" ? "confirmation" : "landing";
  const pvStepLabel = pvStep === "details" ? t("journeys.newJourneys.preview.registration", "Registration form") : pvStep === "confirmation" ? t("journeys.newJourneys.preview.confirmation", "Confirmation") : t("journeys.newJourneys.preview.eventPage", "Event page");
  const showPreviewCol = useSetupRail && njWide;
  /* The tiers the Tickets step has attached, at this event's price where one is set (verifier,
     2026-10-05): without them the customer page said "sells no ticket types" for an event that does. */
  /* Preview tiers from v2 (CC 0195): the event's own tiers, else the chosen group's. */
  const pvCurrency = v2.ticketing?.currency || "USD";
  const pvTiers = ((v2EventTiers ?? (v2SelGroupId ? v2.tiersByGroup[v2SelGroupId] : null)) ?? []).map((tt) => ({ id: tt.id, name: tt.name, priceMinor: tt.priceMinor, currency: pvCurrency, desc: "" }));
  const pvPriceFrom = pvTiers.length ? { amountMinor: Math.min(...pvTiers.map((x) => x.priceMinor)), currency: pvCurrency } : null;
  const pvRows = showPreviewCol ? njPreviewRows({
    id: r0.id || "draft-event", title: r0.title || D?.name || "", category: (r0.tags ?? [])[0] ?? "",
    venue: setupVenue, isVirtual: !!r0.method?.virtual && !r0.method?.inPerson, /* from the method (CC 0203): locationMode is "saved" | "custom" */
    start_at: r0.start ?? "", end_at: r0.end ?? "", timezone: schedZone,
    capacity: r0.capacity ?? 0, description: r0.description ?? "", imageUrl: typeof r0.heroImage === "string" ? r0.heroImage : "",
    locationAddress: setupAddress, bookable: true, bookable_reason: null, registrationOpen: true, soldOut: false,
    waitlistOpen: !!r0.waitlist, spots_remaining: r0.capacity ?? null, waitlist_spots_remaining: null, priceFrom: pvPriceFrom, ticketTiers: pvTiers,
    allowGuests: (r0.maxGuests ?? 0) > 0, maxGuestsPerRegistration: r0.maxGuests ?? 0, public_path: "",
  }, njChrome?.brandColor || NJ_PV_BRAND_DEFAULT) : [];
  const pvDims = NJ_PV_DIMS[pvDevice];
  const colW = Math.min(pvColW, pvColMax());
  /* The customer page pads its device frame by 12px a side, so the stage is the device + 24 on
     both axes; the scale fits BOTH width and height so the whole screen is always in view
     (2026-10-05: it was fitted to width only and lost 12px right + everything below the fold). */
  const PV_PAD = 24;
  const pvStage = { w: pvDims.w + PV_PAD, h: pvDims.h + PV_PAD };
  const pvFitW = colW - 1 - 32;
  const pvScale = Math.min(1, pvFitW / pvStage.w, pvAvail.h > 0 ? pvAvail.h / pvStage.h : 1);
  const previewCol = showPreviewCol ? (
    <aside aria-label={t("journeys.newJourneys.preview.aria", "Customer preview")} style={{ position: "relative", flex: `0 0 ${colW}px`, width: colW, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", background: "#FFFFFF", borderLeft: "1px solid #E6E8EC" }}>
      {/* Drag the left edge to resize the preview column. */}
      <div role="separator" aria-orientation="vertical" aria-label={t("journeys.newJourneys.preview.resize", "Resize preview")} onMouseDown={startPvDrag} onDoubleClick={() => setPvDevice(pvDevice)}
        style={{ position: "absolute", left: -4, top: 0, bottom: 0, width: 8, cursor: "col-resize", zIndex: 3 }} />
      <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid #E6E8EC", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 120px", minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#697180" }}>{t("journeys.newJourneys.badge.preview", "PREVIEW")}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#333333", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pvStepLabel}</div>
        </div>
        <DevicePreviewToggle value={pvDevice} onChange={(d) => setPvDevice(d)} size="sm" ariaLabel={t("journeys.newJourneys.preview.device", "Preview device")} widths={{ tablet: NJ_PV_DIMS.tablet.w }} />
      </div>
      <div style={{ flex: "none", padding: "6px 16px", fontSize: 11, color: "#697180", borderBottom: "1px solid #E6E8EC", fontVariantNumeric: "tabular-nums" }}>
        {pvDims.w} × {pvDims.h} · {Math.round(pvScale * 100)}%
      </div>
      {/* The measured box takes its size from the aside alone: the card is absolutely placed inside
          it, so the scaled card can never change what is measured. */}
      <div ref={pvBoxRef} style={{ position: "relative", flex: "1 1 0", minHeight: 0, overflow: "hidden", background: "#F1F2F6" }}>
        {/* The frame is drawn at the device's real size, then scaled to the column — so Desktop and
            Tablet lay out at their true widths instead of reflowing into a narrow strip. Inert but
            scrollable: clicks, focus and keys are swallowed; the wheel scrolls the frame. */}
        <div aria-hidden="true"
          onMouseDownCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onKeyDownCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onFocusCapture={(e) => { (e.target as HTMLElement).blur?.(); }}
          style={{ position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)", width: Math.floor(pvStage.w * pvScale), height: Math.floor(pvStage.h * pvScale), background: "#FFFFFF", borderRadius: pvDevice === "desktop" ? 8 : 14, overflow: "hidden", boxShadow: "0 1px 2px rgba(51,51,51,0.08), 0 8px 24px -12px rgba(20,19,31,0.18)", cursor: "default" }}>
          <div style={{ width: pvStage.w, height: pvStage.h, transform: `scale(${pvScale})`, transformOrigin: "top left", overflow: "hidden" }}>
            <ErrorBoundary scope="newjourneys:event-preview" label="Customer page" variant="pane" resetKey={pvStep + ":" + pvDevice}>
              {eventLoadingPane ? (eventReadFailed === eventOriginId ? null : <GhostFramedJourney label={t("journeys.newJourneys.eventLoading", "Loading the event…")} />) : <Designer previewJourney="landing" brandColor={njChrome?.brandColor} draft={_njPvSeed} draftRows={pvRows} draftEvent={(pvRows[0] as any)?.id} draftStep={pvStep} device={pvDevice} frameSize={{ tablet: NJ_PV_DIMS.tablet, mobile: NJ_PV_DIMS.mobile }} tenant={slug} slug={slug} tenantName={tenant?.name} />}
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </aside>
  ) : null;

  const attachNoun = ATTACH_NOUN[D?.type ?? ""] ?? "record";
  /* A quiet status line, not an action bar (operator, 2026-09-02).
     It used to carry a "Create the queue" button and copy about values being
     "held on the document" — which read as a warning that saving needed a second
     step. It never did: the journey autosaves, and the strip was about the
     RECORD, which is a different thing and a later one.
     Publish now creates the record itself, so there is no step to prompt for.
     What remains is worth one sentence, because it is not visible anywhere else:
     the queue does not exist for staff or customers yet. */
  const attachStrip = D && needsAttach(D) ? (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 16px", background: "#FBFBFC", borderBottom: "1px solid #EEF0F3", flexShrink: 0 }}>
      <span style={{ fontSize: 11, color: "#697180", flex: 1, lineHeight: 1.5 }}>
        {/* Copy corrected 2026-09-16: "saved as you work" stopped being true for
            a QUEUE the moment its Setup became explicit-save. A strip that
            promises autosave above a header showing "Unsaved changes" is worse
            than no strip at all — the two would contradict each other on the
            same screen.

            The em dashes below are REAL U+2014, not "\u2014": JSX text children
            do not process JavaScript escape sequences, so the escaped form
            rendered the six literal characters on screen — in BOTH arms, which
            is every journey type, not only queues (CD, 2026-09-16). The file's
            own convention is a real dash; this was the only place that broke
            it. */}
        {isQueueJourney
          ? <>{t("journeys.newJourneys.draftHeld", "Draft — your changes are held until you press Save. Publishing creates the {noun} and puts it in front of customers.", { noun: njAttachNoun(D?.type) })}</>
          : <>{t("journeys.newJourneys.draftAutosave", "{audience, select, event {Draft — saved as you work. Publishing creates the {noun} and puts it in front of attendees.} other {Draft — saved as you work. Publishing creates the {noun} and puts it in front of customers.}}", { noun: njAttachNoun(D?.type), audience: attachNoun === "event" ? "event" : "other" })}</>}
      </span>
    </div>
  ) : null;

  /* Back goes where the thing you are editing LIVES (2026-09-16, PP).
     It used to always return to the designer's own journeys list, which is
     right only when that list is where you came from. A queue opened from
     Queues is a queue first and a journey second, so its back arrow belongs to
     the Queues list; an event's to Events. Keyed off `origin.kind` — the
     owning object the document was derived from — rather than the navigation
     history, so it is the same destination whether the page was opened from the
     list, from a deep link or after a reload.

     `origin.route` is deliberately NOT used: it points at the object's own
     detail/console surface (`queues.console`, `events.detail`), which is a
     sideways move, not a way back. A `local` origin has no owning object and a
     `board`/`onestop` document is authored in the designer's own list, so both
     keep the journeys list. Boards would otherwise land on `queues.boards`,
     which is currently hidden.

     A PLAIN const, not a `useMemo`: this sits below conditional returns in the
     component, so a hook here changes the hook count between renders (React
     #310, "rendered more hooks than during the previous render") and the screen
     fails to render at all. The computation is three comparisons. */
  const backList = (() => {
    const kind = D?.origin?.kind;
    if (kind === "queue") return { route: "queues", label: t('journeys.newJourneys.label49', "queues") };
    if (kind === "event") return { route: "events", label: t('journeys.newJourneys.label50', "events") };
    if (kind === "appointment") return { route: "appointments", label: t('journeys.newJourneys.label51', "appointments") };
    /* An UNATTACHED draft has no origin yet, and "Add Queue" on the Queues list
       opens exactly that (2026-09-17, PP): the record is not created until
       Publish, so `origin.kind` is undefined and back fell through to the
       journeys list — a screen the author never visited, from a button on the
       Queues list. Falling back to the document's TYPE fixes it, and is the
       same derivation the header title already uses (`Edit Queue` shows on this
       screen too), so the two agree instead of disagreeing about what the
       document is.

       Type only decides where an ORIGIN-LESS draft goes. Once a record exists
       the origin above wins, which matters because a record can outlive the
       type's default: a board's origin keeps it on the journeys list rather
       than sending it to the currently-hidden `queues.boards`. */
    if (D?.type === "Queue") return { route: "queues", label: t('journeys.newJourneys.label49', "queues") };
    if (D?.type === "Event") return { route: "events", label: t('journeys.newJourneys.label50', "events") };
    return null;
  })();

  const designer = D && p ? (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* overflowX auto is the FLOOR, not the layout: the title block below
          shrinks first and hands its width back, but if a long journey name and
          every control still exceed the bar, the actions must remain reachable.
          They were clipped away entirely at a 924px window with the shell nav
          expanded — Undo, Redo, Preview and Publish all sat past the right edge
          with `overflow: hidden` and no way to scroll to them. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderBottom: "1px solid #E6E8EC", background: "#FFFFFF", flexShrink: 0, overflowX: "auto" }}>
        <span onClick={() => { flushSetup(); if (ui.fromExp) { set({ route: "experience", curExp: ui.fromExp, fromExp: "", addMenu: null }); return; } if (backList) { set({ addMenu: null }); nav.go(backList.route as any); return; } set({ route: "home", addMenu: null }); }} title={ui.fromExp ? t('journeys.newJourneys.title15', "Back to the experience") : backList ? t('journeys.newJourneys.title16', "Back to {label}", { label: backList.label }) : t('journeys.newJourneys.title17', "Back to journeys")} style={{ width: HDR_H, height: HDR_H, borderRadius: 8, border: "1px solid #D1D4DC", background: "#FFFFFF", color: "#697180", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, cursor: "pointer", flex: "none" }}>←</span>
        <span style={glyphBox(p.tint, p.color, 32, 15)}>▤</span>
        {/* Shrinks FIRST and ellipsises. It carried `flex: "none"` with a
            `minWidth: 0` that could therefore never engage, so the bar's
            trailing actions were pushed past the clip instead of the name being
            truncated — which is the wrong thing to protect: a journey's name is
            already in the list you came from, Publish is not. */}
        <div style={{ minWidth: 60, flex: "1 1 auto", overflow: "hidden" }}>
          {/* The title names WHAT you are editing, not the tool (2026-09-16,
              PP). Every journey type opened the same "Journey Designer", so a
              queue's own configuration screen — reached from the Queues list,
              titled after an internal tool — read as somewhere you had ended up
              by accident. Derived from the document's own type/origin, so it
              holds on a deep link and a reload. */}
          <div style={{ fontSize: 16, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{designerTitle}</div>
          <div style={{ fontSize: 11, color: "#697180", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{docDisplayName}</div>
        </div>
        {/* Pages / Flow — only for journey types that HAVE a flow. A Display
            Board and a One-Stop page are one page each: nobody walks through
            them, they arrive and choose. Offering a Flow tab there is a toggle
            between a page and an empty canvas, which reads as something missing
            rather than something absent by design. */}
        {hasFlow && !pagesHidden ? (
          <div style={{ display: "inline-flex", border: "1px solid #D1D4DC", borderRadius: 8, overflow: "hidden", marginLeft: 8, flex: "none" }}>
            {[{ k: "pages", label: t('journeys.newJourneys.label52', "Pages") }, { k: "flow", label: t('journeys.newJourneys.label53', "Flow") }].map((t) => (
              <button key={t.k} onClick={() => { flushSetup(); set({ view: t.k as any, addMenu: null, sel: null, setupView: null }); }} style={{ height: HDR_H, padding: "0 14px", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", background: ui.view === t.k && !ui.setupView ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: ui.view === t.k && !ui.setupView ? "#FFFFFF" : "#333333", fontFamily: FONT }}>{t.label}</button>
            ))}
          </div>
        ) : null}
        {/* Desktop / Mobile — not for a Display Board. That canvas is a lobby TV:
            it has one size, it is never held in a hand, and offering a phone
            width invites an author to check a layout that will never exist
            (operator, 2026-09-02). */}
        {ui.view === "pages" && !setupViewEff && !isBoardJourney ? (
          <div style={{ display: "inline-flex", border: "1px solid #D1D4DC", borderRadius: 8, overflow: "hidden", marginLeft: 8, flex: "none" }}>
            {[{ k: "desktop", label: t('journeys.newJourneys.label54', "Desktop") }, { k: "mobile", label: t('journeys.newJourneys.label55', "Mobile") }].map((t) => (
              <button key={t.k} onClick={() => setViewport(t.k as "desktop" | "mobile")} style={{ height: HDR_H, padding: "0 13px", border: "none", fontSize: 11.5, fontWeight: 700, cursor: "pointer", background: curViewport === t.k ? "#14131F" : "#FFFFFF", color: curViewport === t.k ? "#FFFFFF" : "#333333", fontFamily: FONT }}>{t.label}</button>
            ))}
          </div>
        ) : null}
        {/* Landscape / Portrait — the Display Board's equivalent, and the only
            width question a lobby TV has (operator, 2026-09-02). Phone width was
            never a layout a board would be seen at; how the screen is BOLTED TO
            THE WALL is. */}
        {ui.view === "pages" && !ui.setupView && isBoardJourney ? (
          <div style={{ display: "inline-flex", border: "1px solid #D1D4DC", borderRadius: 8, overflow: "hidden", marginLeft: 8, flex: "none" }}>
            {[{ k: "landscape", label: t('journeys.newJourneys.label56', "Landscape") }, { k: "portrait", label: t('journeys.newJourneys.label57', "Portrait") }].map((t) => (
              <button key={t.k} onClick={() => set({ boardOrientation: t.k as "landscape" | "portrait" })} title={t.k === "landscape" ? i18nT("journeys.newJourneys.orientation.landscape", "A TV on a wall bracket — 16:9") : i18nT("journeys.newJourneys.orientation.portrait", "A TV turned on its side — 9:16")} style={{ height: HDR_H, padding: "0 13px", border: "none", fontSize: 11.5, fontWeight: 700, cursor: "pointer", background: ui.boardOrientation === t.k ? "#14131F" : "#FFFFFF", color: ui.boardOrientation === t.k ? "#FFFFFF" : "#333333", fontFamily: FONT }}>{t.label}</button>
            ))}
          </div>
        ) : null}
        {/* The ‹ Page 1 of 5 › pager is GONE (2026-09-03). It duplicated the
            Pages strip immediately below, which lists every page as a named chip
            — so it answered "which page am I on" a second time, worse, while
            costing ~200px of a 708px bar. With it there the bar overflowed by
            459px and Undo, Redo, Preview and Publish were all clipped past the
            right edge with no way to reach them. A pager that hides Publish is
            not worth the pager. The flow view keeps its summary line, which has
            no equivalent below and shrinks. */}
        {ui.view === "flow" ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minWidth: 0 }}>
            {(() => {
              const all = qfFlatten(qfFlow.steps);
              const pgCount = all.filter((st) => st.kind === "page").length;
              const rest = all.length - pgCount;
              return <span style={{ fontSize: 11.5, color: "#697180", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t("journeys.newJourneys.flowTriggerLine", "Trigger → {pages, plural, one {# page} other {# pages}}{rest, plural, =0 {} one { + # more step} other { + # more steps}} · click a step to edit it", { pages: pgCount, rest: rest > 0 ? rest : 0 })}</span>;
            })()}
          </div>
        ) : <span style={{ flex: 1, minWidth: 0 }} />}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flex: "0 1 auto", minWidth: 0 }}>
          {explicitSave ? (
            /* A queue's Setup writes to the queue RECORD, and on 2026-09-16 (PP)
               those writes became explicit: edits are held until Save, and this
               badge states which of the two states you are in. Autosave stays
               for every OTHER journey type, which is why the badge and the
               button are both behind `isQueueJourney`. */
            <>
              {isEventJourney && isPublishedEvent ? (
                <StatusPill tone={isLive ? "success" : "info"} dot size="sm" testid="nj-event-status">
                  {t("journeys.newJourneys.publishedState", "{state, select, live {Published · Live} done {Published · Ended} other {Published · Upcoming}}", { state: isLive ? "live" : evStatus === "completed" ? "done" : "upcoming" })}
                </StatusPill>
              ) : null}
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, flex: "0 1 auto", minWidth: 0,
                borderRadius: 999, padding: "4px 10px", whiteSpace: "nowrap",
                ...(saveState.phase === "error"
                  ? { background: "#FCEEEE", border: "1px solid #F3C9C6", color: "#8C1D18", fontWeight: 700 }
                  : setupDirty
                    ? { background: "#FAF1DE", border: "1px solid #EBD9AE", color: "#916D0F", fontWeight: 700 }
                    : { background: "#FFFFFF", border: "1px solid #E6E8EC", color: "#697180" }),
              }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, flex: "none", background: saveState.phase === "error" ? "#8C1D18" : setupDirty ? "#916D0F" : saveState.phase === "saved" ? "#027D47" : "#B5BAC6" }} />
                {/* The FAILURE outranks the dirty state (2026-09-16). Keeping the
                    queue dirty on a failed save is what lets the author retry,
                    but it also made the badge read "Unsaved changes" — true, yet
                    strictly less informative than saying an attempt was made
                    and refused. Both mean "not saved"; only one of them tells
                    you the server said no. */}
                <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{saveState.phase === "error"
                  ? t('journeys.newJourneys.text442', "Not saved — press Save to retry")
                  : qHoursIncomplete
                    ? t('journeys.newJourneys.text443', "Pick a day for every hours set")
                    : setupDirty
                      ? t('journeys.newJourneys.text444', "Unsaved changes")
                      : saveState.phase === "saving"
                        ? t('journeys.newJourneys.text417', "Saving…")
                        : saveState.phase === "saved"
                          ? t('journeys.newJourneys.text418', "Saved {arg1}", { arg1: new Date(saveState.at ?? Date.now()).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) })
                          : t('journeys.newJourneys.text445', "No changes")}</span>
              </span>
              {/* Save commits the held Setup edits to the queue record. It is
                  NOT Publish: Publish decides whether customers can see the
                  queue, and the two were never the same act — an author fixing
                  opening hours on a live queue must not have to re-publish to
                  keep them. */}
              {/* Save refuses a body the door will refuse (CD, 2026-09-17).
                  "+ Add another set" creates a set with NO days — deliberately,
                  so the author picks them — but Save posted it anyway, the
                  join door answered 400 "at least one day is required", and
                  the header said "Not saved — press Save to retry" with nothing
                  pointing at the empty set. Pressing Save again could only fail
                  again.
                  Disabled rather than defaulted: a guessed day would be written
                  as the author's intent and enforced on the desk, which is the
                  same mistake the removed Mon–Fri prefill made. The tooltip and
                  the step's own red line say which set. */}
              {isQueueJourney ? (
              <button onClick={saveSetupNow} disabled={!setupDirty || saveState.phase === "saving" || qHoursIncomplete}
                  title={qHoursIncomplete ? t('journeys.newJourneys.title20', "Pick at least one day for every opening-hours set before saving") : setupDirty ? t("journeys.newJourneys.saveOntoTitle", "{kind, select, queue {Save these changes onto the queue} other {Save these changes onto the event}}", { kind: isQueueJourney ? "queue" : "event" }) : t('journeys.newJourneys.title22', "Nothing to save")}
                  style={{ ...(setupDirty && !qHoursIncomplete ? PRIMARY : GHOST), height: HDR_H, flex: "none", ...(setupDirty && !qHoursIncomplete ? {} : { opacity: 0.55, cursor: "default" }) }}>
                  {saveState.phase === "saving" ? busyLabel(t("journeys.newJourneys.busy.saving", "Saving…"), setupDirty) : t('journeys.newJourneys.text446', "Save")}
                </button>
              ) : eventOriginId && eventStatusKnown ? (
                /* An event's Save (CC, 2026-10-06): "Save changes" on a published event, the
                   primary action there; "Save draft" on a draft, beside Publish. */
                <Button size="sm" variant={isPublishedEvent ? "primary" : "secondary"} testid="nj-save" onClick={saveSetupNow}
                  disabled={!setupDirty || saveState.phase === "saving" || endBeforeStart}
                  title={setupDirty ? t("journeys.newJourneys.saveOntoTitle", "{kind, select, queue {Save these changes onto the queue} other {Save these changes onto the event}}", { kind: "event" }) : t('journeys.newJourneys.title22', "Nothing to save")}>
                  {saveState.phase === "saving" ? busyLabel(t("journeys.newJourneys.busy.saving", "Saving…"), isPublishedEvent) : evStatus === "draft" ? t("journeys.newJourneys.saveDraft", "Save draft") : t("journeys.newJourneys.saveChanges", "Save changes")}
                </Button>
              ) : isEventJourney && !eventOriginId ? (
                /* A NEW event's Save draft creates the event as a draft (CC, 2026-10-06): it is then
                   listed under Events › Draft, stays off the public site, and opens again with Edit.
                   Until then its Setup is kept on this journey as you type. */
                <Button size="sm" variant="secondary" testid="nj-save-draft" onClick={() => { void saveDraft(); }}
                  disabled={attachBusy || publishBusy !== null || endBeforeStart}
                  title={t("journeys.newJourneys.saveDraftCreateTitle", "Creates the event as a draft. It isn't public until you publish it.")}>
                  {attachBusy && publishBusy === null ? busyLabel(t("journeys.newJourneys.busy.savingDraft", "Saving draft…")) : t("journeys.newJourneys.saveDraft", "Save draft")}
                </Button>
              ) : null}
              {isEventJourney && publishFail && publishFail.doc === ui.curDoc ? (
                <span role="alert" data-testid="nj-publish-error" style={{ display: "inline-flex", alignItems: "center", gap: 8, maxWidth: 420, fontSize: 12, lineHeight: 1.4, color: "#B42318", background: "#FEF3F2", border: "1px solid #FECDCA", borderRadius: 8, padding: "4px 10px" }}>
                  <span style={{ minWidth: 0 }}>{publishFail.text}</span>
                  {publishFail.check ? <button type="button" data-testid="nj-publish-error-check" onClick={() => nav.go("events")} style={{ border: "none", background: "transparent", padding: 0, color: "#B42318", fontWeight: 700, textDecoration: "underline", cursor: "pointer", whiteSpace: "nowrap", fontSize: 12 }}>Check my events</button> : null}
                  {publishFail.retry ? <button type="button" onClick={() => { if (publishFail.action === "create") void saveDraft(); else void publishNow(); }} disabled={publishBusy !== null || attachBusy} style={{ border: "none", background: "transparent", padding: 0, color: "#B42318", fontWeight: 700, textDecoration: "underline", cursor: "pointer", whiteSpace: "nowrap", fontSize: 12 }}>Try again</button> : null}
                </span>
              ) : null}
              {isEventJourney && eventStatusKnown && !isPublishedEvent && !needsApproval && evStatus !== "cancelled" && evStatus !== "archived" ? (
                <Button size="sm" variant="primary" testid="nj-publish" onClick={() => {
                  /* Publish asks first (CC 0280), in a dialog that says what goes live (CC 0305 item 3). */
                  setEventAsk("publish");
                }}
                  disabled={!allReady || (approvalsOn && approvalBlocked) || publishBusy !== null || attachBusy}
                  title={!allReady ? t('journeys.newJourneys.publishNotReady', "Finish the items marked in Publish first")
                    : (approvalsOn && approvalBlocked) ? approvalBlockReason
                    : !eventOriginId ? t('journeys.newJourneys.title12', "Creates the event from Setup, then takes it live")
                    : undefined}>
                  {publishBusy === "publish" ? busyLabel(t("journeys.newJourneys.busy.publishing", "Publishing…"), true) : t("journeys.newJourneys.publish", "Publish")}
                </Button>
              ) : null}
              {isEventJourney && isPublishedEvent ? (
                <MoreMenu label={t("journeys.newJourneys.eventActions", "Event actions")} testid="nj-event-more" items={[
                  { label: t("journeys.newJourneys.unpublish", "Unpublish"), onClick: confirmUnpublish, testid: "nj-unpublish" },
                  /* No "Cancel event" once the event has ended (the badge above says Published · Ended): the server refuses to cancel an ended event. */
                  ...(evStatus === "completed" ? [] : [{ label: t('journeys.newJourneys.text278', "Cancel event"), danger: true, onClick: confirmCancelEvent, testid: "nj-cancel-event" }]),
                ]} />
              ) : null}
            </>
          ) : (
            <>
              <SavedBadge state="saved" />
              <EditorHistoryButtons history={history} current={D} onRestore={restoreDoc} size="sm"
                hint={t('journeys.newJourneys.hint', "Undo covers pages, blocks and the flow — Setup writes to the record and isn't undoable here.")}
                testidPrefix="nj" />
            </>
          )}
        </span>
        {/* Preview, Undo and Redo are withheld wherever the pages are
            (2026-09-16 queues, 2026-09-17 events — PP). With the page surface
            hidden there is nothing for Preview to show and nothing for the undo
            stack to cover — it spans pages, blocks and flow, all three of which
            these journeys no longer expose, so both were controls over an
            absent surface. They return with the pages: same flag, not a
            deletion. */}
          {pagesHidden ? null : (
            <button onClick={() => set({ previewOpen: true, sel: null, previewViewport: curViewport })} style={{ ...GHOST, height: HDR_H, flex: "none" }}>{t('journeys.newJourneys.text447', "Preview")}</button>
          )}
        {/* The way out is the boxed ← at the head of this bar (2026-09-02,
            operator): the same back affordance as the Confirmation Page
            Designer. It was a filled primary button on the right, which read as
            the header's main action when it is the opposite — leaving. Saving is
            automatic and the SavedBadge above states it permanently. */}
      </div>
      {attachStrip}
      {isEventJourney && eSaveErrors.length ? (
        <div data-testid="nj-save-errors" style={{ padding: "8px 16px", borderBottom: "1px solid #EEF0F3", flexShrink: 0 }}>
          <Banner tone="danger" title={eSaveErrors.every((e) => e.code === "EVENT_LIVE_LOCKED")
            ? t("journeys.newJourneys.saveErrors.liveTitle", "Some changes can't be saved while this event is live.")
            : t("journeys.newJourneys.saveErrors.title", "Some changes weren't saved. They're still here; press Save to try again.")}>
            {eSaveErrors.map((e) => (
              <div key={e.section}>{t("journeys.newJourneys.saveErrors.line", "{section, select, basics {Basics} details {Details} schedule {Schedule} tickets {Tickets} registration {Registration} guestList {Guests} messaging {Messaging} other {{section}}}: {code}{message, select, none {} other { — {message}}}", { section: e.section, code: e.code, message: e.message || "none" })}</div>
            ))}
          </Banner>
        </div>
      ) : null}
      {useSetupRail ? (
        <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
          {setupRail}
          <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {pagesStrip}
            {setupViewEff ? setupPanel : (ui.view === "pages" ? (p.id === "board" ? boardPageView : pagesView) : flowView)}
          </div>
          {previewCol}
        </div>
      ) : (
        <>
          {setupStrip}
          {pagesStrip}
          {setupViewEff ? setupPanel : (ui.view === "pages" ? (p.id === "board" ? boardPageView : pagesView) : flowView)}
        </>
      )}
    </div>
  ) : null;

  /* ── EXPERIENCE ─────────────────────────────────────────────────── */
  const anchor = E?.anchor ? expJourney(E.anchor) : null;
  const experience = E ? (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderBottom: "1px solid #E6E8EC", background: "#FFFFFF", flexShrink: 0 }}>
        <span style={glyphBox("#E4E1F7", "#262265", 32, 15)}>◈</span>
        <div style={{ minWidth: 0, flex: "none" }}>
          <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap" }}>{t('journeys.newJourneys.text448', "Experience Designer")}</div>
          <input value={E.name} onChange={(e) => patchExp((x) => ({ ...x, name: e.target.value }))} style={{ border: "none", outline: "none", fontSize: 11, color: "#697180", fontFamily: FONT, padding: 0, background: "transparent", width: 320 }} />
        </div>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", background: "#E4E1F7", color: "#262265", borderRadius: 999, padding: "3px 10px", flex: "none" }}>{t('journeys.newJourneys.text449', "EXPERIENCE · PHASE 5")}</span>
        <span style={{ flex: 1 }} />
        {/* Same correction as the designer header (2026-09-02): Close and Done
            ran identical code and only Done claimed a save, so the pair implied
            a choice that did not exist. One way out; the experience's own edits
            autosave like everything else. */}
        <button onClick={() => set({ route: "home" })} style={PRIMARY}>{t('journeys.newJourneys.text450', "Back to journeys")}</button>
      </div>
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <div style={{ flex: 1, background: "#F1F2F6", overflow: "auto", padding: "26px 28px", minWidth: 0 }}>
          <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={SEC}>{t('journeys.newJourneys.text451', "Anchor — the event itself")}</div>
            {anchor ? (
              <div onClick={() => openDoc(E.anchor!, ui.curExp)} style={{ width: 460, background: "#FFFFFF", border: "2px solid #262265", borderRadius: 12, padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
                <span style={glyphBox(anchor.tint, anchor.color, 34, 15)}>◆</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 700 }}>{anchor.name}</span>
                  <span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 2 }}>{anchor.pages} {t('journeys.newJourneys.text452', "pages · registration here checks the attendee in to everything below")}</span>
                </span>
                <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: anchor.tint, color: anchor.color, flex: "none" }}>{anchor.ty}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--jrni-color-primary-text, #5142C7)", flex: "none" }}>{t('journeys.newJourneys.text453', "Open →")}</span>
              </div>
            ) : (
              <button onClick={() => set({ expPicker: "anchor" })} style={{ width: 460, padding: 16, border: "1px dashed #B9A8F0", borderRadius: 12, background: "#FCFBFF", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{t('journeys.newJourneys.text454', "+ Pick the anchor journey — the event this experience wraps")}</button>
            )}
            <span style={{ width: 2, height: 18, background: "#C7CBD6" }} />
            <div style={{ width: 560, background: "#FFFFFF", border: "2px solid #262265", borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 4 }}>
                <span style={glyphBox("#E4E1F7", "#262265", 28, 13)}>◈</span>
                <span style={{ flex: 1, minWidth: 0 }}><span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{t('journeys.newJourneys.text455', "Experience hub — after check-in")}</span><span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{t('journeys.newJourneys.text456', "the attendee's home screen while they're here")}</span></span>
                <button onClick={() => set({ expPicker: "attach" })} style={{ height: 28, padding: "0 12px", border: "1px dashed #B9A8F0", borderRadius: 999, background: "#FCFBFF", color: "var(--jrni-color-primary-text, #5142C7)", fontSize: 11.5, fontWeight: 700, cursor: "pointer", flex: "none", fontFamily: FONT }}>{t('journeys.newJourneys.text457', "+ Add to hub")}</button>
              </div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#B5BAC6", margin: "10px 0 7px" }}>{t('journeys.newJourneys.text458', "What the customer can do here")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {E.attached.map((a, i) => {
                  const j = expJourney(a.doc);
                  return (
                    <div key={a.doc + i} onClick={() => (exps[a.doc] ? set({ route: "experience", curExp: a.doc, expPicker: null, fromExp: "" }) : openDoc(a.doc, ui.curExp))} style={{ display: "flex", alignItems: "flex-start", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 11px", cursor: "pointer" }}>
                      <span style={glyphBox(j.tint, j.color, 28, 13)}>{TYPE_GLYPH[j.ty] ?? "▤"}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                          <span style={{ fontSize: 12.5, fontWeight: 700 }}>{HUB_VERB[j.ty] ?? <>{t('journeys.newJourneys.text459', "Open —")}{" "}</>}{j.name}</span>
                          <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: j.tint, color: j.color }}>{j.ty}</span>
                          <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: "0.05em", background: "#262265", color: "#FFFFFF", borderRadius: 3, padding: "1px 5px" }}>{t("journeys.newJourneys.badge.reference", "REFERENCE")}</span>
                        </span>
                        <span style={{ display: "block", fontSize: 10.5, color: "#697180", lineHeight: 1.5, marginTop: 3 }}>{a.scope} · {j.pages} {t('journeys.newJourneys.text439', "pages")}</span>
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 9, flex: "none", paddingTop: 2 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--jrni-color-primary-text, #5142C7)" }}>{t('journeys.newJourneys.text453', "Open →")}</span>
                        <button onClick={(e) => { e.stopPropagation(); patchExp((x) => ({ ...x, attached: x.attached.filter((_, n) => n !== i) })); showToast(t("journeys.newJourneys.toast.detached", "Detached — the journey itself is untouched (it was a reference).")); }} style={{ border: "none", background: "transparent", color: "#697180", fontSize: 11, cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
                      </span>
                    </div>
                  );
                })}
                {E.attached.length === 0 ? <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "18px 12px", textAlign: "center", fontSize: 11.5, color: "#697180" }}>{t('journeys.newJourneys.text460', "Nothing in the hub yet — add the queues, appointments, events or experiences that run inside this one.")}</div> : null}
              </div>
              <div style={{ fontSize: 10, color: "#B5BAC6", marginTop: 10, ...MONO }}>{t('journeys.newJourneys.text461', "one page in the anchor's flow — page type ◈ Experience hub, from the registry")}</div>
            </div>
          </div>
        </div>
        <div style={{ width: 264, background: "#FFFFFF", borderLeft: "1px solid #E6E8EC", padding: 14, overflow: "auto", flexShrink: 0 }}>
          <div style={SEC}>{t('journeys.newJourneys.text462', "Shared context")}</div>
          {[[t("journeys.newJourneys.hubBenefit.oneRegistration", "One registration."), t("journeys.newJourneys.hubBenefit.oneRegistrationBody", "Registering for the anchor event satisfies Contact details in every attached journey — the invariant is met by the experience, not re-asked.")],
            [t("journeys.newJourneys.hubBenefit.scopedAvailability", "Scoped availability."), t("journeys.newJourneys.hubBenefit.scopedAvailabilityBody", "Attached journeys run only inside the anchor's time window and location — the queue closes when the event does.")],
            [t("journeys.newJourneys.hubBenefit.oneIdentity", "One identity."), t("journeys.newJourneys.hubBenefit.oneIdentityBody", "The check-in QR is the same identity everywhere — joining a line or booking a 1:1 needs no second sign-in.")]].map(([b, t]) => (
            <div key={b} style={{ fontSize: 11.5, lineHeight: 1.55, color: "#333333", marginBottom: 10 }}><b>{b}</b> {t}</div>
          ))}
          <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "4px 0 12px" }} />
          <div style={{ ...NOTE, fontSize: 10.5 }}>{t('journeys.newJourneys.text463', "Edits to a journey show here automatically. Detaching never deletes it.")}</div>
          <div style={{ fontSize: 10, color: "#B5BAC6", marginTop: 10, ...MONO }}>{t('journeys.newJourneys.text464', "experience = anchor + attached[] + hub · phase 5")}</div>
        </div>
      </div>
    </div>
  ) : null;

  /* ── Modals ─────────────────────────────────────────────────────── */
  const eat = (e: any) => e.stopPropagation();
  const scopeFor = (ty: string) => ({ Queue: t("journeys.newJourneys.scope.queue", "Runs only during event hours"), Appointment: t("journeys.newJourneys.scope.appointment", "Slots only inside the event window"), Event: t("journeys.newJourneys.scope.event", "Runs inside this experience"), Experience: t("journeys.newJourneys.scope.experience", "Nested experience — its own hub inside this one") } as any)[ty] ?? t("journeys.newJourneys.scope.default", "Available while checked in");
  const pickerRows = E ? [
    ...docKeys.filter((k) => k !== E.anchor && !E.attached.some((a) => a.doc === k)).map((k) => { const j = expJourney(k); return { ...j, sub: docs[k]!.sub, pagesLabel: t("journeys.newJourneys.pagesCount", "{count, plural, one {# page} other {# pages}}", { count: j.pages }) }; }),
    ...Object.keys(exps).filter((k) => k !== ui.curExp && !E.attached.some((a) => a.doc === k)).map((k) => { const x = exps[k]!; return { key: k, name: x.name, ty: "Experience", tint: TYPE_TINT.Experience![0], color: TYPE_TINT.Experience![1], sub: t("journeys.newJourneys.expInside", "{count, plural, one {# journey inside · one check-in} other {# journeys inside · one check-in}}", { count: x.attached.length }), pages: x.attached.length, pagesLabel: t("journeys.newJourneys.journeyCount", "{count, plural, one {# journey} other {# journeys}}", { count: x.attached.length }) }; }),
  ].filter((r) => ui.expPickerTab === "all" || r.ty === ui.expPickerTab) : [];

  /* ── Preview — the pages with the authoring chrome taken away ──────
     The same render the canvas uses (hero, heading, core, blocks), inside a
     scrim, with pointer events off so nothing is selectable and no inspector
     is reachable. Its own device toggle and page stepper.

     Closing it (2026-08-31): the overlay used to paint IN PLACE, inside the
     screen's own stacking context, at z-index 40. The app topbar and rail sit in
     a sibling context above that, so they covered the preview header — the Close
     button was under the chrome and unclickable, and with no scrim click and the
     focus outside the document, an operator had no way out. It now mounts at
     document.body above the chrome, and there are three ways to leave: the Close
     button, a click on the scrim, and Esc. */
  const closePreview = () => set({ previewOpen: false });
  const previewOverlay = ui.previewOpen && D && p ? (
    <OverlayPortal>
      <div role="dialog" aria-modal="true" aria-label={t('journeys.newJourneys.ariaLabel4', "Preview — {name}", { name: D.name })} onClick={closePreview} style={{ position: "fixed", inset: 0, zIndex: 260, background: "rgba(20,19,31,0.72)", display: "flex", flexDirection: "column", padding: "16px clamp(10px, 4vw, 48px) 20px", fontFamily: FONT }}>
      <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", flexShrink: 0, marginBottom: 12 }}>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", background: "rgba(255,255,255,0.16)", color: "#FFFFFF", borderRadius: 999, padding: "3px 10px" }}>{t("journeys.newJourneys.badge.preview", "PREVIEW")}</span>
        <span style={{ color: "#FFFFFF", fontSize: 13.5, fontWeight: 700, minWidth: 0 }}>{D.name}</span>
        <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 11.5 }}>{p.label} {t("journeys.newJourneys.pageOfTotal", "· page {page} of {total}", { page: curIdx + 1, total: D.pages.length })}</span>
        <span style={{ flex: 1 }} />
        <button onClick={() => set({ cur: Math.max(0, curIdx - 1) })} disabled={curIdx === 0} style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.12)", color: "#FFFFFF", cursor: curIdx === 0 ? "default" : "pointer", opacity: curIdx === 0 ? 0.4 : 1 }}>‹</button>
        <button onClick={() => set({ cur: Math.min(D.pages.length - 1, curIdx + 1) })} disabled={curIdx >= D.pages.length - 1} style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.12)", color: "#FFFFFF", cursor: curIdx >= D.pages.length - 1 ? "default" : "pointer", opacity: curIdx >= D.pages.length - 1 ? 0.4 : 1 }}>›</button>
        <span style={{ display: "inline-flex", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, overflow: "hidden" }}>
          {/* A Display Board's only width question is how the TV is mounted, so
              the toggle reads Landscape / Portrait there and Desktop / Mobile
              everywhere else (operator, 2026-09-02). */}
          {(isBoardJourney
            ? [{ k: "landscape", label: t('journeys.newJourneys.label56', "Landscape") }, { k: "portrait", label: t('journeys.newJourneys.label57', "Portrait") }]
            : [{ k: "desktop", label: t('journeys.newJourneys.label54', "Desktop") }, { k: "mobile", label: t('journeys.newJourneys.label55', "Mobile") }]
          ).map((t) => {
            const on = isBoardJourney ? ui.boardOrientation === t.k : ui.previewViewport === t.k;
            /* Preview-scoped (2026-09-02, operator). This briefly wrote
               `page.viewport`, which made a look at the phone layout an edit to
               the page: the canvas behind the scrim changed width, and the
               change persisted after the preview closed. It now writes only the
               preview's own viewport, seeded from the page on open — or, for a
               board, the designer-local orientation. */
            return <button key={t.k} onClick={() => set(isBoardJourney ? { boardOrientation: t.k as "landscape" | "portrait" } : { previewViewport: t.k as "desktop" | "mobile" })} style={{ padding: "6px 13px", border: "none", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT, background: on ? "#FFFFFF" : "transparent", color: on ? "#14131F" : "#FFFFFF" }}>{t.label}</button>;
          })}
        </span>
        <button onClick={closePreview} aria-label={t('journeys.newJourneys.ariaLabel5', "Close preview")} style={{ height: 30, padding: "0 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.12)", color: "#FFFFFF", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{t('journeys.newJourneys.text466', "Close preview")}</button>
      </div>
      <div onClick={closePreview} style={{ flex: 1, minHeight: 0, overflow: isBoardJourney ? "hidden" : "auto" }}>
        {/* A Display Board previews as the SCREEN, not as a page card: the same
            true-size box the canvas draws, scaled to the scrim. Before this it
            fell through to the page renderer, which a board has no blocks for,
            and Preview showed an empty white card (operator, 2026-09-02). */}
        {isBoardJourney ? (
          <div ref={previewFitRef} onClick={(e) => e.stopPropagation()} style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" as const }}>
            {boardScreen(previewFit.w > 0 && previewFit.h > 0 ? Math.min(previewFit.w / B_W, previewFit.h / B_H) : 1)}
          </div>
        ) : (
        <>
        {/* The page card takes no pointer events (nothing in a preview is
            interactive), so a click ON it would otherwise reach the scrim and
            close the preview. This wrapper absorbs those clicks; only the scrim
            around the page closes. */}
        <div onClick={(e) => e.stopPropagation()} style={{ width: "fit-content", maxWidth: "100%", margin: "0 auto" }}>
        {/* Desktop is a real width, not "100%" (2026-09-02, operator). `100%` of
            a `fit-content` parent resolves against the parent's own shrink-to-fit
            width, so Desktop collapsed to roughly the mobile card — the toggle
            read Desktop while the preview showed the narrow experience. 1280 is
            the design system's desktop preview width; `maxWidth: 100%` keeps it
            inside the scrim on a small display. */}
        <div className="nj-page" style={{ width: ui.previewViewport === "desktop" ? 1280 : 440, maxWidth: "100%", margin: "0 auto", background: "#FFFFFF", borderRadius: 16, overflow: "hidden", boxShadow: "0 12px 40px rgba(0,0,0,0.35)", pointerEvents: "none" as const }}>
          {heroBlock ? (
            <div className="nj-block nj-hero" style={{ margin: "14px 14px 12px" }}>
              <div style={{ borderRadius: 10, background: heroImg ? `linear-gradient(rgba(20,19,31,0.45), rgba(20,19,31,0.45)), url(${heroImg}) center/cover` : (({ Gradient: "linear-gradient(135deg, #75ADFA, var(--jrni-color-primary-text, #5142C7))", "Solid brand": "var(--jrni-color-primary-text, #5142C7)", Photo: "#1F2430" } as any)[bval(heroBlock, "bg")] ?? "linear-gradient(135deg, #75ADFA, var(--jrni-color-primary-text, #5142C7))"), color: "#FFFFFF", padding: "22px 18px", textAlign: (bval(heroBlock, "align") === "Left" ? "left" : "center") as any }}>
                <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.01em" }}>{heroBlock.text}</div>
                {bval(heroBlock, "sub") ? <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 5, lineHeight: 1.45 }}>{bval(heroBlock, "sub")}</div> : null}
                {bval(heroBlock, "cta") ? <span style={{ display: "inline-block", marginTop: 10, background: "#FFFFFF", color: "var(--jrni-color-primary-text, #5142C7)", borderRadius: 999, padding: "6px 14px", fontSize: 11, fontWeight: 700 }}>{bval(heroBlock, "cta")}</span> : null}
              </div>
            </div>
          ) : null}
          <div className="nj-heading" style={{ padding: "18px 20px 6px" }}>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.01em" }}>{p.h}</div>
            {p.s ? <div style={{ fontSize: 12.5, color: "#697180", marginTop: 4 }}>{p.s}</div> : null}
          </div>
          {/* A One-Stop page's content is `osBlocks`, drawn by the student's own
              renderer — NOT the designer's copy blocks. Without this branch the
              preview rendered `core()` (null, the page has no core) and
              `contentBlocks` (empty, the blocks live elsewhere), so it showed a
              heading and nothing else. Same fault the canvas had on 2026-09-01,
              in the one place that had not been fixed. */}
          {isOneStopPage ? (
            /* The SAME component the canvas mounts, with authoring switched off
               — not a second copy of the renderer. A preview that renders the
               page through a different path is a preview that can disagree with
               the thing it previews, which is how the canvas and this overlay
               drifted in the first place. */
            <div style={{ padding: "0 14px 10px" }}>
              <OSCanvas
                blocks={osBlocks}
                rendered={osRendered}
                locale={p.osSettings?.locale ?? "en-US"}
                timeZone={p.osSettings?.timeZone ?? tzContext().zone}
                subtitle={p.s}
                readOnly
                sel={null}
                onSelect={() => {}}
                onChange={() => {}}
                onInsert={() => {}}
              />
            </div>
          ) : (<>
            {core()}
            {/* NOT `.map(blockView)`: map passes (item, index, ARRAY), and
                blockView's third parameter is `opts?: { inCell?: boolean }` — so
                every block was rendered with the whole array as its options bag
                (2026-09-08). Arity spelled out. */}
            {contentBlocks.map((c, i) => blockView(c, i))}
          </>)}
          <div style={{ height: 6 }} />
          {stepNavFooter()}
        </div>
        </div>
        <div style={{ textAlign: "center", fontSize: 10.5, color: "rgba(255,255,255,0.5)", marginTop: 10, ...MONO }}>{t('journeys.newJourneys.text467', "preview renders this journey's own pages — no authoring chrome, nothing here is editable · Esc or click outside to close")}</div>
        </>
        )}
      </div>
      </div>
    </OverlayPortal>
  ) : null;

  const modals = (
    <>
      {previewOverlay}
      {eventAsk ? (() => {
        const close = () => setEventAsk(null);
        const title = (r0.title || D?.name || "").trim() || t("journeys.newJourneys.publishAsk.untitled", "This event");
        /* Date and time in the event's own zone, with the zone's short name (CDT), as the emails write it. */
        const when = (() => {
          const s = asDate(r0.start); if (!s) return t("journeys.newJourneys.publishAsk.noDate", "No date set");
          const e = asDate(r0.end);
          const day = (d: Date) => new Intl.DateTimeFormat(currentLocale(), { timeZone: schedZone, weekday: "short", day: "numeric", month: "short", year: "numeric" }).format(d);
          const hm = (d: Date) => new Intl.DateTimeFormat(currentLocale(), { timeZone: schedZone, hour: "numeric", minute: "2-digit" }).format(d);
          const zone = (() => { try { return new Intl.DateTimeFormat("en-US", { timeZone: schedZone, timeZoneName: "short" }).formatToParts(s).find((p) => p.type === "timeZoneName")?.value || ""; } catch { return ""; } })();
          if (r0.allDay) return e && day(e) !== day(s) ? `${day(s)} – ${day(e)}` : day(s);
          const range = e ? (day(e) === day(s) ? `${day(s)}, ${hm(s)} – ${hm(e)}` : `${day(s)}, ${hm(s)} – ${day(e)}, ${hm(e)}`) : `${day(s)}, ${hm(s)}`;
          return zone ? `${range} ${zone}` : range;
        })();
        /* Composed the way the events list composes it before the server answers (eventActions): the
           server's canonical address exists only once the event is public. */
        const address = publicUrlForTitle(String((r0 as { slug?: string }).slug || r0.title || D?.name || ""));
        const price = (minor: number, cur: string) => (minor > 0 ? v2Money(minor / 100, cur) : t("journeys.newJourneys.publishAsk.free", "Free"));
        const copy = eventAsk === "publish"
          ? { head: t("journeys.newJourneys.publishAsk.title", "Publish {title}?", { title }), body: t("journeys.newJourneys.publishAsk.body", "It goes live now: the page is public and registration opens."), go: t("journeys.newJourneys.publish", "Publish"), run: () => { void publishNow(); } }
          : eventAsk === "unpublish"
          ? { head: t("journeys.newJourneys.unpublishAsk.title", "Unpublish {title}?", { title }), body: t("journeys.newJourneys.confirmUnpublish", "Unpublish this event? Registration closes and it comes off the public pages until you publish it again."), go: t("journeys.newJourneys.unpublish", "Unpublish"), run: () => { void runTakeOffline(); } }
          : { head: t("journeys.newJourneys.cancelAsk.title", "Cancel {title}?", { title }), body: t("journeys.newJourneys.confirmCancelEvent", "Cancel this event? Registered guests are told it's canceled. This can't be undone."), go: t('journeys.newJourneys.text278', "Cancel event"), run: () => { void runCancel(); } };
        const row = (label: string, value: ReactNode) => (
          <div style={{ display: "grid", gridTemplateColumns: "120px minmax(0, 1fr)", gap: 12, padding: "9px 0", borderTop: "1px solid #EEF0F4", fontSize: 13, lineHeight: 1.45 }}>
            <span style={{ color: "#697180" }}>{label}</span>
            <span style={{ color: "#262C38", minWidth: 0, overflowWrap: "anywhere" }}>{value}</span>
          </div>
        );
        return (
          <OverlayPortal>
            <div onClick={close} onKeyDown={(e) => { if (e.key === "Escape") close(); }} style={{ position: "fixed", inset: 0, zIndex: 270, background: "rgba(20,23,31,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
              <div role="dialog" aria-modal="true" aria-labelledby="nj-event-ask-title" data-testid={`nj-${eventAsk}-dialog`} onClick={(e) => e.stopPropagation()}
                style={{ width: "100%", maxWidth: 480, background: "#FFFFFF", borderRadius: 14, boxShadow: "0 18px 48px rgba(20,23,31,0.22)", padding: "20px 22px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
                <h2 id="nj-event-ask-title" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#262C38", lineHeight: 1.3 }}>{copy.head}</h2>
                <p style={{ margin: 0, fontSize: 13, color: "#4A5160", lineHeight: 1.5 }}>{copy.body}</p>
                {eventAsk === "publish" ? (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {row(t("journeys.newJourneys.publishAsk.when", "When"), when)}
                    {row(t("journeys.newJourneys.publishAsk.address", "Public address"), <span style={{ ...MONO, fontSize: 12 }}>{address}</span>)}
                    {row(t("journeys.newJourneys.publishAsk.tickets", "Tickets on sale"), pvTiers.length
                      ? <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>{pvTiers.map((x) => <span key={x.id} style={{ display: "flex", gap: 8, justifyContent: "space-between" }}><span style={{ minWidth: 0 }}>{x.name}</span><span style={{ color: "#4A5160", flexShrink: 0 }}>{price(x.priceMinor, x.currency)}</span></span>)}</span>
                      : <span style={{ color: "#697180" }}>{t("journeys.newJourneys.publishAsk.noTickets", "No tickets chosen")}</span>)}
                  </div>
                ) : null}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
                  <Button size="sm" variant="secondary" testid="nj-event-ask-cancel" onClick={close}>{eventAsk === "cancel" ? t("journeys.newJourneys.cancelAsk.keep", "Keep event") : t("journeys.newJourneys.publishAsk.cancel", "Cancel")}</Button>
                  <Button size="sm" variant="primary" testid="nj-event-ask-go" onClick={() => { close(); copy.run(); }}>{copy.go}</Button>
                </div>
              </div>
            </div>
          </OverlayPortal>
        );
      })() : null}
      {ui.tplOpen ? (
        <div onClick={() => set({ tplOpen: false })} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={eat} style={{ width: 820, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, padding: "24px 26px", boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 4 }}>{t('journeys.newJourneys.text468', "New journey")}</div>
            <div style={{ fontSize: 12.5, color: "#697180", marginBottom: 18 }}>{t('journeys.newJourneys.text469', "Pick a starting template — every page and step is editable after.")}</div>
            {/* Four columns for the four v1 types. At six the cards were narrow
                enough to wrap a two-word name ("Queue journey") onto two lines. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              {templates.map((t) => (
                <div key={t.id} onClick={() => pickTemplate(t.id)} style={{ display: "flex", flexDirection: "column", border: "1px solid #DCCCFC", borderRadius: 10, padding: "14px 12px", cursor: "pointer", background: "#FCFBFF" }}>
                  <span style={glyphBox(t.tint, t.color, 30, 14)}>{t.glyph}</span>
                  <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 8 }}>{t.name}</div>
                  {/* flex: 1 keeps the badges on one baseline across cards whose
                      descriptions run to different line counts. */}
                  <div style={{ flex: 1, fontSize: 11, color: "#697180", lineHeight: 1.5, marginTop: 3 }}>{t.sub}</div>
                  <span style={{ alignSelf: "flex-start", marginTop: 10, fontSize: 9, fontWeight: 800, letterSpacing: "0.05em", background: "#F4F5F7", color: "#697180", borderRadius: 4, padding: "2px 7px" }}>{t.tagLabel}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {ui.typePicker ? (
        <div onClick={() => set({ typePicker: null })} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 25, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={eat} style={{ width: 620, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, padding: "24px 26px", boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 4 }}>{t('journeys.newJourneys.text470', "Add a page")}</div>
            <div style={{ fontSize: 12.5, color: "#697180", marginBottom: 18 }}>{t('journeys.newJourneys.text471', "Pick what this page is for. A journey can mix page types freely.")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, maxHeight: "58vh", overflow: "auto" }}>
              {/* A One-Stop page is NOT a page type you add to another journey
                  (operator, 2026-09-02). It is a whole journey type of its own,
                  minted by the One-Stop entry in the new-journey picker: it owns
                  the document's slug, its own block registry and its own publish
                  validator, and it is the ONLY page its journey has. Offered
                  here, it invited an author to drop it into an appointment,
                  queue or event flow, where none of that applies. A One-Stop
                  journey never reaches this picker anyway — it has no "+ Add
                  page" — so the type simply has no place in this list. */}
              {REG.filter((t) => t.id !== "onestop").map((t) => (
                <div key={t.id} onClick={() => addPage(t.id, ui.typePicker?.at ?? null)} style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: "13px 12px", cursor: "pointer" }}>
                  <span style={glyphBox(t.tint, t.color, 30, 14)}>{t.glyph}</span>
                  <div style={{ fontSize: 13, fontWeight: 700, marginTop: 8 }}>{t.label}</div>
                  <div style={{ fontSize: 10.5, color: "#697180", lineHeight: 1.5, marginTop: 3 }}>{t.desc}</div>
                  <div style={{ fontSize: 9.5, fontWeight: 700, color: "#B5BAC6", marginTop: 7, textTransform: "uppercase", letterSpacing: "0.05em" }}>{i18nT("journeys.newJourneys.templateCore", "Core: {core}", { core: t.core ?? i18nT("journeys.newJourneys.templateCoreNone", "none — blocks only") })}</div>
                  {t.phase ? <span style={{ display: "inline-block", marginTop: 6, fontSize: 8.5, fontWeight: 800, letterSpacing: "0.05em", background: "#E0F2F1", color: "#0AA396", borderRadius: 4, padding: "2px 7px", whiteSpace: "nowrap" }}>{i18nT("journeys.newJourneys.templatePhase", "NEW · {phase}", { phase: t.phase })}</span> : null}
                </div>
              ))}
            </div>
            <div style={{ ...NOTE, fontSize: 11, marginTop: 14 }}>{t('journeys.newJourneys.text475', "All three verticals' page types are in the registry (phases 2–4) plus the experience hub (phase 5) — none of them touched the shell to land.")}</div>
          </div>
        </div>
      ) : null}

      {ui.expPicker ? (
        <div onClick={() => set({ expPicker: null })} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.35)", zIndex: 25, display: "flex", justifyContent: "flex-end" }}>
          <div onClick={eat} style={{ width: 420, maxWidth: "94vw", height: "100%", background: "#FFFFFF", boxShadow: "-12px 0 40px rgba(20,19,31,0.22)", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid #E6E8EC", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 15.5, fontWeight: 800, flex: 1 }}>{ui.expPicker === "anchor" ? t('journeys.newJourneys.text476', "Pick the anchor journey") : t('journeys.newJourneys.text477', "Add to the Experience hub")}</span>
                <button onClick={() => set({ expPicker: null })} style={{ width: 26, height: 26, border: "none", borderRadius: 6, background: "#F4F5F7", color: "#697180", fontSize: 12, cursor: "pointer" }}>✕</button>
              </div>
              <div style={{ fontSize: 12, color: "#697180", marginTop: 5, lineHeight: 1.5 }}>{t('journeys.newJourneys.text478', "Anything on the journeys list can go in the hub — queues, appointments, events, and other experiences. It stays an independent document; the hub only references it.")}</div>
              <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                {[{ k: "all", label: t('journeys.newJourneys.label58', "All") }, { k: "Queue", label: t('journeys.newJourneys.label59', "Queues") }, { k: "Appointment", label: t('journeys.newJourneys.label60', "Appointments") }, { k: "Event", label: t('journeys.newJourneys.label61', "Events") }, { k: "Experience", label: t('journeys.newJourneys.label15', "Experiences") }].map((tb) => {
                  const on = ui.expPickerTab === tb.k;
                  return <button key={tb.k} onClick={() => set({ expPickerTab: tb.k })} style={{ height: 26, padding: "0 11px", border: `1px solid ${on ? "var(--jrni-color-primary-1, #5142C7)" : "#D1D4DC"}`, borderRadius: 999, background: on ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", color: on ? "var(--jrni-color-primary-text, #5142C7)" : "#333333", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>{tb.label}</button>;
                })}
              </div>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: "14px 20px 20px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {pickerRows.map((r) => (
                  <div key={r.key} onClick={() => {
                    if (ui.expPicker === "anchor") { patchExp((y) => ({ ...y, anchor: r.key })); showToast(t("journeys.newJourneys.toast.anchorSet", "Anchor set — registration there checks attendees in to the whole experience.")); }
                    else { patchExp((y) => ({ ...y, attached: [...y.attached, { doc: r.key, scope: scopeFor(r.ty) }] })); showToast(t("journeys.newJourneys.toast.addedToHub", "Added to the hub — edits to it show here automatically.")); }
                    set({ expPicker: null });
                  }} style={{ display: "flex", alignItems: "flex-start", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "11px 12px", cursor: "pointer" }}>
                    <span style={glyphBox(r.tint, r.color, 28, 13)}>{TYPE_GLYPH[r.ty] ?? "▤"}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{r.name}</span>
                      <span style={{ display: "block", fontSize: 10.5, color: "#697180", marginTop: 2, lineHeight: 1.5 }}>{r.sub}</span>
                    </span>
                    <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flex: "none" }}>
                      <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: r.tint, color: r.color }}>{r.ty}</span>
                      <span style={{ fontSize: 10.5, color: "#B5BAC6" }}>{r.pagesLabel}</span>
                    </span>
                  </div>
                ))}
                {pickerRows.length === 0 ? <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "22px 12px", textAlign: "center", fontSize: 11.5, color: "#697180" }}>{t('journeys.newJourneys.text479', "Everything of this type is already in the hub.")}</div> : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {ui.renameKey ? (
        <div onClick={() => set({ renameKey: "" })} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 34, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={eat} style={{ width: 380, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, padding: 20, display: "flex", flexDirection: "column", gap: 12, boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
            <div style={{ fontSize: 15.5, fontWeight: 800 }}>{t('journeys.newJourneys.text480', "Rename journey")}</div>
            <input autoFocus value={ui.renameVal} onChange={(e) => set({ renameVal: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") renameRow(ui.renameKey, ui.renameVal); if (e.key === "Escape") set({ renameKey: "" }); }} style={INPUT} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => set({ renameKey: "" })} style={GHOST}>{t('journeys.newJourneys.text481', "Cancel")}</button>
              <button onClick={() => renameRow(ui.renameKey, ui.renameVal)} style={PRIMARY}>{t('journeys.newJourneys.text446', "Save")}</button>
            </div>
          </div>
        </div>
      ) : null}

      

      {msgTargets ? (
        <div onClick={() => setMsgTargets(null)} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={eat} style={{ width: 440, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, padding: 20, display: "flex", flexDirection: "column", gap: 12, boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
            <div>
              <div style={{ fontSize: 15.5, fontWeight: 800 }}>{msgTargets.length === 1 ? t('journeys.newJourneys.text482', "Message {name}", { name: msgTargets[0]!.name }) : t('journeys.newJourneys.text483', "Message everyone in the line ({length})", { length: msgTargets.length })}</div>
              <div style={{ fontSize: 11.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>{msgTargets.length === 1 ? t("journeys.newJourneys.sentToChannel", "{ticket, select, none {Sent to their chosen channel.} other {Sent to their chosen channel — ticket {ticket}.}}", { ticket: msgTargets[0]!.ticket || "none" }) : t('journeys.newJourneys.text485', "One message to every waiting, called and serving customer, on each of their chosen channels.")}</div>
            </div>
            <textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} rows={3} autoFocus placeholder={t('journeys.newJourneys.placeholder26', "We're running about 10 minutes behind — thanks for waiting.")} style={{ ...INPUT, resize: "vertical" as const, minHeight: 70 }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setMsgTargets(null)} style={GHOST}>{t('journeys.newJourneys.text481', "Cancel")}</button>
              <button disabled={msgSending || !msgText.trim()} onClick={async () => {
                if (!queueOriginId) return;
                setMsgSending(true);
                try {
                  if (msgTargets.length > 1 && msgTargets.length === qActive.length) await broadcastQueueMessage(queueOriginId, msgText.trim(), { callerIdempotencyKey: newIdempotencyKey() }); // A2 (2026-09-24) — msgSending guards the second click
                  else for (const e of msgTargets) await sendQueueMessage(queueOriginId, e.id, msgText.trim());
                  showToast(msgTargets.length === 1 ? t("journeys.newJourneys.toast.messageSentOne", "Message sent to {name}", { name: msgTargets[0]!.name }) : t("journeys.newJourneys.toast.messageSentMany", "Message sent to {count} queuers", { count: msgTargets.length }));
                  setMsgTargets(null); setMsgText("");
                  if (queueOriginId) getQueueBoard(queueOriginId).then((b: any) => setQBoard(b ?? null)).catch(() => {});
                } catch (e) { showToast(t("journeys.newJourneys.toast.sendFailed", "Couldn't send — {error}", { error: e instanceof Error ? e.message : String(e) })); }
                finally { setMsgSending(false); }
              }} style={{ ...PRIMARY, opacity: msgSending || !msgText.trim() ? 0.55 : 1 }}>{msgSending ? t('journeys.newJourneys.text486', "Sending…") : t('journeys.newJourneys.text487', "Send")}</button>
            </div>
          </div>
        </div>
      ) : null}

      {evMsg ? (
        <div onClick={() => setEvMsg(null)} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={eat} style={{ width: 460, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, padding: 20, display: "flex", flexDirection: "column", gap: 12, boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
            <div>
              <div style={{ fontSize: 15.5, fontWeight: 800 }}>{evMsg.tierId ? t("journeys.newJourneys.messageTicketType", "Message {name}", { name: liveTickets.find((x) => x.id === evMsg.tierId)?.name ?? t("journeys.newJourneys.thisTicketType", "this ticket type") }) : t('journeys.newJourneys.text489', "Message everyone registered")}</div>
              <div style={{ fontSize: 11.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>{t("journeys.newJourneys.registrantsNote", "{count, plural, one {# registrant. Queues through the event's live-messages API — the toast reports the server's receipt, not an assumption.} other {# registrants. Queues through the event's live-messages API — the toast reports the server's receipt, not an assumption.}}", { count: evMsgCount })}</div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {(["email", "sms"] as const).map((c) => (
                <button key={c} onClick={() => setEvMsgChannel(c)} style={{ padding: "5px 13px", borderRadius: 999, border: `1px solid ${evMsgChannel === c ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}`, background: evMsgChannel === c ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", color: evMsgChannel === c ? "var(--jrni-color-primary-text, #5142C7)" : "#333333", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT, textTransform: "uppercase" }}>{c}</button>
              ))}
            </div>
            {evMsgChannel === "email" ? <input value={evMsgSubject} onChange={(e) => setEvMsgSubject(e.target.value)} placeholder={t('journeys.newJourneys.placeholder27', "Subject")} style={INPUT} /> : null}
            <textarea value={evMsgText} onChange={(e) => setEvMsgText(e.target.value)} rows={3} autoFocus placeholder={evMsgChannel === "sms" ? t('journeys.newJourneys.placeholder28', "SMS · keep it under 160 characters.") : t('journeys.newJourneys.placeholder29', "Doors open at 5:30 — bring your student ID.")} style={{ ...INPUT, resize: "vertical" as const, minHeight: 70 }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setEvMsg(null)} style={GHOST}>{t('journeys.newJourneys.text481', "Cancel")}</button>
              <button disabled={evMsgSending || !evMsgText.trim()} onClick={() => { void sendEventMessage(); }} style={{ ...PRIMARY, opacity: evMsgSending || !evMsgText.trim() ? 0.55 : 1 }}>{evMsgSending ? t('journeys.newJourneys.text492', "Queuing…") : t('journeys.newJourneys.text493', "Send to {evMsgCount}", { evMsgCount })}</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* The ticket-types picker is retired with the old library (CC 0195). */}
      {ui.lib ? (() => {
        const m = LIB_META[ui.lib]!;
        const list = libRows(ui.lib);
        return (
          <div onClick={() => set({ lib: null })} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div onClick={eat} style={{ width: 540, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, overflow: "hidden", boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 22px", borderBottom: "1px solid #E6E8EC" }}>
                <span style={glyphBox(m.tint, m.color, 32, 14)}>{m.glyph}</span>
                <div><div style={{ fontSize: 14.5, fontWeight: 800 }}>{m.title}</div><div style={{ fontSize: 11, color: "#697180" }}>{m.lib} {t('journeys.newJourneys.text499', "— pick what this journey references")}</div></div>
                <span style={{ flex: 1 }} />
                <button onClick={() => set({ lib: null })} style={{ width: 28, height: 28, border: "none", background: "transparent", fontSize: 15, color: "#697180", cursor: "pointer" }}>✕</button>
              </div>
              <div style={{ padding: "14px 22px", maxHeight: "56vh", overflow: "auto" }}>
                {list.length ? list.map((r0row) => {
                  /* Tickets: the row is only a choice until Done (CC 0280). */
                  const r = ui.lib === "tickets" ? { ...r0row, on: libPending ? r0row.id === libPending : r0row.on } : r0row;
                  return (
                  <div key={r.id} aria-disabled={(r as { disabled?: boolean }).disabled ? true : undefined} onClick={() => { if ((r as { disabled?: boolean }).disabled) return; if (ui.lib === "tickets") { setLibPending(r.id); return; } r.pick(); set({ lib: null }); }} style={{ display: "flex", alignItems: "center", gap: 10, border: `1px solid ${r.on ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}`, background: r.on ? "#FCFBFF" : "#FFFFFF", borderRadius: 8, padding: "10px 12px", marginBottom: 6, cursor: (r as { disabled?: boolean }).disabled ? "default" : "pointer", opacity: (r as { disabled?: boolean }).disabled ? 0.55 : 1 }}>
                    <span style={{ width: 15, height: 15, borderRadius: 999, border: `1px solid ${r.on ? "var(--jrni-color-primary-text, #5142C7)" : "#D1D4DC"}`, background: r.on ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: "#FFFFFF", fontSize: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{r.on ? "✓" : ""}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{r.name}</span>
                      <span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{r.meta}</span>
                    </span>
                    <a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); r.edit(); }} style={{ fontSize: 11, fontWeight: 700, flex: "none" }}>{t("journeys.newJourneys.editArrow", "Edit →")}</a>
                  </div>
                  );
                }) : (
                  <div style={{ border: "1px dashed #D1D4DC", borderRadius: 8, padding: "22px 12px", textAlign: "center", fontSize: 11.5, color: "#697180" }}>
                    {t("journeys.newJourneys.nothingInLibrary", "Nothing in the {lib} yet.", { lib: m.lib.toLowerCase() })}<div style={{ marginTop: 10 }}><button onClick={() => goRoute(m.route)} style={GHOST}>{t("journeys.newJourneys.openLibrary", "Open the {lib} →", { lib: m.lib.toLowerCase() })}</button></div>
                  </div>
                )}
                <div style={{ ...NOTE, fontSize: 11, marginTop: 10 }}>{t('journeys.newJourneys.text503', "This journey points at the record — edit it in its own screen and the change shows here.")}</div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "14px 22px", borderTop: "1px solid #E6E8EC" }}>
                <button onClick={() => goRoute(m.route)} style={{ ...GHOST, border: "none", background: "transparent", color: "var(--jrni-color-primary-text, #5142C7)", fontWeight: 700 }}>{t('journeys.newJourneys.text497', "Open the full library →")}</button>
                <button onClick={() => {
                  if (ui.lib === "tickets" && libPending) {
                    const chosen = list.find((x) => x.id === libPending);
                    if (chosen && !chosen.on && !(chosen as { disabled?: boolean }).disabled) chosen.pick();
                  }
                  set({ lib: null });
                }} style={PRIMARY}>{t('journeys.newJourneys.text498', "Done")}</button>
              </div>
            </div>
          </div>
        );
      })() : null}
      {resumeAsk ? (
        <div onClick={() => { if (resumeDiscard !== "busy") { setResumeAsk(null); setResumeDiscard(null); } }} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div role="dialog" aria-modal="true" aria-label="Unfinished event draft" data-testid="nj-resume-ask" onClick={eat}
            style={{ width: 440, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, padding: "20px 22px", display: "flex", flexDirection: "column", gap: 12, boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>You have an unfinished event draft</div>
            <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>
              "{docs[resumeAsk]?.name || "Untitled event"}" was never saved as an event. Continue it, or start a new event and leave it in your journeys.
            </div>
            {resumeDiscard ? (
              /* The confirm step (JAI-2553). The delete is awaited: the draft leaves the list only once the server
                 has removed it, and a refusal keeps it and says so, so nothing looks discarded that is not. */
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#333333" }}>{t("journeys.newJourneys.resume.discardTitle", "Discard this draft?")}</div>
                <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>{t("journeys.newJourneys.resume.discardBody", "\"{name}\" will be deleted and can't be recovered.", { name: docs[resumeAsk]?.name || t("journeys.newJourneys.resume.untitled", "Untitled event") })}</div>
                {resumeDiscard === "failed" ? <div role="alert" data-testid="nj-resume-discard-failed" style={{ fontSize: 12, fontWeight: 600, color: "#B42318" }}>{t("journeys.newJourneys.resume.discardFailed", "Couldn't discard the draft. It's still in your journeys. Try again.")}</div> : null}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" data-testid="nj-resume-discard-keep" disabled={resumeDiscard === "busy"} onClick={() => setResumeDiscard(null)} style={{ ...GHOST, opacity: resumeDiscard === "busy" ? 0.6 : 1 }}>{t("journeys.newJourneys.resume.keepDraft", "Keep draft")}</button>
                  <button type="button" data-testid="nj-resume-discard-confirm" disabled={resumeDiscard === "busy"} aria-busy={resumeDiscard === "busy"}
                    onClick={async () => {
                      const key = resumeAsk;
                      if (!key || resumeDiscard === "busy") return;
                      setResumeDiscard("busy");
                      try { await deleteJourneyDoc(slug, key); }
                      catch { setResumeDiscard("failed"); return; }
                      setOverlays((o) => { const n = { ...(o ?? {}) }; delete n[key]; return n; });
                      setResumeDiscard(null); setResumeAsk(null);
                      showToast(t("journeys.newJourneys.toast.draftDiscarded", "Draft discarded"));
                      pickTemplate("event", undefined, { fresh: true });
                    }}
                    style={{ ...PRIMARY, background: "#B42318", opacity: resumeDiscard === "busy" ? 0.7 : 1, cursor: resumeDiscard === "busy" ? "default" : "pointer" }}>
                    {resumeDiscard === "busy" ? t("journeys.newJourneys.resume.discarding", "Discarding…") : t("journeys.newJourneys.resume.discardDraft", "Discard draft")}
                  </button>
                </div>
              </>
            ) : (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
              <button type="button" data-testid="nj-resume-discard" onClick={() => setResumeDiscard("confirm")} style={{ ...GHOST, color: "#B42318", marginRight: "auto" }}>{t("journeys.newJourneys.resume.discardDraft", "Discard draft")}</button>
              <button type="button" data-testid="nj-resume-new" onClick={() => { setResumeAsk(null); pickTemplate("event", undefined, { fresh: true }); }} style={GHOST}>Start a new event</button>
              <button type="button" data-testid="nj-resume-continue" onClick={() => {
                const key = resumeAsk; setResumeAsk(null);
                if (key && docs[key]) set({ tplOpen: false, route: "designer", curDoc: key, view: "pages", cur: 0, sel: null, selStep: null, setupView: "basics" as any });
              }} style={PRIMARY}>Continue draft</button>
            </div>
            )}
          </div>
        </div>
      ) : null}

    </>
  );


  const onestop = null;
  void onestop;

  return shell(<>
    {ui.route === "home" || !D ? home : ui.route === "designer" ? designer : experience ?? home}
    {modals}
  </>);
}
